import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
/// <reference path="../../types/wiz-view-modules.d.ts" />

import { OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { FaceLandmarker, PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import {
    OVERLAY_TRANSFORM_VERSION,
    OverlayCoordinateSpace,
    OverlaySourceTransform,
    RenderedVideoRect,
    calculateContainedVideoRect,
    canvasPointToSourcePoint,
    clampSourcePolygon,
    coordinatePointToSourcePoint,
    resizeCanvasForDisplay,
    sourcePointToCanvasPoint,
} from './overlay-coordinate';
import { normalizedBboxIou, retainedPoseState, scoreRealtimeTrackMatch } from './realtime-track';
import { evaluateFastFallGate } from './realtime-fast-path';

declare const wiz: any;

const PRIVACY_VIEW_STORAGE_KEY = 'fallai:privacy-view-v2';
const LEGACY_PRIVACY_VIEW_STORAGE_KEY = 'fallai:admin-privacy-view';
const OVERLAY_MODE_STORAGE_KEY = 'fallai:overlay-mode-v1';
const CLEAN_OVERLAY_CLASSES_STORAGE_KEY = 'fallai:clean-overlay-classes-v1';
const HIGH_PASS_STRENGTH_STORAGE_KEY = 'fallai:privacy-high-pass-strength-v2';
const LOW_PASS_STRENGTH_STORAGE_KEY = 'fallai:privacy-low-pass-strength-v2';
const AGENT_WEIGHTS_STORAGE_KEY = 'fallai:agent-weights-v1';
const AGENT_WEIGHTS_CONFIRMED_STORAGE_KEY = 'fallai:agent-weights-confirmed-v1';
const MODEL_REGISTRY_CACHE_KEY = 'fallai:model-registry:v2';
const POSE_DATASETS_CACHE_KEY = 'fallai:pose-datasets:v1';
const FALL_DETECTION_STUDIO_CACHE_KEY = 'fallai:fall-detection-studio:v1';
const POSE_TRAINING_MODEL_KEY = 'fallai-pose-training-base-model';
const POSE_TRAINING_SETTINGS_KEY = 'fallai:pose-training-settings:v1';

const CLEAN_OVERLAY_CONFIG = Object.freeze({
    confirmedMinConfidence: 0.35,
    stableFixtureMinConfidence: 0.30,
    repeatedTentativeMinHits: 3,
    repeatedTentativeMinStability: 0.70,
    stableFixtureMinStability: 0.65,
    minAreaRatio: 0.0008,
    hazardMinAreaRatio: 0.0002,
    contourEpsilonRatio: 0.009,
    maskAlpha: 0.25,
    outlineWidth: 1.5,
    duplicateIou: 0.60,
    maxSummaryItems: 4,
    maxInlineLabels: 10,
});
const DEFAULT_CLEAN_OVERLAY_CLASSES = [
    'facility_bed', 'hospital_bed', 'bed', 'wheelchair', 'walker', 'chair',
    'curtain', 'privacy_curtain', 'door', 'floor_mat', 'floor_mat_candidate',
    'camera_tripod', 'tripod', 'camera', 'iv_pole', 'monitor',
    'equipment_cart', 'medical_cart', 'cart',
    'air_conditioner', 'table', 'cabinet', 'waste_bin',
    'sink', 'toilet', 'refrigerator', 'couch', 'sofa',
    'obstacle', 'cable_on_floor', 'blocked_path',
];
const ENVIRONMENT_CLASS_COLORS: Record<string, string> = Object.freeze({
    facility_bed: '#2563eb', curtain: '#a855f7',
    camera_tripod: '#06b6d4', air_conditioner: '#94a3b8',
    camera: '#0891b2', iv_pole: '#0d9488', monitor: '#6366f1',
    equipment_cart: '#8b5cf6', medical_cart: '#7c3aed', cart: '#9333ea',
    wheelchair: '#22c55e', walker: '#2dd4bf',
    chair: '#facc15', door: '#38bdf8', floor_mat: '#f97316',
    table: '#eab308', cabinet: '#a16207', waste_bin: '#64748b',
    sink: '#67e8f9', toilet: '#e2e8f0', refrigerator: '#cbd5e1', couch: '#ec4899',
    obstacle: '#ef4444', cable_on_floor: '#dc2626', blocked_path: '#f43f5e',
});
const CLEAN_CLASS_MIN_AREA_RATIO: Record<string, number> = Object.freeze({
    facility_bed: 0.002, floor_mat: 0.002, curtain: 0.001, door: 0.001,
    camera_tripod: 0.0003, cable_on_floor: 0.0001, obstacle: 0.0002,
    walker: 0.0005, wheelchair: 0.0005, air_conditioner: 0.0005,
});
const CLEAN_THIN_STRUCTURE_CLASSES = new Set<string>(['camera_tripod', 'cable_on_floor', 'walker', 'wheelchair']);
const ENVIRONMENT_RESULT_TTL_MS = 2500;
const ENVIRONMENT_TIMESTAMP_TOLERANCE_MS = 1500;
const POSE_TIMESTAMP_TOLERANCE_MS = 400;
const POSTURE_TIMESTAMP_TOLERANCE_MS = 600;
const FAST_FALL_TIMESTAMP_TOLERANCE_MS = 800;
const PERSON_TRACK_TTL_MS = 1200;
const POSE_HOLD_MS = 700;
const TRACK_RECOVERY_WINDOW_MS = 1500;
const FRAME_CALLBACK_WATCHDOG_MS = 500;
const ENVIRONMENT_INFERENCE_FPS = 1.5;
const ENVIRONMENT_CLASS_MAX_AREA_RATIO: Record<string, number> = Object.freeze({
    facility_bed: 0.82,
    floor_mat: 0.72,
    curtain: 0.88,
    door: 0.82,
    camera_tripod: 0.28,
    cable_on_floor: 0.18,
    walker: 0.36,
    wheelchair: 0.48,
});
const SKELETON_PART_COLORS = Object.freeze({
    head: '#ef4444', neck: '#f97316', torso: '#facc15', arm: '#22c55e',
    pelvis: '#14b8a6', leg: '#3b82f6', foot: '#a855f7',
});

// ── COCO-17 Skeleton Constants ──
const COCO_SKELETON_PAIRS: [number, number][] = [
    [0, 1], [0, 2], [1, 3], [2, 4],
    [5, 6], [5, 7], [7, 9], [6, 8], [8, 10],
    [5, 11], [6, 12], [11, 12],
    [11, 13], [13, 15], [12, 14], [14, 16],
];
const COCO_KP_COLORS: Record<number, string> = {
    0: SKELETON_PART_COLORS.head, 1: SKELETON_PART_COLORS.head, 2: SKELETON_PART_COLORS.head,
    3: SKELETON_PART_COLORS.head, 4: SKELETON_PART_COLORS.head,
    5: SKELETON_PART_COLORS.torso, 6: SKELETON_PART_COLORS.torso,
    7: SKELETON_PART_COLORS.arm, 8: SKELETON_PART_COLORS.arm,
    9: SKELETON_PART_COLORS.arm, 10: SKELETON_PART_COLORS.arm,
    11: SKELETON_PART_COLORS.pelvis, 12: SKELETON_PART_COLORS.pelvis,
    13: SKELETON_PART_COLORS.leg, 14: SKELETON_PART_COLORS.leg,
    15: SKELETON_PART_COLORS.foot, 16: SKELETON_PART_COLORS.foot,
};
const COCO_HAND_KEYPOINTS = new Set<number>([9, 10]);
const COCO_FACE_KEYPOINTS = new Set<number>([0, 1, 2, 3, 4]);

// ── MediaPipe 33-landmark skeleton ──
const MP_POSE_CONNECTIONS: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 7],
    [0, 4], [4, 5], [5, 6], [6, 8],
    [9, 10],
    [11, 12],
    [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
    [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
    [11, 23], [12, 24], [23, 24],
    [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
    [24, 26], [26, 28], [28, 30], [28, 32], [30, 32],
];

const MP_LANDMARK_COLORS: Record<number, string> = (() => {
    const c: Record<number, string> = {};
    for (let i = 0; i <= 10; i++) c[i] = SKELETON_PART_COLORS.head;
    c[11] = SKELETON_PART_COLORS.torso; c[12] = SKELETON_PART_COLORS.torso;
    for (let i = 13; i <= 22; i++) c[i] = SKELETON_PART_COLORS.arm;
    c[23] = SKELETON_PART_COLORS.pelvis; c[24] = SKELETON_PART_COLORS.pelvis;
    for (let i = 25; i <= 28; i++) c[i] = SKELETON_PART_COLORS.leg;
    for (let i = 29; i <= 32; i++) c[i] = SKELETON_PART_COLORS.foot;
    return c;
})();
const MP_WRIST_LANDMARKS = new Set<number>([15, 16]);
const MP_HAND_DETAIL_LANDMARKS = new Set<number>([17, 18, 19, 20, 21, 22]);
const MP_FACE_LANDMARKS = new Set<number>([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
const MP_TO_COCO17 = [0, 2, 5, 7, 8, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
const COCO17_CONNECTIONS: [number, number][] = [
    [5, 6], [5, 7], [7, 9], [6, 8], [8, 10],
    [5, 11], [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
    [0, 1], [0, 2], [1, 3], [2, 4],
];

const MP_INPUT_SIZE = 320;
const REALTIME_ANALYSIS_INPUT_SIZE = 480;
const MP_MAX_POSES = 20;
const MP_MAX_FACES = 2;
const MP_VISION_WASM_URL = '/assets/mediapipe/wasm';
const MP_POSE_MODEL_URL = '/assets/mediapipe/models/pose_landmarker_lite.task';
const MP_FACE_MODEL_URL = '/assets/mediapipe/models/face_landmarker.task';
const MP_PERSON_COLORS = [
    '#67e8f9', '#f0abfc', '#86efac', '#fbbf24', '#a78bfa',
    '#fb7185', '#38bdf8', '#34d399', '#f97316', '#c084fc',
    '#2dd4bf', '#facc15', '#60a5fa', '#f472b6', '#4ade80',
    '#e879f9', '#22d3ee', '#a3e635', '#fdba74', '#93c5fd',
];

// FaceLandmarker blendshape 기반 실시간 표시 전용 8종 표정입니다.
// 낙상 점수에는 관여하지 않고, 서버 AI-Hub 82 불편/비불편 보조 모델과 독립적으로 동작합니다.
const LIVE_FACIAL_EMOTIONS = [
    { code: 'neutral', label: '중립', color: '#2dd4bf' },
    { code: 'happiness', label: '기쁨', color: '#22c55e' },
    { code: 'surprise', label: '놀람', color: '#38bdf8' },
    { code: 'sadness', label: '슬픔', color: '#818cf8' },
    { code: 'anger', label: '분노', color: '#f43f5e' },
    { code: 'disgust', label: '불쾌', color: '#a3e635' },
    { code: 'fear', label: '공포', color: '#a78bfa' },
    { code: 'contempt', label: '경멸', color: '#f59e0b' },
] as const;

// ── Kalman 1D Filter (constant-velocity model) for occluded landmark prediction ──
class Kalman1D {
    private _pos = 0;
    private _vel = 0;
    private _p00 = 1;
    private _p01 = 0;
    private _p10 = 0;
    private _p11 = 1;
    private _init = false;

    reset() {
        this._init = false;
        this._pos = 0;
        this._vel = 0;
    }

    predict(): number {
        if (!this._init) return 0;
        this._pos += this._vel;
        this._p00 += this._p01 + this._p10 + this._p11 + 0.0005;
        this._p01 += this._p11;
        this._p10 += this._p11;
        this._p11 += 0.005;
        return this._pos;
    }

    update(z: number): number {
        const R = 0.003;
        if (!this._init) {
            this._pos = z;
            this._vel = 0;
            this._p00 = R;
            this._p01 = 0;
            this._p10 = 0;
            this._p11 = 0.1;
            this._init = true;
            return z;
        }
        const s = this._p00 + R;
        const k0 = this._p00 / s;
        const k1 = this._p10 / s;
        const y = z - this._pos;
        this._pos += k0 * y;
        this._vel += k1 * y;
        const p00 = this._p00 * (1 - k0);
        const p01 = this._p01 * (1 - k0);
        this._p10 -= k1 * this._p00;
        this._p11 -= k1 * this._p01;
        this._p00 = p00;
        this._p01 = p01;
        return this._pos;
    }

    get initialized(): boolean { return this._init; }
    get position(): number { return this._pos; }
}

type MpLandmarkFilters = { x: Kalman1D; y: Kalman1D; missCount: number }[];
type MpPoseState = 'LIVE' | 'HELD' | 'PREDICTED' | 'LOST';
type PersistentRenderPose = {
    trackId: number;
    logicalPersonId: string;
    sourcePoseId: string;
    renderEligible: boolean;
    landmarks: any[];
    poseState: MpPoseState | 'RECOVERING';
    poseAlpha: number;
    updatedAtMs: number;
    validUntilMs: number;
    velocityX: number;
    velocityY: number;
    bbox: number[];
};
type PoseDropReason = 'NO_FRAME_CAPTURE' | 'POSE_REQUEST_SKIPPED' | 'POSE_REQUEST_IN_FLIGHT'
    | 'POSE_RESPONSE_EMPTY' | 'POSE_LOW_CONFIDENCE' | 'POSE_RESULT_STALE'
    | 'TRACK_ASSOCIATION_FAILED' | 'CANVAS_RENDER_SKIPPED'
    | 'CANVAS_CLEARED_AFTER_RENDER' | 'VIDEO_TIMESTAMP_MISMATCH' | '';
type PoseFrameEnvelope = {
    capturedAt: number;
    videoTimestampMs: number;
    frameId: number;
    eventCandidate: boolean;
};
type MpPoseTrack = {
    id: number;
    createdAtMs: number;
    centerX: number;
    centerY: number;
    previousCenterX: number;
    previousCenterY: number;
    velocityX: number;
    velocityY: number;
    bbox: number[];
    torsoX: number;
    torsoY: number;
    shoulderX: number;
    shoulderY: number;
    hipX: number;
    hipY: number;
    lastSeenMs: number;
    lastPoseSeenMs: number;
    missed: number;
    hits: number;
    lastLandmarks: any[];
    poseState: MpPoseState;
    recoveryReason: string;
    lastBboxIou: number;
    lastCenterDistance: number;
    lastTrackIouThreshold: number;
    recoveryFromLandmarks: any[];
    recoveryFramesRemaining: number;
    logicalPersonId: string;
    mergedIntoTrackId: number;
    renderEligible: boolean;
};
type EnvironmentDetection = {
    id: string;
    classId: string;
    label: string;
    confidence: number;
    sourceFrameWidth: number;
    sourceFrameHeight: number;
    bboxSource: { x1: number; y1: number; x2: number; y2: number };
    polygonSource?: { x: number; y: number }[][];
    sourceTimestampMs: number;
    frameId: number;
    videoId: string;
    analysisRunId: string;
    source: 'realtime' | 'final_analysis' | 'cached' | 'tracked';
    transformVersion: string;
    coordinateSpace: 'source_pixel';
};
type RealtimePerformancePreset = 'quality' | 'balanced' | 'performance';
type RealtimePerfSample = { at: number; value: number };
type LiveFacialCandidate = { code: string; label: string; score: number; percent: number; color: string };
type TrainingFallLabel = 'Y' | 'N';
type TrainingPostureClass = '' | 'stand' | 'walk' | 'run' | 'sit' | 'lie';
type TrainingFileAssignment = { fallLabel: TrainingFallLabel; postureClass: TrainingPostureClass };
type PrivacyViewMode = 'original' | 'skeleton' | 'skeleton-raw' | 'high' | 'low';
type OverlayMode = 'NONE' | 'SKELETON' | 'ENVIRONMENT' | 'SKELETON_ENVIRONMENT' | 'DEBUG_RAW';
type AgentWeightKey = 'behavior' | 'environment' | 'emotion';
type AgentWeights = Record<AgentWeightKey, number>;
type AgentWeightPreset = 'realtime' | 'balanced' | 'precision' | 'custom';
type RealtimeBenchmarkPreset = Exclude<AgentWeightPreset, 'custom'>;

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
:host {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;
}

.upload-analysis-grid {
  display: grid;
  gap: 1.25rem;
  align-items: start;
}

@media (min-width: 1280px) {
  .upload-analysis-grid {
    grid-template-columns: minmax(0, 1.04fr) minmax(390px, 0.96fr);
    align-items: stretch;
  }
}
.upload-control-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
}

.upload-settings-panel {
  flex: 1 1 auto;
}

.upload-preview-video-raw {
  position: relative;
  z-index: 0;
}

.upload-preview-video-skeleton {
  position: relative;
  z-index: 5;
  clip-path: inset(calc(100% - 74px) 0 0 0);
  color-scheme: dark;
}

.upload-preview-video-skeleton::-webkit-media-controls-enclosure,
.upload-preview-video-skeleton::-webkit-media-controls-panel {
  background: rgba(0, 0, 0, 0.92);
}

.low-pass-filter-overlay {
  position: absolute;
  inset: 0 0 54px;
  z-index: 1;
  background: rgba(0, 0, 0, 0.001);
}

.upload-preview-video-low {
  position: relative;
  z-index: 0;
  --low-pass-controls-filter: none;
}

.upload-preview-video-low::-webkit-media-controls-enclosure,
.upload-preview-video-low::-webkit-media-controls-panel {
  -webkit-backdrop-filter: var(--low-pass-controls-filter);
  backdrop-filter: var(--low-pass-controls-filter);
}

.privacy-filter-range {
  min-width: 8rem;
  accent-color: #2563eb;
  cursor: pointer;
}

.clean-overlay-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  flex: 0 0 auto;
  border-radius: 9999px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.clean-overlay-legend {
  position: absolute;
  right: 0.75rem;
  bottom: 6rem;
  z-index: 18;
  min-width: 8.5rem;
  max-width: 12rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  background: rgba(9, 9, 11, 0.64);
  padding: 0.55rem 0.65rem;
  color: rgb(228, 228, 231);
  font-size: 0.65rem;
  pointer-events: none;
  backdrop-filter: blur(8px);
}

.clean-overlay-legend-toggle {
  position: absolute;
  right: 0.75rem;
  bottom: 3.35rem;
  z-index: 19;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 9999px;
  background: rgba(9, 9, 11, 0.68);
  padding: 0.35rem 0.65rem;
  color: rgb(228, 228, 231);
  font-size: 0.65rem;
  font-weight: 700;
  backdrop-filter: blur(8px);
}

.clean-overlay-legend-toggle:hover {
  background: rgba(24, 24, 27, 0.86);
}

.clean-overlay-legend-title {
  margin-bottom: 0.35rem;
  color: rgb(250, 250, 250);
  font-weight: 700;
}

.clean-overlay-legend-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

@media (max-width: 720px) {
  .clean-overlay-legend {
    display: none;
  }
  .clean-overlay-legend-toggle {
    display: none;
  }
}`],
})
export class PageDashboardComponent implements OnInit, OnDestroy {
    public prototypeInfo: any = null;
    public inputMode: string = 'upload';
    public analysisProfile: string = 'balanced';
    public selectedModelType: string = 'rf-dual';
    public loadingInfo: boolean = true;
    public privacyViewMode: PrivacyViewMode = 'skeleton';
    public overlayMode: OverlayMode = 'NONE';
    public agentWeightManagerOpen: boolean = false;
    public agentWeightPreset: AgentWeightPreset = 'balanced';
    public agentWeights: AgentWeights = { behavior: 60, environment: 30, emotion: 10 };
    public agentWeightsConfirmed: boolean = false;
    public agentWeightsDirty: boolean = false;
    public cleanOverlayShowClasses: string[] = [...DEFAULT_CLEAN_OVERLAY_CLASSES];
    public cleanLegendOpen: boolean = false;
    private lastOverlayComparisonSignature: string = '';
    private cleanStableCanonicalClass: Record<string, string> = {};
    private latestCleanRenderedObjects: any[] = [];
    public readonly overlayModeOptions: { value: OverlayMode; label: string }[] = [
        { value: 'NONE', label: '표시 없음' },
        { value: 'SKELETON', label: 'Skeleton' },
        { value: 'ENVIRONMENT', label: '환경 객체' },
        { value: 'SKELETON_ENVIRONMENT', label: 'Skeleton + 환경 객체' },
        { value: 'DEBUG_RAW', label: 'Debug Raw' },
    ];

    public selectedFile: File | null = null;
    public selectedFiles: File[] = [];
    public selectedVideoUrl: string = '';
    public uploadWorkflowMode: 'analyze' | 'train' = 'train';
    public trainingTargetModel: string = 'pose-backbone';
    public videoMeta: any = { duration: 0, width: 0, height: 0, fps: 0 };
    public analysisSavedName: string = '';
    public dragover: boolean = false;
    public analyzing: boolean = false;
    public uploadProgress: number = 0;
    private analysisProgressTimer: any = null;
    private analysisAbortController: AbortController | null = null;
    public analysisResult: any = null;
    public rootAnalysisResult: any = null;
    public selectedPersonAnalysisId: string = 'overall';
    public errorMessage: string = '';
    public uploadLogEntries: any[] = [];
    public uploadChunkSummary: any = null;
    public selectedShadowEvent: any = null;
    public shadowWindowsOpen: boolean = false;
    public shadowFeedbackSubmitting: boolean = false;
    public shadowFeedbackMessage: string = '';
    public shadowFeedbackMessageType: 'success' | 'error' | 'info' = 'info';

    public feedbackStatus: string = '';
    public feedbackActualLabel: string = '';
    public feedbackPostureClass: string = '';
    public feedbackNote: string = '';
    public feedbackSubmitting: boolean = false;
    public feedbackRetrainDone: boolean = false;
    public feedbackRetrainRequested: boolean = false;
    public feedbackMessage: string = '';
    public feedbackMessageType: string = 'info';
    public feedbackAmbiguityFlag: boolean = false;
    public feedbackOcclusionFlag: boolean = false;
    public feedbackShortClipFlag: boolean = false;
    public feedbackStaticFilterLabel: string = '';
    public feedbackElapsedSec: number = 0;
    private feedbackTimerHandle: any = null;
    public entityCorrectionSubmitting: Record<string, boolean> = {};
    public entityCorrectionMessage: string = '';
    public entityCorrectionMessageType: 'success' | 'error' | 'info' = 'info';
    private entityCorrectionMessageTimer: any = null;
    public movingObjectShadowQueueOpen: boolean = false;
    public movingObjectShadowQueueItems: any[] = [];
    public movingObjectShadowQueueLoading: boolean = false;
    public movingObjectShadowQueueLoadingMore: boolean = false;
    public movingObjectShadowQueueError: string = '';
    public movingObjectShadowQueueStatus: 'pending' | 'reviewed' | 'all' = 'pending';
    public movingObjectShadowQueueWouldVeto: 'all' | 'true' = 'true';
    public movingObjectShadowQueueNextCursor: string = '';
    public movingObjectShadowQueueHasMore: boolean = false;
    public readonly movingObjectShadowQueueLimit: number = 50;

    public trainingUploadLabel: string = 'N';
    public trainingUploadPostureClass: string = '';
    public trainingUploadNote: string = '';
    public trainingFileAssignments: Record<string, TrainingFileAssignment> = {};
    public trainingFilePage: number = 0;
    public readonly trainingFilePageSize: number = 50;
    public trainingUploading: boolean = false;
    public trainingUploadDone: number = 0;
    public trainingUploadTotal: number = 0;
    public trainingUploadMessage: string = '';
    public trainingUploadMessageType: string = 'info';
    private lastTrainingRegistrationTargetModel: string = '';
    public trainingJob: any = null;
    public continuousTrainingStatus: any = null;
    public continuousTrainingItems: any[] = [];
    public backgroundTrainingExpanded: boolean = false;
    public continuousTrainingResuming: Record<string, boolean> = {};
    public trainingJobStarting: boolean = false;
    public trainingJobApplying: boolean = false;
    public poseStudioRegistry: any = null;
    public poseStudioBaseModelId: string = '';
    public poseStudioModelCategory: string = 'all';
    public poseStudioDatasetId: string = '';
    public poseStudioDatasetName: string = '';
    public poseStudioDatasetPath: string = '';
    public poseStudioDatasetSourceType: string = 'aihub-fall';
    public poseStudioDatasetRegistrationMode: 'reference' | 'video-reference' | 'zip' | 'aihub-download' = 'reference';
    public aihubDatasetKey: string = '';
    public aihubFileKeys: string = '';
    public aihubApiKey: string = '';
    public aihubApiKeyVisible: boolean = false;
    public aihubDownloadConfig: any = null;
    public aihubDownloadJob: any = null;
    public aihubDownloadActionBusy: boolean = false;
    private aihubDownloadPollHandle: any = null;
    public poseStudioVideoSampleFps: number = 2;
    public poseStudioDatasetEditingId: string = '';
    public poseStudioDatasetFile: File | null = null;
    public poseStudioDatasetFormOpen: boolean = false;
    public poseStudioDatasetRegistrationReturnToFall: boolean = false;
    public poseStudioDatasetUploadProgress: number = 0;
    public poseStudioDatasetUploadStage: string = '';
    public poseStudioDatasetPreview: any = null;
    public poseStudioDatasetPreviewBusy: boolean = false;
    private poseStudioDeletedDatasetIds: Set<string> = new Set();
    public poseStudioEpochs: number = 100;
    public poseStudioBatch: number = 16;
    public poseStudioImageSize: number = 640;
    public poseStudioLearningRate: number = 0.001;
    public poseStudioOptimizer: string = 'AdamW';
    public poseStudioDevice: string = 'auto';
    public poseStudioPreset: 'fast' | 'balanced' | 'accuracy' | 'custom' = 'balanced';
    public poseStudioBenchmarkManifest: string = '';
    public poseStudioTrainingSettingsSignature: string = '';
    public poseStudioTrainingSettingsSavedAt: string = '';
    public poseStudioEvaluationBusy: boolean = false;
    public poseHistoryCompareLeftId: string = '';
    public poseHistoryCompareRightId: string = '';
    public poseStudioBusy: boolean = false;
    public poseStudioMessage: string = '';
    public poseStudioMessageType: string = 'info';
    public poseStudioJob: any = null;
    public trainingPipelineType: 'dataset_management' | 'fall_detection' = 'fall_detection';
    public poseModelTrainingUiEnabled: boolean = false;
    public trainingModeEntering: boolean = false;
    private trainingModeBootstrapRunning: boolean = false;
    public fallDetectionStudio: any = null;
    public fallDetectionDatasetIds: string[] = [];
    public fallDetectionDatasetCandidateId: string = '';
    public fallDetectionNormalSupplementIds: string[] = [];
    public fallDetectionNormalSupplementCandidateId: string = '';
    private fallDetectionNormalSupplementsInitialized: boolean = false;
    public fallDetectionPoseModelId: string = '';
    public fallDetectionClassifierTypes: string[] = [];
    public fallDetectionFps: number = 6;
    public fallDetectionWindowSize: number = 1;
    public fallDetectionStride: number = 0.5;
    public fallDetectionTracking: boolean = true;
    public fallDetectionKeypointConfidence: number = 0.25;
    public fallDetectionPersonVisibility: number = 0;
    public fallDetectionAdvancedSettingsOpen: boolean = false;
    public fallDetectionJob: any = null;
    public fallDetectionBusy: boolean = false;
    public fallDetectionValidationBusy: boolean = false;
    public fallDetectionMessage: string = '';
    public fallDetectionMessageType: string = 'info';
    public fallDetectionDeletingJobId: string = '';
    public fallDetectionStoppingJobId: string = '';
    public fallDetectionCompletedMessage: string = '';
    public fallCandidateTestJobId: string = '';
    public fallCandidateDetailView: 'raw' | 'final' | 'improvement' = 'final';
    public fallCandidateTestFile: File | null = null;
    public fallCandidateServerVideoId: string = '';
    public fallCandidateTestVideoUrl: string = '';
    public fallCandidateTestVideoDurationSec: number = 0;
    public fallCandidateTestBusy: boolean = false;
    public fallCandidateTestProgress: number = 0;
    public fallCandidateTestMessage: string = '';
    public fallCandidateTestResult: any = null;
    public fallCandidateTestCompleting: boolean = false;
    public fallCandidateBatchStarting: boolean = false;
    private fallCandidateTestProgressHandle: any = null;
    private fallCandidatePoseAnimationHandle: number | null = null;
    private poseStudioPollHandle: any = null;
    private trainingJobPollHandle: any = null;
    private poseStudioSnapshotLoading: boolean = false;
    private continuousTrainingPollHandle: any = null;

    public fallbackTrainingTargetOptions = [
        { key: 'rf-dual', label: 'RF-Dual 통합', job_type: 'full', training_mode: 'dashboard', registration_supported: true, requires_posture_label: false, description: 'RF-Fall v2와 XG-Posture 후보를 함께 만들고 한 번에 적용합니다.' },
        { key: 'rf-fall-v2', label: 'RF-Fall v2', job_type: 'rf', training_mode: 'dashboard', registration_supported: true, requires_posture_label: false, description: '실제 RF-Fall v2 후보를 만들며 적용 전 운영 모델은 유지됩니다.' },
        { key: 'xg-posture', label: 'XG-Posture', job_type: 'posture', training_mode: 'dashboard', registration_supported: true, requires_posture_label: true, description: '행동 후보를 별도 저장하며 적용 확인 전 운영 모델은 유지됩니다.' },
        { key: 'pose-backbone', label: 'Pose 모델', job_type: 'pose-studio', training_mode: 'pose-studio', registration_supported: false, requires_posture_label: false, description: '모델 관리에서 등록한 Pose 모델을 선택해 새 후보를 학습합니다.' },
        { key: 'xg-posture-occlusion-aux', label: 'XG-Posture 가림 보조', job_type: 'occlusion', training_mode: 'continuous', registration_supported: false, continuous_status_name: 'xg-posture-occlusion-aux', requires_posture_label: true, description: 'AI-Hub 자세 데이터와 합성 가림을 사용하는 전용 연속 학습 상태를 조회하고 재개합니다.' },
        { key: 'facial-aihub82', label: 'AI-Hub 82 표정', job_type: 'aihub82', training_mode: 'continuous', registration_supported: false, continuous_status_name: 'aihub82', requires_posture_label: false, description: 'AI-Hub 82 표정 라벨 계약을 사용하는 전용 연속 학습 상태를 조회하고 재개합니다.' },
        { key: 'driver-aihub173', label: 'AI-Hub 173 상태', job_type: 'aihub173', training_mode: 'continuous', registration_supported: false, continuous_status_name: 'aihub173', requires_posture_label: false, description: 'AI-Hub 173 상태 라벨 계약을 사용하는 전용 연속 학습 상태를 조회하고 재개합니다.' },
    ];

    public postureClasses = [
        { code: 'stand', label: '서기', icon: '🧍' },
        { code: 'walk', label: '걷기', icon: '🚶' },
        { code: 'sit', label: '앉기', icon: '🪑' },
        { code: 'run', label: '뛰기', icon: '🏃' },
        { code: 'lie', label: '눕기', icon: '🛌' },
        { code: 'fall', label: '낙상', icon: '⚠️' },
    ];

    public staticFilterValidationLabels = [
        { code: 'static_object_false_positive', label: '정지 사물 오검출', actualLabel: 'N' },
        { code: 'blanket_person', label: '실제 사람 · 이불/담요 가림', actualLabel: 'N' },
        { code: 'low_light_person', label: '실제 사람 · 저조도', actualLabel: 'N' },
        { code: 'true_person_long_still', label: '실제 사람 · 장시간 부동', actualLabel: 'N' },
        { code: 'true_person_fall', label: '실제 사람 · 낙상', actualLabel: 'Y' },
    ];

    public webcamStream: MediaStream | null = null;
    public webcamReady: boolean = false;
    public webcamError: string = '';

    public poseLandmarker: any = null;
    public faceLandmarker: any = null;
    public mpRunning: boolean = false;
    public mpProcessing: boolean = false;
    public mpRafId: number = 0;
    public highPassEdgeStrength: number = 30;
    public lowPassBlurStrength: number = 35;
    public mpInitializing: boolean = false;
    private mpInitializationPromise: Promise<void> | null = null;
    public mpReady: boolean = false;
    public mpFps: number = 0;
    public mpFrameCount: number = 0;
    public mpFpsTimer: any = null;
    public mpResizeObserver: any = null;
    public mpLastFrameTime: number = 0;
    public mpDetectedPoseCount: number = 0;
    private mpPoseWarmed: boolean = false;
    private mpPersistentRenderPoses: PersistentRenderPose[] = [];
    private mpSkeletonRenderRafId: number = 0;
    private mpSkeletonRenderLastAt: number = 0;
    private mpSkeletonCoverageLastVideoMs: number = -1;
    private mpSkeletonPersonVisibleMs: number = 0;
    private mpSkeletonVisibleMs: number = 0;
    private mpSkeletonMissingGapStartedMs: number = -1;
    private mpSkeletonMissingGapCount: number = 0;
    private mpSkeletonMissingOver700Count: number = 0;
    private mpSkeletonLongestMissingMs: number = 0;
    private mpSkeletonPredictedDrawCount: number = 0;
    public realtimeDeveloperMode: boolean = false;
    public realtimeDisablePrecision: boolean = false;
    public realtimeFastPoseStatus: string = 'CAPTURE_WAIT';
    public realtimeFastPoseCounters: any = {
        videoFrame: 0,
        request: 0,
        response: 0,
        validResponse: 0,
        trackCreated: 0,
        render: 0,
        draw: 0,
        clear: 0,
    };
    public mpPoseDelegate: string = '';
    public faceMeshReady: boolean = false;
    public faceMeshDetected: boolean = false;
    public faceMeshError: string = '';
    public faceMeshDelegate: string = '';
    public faceMeshFaceCount: number = 0;
    public faceMeshLandmarkCount: number = 0;
    public faceMeshFps: number = 0;
    public faceMeshFrameCount: number = 0;
    public liveFacialEmotion: string = 'neutral';
    public liveFacialEmotionLabel: string = '얼굴 찾는 중';
    public liveFacialEmotionConfidence: number = 0;
    public liveFacialTop3: LiveFacialCandidate[] = [];
    private liveFacialSmoothedScores: Record<string, number> = {};
    private liveFacialCandidateCode: string = '';
    private liveFacialCandidateSince: number = 0;
    private faceMeshLastSeenMs: number = 0;
    private faceMeshLastUiRenderMs: number = 0;
    private faceMeshLastInferenceMs: number = 0;
    private faceMeshInferenceIntervalMs: number = 1500;
    private faceMeshDeferredTimer: any = null;
    public kalmanFilters: { x: Kalman1D; y: Kalman1D; missCount: number }[] = [];
    private mpPoseKalmanFilters: { x: Kalman1D; y: Kalman1D; missCount: number }[][] = [];
    private mpPoseTrackFilters: Record<number, MpLandmarkFilters> = {};
    private mpPoseTracks: MpPoseTrack[] = [];
    private mpNextPoseTrackId: number = 1;
    private mpPrimaryPoseTrackId: number = 0;
    private readonly KF_MAX_MISS: number = 15;
    private readonly KF_VIS_THRESHOLD: number = 0.15;
    private readonly KF_HAND_VIS_THRESHOLD: number = 0.55;

    public realtimeActive: boolean = false;
    public realtimeStarting: boolean = false;
    public realtimeSourceMode: 'camera' | 'video' = 'camera';
    public realtimeUploadedVideoUrl: string = '';
    public realtimeUploadedVideoName: string = '';
    public realtimeUploadedAnalysisComplete: boolean = false;
    public realtimeUploadedAnalysisRunning: boolean = false;
    public realtimePrecisionAnalysisPending: boolean = false;
    public realtimeUploadedAnalysisError: string = '';
    public realtimeLocalFallState: 'idle' | 'suspected' | 'verifying' | 'confirmed' | 'cleared' | 'review_required' = 'idle';
    public realtimeLocalFallRiskPercent: number = 0;
    public realtimeFastPosture: string = '';
    public realtimeFastPostureLabel: string = '확인 중';
    public realtimeFastPostureStatus: 'idle' | 'estimated' | 'precision' = 'idle';
    private realtimeFastFallConsecutiveHits: number = 0;
    private realtimeFastTransitionSignalHits: number = 0;
    private realtimeFastTransitionSignalAtMs: number = 0;
    private realtimeFastFallEventKey: string = '';
    private realtimeFastFallCreatedAt: number = 0;
    private realtimeFastFallLastEvaluatedAt: number = 0;
    private realtimeFallEvidenceSignals = new Set<string>();
    private realtimeFallStrongKinematicEvidence: boolean = false;
    private realtimeFallPeakFastRisk: number = 0;
    private realtimeFallPeakDescentTimestampMs: number = 0;
    private realtimeFallLowestPointTimestampMs: number = 0;
    private realtimeFallTrackChangesAtCandidate: number = 0;
    private mpDuplicateLogicalRenderCount: number = 0;
    private mpMergedTrackResidualRenderCount: number = 0;
    private mpDuplicatePoseCandidateCount: number = 0;
    private realtimeFastPostureFirstAt: number = 0;
    private realtimePostureCandidate: string = '';
    private realtimePostureCandidateSinceMs: number = 0;
    private realtimeLastPostureEvent: string = '';
    private realtimeLastPostureEventAtMs: number = 0;
    public realtimeLocalFallInterval: { startSec: number; endSec: number } | null = null;
    private realtimeLocalFallSamples: {
        sourceTime: number;
        centerY: number;
        torsoAngle: number;
        bboxAspect: number;
        hipY: number;
        bbox: { cx: number; cy: number; w: number; h: number; conf: number };
        keypoints: { x: number; y: number; conf: number }[];
    }[] = [];
    private realtimeLocalFallConsecutiveHits: number = 0;
    private realtimeLocalFallConsecutiveMisses: number = 0;
    private realtimeLocalFallLockUntil: number = 0;
    private realtimeLocalFallEventLogged: boolean = false;
    public realtimeRfFallStatus: 'idle' | 'warming' | 'ready' | 'error' = 'idle';
    public realtimeRfFallLatencyMs: number = 0;
    public realtimeRfFallModel: string = '';
    public realtimeRfFallWindowFrameCount: number = 0;
    private realtimeRfFallPending: boolean = false;
    private realtimeRfFallQueued: boolean = false;
    private realtimeRfFallSequence: number = 0;
    private realtimeRfFallLastDispatchSourceTime: number = -1;
    private realtimeRfFallController: AbortController | null = null;
    private mpLastPoseSeenMs: number = 0;
    public realtimeEnvironmentSnapshotPending: boolean = false;
    public realtimeEnvironmentSnapshotCount: number = 0;
    private realtimeEnvironmentSnapshotTimer: any = null;
    private realtimeEnvironmentSnapshotController: AbortController | null = null;
    private realtimeEnvironmentSnapshotResult: any = null;
    private realtimeEnvironmentTracks: any[] = [];
    private realtimeEnvironmentNextTrackId: number = 1;
    private realtimeEnvironmentLastSourceTime: number = -1;
    private realtimeEnvironmentStartedAt: number = 0;
    private realtimeEnvironmentFirstDisplayedAt: number = 0;
    private realtimeEnvironmentLatencyEwmaMs: number = 0;
    private realtimeEnvironmentAdaptiveResolution: number = 480;
    private realtimeEnvironmentTimeoutCount: number = 0;
    private realtimeEnvironmentFirstRequestDispatchedAt: number = 0;
    private realtimeUploadedFinalizeRunning: boolean = false;
    private realtimeUploadedVideoFile: File | null = null;
    private realtimeUploadedAnalysisController: AbortController | null = null;
    private realtimeUploadedAnalysisRunId: number = 0;
    public realtimeChunkCount: number = 0;
    public postureTimeline: { time: string; posture: string; label: string; score: number; chunkId: number }[] = [];
    public postureTransitions: { time: string; from: string; to: string; fromLabel: string; toLabel: string }[] = [];
    public currentPosture: string = '';
    public currentPostureLabel: string = '';
    public postureTransitionText: string = '';
    public realtimeCumulativeScore: number = 0;
    public realtimeScoreHistory: number[] = [];
    public realtimeIntervalSec: number = 5;
    public realtimeOverlapSec: number = 3;
    public realtimeLastElapsed: number = 0;
    public realtimePendingConfirmation: boolean = false;
    private recorderSlots: { recorder: MediaRecorder; chunks: Blob[]; timer: any; slotId: number; durationSec: number; startedAtMs: number; sourceStartTime: number }[] = [];
    private recorderSpawnTimer: any = null;
    private realtimeAnalysisCanvas: HTMLCanvasElement | null = null;
    private realtimeAnalysisContext: CanvasRenderingContext2D | null = null;
    private realtimeAnalysisStream: MediaStream | null = null;
    private realtimeAnalysisCoordinateMeta: any = null;
    private realtimeRingCaptureTimer: any = null;
    public realtimeRingBuffer: { sourceTime: number; capturedAt: number }[] = [];
    public readonly realtimeRingBufferMaxFrames: number = 12;
    public readonly realtimeAnalysisCaptureFps: number = 3;
    public realtimeEffectiveCaptureFps: number = 3;
    private realtimeLastCaptureAt: number = 0;
    public realtimeVideoFps: number = 0;
    private realtimeVideoFrameCallbackId: number = 0;
    private realtimeVideoFrameCount: number = 0;
    private realtimeVideoFrameMeasureStartedAt: number = 0;
    private mpPoseVideoFrameCallbackId: number = 0;
    private mpPoseFallbackTimer: any = null;
    private mpPoseIntervalFallbackTimer: any = null;
    private mpFrameCallbackWatchdogTimer: any = null;
    private mpLastVideoFrameCallbackAt: number = 0;
    private mpLastSubmittedFrameId: number = -1;
    private mpPoseQueueLength: number = 0;
    private mpPoseMaxQueueLength: number = 0;
    private mpInFlightFrame: PoseFrameEnvelope | null = null;
    private mpNormalPendingFrame: PoseFrameEnvelope | null = null;
    private mpEventCandidateFrame: PoseFrameEnvelope | null = null;
    private mpPriorityTransitionUntilMs: number = 0;
    private mpPoseFrameDiagnostics: any[] = [];
    private mpLastRenderedTrackId: number = 0;
    private mpTrackIdChangeCount: number = 0;
    private mpLastSkeletonRenderedAt: number = 0;
    private mpLongestSkeletonGapMs: number = 0;
    private mpPoseFirstRequestedAt: number = 0;
    private mpPoseFirstRenderedAt: number = 0;
    private nextSlotId: number = 0;
    private realtimeSessionId: string = '';
    private realtimeSessionStartedAt: number = 0;
    private dispatchQueue: { blob: Blob; chunkId: number; durationSec: number; chunkWindow: any; facialSnapshot?: any; attempt?: number }[] = [];
    private dispatchWorkerRunning: boolean = false;
    private realtimeWindowKeys: Set<string> = new Set();
    private uploadAnalysisRequestKey: string = '';
    private assetVersionCheckTimer: any = null;
    public dispatchQueueSize: number = 0;
    public dispatchMaxQueue: number = 1;
    public realtimeDispatching: boolean = false;
    public realtimePerformancePreset: RealtimePerformancePreset = 'balanced';
    public realtimePerformancePanelOpen: boolean = false;
    private realtimePerfSamples: Record<string, RealtimePerfSample[]> = {};
    public realtimeBenchmarkRunning: boolean = false;
    public realtimeBenchmarkDurationSec: number = 300;
    public realtimeBenchmarkElapsedSec: number = 0;
    public readonly realtimeBenchmarkWarmupSec: number = 15;
    public realtimeBenchmarkWarmupRemainingSec: number = 0;
    public realtimeBenchmarkWarmingUp: boolean = false;
    public realtimeBenchmarkPresetIndex: number = -1;
    public realtimeBenchmarkResults: any[] = [];
    public realtimeBenchmarkGpuInfo: string = '확인 전';
    public realtimeBenchmarkSourceLabel: string = '운영 카메라/영상';
    private readonly realtimeBenchmarkPresets: RealtimeBenchmarkPreset[] = ['realtime', 'balanced', 'precision'];
    private realtimeBenchmarkTimer: any = null;
    private realtimeBenchmarkSegmentStartedAt: number = 0;
    private realtimeBenchmarkMeasurementStartedAt: number = 0;
    private realtimeBenchmarkSamples: Record<string, number[]> = {};
    private realtimeBenchmarkFpsSamples: number[] = [];
    private realtimeBenchmarkFaceFpsSamples: number[] = [];
    private realtimeBenchmarkHeapSamples: number[] = [];
    private realtimeBenchmarkStartStats: any = null;
    private realtimeBenchmarkLongTaskCount: number = 0;
    private realtimeBenchmarkLongTaskMs: number = 0;
    private realtimeBenchmarkLongTaskObserver: any = null;
    private realtimeBenchmarkTelemetryPending: boolean = false;
    private realtimeBenchmarkGpuSamples: Record<string, number[]> = {};
    private realtimeBenchmarkLatestTelemetry: any = null;
    private benchmarkReferenceCanvas: HTMLCanvasElement | null = null;
    private benchmarkReferenceAnimationId: number = 0;
    private benchmarkReferenceSourceActive: boolean = false;
    private realtimeUiRenderTimer: any = null;
    private realtimeLastUiRenderAt: number = 0;
    private realtimeLatestAppliedChunkId: number = -1;
    private realtimeLatestShadowChunkId: number = -1;
    private realtimeShadowPollTimers: Record<string, any> = {};
    private realtimeVideoEnded: boolean = false;
    private realtimeVideoEndedAt: number = 0;
    private realtimeFinalAcceptedTimestampMs: number = 0;
    private realtimeEndFinalizeTimer: any = null;
    private realtimeCancelledPrecisionCount: number = 0;
    private realtimeLatePrecisionIgnoredCount: number = 0;
    private readonly REALTIME_END_GRACEFUL_FLUSH_MS: number = 1500;
    private readonly REALTIME_END_HARD_STOP_MS: number = 2000;
    private readonly REALTIME_END_PRECISION_FLUSH_MS: number = 120000;
    public realtimeDegradedMode: boolean = false;
    private realtimeHealthySince: number = 0;
    public realtimeDelayedResultCount: number = 0;
    private realtimeOverlayRafId: number = 0;
    private realtimeOverlayPendingResult: any = null;
    private realtimeLastOverlayDrawAt: number = 0;
    private realtimeOverlayClearTimer: any = null;
    private readonly REALTIME_SKELETON_TTL_MS: number = 6000;

    public realtimeOverlayScore: number = 0;
    public realtimeOverlayLevel: string = 'low';
    public realtimeOverlayLabel: string = '대기';
    public realtimeOverlayBasis: string = '';
    public realtimeOverlayRuntime: string = '';
    public realtimeOverlayFlash: boolean = false;
    public realtimeOverlayFlashTimer: any = null;
    public lastNonEmptyOverlayBasis: string = '';

    public realtimeLogEntries: any[] = [];
    private realtimeFeedSequence: number = 0;
    private readonly REALTIME_FEED_RISK_RISE_THRESHOLD: number = 15;
    private realtimeFeedLastTone: 'normal' | 'review' | 'suspected' | 'confirmed' | '' = '';
    private realtimeFeedLastRiskPercent: number | null = null;
    private realtimeFeedPersonPresent: boolean | null = null;
    private realtimeFeedPersonMissCount: number = 0;
    private realtimeFeedActiveSignals: Set<string> = new Set();
    public logDetailOpen: boolean = false;
    public logDetailEntry: any = null;
    public logDetailVideoUrl: string = '';
    public logDetailVideoLoading: boolean = false;
    public logDetailVideoError: string = '';
    public logDetailVideoRange: { startSec: number; endSec: number } | null = null;
    public logDetailVideoMode: 'blob' | 'upload' | '' = '';
    public logFeedbackStatus: string = '';
    public logFeedbackActualLabel: string = '';
    public logFeedbackPostureClass: string = '';
    public logFeedbackNote: string = '';
    public logFeedbackSubmitting: boolean = false;
    public logFeedbackAmbiguityFlag: boolean = false;
    public logFeedbackOcclusionFlag: boolean = false;
    public logFeedbackShortClipFlag: boolean = false;
    public logFeedbackStaticFilterLabel: string = '';
    public logFeedbackRetrainRequested: boolean = false;
    public logFeedbackElapsedSec: number = 0;
    private logFeedbackTimerHandle: any = null;
    public logFeedbackMessage: string = '';
    public logFeedbackMessageType: string = 'info';

    public detectionReplayPlaying: boolean = false;
    public detectionReplayIndex: number = 0;
    public detectionReplayTimer: any = null;
    public bboxOverlayEnabled: boolean = false;
    public alertWorkflow: any = null;
    public emergencyPopupOpen: boolean = false;
    public referencePopupOpen: boolean = false;
    public referencePopupData: any = null;
    public referencePopupLoading: boolean = false;
    public realtimePerfStats: any = {
        avgRtt: 0, avgServer: 0, currentInterval: 0, discarded: 0, droppedFrames: 0,
        queued: 0, maxQueue: 0, retrying: 0, failed: 0, renderedSkeletons: 0,
        ghostSkeletonsRemoved: 0, faceCacheHits: 0, faceCacheMisses: 0,
        skippedChunks: 0, discardedShadow: 0, ringBufferFrames: 0,
    };
    public realtimeDiagnosticsOpen: boolean = false;
    public realtimeDiagnostics: any = {
        video_current_time: 0,
        analyzed_chunk_start: 0,
        analyzed_chunk_end: 0,
        result_generated_time: null,
        video_to_result_lag_ms: 0,
        raw_person_track_count: 0,
        logical_person_count: 0,
        pose_detected_person_count: 0,
        primary_track_candidate_count: 0,
        safe_skeleton_count: 0,
        rendered_skeleton_count: 0,
        skeleton_exclusion_reasons: {},
        detected_environment_object_count: 0,
        rendered_environment_object_count: 0,
        filtered_person_count: 0,
        filtered_object_count: 0,
        stale_overlay_discard_count: 0,
        overlay_disposition: 'waiting',
        person_filter_reason: '',
        analysis_failed: false,
        display_mode: 'NONE',
        render_skeleton_called: false,
        render_environment_called: false,
        input_skeleton_count: 0,
        input_environment_count: 0,
    };
    public realtimeRoundTripHistory: number[] = [];
    public realtimeServerTimeHistory: number[] = [];
    public realtimeAdaptiveEnabled: boolean = false;

    public POSTURE_LABEL_MAP: Record<string, string> = {
        stand: '서기', walk: '걷기', run: '뛰기', sit: '앉기', lie: '눕기', fall: '낙상', object: '사물', unknown: '미확인'
    };
    public DECISION_STATE_META: Record<string, any> = {
        safe: { label: '정상', color: 'text-emerald-700', icon: '✅', badgeCls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
        posture_only: { label: '낙상 아님 · 자세 감지', color: 'text-sky-700', icon: '🧍', badgeCls: 'bg-sky-50 text-sky-800 border-sky-200' },
        fall_suspected: { label: '낙상 의심', color: 'text-orange-700', icon: '⚠️', badgeCls: 'bg-orange-50 text-orange-800 border-orange-200' },
        fall_confirmed: { label: '낙상 확정', color: 'text-rose-700', icon: '🚨', badgeCls: 'bg-rose-50 text-rose-800 border-rose-200' },
        near_fall_recovered: { label: '넘어질 뻔했으나 회복', color: 'text-amber-700', icon: '↩️', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
        non_fall: { label: '낙상 아님', color: 'text-emerald-700', icon: '✅', badgeCls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
        static_object_filtered: { label: '사람 아님 · 분석 제외', color: 'text-zinc-700', icon: '⏸', badgeCls: 'bg-zinc-100 text-zinc-800 border-zinc-300' },
        object_filtered: { label: '사람 아님 · 분석 제외', color: 'text-zinc-700', icon: '⏸', badgeCls: 'bg-zinc-100 text-zinc-800 border-zinc-300' },
        uncertain: { label: '판단 확인 필요', color: 'text-amber-700', icon: '❓', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
        insufficient_data: { label: '판단 확인 필요', color: 'text-amber-700', icon: '📹', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
        insufficient_detection: { label: '사람 검출 부족', color: 'text-amber-700', icon: '📹', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
        FALL: { label: '낙상 의심 이벤트 · Shadow', color: 'text-orange-700', icon: '⚠️', badgeCls: 'bg-orange-50 text-orange-800 border-orange-300' },
        NON_FALL_CONTROLLED_TRANSITION: { label: '통제된 자세 전환 · Shadow', color: 'text-sky-700', icon: '↘', badgeCls: 'bg-sky-50 text-sky-800 border-sky-200' },
        NON_FALL_NORMAL_ACTIVITY: { label: '정상 활동 · Shadow', color: 'text-emerald-700', icon: '✅', badgeCls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
        INDETERMINATE_OCCLUSION: { label: '판단 보류 · 가림', color: 'text-amber-700', icon: '👁', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
        INDETERMINATE_LOW_CONFIDENCE: { label: '판단 보류 · 낮은 신뢰도', color: 'text-amber-700', icon: '📹', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' },
    };

    @ViewChild('webcamVideo') webcamVideoRef!: ElementRef<HTMLVideoElement>;
    @ViewChild('mpPoseCanvas') set _mpPoseCanvasSetter(el: ElementRef<HTMLCanvasElement>) { this._mpPoseCanvasEl = el; }
    private _mpPoseCanvasEl: ElementRef<HTMLCanvasElement> | null = null;
    @ViewChild('mpFaceCanvas') set _mpFaceCanvasSetter(el: ElementRef<HTMLCanvasElement>) { this._mpFaceCanvasEl = el; }
    private _mpFaceCanvasEl: ElementRef<HTMLCanvasElement> | null = null;
    private mpInputCanvas: HTMLCanvasElement | null = null;
    private mpInputCtx: CanvasRenderingContext2D | null = null;
    private mpInputTransform: { side: number; offsetX: number; offsetY: number; drawW: number; drawH: number } | null = null;
    @ViewChild('realtimeBboxCanvas') realtimeBboxCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('realtimeEnvironmentMaskCanvas') realtimeEnvironmentMaskCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('realtimeDebugCanvas') realtimeDebugCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('uploadBboxCanvas') uploadBboxCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('uploadPreviewVideo') uploadPreviewVideoRef!: ElementRef<HTMLVideoElement>;
    @ViewChild('detCanvas') detCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('environmentMaskCanvas') environmentMaskCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('fallCandidateTestVideo') fallCandidateTestVideoRef!: ElementRef<HTMLVideoElement>;
    @ViewChild('fallCandidateTestCanvas') fallCandidateTestCanvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('logScrollContainer') logScrollContainerRef!: ElementRef;

    constructor(@Inject( Service) public service: Service) { }

    private hasRealtimeInputSource(): boolean {
        if (this.inputMode !== 'webcam') return false;
        if (this.realtimeSourceMode === 'video') {
            return Boolean(this.realtimeUploadedVideoUrl);
        }
        return Boolean(this.webcamStream);
    }

    private clientPoseOwnsRealtimeSkeleton(): boolean {
        return this.hasRealtimeInputSource()
            && this.mpReady
            && this.mpRunning
            && Number(this.mpDetectedPoseCount || 0) > 0;
    }

    private realtimeCaptureTargetFps(): number {
        if (this.realtimeDegradedMode || this.realtimePerformancePreset === 'performance') return 2;
        return this.realtimeAnalysisCaptureFps;
    }

    private realtimePresetConfig(preset: RealtimePerformancePreset = this.realtimePerformancePreset) {
        const base = preset === 'quality'
            ? { overlayFps: 15, uiFps: 4 }
            : preset === 'performance' ? { overlayFps: 8, uiFps: 2 } : { overlayFps: 12, uiFps: 3 };
        const budget = this.agentComputeBudget();
        return {
            ...base,
            analysisFps: budget.behaviorFps,
            faceIntervalMs: budget.emotionFps > 0 ? Math.round(1000 / budget.emotionFps) : 60_000,
            environmentIntervalMs: budget.environmentFps > 0 ? Math.round(1000 / budget.environmentFps) : 60_000,
        };
    }

    public agentWeightTotal(): number {
        return this.agentWeights.behavior + this.agentWeights.environment + this.agentWeights.emotion;
    }

    public showAgentWeightControl(): boolean {
        return this.uploadWorkflowMode !== 'train'
            && this.overlayMode === 'SKELETON_ENVIRONMENT'
            && this.canShowSkeletonOverlay();
    }

    public showRealtimeAgentWeightSummary(): boolean {
        return this.agentWeightsConfirmed
            && this.overlayMode !== 'NONE'
            && this.canShowSkeletonOverlay();
    }

    public realtimeAgentWeightIndicators(): { key: AgentWeightKey; label: string; weight: number; detail: string; color: string }[] {
        return [
            {
                key: 'behavior',
                label: '자세 분석',
                weight: this.agentWeights.behavior,
                detail: this.realtimeFastPostureLabel || '분석 대기',
                color: '#38bdf8',
            },
            {
                key: 'environment',
                label: '환경 분석',
                weight: this.agentWeights.environment,
                detail: this.agentWeights.environment <= 0
                    ? '사용 안 함'
                    : (this.realtimeEnvironmentSnapshotPending ? '분석 중' : `객체 ${this.realtimeEnvironmentSnapshotCount}개`),
                color: '#34d399',
            },
            {
                key: 'emotion',
                label: '감정 분석',
                weight: this.agentWeights.emotion,
                detail: this.agentWeights.emotion <= 0
                    ? '사용 안 함'
                    : (this.faceMeshDetected ? this.liveFacialEmotionLabel : '분석 대기'),
                color: '#a78bfa',
            },
        ];
    }

    public agentComputeBudget(): { behaviorFps: number; environmentFps: number; emotionFps: number; behaviorResolution: number; environmentResolution: number; emotionResolution: number } {
        const behavior = this.agentWeights.behavior;
        const environment = this.agentWeights.environment;
        const emotion = this.agentWeights.emotion;
        return {
            behaviorFps: behavior <= 0 ? 1 : Math.max(2, Math.min(24, Math.round(15 * behavior / 60))),
            environmentFps: environment <= 0 ? 0 : Math.max(0.25, Math.min(3, Number((1.5 * environment / 30).toFixed(2)))),
            emotionFps: emotion <= 0 ? 0 : Math.max(0.2, Math.min(3, Number((emotion / 10).toFixed(2)))),
            behaviorResolution: behavior >= 70 ? 480 : (behavior >= 40 ? 384 : 320),
            environmentResolution: environment >= 35 ? 512 : (environment > 0 ? 320 : 0),
            emotionResolution: emotion >= 20 ? 320 : (emotion > 0 ? 224 : 0),
        };
    }

    public agentWeightMetadata(): any {
        const budget = this.agentComputeBudget();
        return {
            agent_weights: { ...this.agentWeights },
            agent_weight_version: 'orchestrator-user-v1',
            compute_budget: {
                behavior_fps: budget.behaviorFps,
                environment_fps: budget.environmentFps,
                emotion_fps: budget.emotionFps,
                behavior_resolution: budget.behaviorResolution,
                environment_resolution: budget.environmentResolution,
                emotion_resolution: budget.emotionResolution,
            },
        };
    }

    private restoreAgentWeights() {
        try {
            const saved = JSON.parse(String(localStorage.getItem(AGENT_WEIGHTS_STORAGE_KEY) || 'null'));
            if (!saved || !['behavior', 'environment', 'emotion'].every(key => Number.isFinite(Number(saved[key])))) return;
            const values = {
                behavior: Math.max(0, Math.min(100, Math.round(Number(saved.behavior)))),
                environment: Math.max(0, Math.min(100, Math.round(Number(saved.environment)))),
                emotion: Math.max(0, Math.min(100, Math.round(Number(saved.emotion)))),
            };
            if (values.behavior + values.environment + values.emotion !== 100) return;
            // 기존 Real-Time(80·20·0)은 감정 추론을 완전히 건너뛰므로
            // 자세 중심 비율을 유지하면서 표정 모델이 함께 실행되는 새 프리셋으로 이전한다.
            const isLegacyRealtime = values.behavior === 80 && values.environment === 20 && values.emotion === 0;
            this.agentWeights = isLegacyRealtime
                ? { behavior: 70, environment: 20, emotion: 10 }
                : values;
            if (isLegacyRealtime) this.saveAgentWeights();
            this.agentWeightsConfirmed = localStorage.getItem(AGENT_WEIGHTS_CONFIRMED_STORAGE_KEY) === 'true';
        } catch (e) { }
    }

    private clearStoredAgentWeightsForNoOverlay() {
        if (this.overlayMode !== 'NONE') return;
        this.agentWeights = { behavior: 60, environment: 30, emotion: 10 };
        this.agentWeightPreset = 'balanced';
        this.agentWeightsConfirmed = false;
        this.agentWeightsDirty = false;
        this.agentWeightManagerOpen = false;
        try {
            localStorage.removeItem(AGENT_WEIGHTS_STORAGE_KEY);
            localStorage.removeItem(AGENT_WEIGHTS_CONFIRMED_STORAGE_KEY);
        } catch (e) { }
    }

    private saveAgentWeights() {
        try { localStorage.setItem(AGENT_WEIGHTS_STORAGE_KEY, JSON.stringify(this.agentWeights)); } catch (e) { }
    }

    private restoreRealtimeBenchmarkReport() {
        try {
            const report = JSON.parse(String(localStorage.getItem('fallai:agent-weight-benchmark-v1') || 'null'));
            if (report?.version !== 'agent-weight-benchmark-v1' || !Array.isArray(report?.results)) return;
            this.realtimeBenchmarkResults = report.results;
            this.realtimeBenchmarkGpuInfo = String(report.gpuInfo || 'GPU 식별 정보 없음');
        } catch (e) { }
    }

    public async openAgentWeightManager() {
        this.agentWeightManagerOpen = true;
        await this.service.render();
    }

    public async closeAgentWeightManager() {
        this.agentWeightManagerOpen = false;
        await this.service.render();
    }

    public async confirmAgentWeights() {
        if (!this.agentWeightsDirty && !this.agentWeightsConfirmed) return;
        this.saveAgentWeights();
        this.agentWeightsConfirmed = true;
        this.agentWeightsDirty = false;
        try { localStorage.setItem(AGENT_WEIGHTS_CONFIRMED_STORAGE_KEY, 'true'); } catch (e) { }
        this.agentWeightManagerOpen = false;
        await this.service.render();
    }

    public async applyAgentWeightPreset(preset: RealtimeBenchmarkPreset) {
        const presets: Record<string, AgentWeights> = {
            realtime: { behavior: 70, environment: 20, emotion: 10 },
            balanced: { behavior: 60, environment: 30, emotion: 10 },
            precision: { behavior: 45, environment: 30, emotion: 25 },
        };
        this.agentWeights = { ...presets[preset] };
        this.agentWeightPreset = preset;
        await this.commitAgentWeights();
    }

    public async onAgentWeightInput(key: AgentWeightKey, rawValue: any) {
        const nextValue = Math.max(0, Math.min(100, Math.round(Number(rawValue) || 0)));
        const otherKeys = (['behavior', 'environment', 'emotion'] as AgentWeightKey[]).filter(item => item !== key);
        const remaining = 100 - nextValue;
        const otherTotal = otherKeys.reduce((sum, item) => sum + this.agentWeights[item], 0);
        let firstValue = 0;
        if (otherTotal > 0) firstValue = Math.round(remaining * this.agentWeights[otherKeys[0]] / otherTotal);
        else firstValue = Math.round(remaining / 2);
        this.agentWeights = {
            ...this.agentWeights,
            [key]: nextValue,
            [otherKeys[0]]: firstValue,
            [otherKeys[1]]: remaining - firstValue,
        };
        this.agentWeightPreset = 'custom';
        await this.commitAgentWeights();
    }

    private async commitAgentWeights() {
        this.saveAgentWeights();
        this.agentWeightsDirty = true;
        this.agentWeightsConfirmed = false;
        try { localStorage.removeItem(AGENT_WEIGHTS_CONFIRMED_STORAGE_KEY); } catch (e) { }
        if (this.rootAnalysisResult) this.applyAnalysisRoot(this.applyClientAgentWeights(this.rootAnalysisResult), true);
        this.faceMeshInferenceIntervalMs = this.realtimePresetConfig().faceIntervalMs;
        await this.service.render();
    }

    private applyClientAgentWeights(result: any): any {
        if (!result || typeof result !== 'object') return result;
        const behaviorRisk = Math.max(0, Math.min(1, Number(
            result.unweighted_displayed_risk_score ?? result.raw_model_risk_score ?? result.model_output?.risk_score ?? result.risk_score ?? 0
        )));
        const environmentObjects = Array.isArray(result.environment_analysis?.objects) ? result.environment_analysis.objects : [];
        const environmentRisk = Math.max(0, Math.min(1, Number(
            result.environment_analysis?.risk_score ?? environmentObjects.reduce((max: number, item: any) => Math.max(max, Number(item?.risk_score ?? (this.isEnvironmentHazard(item) ? item?.confidence : 0) ?? 0)), 0)
        )));
        const face = this.facialStateAux(result) || {};
        const emotionRisk = Math.max(0, Math.min(1, Number(face.trusted_distress_score ?? face.distress_score ?? face.support_score ?? face.driver_state_risk ?? 0)));
        const weighted = behaviorRisk * this.agentWeights.behavior / 100
            + environmentRisk * this.agentWeights.environment / 100
            + emotionRisk * this.agentWeights.emotion / 100;
        result.unweighted_displayed_risk_score = Number(result.unweighted_displayed_risk_score ?? result.displayed_risk_score ?? result.risk_score ?? behaviorRisk);
        result.orchestrator_weighted_risk_score = Number(weighted.toFixed(4));
        result.final_risk_score = result.orchestrator_weighted_risk_score;
        result.displayed_risk_score = result.orchestrator_weighted_risk_score;
        result.agent_weighting = {
            ...this.agentWeightMetadata(),
            component_scores: { behavior: behaviorRisk, environment: environmentRisk, emotion: emotionRisk },
            total_weight: 100,
        };
        return result;
    }

    public async setRealtimePerformancePreset(value: RealtimePerformancePreset) {
        if (!['quality', 'balanced', 'performance'].includes(String(value))) return;
        this.realtimePerformancePreset = value;
        try { localStorage.setItem('fallai-realtime-performance-preset', value); } catch (e) { }
        await this.service.render();
    }

    public realtimePerformancePresetLabel(): string {
        if (this.realtimePerformancePreset === 'quality') return '품질 우선';
        if (this.realtimePerformancePreset === 'performance') return '성능 우선';
        return '균형';
    }

    public async toggleRealtimePerformancePanel() {
        this.realtimePerformancePanelOpen = !this.realtimePerformancePanelOpen;
        await this.service.render();
    }

    public realtimeBenchmarkPresetLabel(preset?: RealtimeBenchmarkPreset): string {
        const value = preset || this.realtimeBenchmarkPresets[Math.max(0, this.realtimeBenchmarkPresetIndex)];
        if (value === 'realtime') return '⚡ Real-Time';
        if (value === 'precision') return '🎯 Precision';
        return '⚖ Balanced';
    }

    public realtimeBenchmarkProgress(): number {
        if (!this.realtimeBenchmarkRunning || this.realtimeBenchmarkDurationSec <= 0) return 0;
        return Math.max(0, Math.min(100, this.realtimeBenchmarkElapsedSec / this.realtimeBenchmarkDurationSec * 100));
    }

    public realtimeBenchmarkRemainingText(): string {
        if (!this.realtimeBenchmarkRunning) return '';
        const currentRemaining = Math.max(0, this.realtimeBenchmarkDurationSec - this.realtimeBenchmarkElapsedSec);
        const remainingPresets = Math.max(0, this.realtimeBenchmarkPresets.length - this.realtimeBenchmarkPresetIndex - 1);
        const warmupRemaining = this.realtimeBenchmarkWarmingUp ? this.realtimeBenchmarkWarmupRemainingSec : 0;
        const total = currentRemaining + warmupRemaining + remainingPresets * (this.realtimeBenchmarkDurationSec + this.realtimeBenchmarkWarmupSec);
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        return `${minutes}분 ${String(seconds).padStart(2, '0')}초 남음`;
    }

    private realtimeBenchmarkStats(values: number[]): { avg: number; p95: number; max: number; count: number } {
        const clean = (values || []).filter(value => Number.isFinite(value) && value >= 0).sort((a, b) => a - b);
        if (clean.length === 0) return { avg: 0, p95: 0, max: 0, count: 0 };
        const avg = clean.reduce((sum, value) => sum + value, 0) / clean.length;
        const p95Index = Math.min(clean.length - 1, Math.max(0, Math.ceil(clean.length * 0.95) - 1));
        return {
            avg: Math.round(avg * 10) / 10,
            p95: Math.round(clean[p95Index] * 10) / 10,
            max: Math.round(clean[clean.length - 1] * 10) / 10,
            count: clean.length,
        };
    }

    private async resolveRealtimeBenchmarkGpuInfo(): Promise<string> {
        try {
            const nav: any = navigator as any;
            if (nav?.gpu?.requestAdapter) {
                const adapter: any = await nav.gpu.requestAdapter({ powerPreference: 'high-performance' });
                const info: any = adapter?.info || (adapter?.requestAdapterInfo ? await adapter.requestAdapterInfo() : null);
                const text = [info?.vendor, info?.architecture, info?.device, info?.description].filter(Boolean).join(' · ');
                if (text) return `WebGPU · ${text}`;
            }
            const canvas = document.createElement('canvas');
            const gl: any = canvas.getContext('webgl2') || canvas.getContext('webgl');
            const extension = gl?.getExtension('WEBGL_debug_renderer_info');
            const renderer = extension ? gl.getParameter(extension.UNMASKED_RENDERER_WEBGL) : gl?.getParameter(gl.RENDERER);
            if (renderer) return `WebGL · ${renderer}`;
        } catch (e) { }
        return 'GPU 식별 정보 비공개 · 지연/드롭 기반 부하 지표 사용';
    }

    private async collectRealtimeBenchmarkSystemTelemetry() {
        if (!this.realtimeBenchmarkRunning || this.realtimeBenchmarkWarmingUp || this.realtimeBenchmarkTelemetryPending) return;
        this.realtimeBenchmarkTelemetryPending = true;
        try {
            const { code, data }: any = await wiz.call('performance_telemetry');
            if (code !== 200 || !data) return;
            this.realtimeBenchmarkLatestTelemetry = data;
            const devices = Array.isArray(data.devices) ? data.devices : [];
            const gpu = devices[0] || null;
            if (gpu) {
                const push = (key: string, value: any) => {
                    const number = Number(value);
                    if (!Number.isFinite(number)) return;
                    const samples = this.realtimeBenchmarkGpuSamples[key] || [];
                    samples.push(number);
                    this.realtimeBenchmarkGpuSamples[key] = samples;
                };
                push('utilization', gpu.utilization_gpu_percent);
                push('memoryUsed', gpu.memory_used_mb);
                push('temperature', gpu.temperature_c);
                push('powerDraw', gpu.power_draw_w);
                const serverGpu = `${gpu.name || 'GPU'} · nvidia-smi 실제 사용률`;
                if (!this.realtimeBenchmarkGpuInfo.includes(serverGpu)) this.realtimeBenchmarkGpuInfo += ` / 서버 ${serverGpu}`;
            }
        } catch (e) { }
        finally { this.realtimeBenchmarkTelemetryPending = false; }
    }

    private loadBenchmarkReferenceImage(): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('표준 4시점 참조 장면을 불러오지 못했습니다.'));
            image.src = `/wiz/api/page.dashboard/benchmark_reference_frame?cache=${Date.now()}`;
        });
    }

    private stopBenchmarkReferenceSource() {
        if (this.benchmarkReferenceAnimationId) cancelAnimationFrame(this.benchmarkReferenceAnimationId);
        this.benchmarkReferenceAnimationId = 0;
        this.benchmarkReferenceCanvas = null;
        this.benchmarkReferenceSourceActive = false;
    }

    private async prepareBenchmarkReferenceSource(): Promise<boolean> {
        if (this.realtimeActive || this.realtimeStarting) await this.stopRealtimeAnalysis();
        this.stopWebcamStream();
        this.clearRealtimeUploadedVideoSource();
        this.inputMode = 'webcam';
        this.realtimeSourceMode = 'camera';
        this.errorMessage = '';
        this.webcamError = '';
        await this.service.render();
        try {
            const referenceImage = await this.loadBenchmarkReferenceImage();
            const canvas = document.createElement('canvas');
            canvas.width = 640;
            canvas.height = 480;
            const context = canvas.getContext('2d', { alpha: false });
            const captureStream = (canvas as any).captureStream;
            if (!context || typeof captureStream !== 'function') {
                throw new Error('현재 브라우저가 표준 참조 장면 캡처를 지원하지 않습니다.');
            }
            this.benchmarkReferenceCanvas = canvas;
            const draw = () => {
                if (!this.benchmarkReferenceSourceActive || this.benchmarkReferenceCanvas !== canvas) return;
                context.fillStyle = '#000';
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.drawImage(referenceImage, 0, 0, canvas.width, canvas.height);
                this.benchmarkReferenceAnimationId = requestAnimationFrame(draw);
            };
            this.benchmarkReferenceSourceActive = true;
            draw();
            this.webcamStream = captureStream.call(canvas, 30);
            this.webcamReady = true;
            this.realtimeBenchmarkSourceLabel = '표준 4시점 참조 장면';
            await this.service.render();
            const videoEl = this.webcamVideoRef?.nativeElement;
            if (!videoEl || !this.webcamStream) throw new Error('참조 장면 플레이어를 준비하지 못했습니다.');
            videoEl.srcObject = this.webcamStream;
            await videoEl.play();
            await this.initMediaPipePose();
            await this.startRealtimeAnalysis();
            return this.realtimeActive;
        } catch (error: any) {
            this.stopBenchmarkReferenceSource();
            this.stopWebcamStream();
            this.errorMessage = error?.message || '표준 참조 장면 측정을 시작하지 못했습니다.';
            await this.service.render();
            return false;
        }
    }

    public async startReferenceRealtimeBenchmark() {
        if (this.realtimeBenchmarkRunning) return;
        this.agentWeightManagerOpen = false;
        this.realtimePerformancePanelOpen = true;
        if (await this.prepareBenchmarkReferenceSource()) await this.startRealtimeBenchmark();
    }

    public async startRealtimeBenchmark() {
        if (this.realtimeBenchmarkRunning) return;
        if (!this.realtimeActive || !this.hasRealtimeInputSource()) {
            this.errorMessage = '프리셋 성능 측정은 영상 또는 카메라 실시간 분석을 시작한 뒤 실행할 수 있습니다.';
            await this.service.render();
            return;
        }
        this.realtimeBenchmarkDurationSec = Math.max(60, Math.min(1800, Math.round(Number(this.realtimeBenchmarkDurationSec) || 300)));
        this.realtimeBenchmarkRunning = true;
        this.realtimeBenchmarkResults = [];
        if (!this.benchmarkReferenceSourceActive) this.realtimeBenchmarkSourceLabel = '운영 카메라/영상';
        this.realtimeBenchmarkGpuInfo = await this.resolveRealtimeBenchmarkGpuInfo();
        this.realtimeBenchmarkLongTaskCount = 0;
        this.realtimeBenchmarkLongTaskMs = 0;
        try {
            const Observer: any = (window as any).PerformanceObserver;
            if (Observer) {
                this.realtimeBenchmarkLongTaskObserver = new Observer((list: any) => {
                    for (const entry of list.getEntries()) {
                        this.realtimeBenchmarkLongTaskCount += 1;
                        this.realtimeBenchmarkLongTaskMs += Number(entry?.duration || 0);
                    }
                });
                this.realtimeBenchmarkLongTaskObserver.observe({ entryTypes: ['longtask'] });
            }
        } catch (e) { }
        await this.beginRealtimeBenchmarkPreset(0);
        this.realtimeBenchmarkTimer = setInterval(() => { void this.tickRealtimeBenchmark(); }, 1000);
        await this.service.render();
    }

    private async beginRealtimeBenchmarkPreset(index: number) {
        this.realtimeBenchmarkPresetIndex = index;
        this.realtimeBenchmarkElapsedSec = 0;
        this.realtimeBenchmarkWarmingUp = true;
        this.realtimeBenchmarkWarmupRemainingSec = this.realtimeBenchmarkWarmupSec;
        this.realtimeBenchmarkMeasurementStartedAt = 0;
        this.realtimeBenchmarkSamples = {};
        this.realtimeBenchmarkFpsSamples = [];
        this.realtimeBenchmarkFaceFpsSamples = [];
        this.realtimeBenchmarkHeapSamples = [];
        this.realtimeBenchmarkGpuSamples = {};
        await this.applyAgentWeightPreset(this.realtimeBenchmarkPresets[index]);
        this.realtimeBenchmarkSegmentStartedAt = performance.now();
    }

    private resetRealtimeBenchmarkMeasurementWindow() {
        this.realtimeBenchmarkSamples = {};
        this.realtimeBenchmarkFpsSamples = [];
        this.realtimeBenchmarkFaceFpsSamples = [];
        this.realtimeBenchmarkHeapSamples = [];
        this.realtimeBenchmarkGpuSamples = {};
        this.realtimeBenchmarkMeasurementStartedAt = performance.now();
        this.realtimeBenchmarkStartStats = {
            droppedFrames: Number(this.realtimePerfStats.droppedFrames || 0),
            failed: Number(this.realtimePerfStats.failed || 0),
            skippedChunks: Number(this.realtimePerfStats.skippedChunks || 0),
            longTaskCount: this.realtimeBenchmarkLongTaskCount,
            longTaskMs: this.realtimeBenchmarkLongTaskMs,
        };
    }

    private async tickRealtimeBenchmark() {
        if (!this.realtimeBenchmarkRunning || this.realtimeBenchmarkPresetIndex < 0) return;
        if (this.realtimeBenchmarkWarmingUp) {
            const warmupElapsed = Math.floor((performance.now() - this.realtimeBenchmarkSegmentStartedAt) / 1000);
            this.realtimeBenchmarkWarmupRemainingSec = Math.max(0, this.realtimeBenchmarkWarmupSec - warmupElapsed);
            if (warmupElapsed < this.realtimeBenchmarkWarmupSec) {
                await this.service.render();
                return;
            }
            this.realtimeBenchmarkWarmingUp = false;
            this.resetRealtimeBenchmarkMeasurementWindow();
        }
        this.realtimeBenchmarkElapsedSec = Math.min(
            this.realtimeBenchmarkDurationSec,
            Math.floor((performance.now() - this.realtimeBenchmarkMeasurementStartedAt) / 1000),
        );
        this.realtimeBenchmarkFpsSamples.push(Number(this.mpFps || 0));
        this.realtimeBenchmarkFaceFpsSamples.push(Number(this.faceMeshFps || 0));
        const memory: any = (performance as any).memory;
        if (Number.isFinite(Number(memory?.usedJSHeapSize))) this.realtimeBenchmarkHeapSamples.push(Number(memory.usedJSHeapSize) / 1024 / 1024);
        if (this.realtimeBenchmarkElapsedSec % 2 === 0) void this.collectRealtimeBenchmarkSystemTelemetry();
        if (this.realtimeBenchmarkElapsedSec >= this.realtimeBenchmarkDurationSec) {
            this.finishRealtimeBenchmarkPreset();
            const nextIndex = this.realtimeBenchmarkPresetIndex + 1;
            if (nextIndex < this.realtimeBenchmarkPresets.length) await this.beginRealtimeBenchmarkPreset(nextIndex);
            else await this.stopRealtimeBenchmark(false);
        }
        await this.service.render();
    }

    private finishRealtimeBenchmarkPreset() {
        const preset = this.realtimeBenchmarkPresets[this.realtimeBenchmarkPresetIndex];
        if (!preset || !this.realtimeBenchmarkStartStats) return;
        const pose = this.realtimeBenchmarkStats(this.realtimeBenchmarkSamples.poseInference || []);
        const environment = this.realtimeBenchmarkStats(this.realtimeBenchmarkSamples.environmentInference || []);
        const emotion = this.realtimeBenchmarkStats(this.realtimeBenchmarkSamples.emotionAux || []);
        const server = this.realtimeBenchmarkStats(this.realtimeBenchmarkSamples.serverTotal || []);
        const sourceDelay = this.realtimeBenchmarkStats(this.realtimeBenchmarkSamples.sourceDelay || []);
        const poseFps = this.realtimeBenchmarkStats(this.realtimeBenchmarkFpsSamples);
        const faceFps = this.realtimeBenchmarkStats(this.realtimeBenchmarkFaceFpsSamples);
        const heap = this.realtimeBenchmarkStats(this.realtimeBenchmarkHeapSamples);
        const gpuUtilization = this.realtimeBenchmarkStats(this.realtimeBenchmarkGpuSamples.utilization || []);
        const gpuMemory = this.realtimeBenchmarkStats(this.realtimeBenchmarkGpuSamples.memoryUsed || []);
        const gpuTemperature = this.realtimeBenchmarkStats(this.realtimeBenchmarkGpuSamples.temperature || []);
        const gpuPower = this.realtimeBenchmarkStats(this.realtimeBenchmarkGpuSamples.powerDraw || []);
        const droppedFrames = Math.max(0, Number(this.realtimePerfStats.droppedFrames || 0) - Number(this.realtimeBenchmarkStartStats.droppedFrames || 0));
        const failed = Math.max(0, Number(this.realtimePerfStats.failed || 0) - Number(this.realtimeBenchmarkStartStats.failed || 0));
        const skippedChunks = Math.max(0, Number(this.realtimePerfStats.skippedChunks || 0) - Number(this.realtimeBenchmarkStartStats.skippedChunks || 0));
        const dropRate = droppedFrames / Math.max(1, droppedFrames + pose.count);
        const budget = this.agentComputeBudget();
        const frameBudgetMs = 1000 / Math.max(1, budget.behaviorFps);
        const loadProxy = Math.min(100, Math.max(
            pose.p95 > 0 ? pose.p95 / frameBudgetMs * 100 : 0,
            dropRate * 100,
        ));
        const passed = pose.count > 0
            && poseFps.avg >= budget.behaviorFps * 0.75
            && pose.p95 <= frameBudgetMs * 1.5
            && dropRate <= 0.15
            && (sourceDelay.count === 0 || sourceDelay.p95 <= 2000)
            && failed === 0;
        this.realtimeBenchmarkResults.push({
            preset,
            label: this.realtimeBenchmarkPresetLabel(preset),
            weights: { ...this.agentWeights },
            durationSec: Math.max(1, Math.round((performance.now() - this.realtimeBenchmarkMeasurementStartedAt) / 1000)),
            targetPoseFps: budget.behaviorFps,
            poseFps,
            faceFps,
            pose,
            environment,
            emotion,
            server,
            sourceDelay,
            droppedFrames,
            dropRatePercent: Math.round(dropRate * 1000) / 10,
            failed,
            skippedChunks,
            heapMaxMb: heap.max,
            gpuTelemetryProvider: String(this.realtimeBenchmarkLatestTelemetry?.provider || 'unavailable'),
            gpuUtilization,
            gpuMemory,
            gpuTemperature,
            gpuPower,
            longTaskCount: Math.max(0, this.realtimeBenchmarkLongTaskCount - Number(this.realtimeBenchmarkStartStats.longTaskCount || 0)),
            longTaskMs: Math.round(Math.max(0, this.realtimeBenchmarkLongTaskMs - Number(this.realtimeBenchmarkStartStats.longTaskMs || 0))),
            computeLoadProxyPercent: Math.round(loadProxy * 10) / 10,
            passed,
        });
    }

    public async stopRealtimeBenchmark(keepPartial: boolean = true) {
        if (!this.realtimeBenchmarkRunning) return;
        if (keepPartial && this.realtimeBenchmarkElapsedSec >= 10) this.finishRealtimeBenchmarkPreset();
        this.realtimeBenchmarkRunning = false;
        if (this.realtimeBenchmarkTimer) clearInterval(this.realtimeBenchmarkTimer);
        this.realtimeBenchmarkTimer = null;
        try { this.realtimeBenchmarkLongTaskObserver?.disconnect(); } catch (e) { }
        this.realtimeBenchmarkLongTaskObserver = null;
        const report = {
            version: 'agent-weight-benchmark-v1',
            measuredAt: new Date().toISOString(),
            gpuInfo: this.realtimeBenchmarkGpuInfo,
            sourceMode: this.realtimeSourceMode,
            sourceLabel: this.realtimeBenchmarkSourceLabel,
            modelType: this.selectedModelType,
            results: this.realtimeBenchmarkResults,
            latestSystemTelemetry: this.realtimeBenchmarkLatestTelemetry,
        };
        try { localStorage.setItem('fallai:agent-weight-benchmark-v1', JSON.stringify(report)); } catch (e) { }
        await this.service.render();
        if (this.benchmarkReferenceSourceActive) {
            setTimeout(() => {
                if (this.benchmarkReferenceSourceActive && !this.realtimeBenchmarkRunning) void this.stopRealtimeAnalysis();
            }, 0);
        }
    }

    public exportRealtimeBenchmark() {
        if (this.realtimeBenchmarkResults.length === 0) return;
        const report = {
            version: 'agent-weight-benchmark-v1',
            measuredAt: new Date().toISOString(),
            gpuInfo: this.realtimeBenchmarkGpuInfo,
            sourceMode: this.realtimeSourceMode,
            sourceLabel: this.realtimeBenchmarkSourceLabel,
            modelType: this.selectedModelType,
            results: this.realtimeBenchmarkResults,
            latestSystemTelemetry: this.realtimeBenchmarkLatestTelemetry,
        };
        const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `fallai-agent-weight-benchmark-${Date.now()}.json`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    private recordRealtimePerf(stage: string, valueMs: number) {
        const value = Number(valueMs);
        if (!Number.isFinite(value) || value < 0) return;
        const now = Date.now();
        const cutoff = now - 30000;
        const samples = this.realtimePerfSamples[stage] || [];
        samples.push({ at: now, value });
        while (samples.length > 0 && samples[0].at < cutoff) samples.shift();
        this.realtimePerfSamples[stage] = samples;
        if (this.realtimeBenchmarkRunning) {
            const benchmarkSamples = this.realtimeBenchmarkSamples[stage] || [];
            benchmarkSamples.push(value);
            if (benchmarkSamples.length > 20_000) benchmarkSamples.shift();
            this.realtimeBenchmarkSamples[stage] = benchmarkSamples;
        }
    }

    private realtimePerfSummary(stage: string): { avg: number; p95: number; max: number; count: number } {
        const cutoff = Date.now() - 30000;
        const samples = (this.realtimePerfSamples[stage] || []).filter(item => item.at >= cutoff);
        if (samples.length === 0) return { avg: 0, p95: 0, max: 0, count: 0 };
        const values = samples.map(item => item.value).sort((a, b) => a - b);
        const total = values.reduce((sum, value) => sum + value, 0);
        const p95Index = Math.min(values.length - 1, Math.max(0, Math.ceil(values.length * 0.95) - 1));
        return {
            avg: Math.round((total / values.length) * 10) / 10,
            p95: Math.round(values[p95Index] * 10) / 10,
            max: Math.round(values[values.length - 1] * 10) / 10,
            count: values.length,
        };
    }

    public realtimePerformanceRows(): any[] {
        return [
            ['captureDelay', 'captureDelay'],
            ['capture', '영상 캡처'],
            ['resize', '480px 리사이즈'],
            ['encode', 'encodeTime · 청크 인코딩'],
            ['upload', 'uploadTime · 청크 업로드'],
            ['networkReturn', 'networkReturn'],
            ['waiting', '서버 응답 대기'],
            ['parse', '응답 파싱'],
            ['overlay', '스켈레톤 그리기'],
            ['poseInference', 'Pose 추론'],
            ['environmentInference', '환경 추론'],
            ['fastFallGate', '빠른 낙상 후보'],
            ['eventFeedRender', '이벤트 피드 반영'],
            ['ui', '화면 상태 갱신'],
            ['log', '이벤트 로그'],
            ['total', '전체 클라이언트 지연'],
            ['sourceDelay', 'sourceDelay · 영상 대비 지연'],
            ['overlayAge', 'overlayAge'],
            ['queueWait', 'queueWait · 서버 큐'],
            ['inferenceTime', 'inferenceTime'],
            ['decode', '서버 청크 디코드'],
            ['personDetection', '사람 검출·Pose'],
            ['tracking', 'Logical tracking'],
            ['logicalPersonAnalysis', '사람별 RF-Dual'],
            ['rfFall', 'RF-Fall'],
            ['rfFallWindow', '실시간 RF Pose 창'],
            ['posture', 'XG-Posture'],
            ['personObjectFilter', '사람·사물 필터'],
            ['shadow', 'Shadow 비동기'],
            ['emotionAux', '표정 보조'],
            ['auxiliaryWorker', '비동기 보조 전체'],
            ['environmentAux', '환경 보조'],
            ['responseBuild', '응답 조립'],
            ['serverTotal', '전체 서버 지연'],
        ].map(([key, label]) => ({ key, label, ...this.realtimePerfSummary(key) }));
    }

    private requestRealtimeUiRender(force: boolean = false) {
        const minInterval = 1000 / this.realtimePresetConfig().uiFps;
        const now = performance.now();
        const run = () => {
            this.realtimeUiRenderTimer = null;
            this.realtimeLastUiRenderAt = performance.now();
            const startedAt = performance.now();
            try {
                Promise.resolve(this.service.render()).finally(() => this.recordRealtimePerf('ui', performance.now() - startedAt));
            } catch (e) {
                this.recordRealtimePerf('ui', performance.now() - startedAt);
            }
        };
        if (force || now - this.realtimeLastUiRenderAt >= minInterval) {
            if (this.realtimeUiRenderTimer) clearTimeout(this.realtimeUiRenderTimer);
            run();
            return;
        }
        if (!this.realtimeUiRenderTimer) {
            this.realtimeUiRenderTimer = setTimeout(run, Math.max(0, minInterval - (now - this.realtimeLastUiRenderAt)));
        }
    }

    private getChunkConfig(): { version: string; chunkSec: number; spawnMs: number; maxSlots: number; maxQueue: number; bootstrapDurations: number[]; strideSec: number } {
        const backend = this.prototypeInfo?.chunk_policy?.realtime || {};
        if (backend?.enabled === true || backend?.steady_sec || backend?.stride_sec) {
            return {
                version: String(backend?.version || 'overlap-4s-stride2-v3'),
                chunkSec: Number(backend?.steady_sec || 4),
                spawnMs: Number(backend?.spawn_ms || 2000),
                maxSlots: Number(backend?.max_slots || 2),
                maxQueue: Number(backend?.max_queue || 120),
                bootstrapDurations: Array.isArray(backend?.dense_intro) ? backend.dense_intro.map((v: any) => Number(v || 0)).filter((v: number) => v > 0) : [],
                strideSec: Number(backend?.stride_sec || 2),
            };
        }
        const key = this.selectedModelType || '';
        if (key === 'rf-dual' || key === 'rf-pipeline') return { version: 'legacy-rf-dual-v1', chunkSec: 4, spawnMs: 4000, maxSlots: 2, maxQueue: 120, bootstrapDurations: [], strideSec: 4 };
        return { version: 'legacy-generic-v1', chunkSec: 4, spawnMs: 4000, maxSlots: 2, maxQueue: 120, bootstrapDurations: [], strideSec: 4 };
    }

    private formatChunkSecond(value: number): string {
        const rounded = Math.round((Number(value || 0)) * 10) / 10;
        return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    }

    private buildChunkLabel(startSec: number, endSec: number): string { return `${this.formatChunkSecond(startSec)}~${this.formatChunkSecond(endSec)}초`; }
    private buildRealtimeChunkWindow(startedAtMs: number, durationSec: number) {
        const startSec = Math.max(0, (startedAtMs - this.realtimeSessionStartedAt) / 1000);
        const endSec = startSec + Number(durationSec || 0);
        return { startSec: Math.round(startSec * 10) / 10, endSec: Math.round(endSec * 10) / 10, label: this.buildChunkLabel(startSec, endSec) };
    }

    private realtimeSourceTime(): number {
        const videoTime = Number(this.webcamVideoRef?.nativeElement?.currentTime);
        if (Number.isFinite(videoTime) && videoTime >= 0) return videoTime;
        return Math.max(0, (Date.now() - this.realtimeSessionStartedAt) / 1000);
    }

    private buildRealtimeSourceWindow(sourceStartTime: number, durationSec: number) {
        const sourceEndTime = this.realtimeSourceTime();
        const startSec = Math.max(0, Number.isFinite(sourceStartTime) ? sourceStartTime : sourceEndTime - durationSec);
        const endSec = Math.max(startSec, sourceEndTime || startSec + durationSec);
        return {
            startSec: Math.round(startSec * 1000) / 1000,
            endSec: Math.round(endSec * 1000) / 1000,
            label: this.buildChunkLabel(startSec, endSec),
            ringBufferFrames: this.realtimeRingBuffer.length,
        };
    }

    private startRealtimeVideoFpsMeasure(videoEl: HTMLVideoElement) {
        this.realtimeVideoFrameCount = 0;
        this.realtimeVideoFrameMeasureStartedAt = performance.now();
        const callback = (_now: number) => {
            if (!this.realtimeActive) return;
            this.realtimeVideoFrameCount += 1;
            const elapsed = performance.now() - this.realtimeVideoFrameMeasureStartedAt;
            if (elapsed >= 1000) {
                this.realtimeVideoFps = Math.round((this.realtimeVideoFrameCount * 1000 / elapsed) * 10) / 10;
                this.realtimeVideoFrameCount = 0;
                this.realtimeVideoFrameMeasureStartedAt = performance.now();
            }
            const requestVideoFrameCallback = (videoEl as any).requestVideoFrameCallback;
            if (typeof requestVideoFrameCallback === 'function') {
                this.realtimeVideoFrameCallbackId = requestVideoFrameCallback.call(videoEl, callback);
            }
        };
        const requestVideoFrameCallback = (videoEl as any).requestVideoFrameCallback;
        if (typeof requestVideoFrameCallback === 'function') {
            this.realtimeVideoFrameCallbackId = requestVideoFrameCallback.call(videoEl, callback);
        }
    }

    private startRealtimeRingCapture() {
        const videoEl = this.webcamVideoRef?.nativeElement;
        if (!videoEl) throw new Error('실시간 분석 영상을 준비하지 못했습니다.');
        this.stopRealtimeRingCapture();
        this.realtimeAnalysisCanvas = document.createElement('canvas');
        this.realtimeAnalysisCanvas.width = REALTIME_ANALYSIS_INPUT_SIZE;
        this.realtimeAnalysisCanvas.height = REALTIME_ANALYSIS_INPUT_SIZE;
        this.realtimeAnalysisContext = this.realtimeAnalysisCanvas.getContext('2d', { alpha: false });
        if (!this.realtimeAnalysisContext) throw new Error('저해상도 분석 Canvas를 만들지 못했습니다.');
        const captureStream = (this.realtimeAnalysisCanvas as any).captureStream;
        if (typeof captureStream !== 'function') throw new Error('현재 브라우저가 Canvas 실시간 캡처를 지원하지 않습니다.');
        this.realtimeAnalysisStream = captureStream.call(this.realtimeAnalysisCanvas, this.realtimeAnalysisCaptureFps);
        this.realtimeRingBuffer = [];
        this.realtimeLastCaptureAt = 0;
        const capture = () => {
            if (!this.realtimeActive || !this.realtimeAnalysisCanvas || !this.realtimeAnalysisContext) return;
            const targetFps = this.realtimeCaptureTargetFps();
            this.realtimeEffectiveCaptureFps = targetFps;
            const captureStartedAt = performance.now();
            const expectedIntervalMs = 1000 / Math.max(1, targetFps);
            if (this.realtimeLastCaptureAt > 0) {
                this.recordRealtimePerf('captureDelay', Math.max(0, captureStartedAt - this.realtimeLastCaptureAt - expectedIntervalMs));
            }
            if (videoEl.readyState < 2) {
                this.realtimeRingCaptureTimer = setTimeout(capture, expectedIntervalMs);
                return;
            }
            this.realtimeLastCaptureAt = captureStartedAt;
            const sourceW = Math.max(1, Number(videoEl.videoWidth || videoEl.clientWidth || REALTIME_ANALYSIS_INPUT_SIZE));
            const sourceH = Math.max(1, Number(videoEl.videoHeight || videoEl.clientHeight || REALTIME_ANALYSIS_INPUT_SIZE));
            const scale = Math.min(REALTIME_ANALYSIS_INPUT_SIZE / sourceW, REALTIME_ANALYSIS_INPUT_SIZE / sourceH);
            const drawW = Math.max(1, Math.round(sourceW * scale));
            const drawH = Math.max(1, Math.round(sourceH * scale));
            const offsetX = Math.floor((REALTIME_ANALYSIS_INPUT_SIZE - drawW) / 2);
            const offsetY = Math.floor((REALTIME_ANALYSIS_INPUT_SIZE - drawH) / 2);
            this.realtimeAnalysisCoordinateMeta = {
                coordinate_space: 'letterboxed_analysis_input',
                source_width: sourceW,
                source_height: sourceH,
                analysis_width: REALTIME_ANALYSIS_INPUT_SIZE,
                analysis_height: REALTIME_ANALYSIS_INPUT_SIZE,
                content_offset_x: offsetX,
                content_offset_y: offsetY,
                content_width: drawW,
                content_height: drawH,
                normalized: false,
            };
            const resizeStartedAt = performance.now();
            this.realtimeAnalysisContext.fillStyle = '#000';
            this.realtimeAnalysisContext.fillRect(0, 0, REALTIME_ANALYSIS_INPUT_SIZE, REALTIME_ANALYSIS_INPUT_SIZE);
            this.realtimeAnalysisContext.drawImage(videoEl, offsetX, offsetY, drawW, drawH);
            this.recordRealtimePerf('resize', performance.now() - resizeStartedAt);
            this.recordRealtimePerf('capture', performance.now() - captureStartedAt);
            this.realtimeRingBuffer.push({ sourceTime: this.realtimeSourceTime(), capturedAt: Date.now() });
            if (this.realtimeRingBuffer.length > this.realtimeRingBufferMaxFrames) this.realtimeRingBuffer.shift();
            this.realtimeRingCaptureTimer = setTimeout(capture, 1000 / Math.max(1, this.realtimeCaptureTargetFps()));
        };
        capture();
        this.startRealtimeVideoFpsMeasure(videoEl);
    }

    private stopRealtimeRingCapture() {
        if (this.realtimeRingCaptureTimer) { clearInterval(this.realtimeRingCaptureTimer); this.realtimeRingCaptureTimer = null; }
        const videoEl = this.webcamVideoRef?.nativeElement as any;
        if (videoEl && this.realtimeVideoFrameCallbackId && typeof videoEl.cancelVideoFrameCallback === 'function') {
            try { videoEl.cancelVideoFrameCallback(this.realtimeVideoFrameCallbackId); } catch (e) { }
        }
        this.realtimeVideoFrameCallbackId = 0;
        for (const track of this.realtimeAnalysisStream?.getTracks?.() || []) {
            try { track.stop(); } catch (e) { }
        }
        this.realtimeAnalysisStream = null;
        this.realtimeAnalysisCanvas = null;
        this.realtimeAnalysisContext = null;
        this.realtimeAnalysisCoordinateMeta = null;
        this.realtimeRingBuffer = [];
    }

    private attachRealtimeCoordinateMetadata(result: any): any {
        if (!result || !this.realtimeAnalysisCoordinateMeta) return result;
        result.client_coordinate_metadata = {
            ...this.realtimeAnalysisCoordinateMeta,
        };
        return result;
    }

    private async readJsonResponse(res: Response): Promise<any> {
        const text = await res.text();
        const trimmed = String(text || '').trim();
        if (!trimmed) {
            throw new Error(`서버 응답이 비어 있습니다. HTTP ${res.status || 'unknown'}`);
        }
        const contentType = String(res.headers?.get('content-type') || '').toLowerCase();
        if (contentType.includes('text/html') || /^<!doctype\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) {
            const titleMatch = trimmed.match(/<title[^>]*>(.*?)<\/title>/i);
            const title = titleMatch ? titleMatch[1].replace(/\s+/g, ' ').trim() : '';
            const detail = title ? ` · ${title}` : '';
            throw new Error(`서버가 JSON 대신 HTML 오류 페이지를 반환했습니다. HTTP ${res.status || 'unknown'}${detail}. 화면을 새로고침한 뒤 다시 시도하고, 계속 반복되면 파일 수를 줄여 나눠 등록하거나 학습 현황/서버 로그를 확인하세요.`);
        }
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            const normalized = trimmed.replace(/([:\[,]\s*)(?:NaN|-?Infinity)(?=\s*[,}\]])/g, (_match, prefix) => `${prefix}0`);
            if (normalized !== trimmed) {
                try {
                    return JSON.parse(normalized);
                } catch (_normalizedError) { }
            }
            const snippet = trimmed.replace(/\s+/g, ' ').slice(0, 180);
            if (/^upstream\b/i.test(snippet) || snippet.includes('upstream connect error')) {
                throw new Error(`서버 업스트림 연결이 끊겼습니다. 요청은 일부 처리됐을 수 있으니 학습 현황을 새로고침해 확인하세요. (${snippet})`);
            }
            throw new Error(`서버가 JSON이 아닌 응답을 반환했습니다. HTTP ${res.status || 'unknown'} · ${snippet}`);
        }
    }

    private requestErrorMessage(error: any, context: string = '요청'): string {
        const raw = String(error?.message || error || '').trim();
        if (raw.includes('JSON 대신 HTML') || raw.includes('<!DOCTYPE') || raw.includes('Unexpected token')) {
            return `${context} 오류: 서버가 정상 JSON 대신 오류 화면을 반환했습니다. 먼저 페이지를 새로고침하고 다시 시도하세요. 계속 반복되면 선택한 파일을 100개 이하로 나눠 등록하고, 학습 현황 카드에서 저장 여부를 확인하세요. 상세: ${raw}`;
        }
        if (raw.includes('upstream connect error') || raw.includes('업스트림')) {
            return `${context} 오류: 서버 연결이 중간에 끊겼습니다. 일부 파일은 저장됐을 수 있으니 학습 현황을 새로고침한 뒤, 실패한 파일만 다시 등록하세요. 상세: ${raw}`;
        }
        return `${context} 오류: ${raw || '알 수 없는 오류'}`;
    }

    private currentRootAnalysisResult(): any {
        return this.rootAnalysisResult || this.analysisResult || null;
    }

    private findPersonAnalysisItem(personId: string, root?: any): any {
        const target = String(personId || '');
        const people = (root || this.currentRootAnalysisResult())?.multi_person?.people || [];
        if (!Array.isArray(people) || !target) return null;
        return people.find((item: any) => String(item?.id || '') === target || String(item?.track_id || '') === target || String(item?.person_label || '') === target) || null;
    }

    private applyAnalysisRoot(result: any, preserveSelection: boolean = true) {
        result = this.applyClientAgentWeights(result);
        this.rootAnalysisResult = result || null;
        const previousId = preserveSelection ? String(this.selectedPersonAnalysisId || 'overall') : 'overall';
        const selectedItem = previousId !== 'overall' ? this.findPersonAnalysisItem(previousId, result) : null;
        if (selectedItem?.result) {
            this.selectedPersonAnalysisId = String(selectedItem.id || previousId);
            this.analysisResult = selectedItem.result;
        } else {
            this.selectedPersonAnalysisId = 'overall';
            this.analysisResult = result || null;
        }
        this.detectionReplayIndex = 0;
        this.selectedShadowEvent = null;
        this.shadowWindowsOpen = false;
        this.shadowFeedbackMessage = '';
        if (this.detectionReplayPlaying) this.stopDetectionReplay();
    }

    private clearAnalysisState() {
        this.rootAnalysisResult = null;
        this.analysisResult = null;
        this.analysisSavedName = '';
        this.alertWorkflow = null;
        this.selectedPersonAnalysisId = 'overall';
        this.selectedShadowEvent = null;
        this.shadowWindowsOpen = false;
        this.shadowFeedbackMessage = '';
        this.detectionReplayIndex = 0;
        this.resetUploadFeedbackForm();
        if (this.detectionReplayPlaying) this.stopDetectionReplay();
    }

    private resetUploadFeedbackForm() {
        this.feedbackStatus = '';
        this.feedbackActualLabel = '';
        this.feedbackPostureClass = '';
        this.feedbackNote = '';
        this.feedbackRetrainRequested = false;
        this.feedbackMessage = '';
        this.feedbackMessageType = 'info';
        this.feedbackAmbiguityFlag = false;
        this.feedbackOcclusionFlag = false;
        this.feedbackShortClipFlag = false;
        this.feedbackStaticFilterLabel = '';
    }

    public selectPersonAnalysis(personId: string) {
        const root = this.currentRootAnalysisResult();
        if (!root) return;
        const nextId = String(personId || 'overall');
        if (nextId === 'overall' || nextId === String(this.selectedPersonAnalysisId || 'overall')) {
            this.selectedPersonAnalysisId = 'overall';
            this.analysisResult = root;
        } else {
            const item = this.findPersonAnalysisItem(nextId, root);
            if (!item?.result) return;
            this.selectedPersonAnalysisId = String(item.id || nextId);
            this.analysisResult = item.result;
        }
        this.resetUploadFeedbackForm();
        this.selectedShadowEvent = null;
        this.shadowWindowsOpen = false;
        this.shadowFeedbackMessage = '';
        this.detectionReplayIndex = 0;
        if (this.detectionReplayPlaying) this.stopDetectionReplay();
        this.updateRealtimeOverlay();
        this.service.render();
    }

    public multiPersonPeople(): any[] {
        const people = this.currentRootAnalysisResult()?.multi_person?.people || [];
        return Array.isArray(people)
            ? people.filter((item: any) => item?.is_logical_primary !== false)
            : [];
    }

    public multiPersonTrackPeople(): any[] {
        const people = this.currentRootAnalysisResult()?.multi_person?.people || [];
        return Array.isArray(people) ? people : [];
    }

    public shadowOnlyTrackItems(): any[] {
        const tracks = this.currentRootAnalysisResult()?.multi_person?.shadow_only_tracks || [];
        return Array.isArray(tracks) ? tracks : [];
    }

    public hasShadowOnlyTrackItems(): boolean {
        return this.shadowOnlyTrackItems().length > 0;
    }

    public shadowOnlyTrackMetaText(item: any): string {
        const frames = Number(item?.frame_count || 0);
        const shadow = item?.moving_object_shadow || item?.result?.moving_object_shadow || {};
        const status = String(shadow?.calculation_status || 'unavailable');
        const wouldVeto = shadow?.would_veto_flag === true ? 'would-veto' : '관찰';
        return `${frames}프레임 · ${status} · ${wouldVeto} · 행동/낙상 판정 미실행`;
    }

    public hasMultiPersonCards(): boolean {
        return this.multiPersonPeople().length > 0;
    }

    public requiresEntityReanalysis(): boolean {
        const root = this.currentRootAnalysisResult() || {};
        if (!Array.isArray(root?.multi_person?.people) || root.multi_person.people.length === 0) return false;
        const filters = Array.isArray(root?.runtime_filters?.items) ? root.runtime_filters.items : [];
        return !filters.some((item: any) => (
            item?.family === 'pose-entity-filter'
            && item?.applied === true
            && String(item?.version || '') === 'v1'
            && String(item?.artifact_sha256 || '').length >= 32
        ));
    }

    public hasSelectedUploadVideo(): boolean {
        return Boolean(this.selectedFile && this.selectedVideoUrl);
    }

    public canShowUploadAnalysisResult(): boolean {
        return this.inputMode !== 'webcam' && Boolean(this.analysisResult) && this.hasSelectedUploadVideo();
    }

    public multiPersonSummaryLine(): string {
        const rootMulti = this.currentRootAnalysisResult()?.multi_person || {};
        const people = this.multiPersonPeople();
        const correctionCount = people.filter((item: any) => Boolean(item?.manual_entity_correction)).length;
        const personCount = people.filter((item: any) => this.personEntityType(item) === 'person').length;
        const rawPersonTrackCount = Number(rootMulti?.raw_person_track_count ?? this.multiPersonTrackPeople().filter((item: any) => this.personEntityType(item) === 'person').length);
        const objectCount = people.filter((item: any) => this.personEntityType(item) === 'object').length;
        const reviewCount = people.filter((item: any) => this.personEntityType(item) === 'uncertain').length;
        return `사람 ${personCount}명 · 연결된 검출 트랙 ${rawPersonTrackCount}개 · 사람 제외 ${objectCount}${reviewCount ? ` · 확인 필요 ${reviewCount}` : ''}${correctionCount ? ` · 수동 정정 ${correctionCount}` : ''}`;
    }

    public multiPersonCardItems(): any[] {
        return this.multiPersonPeople();
    }

    public isPersonAnalysisSelected(): boolean {
        return this.selectedPersonAnalysisId !== 'overall' && Boolean(this.analysisResult?.person_id || this.analysisResult?.runtime_inference?.person_track);
    }

    public selectedPersonDetailsVisible(): boolean {
        return !this.hasMultiPersonCards() || this.isPersonAnalysisSelected();
    }

    public realtimeDetailOverlayTop(): string {
        return this.hasMultiPersonCards() ? '148px' : '12px';
    }

    public personCardClass(item: any): string {
        const selected = String(item?.id || '') === String(this.selectedPersonAnalysisId || 'overall');
        const level = item?.risk_level || 'low';
        const base = selected ? 'ring-2 ring-sky-400 border-sky-300 ' : 'border-zinc-200 ';
        if (this.personEntityType(item) === 'object') return base + 'bg-zinc-50 text-zinc-700';
        if (level === 'high') return base + 'bg-rose-50 text-rose-950';
        if (level === 'medium') return base + 'bg-amber-50 text-amber-950';
        return base + 'bg-white text-zinc-900';
    }

    public personCardDotClass(item: any): string {
        if (this.personEntityType(item) === 'object') return 'bg-zinc-400';
        const level = item?.risk_level || 'low';
        if (level === 'high') return 'bg-rose-500';
        if (level === 'medium') return 'bg-amber-500';
        return 'bg-emerald-500';
    }

    public personCardRiskText(item: any): string {
        if (this.personEntityType(item) === 'object') return '분석 제외';
        if (item?.manual_entity_correction && this.modelPersonEntityType(item) === 'object') return '수동 정정';
        const score = Number(item?.raw_model_risk_score ?? item?.result?.raw_model_risk_score ?? item?.result?.model_output?.risk_score ?? item?.risk_score ?? 0);
        return `RF 원시 점수 ${Math.round(score * 100)}%`;
    }

    public personCardLogicalText(item: any): string {
        const logicalId = String(item?.logical_person_id || '').trim();
        const primaryLabel = String(item?.person_label || item?.track_id || '').trim();
        const aliases = Array.isArray(item?.alias_tracks) ? item.alias_tracks : [];
        const linkedLabels = [primaryLabel, ...aliases.map((alias: any) => String(alias?.person_label || alias?.track_id || '').trim())].filter(Boolean);
        if (!logicalId) return linkedLabels.length ? `검출 트랙 ${linkedLabels.join(', ')}` : '';
        return `${logicalId} · 연결된 검출 트랙 ${linkedLabels.join(', ')}`;
    }

    public personCardAliasText(item: any): string {
        const aliases = Array.isArray(item?.alias_tracks) ? item.alias_tracks : [];
        return aliases.map((alias: any) => {
            const label = String(alias?.person_label || alias?.track_id || 'alias');
            const raw = Math.round(Number(alias?.raw_model_risk_score || 0) * 100);
            return `${label} RF 원시 ${raw}%`;
        }).join(' · ');
    }

    public personCardMetaText(item: any): string {
        const frames = Number(item?.frame_count || 0);
        const human = Math.round(Number(item?.result?.entity_classification?.person_evidence_score ?? item?.human_pose_ratio ?? item?.result?.entity_classification?.human_pose_ratio ?? 0) * 100);
        const threshold = Math.round(Number(item?.result?.entity_classification?.threshold || 0.60) * 100);
        const poseValid = Math.round(Number(item?.pose_valid_ratio || item?.result?.entity_classification?.components?.pose_valid_ratio || 0) * 100);
        const detector = Math.round(Number(item?.detector_confidence ?? item?.avg_conf ?? 0) * 100);
        return `${frames}프레임 · 사람 근거 ${human}%/기준 ${threshold}% · 포즈 유효 ${poseValid}% · 검출 ${detector}%`;
    }

    public personCardBehaviorText(item: any): string {
        if (!item?.manual_entity_correction) return String(item?.behavior_label || '');
        if (this.personEntityType(item) === 'object') return '행동 분석 제외';
        if (this.modelPersonEntityType(item) === 'object') return '수동 정정 · 행동 미분석';
        return String(item?.behavior_label || '');
    }

    public personEntityType(item: any): string {
        const corrected = String(item?.manual_entity_correction?.corrected_entity || '').trim();
        if (corrected === 'person' || corrected === 'object') return corrected;
        return this.modelPersonEntityType(item);
    }

    public modelPersonEntityType(item: any): string {
        return String(item?.entity_type || item?.result?.entity_classification?.entity_type || 'unknown');
    }

    private entityShadowRecordId(item: any): string {
        return String(
            item?.moving_object_shadow?.shadow_record_id
            || item?.result?.moving_object_shadow?.shadow_record_id
            || item?.result?.entity_classification?.shadow_diagnostics?.shadow_record_id
            || '',
        ).trim();
    }

    private entitySourceEvidenceToken(item: any): string {
        return String(
            item?.source_evidence_token
            || item?.moving_object_shadow?.source_evidence_token
            || item?.result?.source_evidence_token
            || item?.result?.moving_object_shadow?.source_evidence_token
            || item?.result?.entity_classification?.shadow_diagnostics?.source_evidence_token
            || '',
        ).trim();
    }

    private isMovingObjectShadowQueueItem(item: any): boolean {
        return item?._movingObjectShadowQueueItem === true || item?.shadow_review_queue_item === true;
    }

    private hasTrackVisualEvidence(item: any): boolean {
        return item?.visual_evidence_available === true
            || item?.moving_object_shadow?.source_evidence_available === true
            || item?.result?.visual_evidence_available === true
            || item?.result?.moving_object_shadow?.source_evidence_available === true
            || item?.result?.entity_classification?.shadow_diagnostics?.source_evidence_available === true;
    }

    public personEntityLabel(item: any): string {
        const type = this.personEntityType(item);
        if (type === 'person') return '사람';
        if (type === 'object') return '사람 제외';
        if (type === 'uncertain') return '확인 필요';
        return '판정 불가';
    }

    public personEntityBadgeClass(item: any): string {
        const type = this.personEntityType(item);
        const corrected = Boolean(item?.manual_entity_correction);
        if (type === 'person') return corrected ? 'bg-sky-100 text-sky-800 ring-1 ring-sky-300' : 'bg-sky-100 text-sky-700';
        if (type === 'object') return corrected ? 'bg-zinc-200 text-zinc-800 ring-1 ring-zinc-400' : 'bg-zinc-200 text-zinc-700';
        if (type === 'uncertain') return 'bg-amber-100 text-amber-700';
        return 'bg-amber-100 text-amber-700';
    }

    public canCorrectEntity(item: any): boolean {
        const modelType = this.modelPersonEntityType(item);
        const validModelDecision = modelType === 'person' || modelType === 'object' || modelType === 'uncertain';
        const hasOpaqueShadowRecord = Boolean(this.entityShadowRecordId(item));
        const hasSourceEvidenceToken = Boolean(this.entitySourceEvidenceToken(item));
        const hasTrackVisualEvidence = this.hasTrackVisualEvidence(item);
        const isShadowOnlyTrack = item?.shadow_only_track === true || item?.result?.shadow_only_track === true;
        const hasUploadReviewSource = this.inputMode !== 'webcam'
            && Boolean(this.analysisSavedName)
            && Boolean(item?.track_id);
        if (this.isMovingObjectShadowQueueItem(item)) {
            return hasOpaqueShadowRecord
                && item?.source_evidence_available === true
                && item?.ground_truth_eligible === true
                && hasSourceEvidenceToken;
        }
        const requiresOpaqueShadowReview = isShadowOnlyTrack
            || this.inputMode === 'webcam'
            || !this.analysisSavedName
            || !validModelDecision;
        if (requiresOpaqueShadowReview) {
            return hasOpaqueShadowRecord && hasSourceEvidenceToken && hasTrackVisualEvidence;
        }
        return validModelDecision && hasUploadReviewSource && hasTrackVisualEvidence;
    }

    public isEntityCorrectionSubmitting(item: any): boolean {
        return Boolean(this.entityCorrectionSubmitting[
            String(item?.id || item?.track_id || this.entityShadowRecordId(item) || '')
        ]);
    }

    public entityLabelChoiceClass(item: any, label: 'person' | 'object'): string {
        if (this.personEntityType(item) === label) {
            return label === 'person'
                ? 'border-sky-300 bg-sky-100 text-sky-800'
                : 'border-zinc-400 bg-zinc-200 text-zinc-800';
        }
        return 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50';
    }

    public entityCorrectionShortLabel(item: any): string {
        return this.personEntityType(item) === 'person' ? '사물로 수정' : '사람으로 수정';
    }

    public entityCorrectionButtonTitle(item: any, correctedType?: 'person' | 'object'): string {
        if (!this.canCorrectEntity(item)) {
            if (this.isMovingObjectShadowQueueItem(item)) {
                return '수치 신호만 있는 triage 기록입니다. 서버가 발급한 증거 토큰과 원본 근거가 확인된 경우에만 라벨링할 수 있습니다.';
            }
            return '선택 트랙 오버레이와 결합된 원본 근거가 있는 현재 분석 결과에서만 라벨을 지정할 수 있습니다.';
        }
        const target = correctedType || (this.personEntityType(item) === 'person' ? 'object' : 'person');
        const targetLabel = target === 'person' ? '사람' : '사물';
        const action = this.personEntityType(item) === target ? '수동 확인' : '수동 라벨 지정';
        return `${targetLabel}으로 ${action}`;
    }

    private clearLegacyEntityCorrectionOperatorStorage() {
        try {
            localStorage.removeItem('fallai:entity-correction-operator-name');
            localStorage.removeItem('fallai:entity-correction-operator-id');
        } catch (e) { }
    }

    private showEntityCorrectionMessage(message: string, type: 'success' | 'error' | 'info' = 'info') {
        this.entityCorrectionMessage = message;
        this.entityCorrectionMessageType = type;
        if (this.entityCorrectionMessageTimer) clearTimeout(this.entityCorrectionMessageTimer);
        this.entityCorrectionMessageTimer = setTimeout(async () => {
            this.entityCorrectionMessage = '';
            this.entityCorrectionMessageTimer = null;
            await this.service.render();
        }, 5000);
    }

    private normalizeMovingObjectShadowEntity(value: any): 'person' | 'object' | 'uncertain' {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized === 'person' || normalized === 'human' || normalized === '사람') return 'person';
        if (normalized === 'object' || normalized === '사물') return 'object';
        return 'uncertain';
    }

    private normalizeMovingObjectShadowQueueItem(raw: any): any {
        const source = raw && typeof raw === 'object' ? raw : {};
        const sourceShadow = source?.moving_object_shadow && typeof source.moving_object_shadow === 'object'
            ? source.moving_object_shadow
            : {};
        const shadowRecordId = String(
            sourceShadow?.shadow_record_id || source?.shadow_record_id || '',
        ).trim();
        const manualLabel = this.normalizeMovingObjectShadowEntity(
            sourceShadow?.manual_label || source?.manual_label || '',
        );
        const entityType = this.normalizeMovingObjectShadowEntity(
            source?.entity_type
            || sourceShadow?.final_pose_entity_filter_decision
            || sourceShadow?.final_entity_decision
            || source?.final_pose_entity_filter_decision,
        );
        const analysisScope = String(
            source?.analysis_scope || sourceShadow?.analysis_scope || 'full_rf_dual',
        ).trim();
        const shadowOnlyTrack = source?.shadow_only_track === true || sourceShadow?.shadow_only_track === true;
        const sourceEvidenceAvailable = source?.source_evidence_available === true
            || sourceShadow?.source_evidence_available === true;
        const groundTruthEligible = source?.ground_truth_eligible === true
            || sourceShadow?.ground_truth_eligible === true;
        const sourceEvidenceToken = String(
            source?.source_evidence_token || sourceShadow?.source_evidence_token || '',
        ).trim();
        return {
            ...source,
            id: String(source?.id || shadowRecordId),
            track_id: String(source?.track_id || sourceShadow?.track_id || ''),
            person_label: String(source?.person_label || source?.track_label || 'Shadow track'),
            entity_type: entityType,
            analysis_scope: analysisScope,
            shadow_only_track: shadowOnlyTrack,
            shadow_review_queue_item: source?.shadow_review_queue_item === true,
            source_evidence_available: sourceEvidenceAvailable,
            ground_truth_eligible: groundTruthEligible,
            source_evidence_token: sourceEvidenceToken,
            _movingObjectShadowQueueItem: true,
            moving_object_shadow: {
                ...sourceShadow,
                shadow_record_id: shadowRecordId,
                manual_label: sourceShadow?.manual_label ?? source?.manual_label ?? null,
                would_veto_flag: sourceShadow?.would_veto_flag ?? source?.would_veto_flag ?? false,
                frame_range: sourceShadow?.frame_range ?? source?.frame_range ?? [],
                analysis_scope: analysisScope,
                shadow_only_track: shadowOnlyTrack,
                source_evidence_available: sourceEvidenceAvailable,
                ground_truth_eligible: groundTruthEligible,
                source_evidence_token: sourceEvidenceToken,
            },
            manual_entity_correction: manualLabel === 'person' || manualLabel === 'object'
                ? { corrected_entity: manualLabel }
                : source?.manual_entity_correction || null,
        };
    }

    public async openMovingObjectShadowReviewQueue() {
        this.movingObjectShadowQueueOpen = true;
        await this.service.render();
        await this.loadMovingObjectShadowReviewQueue(true);
    }

    public async closeMovingObjectShadowReviewQueue() {
        this.movingObjectShadowQueueOpen = false;
        await this.service.render();
    }

    public async setMovingObjectShadowQueueStatus(status: 'pending' | 'reviewed' | 'all') {
        if (this.movingObjectShadowQueueLoading || this.movingObjectShadowQueueLoadingMore) return;
        this.movingObjectShadowQueueStatus = status;
        await this.loadMovingObjectShadowReviewQueue(true);
    }

    public async setMovingObjectShadowQueueWouldVeto(value: 'all' | 'true') {
        if (this.movingObjectShadowQueueLoading || this.movingObjectShadowQueueLoadingMore) return;
        this.movingObjectShadowQueueWouldVeto = value;
        await this.loadMovingObjectShadowReviewQueue(true);
    }

    public async loadMovingObjectShadowReviewQueue(reset: boolean = true) {
        if (this.movingObjectShadowQueueLoading || this.movingObjectShadowQueueLoadingMore) return;
        if (!reset && (!this.movingObjectShadowQueueHasMore || !this.movingObjectShadowQueueNextCursor)) return;
        if (reset) this.movingObjectShadowQueueLoading = true;
        else this.movingObjectShadowQueueLoadingMore = true;
        this.movingObjectShadowQueueError = '';
        if (reset) {
            this.movingObjectShadowQueueNextCursor = '';
            this.movingObjectShadowQueueHasMore = false;
        }
        await this.service.render();
        try {
            const params = {
                status: this.movingObjectShadowQueueStatus,
                would_veto: this.movingObjectShadowQueueWouldVeto,
                cursor: reset ? '' : this.movingObjectShadowQueueNextCursor,
                limit: String(this.movingObjectShadowQueueLimit),
            };
            const { code, data } = await wiz.call('moving_object_shadow_review_queue', params);
            if (code !== 200) throw new Error(data?.message || '움직이는 사물 Shadow 검토 큐를 불러오지 못했습니다.');
            const incoming = Array.isArray(data?.items)
                ? data.items.map((item: any) => this.normalizeMovingObjectShadowQueueItem(item))
                : [];
            const merged = reset ? incoming : [...this.movingObjectShadowQueueItems, ...incoming];
            const seenRecordIds = new Set<string>();
            this.movingObjectShadowQueueItems = merged.filter((item: any) => {
                const recordId = this.entityShadowRecordId(item);
                if (!recordId || seenRecordIds.has(recordId)) return false;
                seenRecordIds.add(recordId);
                return true;
            });
            this.movingObjectShadowQueueNextCursor = String(data?.next_cursor || '').trim();
            this.movingObjectShadowQueueHasMore = data?.has_more === true;
        } catch (error: any) {
            this.movingObjectShadowQueueError = error?.message || '움직이는 사물 Shadow 검토 큐를 불러오지 못했습니다.';
            if (reset) this.movingObjectShadowQueueItems = [];
        } finally {
            this.movingObjectShadowQueueLoading = false;
            this.movingObjectShadowQueueLoadingMore = false;
            await this.service.render();
        }
    }

    public movingObjectShadowQueueItemTitle(item: any): string {
        const trackId = String(item?.track_id || '').trim();
        return trackId ? `Track ${trackId}` : String(item?.person_label || 'Shadow track');
    }

    public movingObjectShadowQueueItemRecordText(item: any): string {
        const recordId = this.entityShadowRecordId(item);
        if (!recordId) return 'record ID 없음';
        return recordId.length > 24 ? `${recordId.slice(0, 12)}…${recordId.slice(-8)}` : recordId;
    }

    public movingObjectShadowQueueItemMetaText(item: any): string {
        const shadow = item?.moving_object_shadow || {};
        const frameRange = Array.isArray(shadow?.frame_range)
            ? shadow.frame_range
            : Array.isArray(item?.frame_range) ? item.frame_range : [];
        const frameText = frameRange.length >= 2 ? `프레임 ${frameRange[0]}–${frameRange[1]}` : '프레임 범위 미기록';
        const recordedAt = String(item?.recorded_at || shadow?.recorded_at || item?.created_at || '').trim();
        const vetoText = shadow?.would_veto_flag === true ? 'would-veto' : '관찰';
        return `${frameText} · ${vetoText}${recordedAt ? ` · ${recordedAt}` : ''}`;
    }

    public movingObjectShadowQueueItemSignalText(item: any): string {
        const shadow = item?.moving_object_shadow || {};
        const numeric = (raw: any) => raw === null || raw === undefined || raw === '' ? Number.NaN : Number(raw);
        const ratio = numeric(shadow?.skeleton_ratio_anomaly ?? item?.skeleton_ratio_anomaly);
        const symmetry = numeric(shadow?.skeleton_symmetry_score ?? item?.skeleton_symmetry_score);
        const bbox = numeric(shadow?.bbox_conf_mean ?? item?.bbox_conf_mean);
        const movement = numeric(shadow?.movement_energy ?? item?.movement_energy);
        const value = (number: number) => Number.isFinite(number) ? number.toFixed(3) : '—';
        return `비율이상 ${value(ratio)} · 대칭 ${value(symmetry)} · bbox ${value(bbox)} · 움직임 ${value(movement)}`;
    }

    public movingObjectShadowQueueItemStatusLabel(item: any): string {
        const shadow = item?.moving_object_shadow || {};
        const manualLabel = this.normalizeMovingObjectShadowEntity(
            shadow?.manual_label || item?.manual_label || item?.manual_entity_correction?.corrected_entity,
        );
        if (manualLabel === 'person') return '근거 검토 완료 · 사람';
        if (manualLabel === 'object') return '근거 검토 완료 · 사물';
        return '신호 triage';
    }

    public async requestEntityCorrection(item: any, requestedType?: 'person' | 'object') {
        if (!this.canCorrectEntity(item) || this.isEntityCorrectionSubmitting(item)) return;
        const currentType = this.personEntityType(item);
        const correctedType: 'person' | 'object' = requestedType || (currentType === 'person' ? 'object' : 'person');
        const correctedLabel = correctedType === 'person' ? '사람' : '사물';
        const actionLabel = correctedType === currentType ? '확인' : '지정';
        const policy = '라벨 변경 이력은 삭제하지 않고 Shadow 검증 자료에 누적되며 현재 운영 판정에는 자동 적용되지 않습니다.';
        if (!window.confirm(`${item?.person_label || '선택 개체'}를 ${correctedLabel}으로 ${actionLabel}하시겠습니까?\n\n${policy}`)) return;

        const key = String(item?.id || item?.track_id || this.entityShadowRecordId(item) || '');
        this.entityCorrectionSubmitting[key] = true;
        await this.service.render();
        try {
            const shadowRecordId = this.entityShadowRecordId(item);
            const shadowReviewQueueItem = this.isMovingObjectShadowQueueItem(item);
            const sourceEvidenceToken = this.entitySourceEvidenceToken(item);
            const shadowOnlyReview = Boolean(shadowRecordId) && (
                shadowReviewQueueItem
                || item?.shadow_only_track === true
                || item?.result?.shadow_only_track === true
                || this.inputMode === 'webcam'
                || !this.analysisSavedName
                || !['person', 'object', 'uncertain'].includes(this.modelPersonEntityType(item))
            );
            const params = {
                feedback_kind: shadowOnlyReview ? 'shadow_entity_review' : 'entity_correction',
                shadow_only_track: item?.shadow_only_track === true || item?.result?.shadow_only_track === true ? 'true' : 'false',
                review_queue_item: shadowReviewQueueItem ? 'true' : 'false',
                source_evidence_token: sourceEvidenceToken,
                shadow_record_id: shadowOnlyReview ? shadowRecordId : '',
                saved_name: this.analysisSavedName,
                track_id: String(item?.track_id || ''),
                previous_entity: currentType,
                corrected_entity: correctedType,
                predicted_label: 'N',
                feedback_status: 'incorrect',
                actual_label: 'N',
                note: 'dashboard entity review; no reviewer identity collected',
                event_start_sec: String(Number(item?.first_time_sec || 0)),
                event_end_sec: String(Number(item?.last_time_sec || 0)),
            };
            const { code, data } = await wiz.call('submit_analysis_feedback', params);
            if (code !== 200) throw new Error(data?.message || '개체 판정 정정 저장에 실패했습니다.');
            item.manual_entity_correction = data?.correction || null;
            if (item?.moving_object_shadow) item.moving_object_shadow.manual_label = data?.manual_label || correctedType;
            if (item?.result?.moving_object_shadow) item.result.moving_object_shadow.manual_label = data?.manual_label || correctedType;
            if (item?.result?.entity_classification?.shadow_diagnostics) {
                item.result.entity_classification.shadow_diagnostics.manual_label = data?.manual_label || correctedType;
            }
            if (item?._movingObjectShadowQueueItem === true) {
                if (!item.manual_entity_correction) {
                    item.manual_entity_correction = {
                        corrected_entity: data?.manual_label || correctedType,
                    };
                }
                if (this.movingObjectShadowQueueStatus === 'pending') {
                    const reviewedRecordId = this.entityShadowRecordId(item);
                    this.movingObjectShadowQueueItems = this.movingObjectShadowQueueItems.filter(
                        (queueItem: any) => this.entityShadowRecordId(queueItem) !== reviewedRecordId,
                    );
                }
            }
            if (data?.static_filter_validation_summary && this.prototypeInfo) {
                this.prototypeInfo.static_object_filter_validation_intake = data.static_filter_validation_summary;
            }
            this.showEntityCorrectionMessage(
                data?.learning_message || `${item?.person_label || '선택 개체'}를 ${correctedLabel}로 정정했습니다.`,
                'success',
            );
        } catch (e: any) {
            this.showEntityCorrectionMessage(e?.message || '개체 판정 정정 저장에 실패했습니다.', 'error');
        } finally {
            delete this.entityCorrectionSubmitting[key];
            await this.service.render();
        }
    }

    public personEntityReason(item: any): string {
        if (item?.manual_entity_correction) {
            const modelType = this.modelPersonEntityType(item);
            const modelLabel = modelType === 'person' ? '사람' : modelType === 'object' ? '사물' : '확인 필요';
            return `수동 라벨: 모델 ${modelLabel} 판정에 ${this.personEntityLabel(item)} 라벨을 저장했습니다. 운영 기준은 변경되지 않습니다.`;
        }
        const classification = item?.result?.entity_classification || {};
        const reasonCode = String(classification?.reason_code || item?.entity_reason_code || '').trim();
        if (reasonCode === 'fixed_low_confidence_object') {
            return 'YOLO-Pose 사람 후보를 작은 크기·낮은 검출 신뢰도·정지성 근거로 제외했습니다. 사물 종류를 직접 인식한 결과는 아닙니다.';
        }
        if (reasonCode === 'static_object_suppressed') {
            return 'YOLO-Pose 사람 후보를 정지성·골격 후처리에서 제외했습니다. 사물 종류를 직접 인식한 결과는 아닙니다.';
        }
        if (reasonCode === 'sparse_low_confidence_detection') {
            return 'YOLO-Pose가 잠깐 만든 저신뢰 사람 후보를 후처리에서 제외했습니다.';
        }
        return String(item?.entity_reason || classification?.reason || '').trim();
    }

    public personCardFacialText(item: any): string {
        if (this.personEntityType(item) === 'object') return '표정 분석 제외';
        const result = item?.result || {};
        const face = this.facialStateAux(result) || item?.facial_state || null;
        if (!face) return '표정 대기';
        const displayResult = this.facialStateAux(result) ? result : { facial_state: face };
        const label = this.facialStateLabel(displayResult);
        const percent = this.facialStatePercent(displayResult);
        if (face?.reason === 'person_track_skip') return '표정 분석 꺼짐';
        if (face?.reason === 'face_not_detected' || face?.face_visible === false) return '표정 얼굴 미검출';
        if (face?.available && percent > 0) return `표정 ${label} ${percent}%`;
        return `표정 ${label}`;
    }

    public selectedPersonHeaderText(): string {
        if (this.selectedPersonAnalysisId === 'overall') return '개체 선택';
        const item = this.findPersonAnalysisItem(this.selectedPersonAnalysisId);
        return item?.person_label ? `${item.person_label} · ${this.personEntityLabel(item)}` : '선택 개체 분석';
    }

    private async warmupRealtimeModels() {
        const start = performance.now();
        try {
            const res = await fetch(`/wiz/api/page.dashboard/warmup_models?realtime=true&model_type=${encodeURIComponent(this.selectedModelType || 'rf-dual')}`);
            const json = await this.readJsonResponse(res);
            const elapsed = Math.round(performance.now() - start);
            if (json?.code === 200) {
                this.realtimePerfStats.warmupMs = Number(json?.data?.elapsed_ms || elapsed);
                this.realtimePerfStats.warmed = Array.isArray(json?.data?.warmed_up) ? json.data.warmed_up.join(', ') : '';
            }
        } catch (e) {
            this.realtimePerfStats.warmupFailed = true;
        }
    }

    private fallbackLogDescription(result: any): string {
        const summary = String(result?.summary || '').trim();
        if (summary) return summary;
        const explain = Array.isArray(result?.explain) ? result.explain : [];
        if (explain.length > 0) return String(explain[0] || '');
        const basis = Array.isArray(result?.analysis_basis) ? result.analysis_basis : [];
        if (basis.length > 0) return String(basis[0]?.description || basis[0]?.label || '');
        return '';
    }

    private nonFallRealtimeLabel(result: any): string {
        const ri = result?.runtime_inference || {};
        const shortClip = ri?.short_clip || {};
        const postureRaw = ri?.posture_label || result?.posture_label || '';
        if (shortClip?.confidence_level === 'insufficient' || (shortClip?.is_short_clip && ri?.posture_available === false)) {
            return '분석 준비 중';
        }
        if (postureRaw && postureRaw !== 'unknown') {
            const postureKr = this.POSTURE_LABEL_MAP[postureRaw] || postureRaw;
            return postureKr ? `비낙상·${postureKr}` : '비낙상';
        }
        if (ri?.posture_available === false) return '비낙상·서기';
        if (postureRaw === 'unknown') {
            const topCode = this.topNonFallPostureCode(result);
            if (topCode && topCode !== 'unknown') {
                const postureKr = this.POSTURE_LABEL_MAP[topCode] || topCode;
                return `비낙상·${postureKr}`;
            }
            return '비낙상·서기';
        }
        return '비낙상';
    }

    private createLogEntry(result: any, options: any = {}) {
        const ri = result?.runtime_inference || {};
        const displayProbs = this.displayPostureProbs(result);
        let postureRaw = options.posture || ri?.posture_label || result?.posture_label || '';
        if (postureRaw === 'unknown') {
            const topCode = this.topNonFallPostureCode(result);
            if (topCode && topCode !== 'unknown') postureRaw = topCode;
        }
        if (!postureRaw && result?.fall_detected === false) postureRaw = 'stand';
        if (postureRaw === 'unknown' && Object.keys(displayProbs).length === 0) postureRaw = result?.fall_detected === false ? 'stand' : '';
        const postureKr = postureRaw ? (this.POSTURE_LABEL_MAP[postureRaw] || '') : '';
        const behaviorTransition = options.behaviorTransition || result?.behavior_transition || {};
        const transitionDisplayRaw = behaviorTransition?.display_label || '';
        const transitionDisplay = behaviorTransition?.changed_from_previous === true ? transitionDisplayRaw : '';
        const transitionContext = behaviorTransition?.context_label || '';
        const postureDisplayLabel = transitionDisplayRaw || postureKr;
        const facial = this.facialStateAux(result);
        const liveFacial = options.liveFacial || null;
        const facialLabel = liveFacial?.label || (facial ? this.facialStateLabel(result) : '');
        const facialPercent = Number(liveFacial?.confidence ?? (facial ? this.facialStatePercent(result) : 0));
        const decState = options.decisionState || ri?.decision_state || result?.decision_state || 'safe';
        const nonFallLabel = this.nonFallRealtimeLabel(result);
        const logLabel = options.label || (result?.fall_detected ? '낙상' : nonFallLabel);
        const summary = result?.log_summary || {};
        const baseOneLine = options.oneLine || summary.line || '';
        const oneLine = transitionDisplay && baseOneLine && !baseOneLine.includes(transitionDisplay) ? `${transitionDisplay} · ${baseOneLine}` : (transitionDisplay || baseOneLine);
        const basisText = options.basis || summary.description || this.fallbackLogDescription(result);
        return {
            id: options.id,
            timestamp: options.timestamp || new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            blobUrl: options.blobUrl || '',
            result: { ...result },
            score: result?.risk_score || 0,
            displayedScore: result?.displayed_risk_score ?? result?.event_risk_score ?? result?.raw_model_risk_score ?? result?.risk_score ?? 0,
            rawScore: result?.raw_model_risk_score ?? result?.model_output?.risk_score ?? result?.risk_score ?? 0,
            rawLevel: result?.raw_risk_level || result?.risk_level || 'low',
            level: result?.display_risk_level || result?.risk_level || 'low',
            displayRiskLabel: result?.display_risk_label || '',
            overlapContext: result?.overlap_context || null,
            label: logLabel,
            posture: postureRaw,
            postureLabel: postureDisplayLabel,
            currentPostureLabel: postureKr,
            facialLabel,
            facialPercent,
            liveFacial,
            transitionLabel: transitionDisplay,
            transitionContext,
            behaviorTransition,
            decisionState: decState,
            basis: transitionContext && basisText && !basisText.includes(transitionContext) ? `${transitionContext} · ${basisText}` : (transitionContext || basisText),
            runtimeKey: result?.runtime_key || '',
            elapsed: options.elapsed ?? this.realtimeLastElapsed,
            cumulativeScore: options.cumulativeScore ?? this.realtimeCumulativeScore,
            feedbackDone: Boolean(options.feedbackDone),
            oneLine,
            chunkLabel: options.chunkLabel || options.chunkWindow?.label || '',
            chunkWindow: options.chunkWindow || null,
        };
    }

    private syncUploadChunkLogs(result: any) {
        const logs = Array.isArray(result?.chunk_analysis?.logs) ? result.chunk_analysis.logs : [];
        this.uploadChunkSummary = result?.chunk_analysis?.summary || null;
        this.uploadLogEntries = logs.map((item: any) => this.createLogEntry(item?.result || {}, {
            id: item?.chunk_id,
            timestamp: '업로드 분할 분석',
            elapsed: Number(item?.elapsed_sec || 0),
            basis: item?.description || item?.facial_state?.label || '',
            oneLine: item?.one_line || '',
            behaviorTransition: item?.behavior_transition || item?.result?.behavior_transition || {},
            chunkLabel: item?.chunk_label || item?.window?.label || '',
            chunkWindow: item?.window || null,
        }));
    }

    public chunkTimelineTotal(entries: any[], fallbackTotal: number = 0): number {
        const maxEnd = (entries || []).reduce((acc: number, entry: any) => Math.max(acc, Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? 0)), 0);
        return Math.max(maxEnd, Number(fallbackTotal || 0), 1);
    }
    public chunkBarStyle(entry: any, totalSec: number) {
        const total = Math.max(Number(totalSec || 1), 1);
        const start = Number(entry?.chunkWindow?.start_sec ?? entry?.chunkWindow?.startSec ?? 0);
        const end = Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? start);
        const width = Math.max(end - start, 0.4);
        return { left: `${(start / total) * 100}%`, width: `${Math.max((width / total) * 100, 3)}%` };
    }
    public chunkBarLeft(entry: any, totalSec: number): string { return this.chunkBarStyle(entry, totalSec).left; }
    public chunkBarWidth(entry: any, totalSec: number): string { return this.chunkBarStyle(entry, totalSec).width; }

    public async ngOnInit() {
        await this.service.init(this);
        const runtimeParams = new URLSearchParams(window.location.search);
        this.realtimeDeveloperMode = runtimeParams.get('developer') === '1';
        this.realtimeDisablePrecision = this.realtimeDeveloperMode && runtimeParams.get('disablePrecision') === '1';
        // MediaPipe WebGL/WASM은 분석 입력을 실제로 시작할 때만 초기화합니다.
        // 학습 화면에서는 모델·그래프를 만들지 않아 불필요한 GL 로그와 초기 부하를 방지합니다.
        this.clearLegacyEntityCorrectionOperatorStorage();
        this.startAssetVersionCheck();
        this.restorePrivacyViewMode();
        this.restorePrivacyFilterStrengths();
        this.restoreOverlayPreferences();
        this.restoreAgentWeights();
        this.clearStoredAgentWeightsForNoOverlay();
        this.restoreRealtimeBenchmarkReport();
        try {
            if (localStorage.getItem('fallai-open-training-mode') === '1') {
                this.inputMode = 'upload';
                this.uploadWorkflowMode = 'train';
                localStorage.removeItem('fallai-open-training-mode');
            }
        } catch (_e) { }
        if (this.uploadWorkflowMode === 'train') {
            this.trainingModeEntering = true;
            this.resetFallDetectionDraftSelection();
            this.restorePoseStudioTrainingSettings();
            this.restorePoseStudioDatasetsCache();
            this.restorePoseStudioRegistryCache();
            this.restoreFallDetectionStudioCache();
            // 서버 Registry를 기다리기 전에 캐시 기반 학습 화면을 먼저 그립니다.
            await this.service.render();
            this.scrollToFallDetectionStepOne();
            void this.finishTrainingModeBootstrap(false);
        }
        try {
            const savedPreset = localStorage.getItem('fallai-realtime-performance-preset') as RealtimePerformancePreset;
            if (['quality', 'balanced', 'performance'].includes(String(savedPreset))) this.realtimePerformancePreset = savedPreset;
        } catch (e) { }
        await this.loadPrototypeInfo();
        try {
            const requestedTrainingTarget = String(localStorage.getItem('fallai-training-target') || '');
            if (requestedTrainingTarget && this.trainingTargetOptionsForView().some((item: any) => item.key === requestedTrainingTarget)) {
                this.trainingTargetModel = requestedTrainingTarget;
                localStorage.removeItem('fallai-training-target');
            }
        } catch (_e) { }
        await this.loadContinuousTrainingStatus();
        this.startContinuousTrainingPolling();
        if (this.inputMode === 'webcam') await this.ensureWebcamReady();
        await this.service.render();
    }

    private restorePrivacyViewMode() {
        try {
            const saved = localStorage.getItem(PRIVACY_VIEW_STORAGE_KEY);
            if (saved === 'original' || saved === 'skeleton' || saved === 'skeleton-raw' || saved === 'high' || saved === 'low') {
                this.privacyViewMode = saved as PrivacyViewMode;
                return;
            }
            const legacySaved = localStorage.getItem(LEGACY_PRIVACY_VIEW_STORAGE_KEY);
            this.privacyViewMode = legacySaved === 'raw' ? 'skeleton-raw' : 'skeleton';
        } catch (e) {
            this.privacyViewMode = 'skeleton';
        }
    }

    private restorePrivacyFilterStrengths() {
        try {
            this.highPassEdgeStrength = this.clampPrivacyFilterStrength('high', localStorage.getItem(HIGH_PASS_STRENGTH_STORAGE_KEY));
            this.lowPassBlurStrength = this.clampPrivacyFilterStrength('low', localStorage.getItem(LOW_PASS_STRENGTH_STORAGE_KEY));
        } catch (e) {
            this.highPassEdgeStrength = 30;
            this.lowPassBlurStrength = 35;
        }
    }

    private storedOverlayMode(): OverlayMode | null {
        try {
            const savedMode = String(localStorage.getItem(OVERLAY_MODE_STORAGE_KEY) || '');
            const allowed = this.overlayModeOptions.some(item => item.value === savedMode);
            if (allowed && (savedMode !== 'DEBUG_RAW' || this.isAdminUser())) {
                return savedMode as OverlayMode;
            }
        } catch (e) { }
        return null;
    }

    private restoreOverlayPreferences() {
        const savedMode = this.storedOverlayMode();
        const overlayRestored = Boolean(savedMode && this.privacyViewMode !== 'original');
        if (overlayRestored && savedMode) this.overlayMode = savedMode;
        try {
            const savedClasses = JSON.parse(String(localStorage.getItem(CLEAN_OVERLAY_CLASSES_STORAGE_KEY) || 'null'));
            if (Array.isArray(savedClasses) && savedClasses.every(value => typeof value === 'string')) {
                this.cleanOverlayShowClasses = savedClasses;
            }
        } catch (e) {
            this.cleanOverlayShowClasses = [...DEFAULT_CLEAN_OVERLAY_CLASSES];
        }
        if (this.privacyViewMode === 'original' || !overlayRestored) {
            this.applyDefaultOverlayModeForPrivacyView(this.privacyViewMode);
        }
    }

    private startAssetVersionCheck() {
        if (this.assetVersionCheckTimer) clearInterval(this.assetVersionCheckTimer);
        const check = async () => {
            try {
                const current = String((window as any).__FALLAI_ASSET_VERSION__ || '');
                if (!current) return;
                const response = await fetch(`/?asset-version-check=${Date.now()}`, { cache: 'no-store' });
                const html = await response.text();
                const match = html.match(/__FALLAI_ASSET_VERSION__\s*=\s*'([^']+)'/);
                const latest = String(match?.[1] || '');
                if (latest && latest !== current && !this.analyzing && !this.realtimeActive) {
                    window.location.reload();
                }
            } catch (e) { }
        };
        this.assetVersionCheckTimer = setInterval(check, 60_000);
    }

    public isAdminUser(): boolean {
        return this.service?.auth?.check?.role?.('admin') === true;
    }

    public overlayModeVisibleOptions(): { value: OverlayMode; label: string }[] {
        return this.overlayModeOptions.filter(item => item.value !== 'DEBUG_RAW' || this.isAdminUser());
    }

    public canShowRawVideo(): boolean { return !this.shouldHideRawVideo(); }

    public canShowSkeletonOverlay(): boolean {
        return this.privacyViewMode !== 'original' && this.overlayMode !== 'NONE';
    }

    public canShowPersonOverlay(): boolean {
        return this.canShowSkeletonOverlay() && ['SKELETON', 'SKELETON_ENVIRONMENT', 'DEBUG_RAW'].includes(this.overlayMode);
    }

    public canShowEnvironmentMaskOverlay(): boolean {
        return this.canShowSkeletonOverlay() && ['ENVIRONMENT', 'SKELETON_ENVIRONMENT', 'DEBUG_RAW'].includes(this.overlayMode);
    }

    public showFaceDebugOverlay(): boolean {
        return this.canShowSkeletonOverlay() && this.overlayMode === 'DEBUG_RAW';
    }

    public showFaceCanvasOverlay(): boolean {
        return this.canShowSkeletonOverlay() && ['SKELETON_ENVIRONMENT', 'DEBUG_RAW'].includes(this.overlayMode);
    }

    public showClientPoseCanvasOverlay(): boolean {
        return this.canShowPersonOverlay()
            && this.hasRealtimeInputSource()
            && this.mpRunning;
    }

    public showPrivacyMaskOverlay(): boolean {
        // 얼굴 특징 표시가 활성화된 모드에서는 privacy veil보다 눈·눈썹·입 FaceMesh를 우선한다.
        return this.showFaceCanvasOverlay()
            && this.canShowRawVideo()
            && !this.isSkeletonCompositeMode()
            && !this.showFacialFeatureOverlay();
    }

    public showLiveFacialModelPanel(): boolean {
        // Privacy View 종류가 아니라 실제 표시 선택을 따른다.
        // Skeleton/결합 모드는 얼굴·표정을, 환경 객체 전용 모드는 객체만 표시한다.
        return this.inputMode === 'webcam' && this.canShowPersonOverlay();
    }

    public showFacialFeatureOverlay(): boolean {
        return this.showLiveFacialModelPanel() && this.canShowSkeletonOverlay();
    }

    public showFaceCanvasLayer(): boolean {
        return this.showPrivacyMaskOverlay()
            || this.showFacialFeatureOverlay()
            || this.showFaceDebugOverlay();
    }

    private replaceSkeletonFaceWithExpressionFeatures(): boolean {
        return this.showFacialFeatureOverlay() && this.faceMeshDetected;
    }

    public cleanBackgroundVisible(): boolean {
        return false;
    }

    public shouldHideRawVideo(): boolean { return this.isSkeletonPrivacyMode(); }

    public isCleanOverlayMode(): boolean { return this.overlayMode === 'SKELETON_ENVIRONMENT'; }
    public isDebugOverlayMode(): boolean { return this.overlayMode === 'DEBUG_RAW'; }

    public async toggleCleanLegend() {
        this.cleanLegendOpen = !this.cleanLegendOpen;
        await this.service.render();
    }

    public overlayModeLabel(): string {
        return this.overlayModeOptions.find(item => item.value === this.overlayMode)?.label || 'Skeleton + 환경 객체';
    }

    private defaultOverlayModeForPrivacyView(mode: PrivacyViewMode): OverlayMode {
        if (mode === 'original' || mode === 'skeleton' || mode === 'skeleton-raw') return 'NONE';
        if (mode === 'high' || mode === 'low') return 'SKELETON';
        return 'NONE';
    }

    private applyDefaultOverlayModeForPrivacyView(mode: PrivacyViewMode) {
        this.overlayMode = this.defaultOverlayModeForPrivacyView(mode);
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            display_mode: this.overlayMode,
            display_mode_label: this.overlayModeLabel(),
        };
        if (!this.canShowSkeletonOverlay()) this.clearRealtimeServerDetectionOverlay();
    }

    public isOverlayModeLocked(): boolean {
        // 원본 전용만 Overlay를 잠근다. High/Low 필터에서도 Skeleton·환경·결합을 선택할 수 있다.
        return this.isOriginalPrivacyMode();
    }

    public async setOverlayMode(value: string) {
        if (this.isOverlayModeLocked()) return;
        if (!this.overlayModeOptions.some(item => item.value === value)) return;
        if (value === 'DEBUG_RAW' && !this.isAdminUser()) return;
        this.overlayMode = value as OverlayMode;
        try { localStorage.setItem(OVERLAY_MODE_STORAGE_KEY, this.overlayMode); } catch (e) { }
        this.clearStoredAgentWeightsForNoOverlay();
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            display_mode: this.overlayMode,
            display_mode_label: this.overlayModeLabel(),
        };
        if (this.overlayMode !== 'SKELETON_ENVIRONMENT') this.cleanLegendOpen = false;
        if (!this.canShowSkeletonOverlay()) this.clearRealtimeServerDetectionOverlay();
        if (this.realtimeActive) this.ensureRealtimeEnvironmentSnapshotsForOverlay();
        // 분석 종료 뒤 표시 모드만 바꾸는 경우에도 마지막 환경 결과를 즉시
        // 다시 그립니다. 재생/분석 중 여부와 Canvas 표시 선택은 독립 상태입니다.
        const cachedOverlay = this.realtimeEnvironmentSnapshotResult || this.currentRootAnalysisResult();
        if (cachedOverlay && this.hasRealtimeInputSource()) {
            this.scheduleRealtimeServerDetectionOverlay(cachedOverlay);
        }
        this.drawUploadDetectionAtCurrentTime();
        await this.service.render();
    }

    private ensureRealtimeEnvironmentSnapshotsForOverlay() {
        if (!this.canShowEnvironmentMaskOverlay()
            || !this.realtimeActive
            || this.realtimeDisablePrecision
            || this.realtimeEnvironmentSnapshotPending
            || this.realtimeEnvironmentSnapshotTimer
        ) return;
        const videoEl = this.webcamVideoRef?.nativeElement;
        if (!videoEl || !this.realtimeEnvironmentSourceReady(videoEl)) return;
        // 기존 결과 유무와 관계없이 선택 중에는 snapshot을 계속 갱신합니다.
        // 정적 사물도 카메라 이동·신규 유입에 따라 위치가 달라질 수 있습니다.
        this.startRealtimeEnvironmentSnapshots(videoEl);
    }

    public async toggleRealtimeDiagnostics() {
        this.realtimeDiagnosticsOpen = !this.realtimeDiagnosticsOpen;
        await this.service.render();
    }

    public realtimeDiagnosticRows(): { label: string; value: string }[] {
        const item = this.realtimeDiagnostics || {};
        const seconds = (value: any) => `${Number(value || 0).toFixed(1)}초`;
        const generatedAt = Number(item.result_generated_time || 0);
        return [
            { label: 'videoTimestampMs', value: `${Math.round(Number(item.video_timestamp_ms || 0))}ms` },
            { label: '최초 Person/Skeleton', value: `${Math.round(Number(item.person_detection_first_ms || 0))}ms / ${Math.round(Number(item.skeleton_first_display_ms || 0))}ms` },
            { label: '최초 자세/환경', value: `${Math.round(Number(item.posture_first_display_ms || 0))}ms / ${Math.round(Number(item.environment_first_display_ms || 0))}ms` },
            { label: 'captureDelayMs', value: `${Math.round(Number(item.capture_delay_ms || 0))}ms` },
            { label: 'Pose Queue', value: `${Number(item.pose_queue_length || 0)} / max ${Number(item.pose_queue_max_length || 0)}` },
            { label: 'Pose 추론/나이', value: `${Math.round(Number(item.pose_inference_ms || 0))}ms · ${Math.round(Number(item.pose_result_age_ms || 0))}ms` },
            { label: 'Pose 렌더 지연', value: `${Math.round(Number(item.pose_render_delay_ms || 0))}ms` },
            { label: '환경 Queue', value: String(item.environment_queue_length || 0) },
            { label: '환경 추론/나이', value: `${Math.round(Number(item.environment_inference_ms || item.environment_inference_latency_ms || 0))}ms · ${Math.round(Number(item.environment_result_age_ms || 0))}ms` },
            { label: 'Fast Fall 지연', value: `${Math.round(Number(item.fast_fall_detection_ms || 0))}ms` },
            { label: 'Event Feed 지연', value: `${Math.round(Number(item.event_feed_render_delay_ms || 0))}ms` },
            { label: '정밀 분석', value: `${item.precision_analysis_status || 'idle'} · ${Math.round(Number(item.precision_analysis_elapsed_ms || 0))}ms` },
            { label: '표시 모드', value: `${item.display_mode_label || this.overlayModeLabel()} · ${item.display_mode || this.overlayMode}` },
            { label: '영상 현재 시각', value: seconds(item.video_current_time) },
            { label: '분석 구간', value: `${seconds(item.analyzed_chunk_start)} ~ ${seconds(item.analyzed_chunk_end)}` },
            { label: '결과 생성 시각', value: generatedAt > 0 ? new Date(generatedAt * 1000).toLocaleTimeString('ko-KR', { hour12: false }) : '정보 없음' },
            { label: 'sourceTimestamp', value: seconds(item.source_timestamp) },
            { label: 'overlayAge', value: `${Math.round(Number(item.overlay_age_ms || 0))}ms` },
            { label: '영상-분석 지연', value: `${Math.round(Number(item.video_to_result_lag_ms || 0))}ms` },
            { label: '사람 원시 트랙', value: String(item.raw_person_track_count || 0) },
            { label: '논리적 사람', value: String(item.logical_person_count || 0) },
            { label: 'Pose 검출', value: String(item.pose_detected_person_count || 0) },
            { label: 'Pose 상태', value: `${item.pose_state || 'LOST'} · fresh ${item.pose_fresh ? 'Y' : 'N'} · predicted ${item.pose_predicted ? 'Y' : 'N'}` },
            { label: 'Pose/Track TTL', value: `${Math.round(Number(item.pose_hold_remaining_ms || 0))}ms / ${Number(item.person_track_ttl_ms || PERSON_TRACK_TTL_MS)}ms` },
            { label: 'Track 복구', value: String(item.track_recovery_reason || '없음') },
            { label: '실시간 RF', value: `${this.realtimeRfFallStatus} · ${this.realtimeRfFallModel || '준비 중'}` },
            { label: 'RF 창/지연', value: `${this.realtimeRfFallWindowFrameCount}점 · ${this.realtimeRfFallLatencyMs}ms` },
            { label: 'Primary 후보', value: String(item.primary_track_candidate_count || 0) },
            { label: '안전 Skeleton', value: String(item.safe_skeleton_count || 0) },
            { label: '화면 Skeleton', value: String(item.rendered_skeleton_count || 0) },
            { label: 'Skeleton 렌더', value: `${item.render_skeleton_called ? '호출' : '미호출'} · 입력 ${item.input_skeleton_count || 0}` },
            { label: 'Skeleton 제외 사유', value: this.realtimeSkeletonExclusionSummary() },
            { label: '환경 객체 검출', value: String(item.detected_environment_object_count || 0) },
            { label: '환경 추론 상태', value: `${item.environment_inference_status || '대기'} · ${Number(item.environment_inference_fps || 0).toFixed(1)}fps` },
            { label: '환경 지연/나이', value: `${Math.round(Number(item.environment_inference_latency_ms || 0))}ms · ${Math.round(Number(item.environment_result_age_ms || 0))}ms` },
            { label: '환경 Source', value: String(item.environment_source || '정보 없음') },
            { label: '환경 Raw 후보', value: String(item.raw_environment_candidate_count || 0) },
            { label: '환경 Filter Drop', value: String(item.environment_filter_drop_count || 0) },
            { label: '화면 객체', value: String(item.rendered_environment_object_count || 0) },
            { label: '환경 렌더', value: `${item.render_environment_called ? '호출' : '미호출'} · 입력 ${item.input_environment_count || 0}` },
            { label: '좌표계', value: String(item.coordinate_space || '정보 없음') },
            { label: '환경 좌표계', value: `${item.environment_coordinate_space || '정보 없음'} · ${item.environment_source_frame_size || '정보 없음'}` },
            { label: '입력 좌표 크기', value: item.coordinate_width && item.coordinate_height ? `${item.coordinate_width}×${item.coordinate_height}` : '정보 없음' },
            { label: '레터박스 보정', value: `${Number(item.letterbox_offset_x || 0).toFixed(1)}, ${Number(item.letterbox_offset_y || 0).toFixed(1)}` },
            { label: 'Canvas 표시', value: `S ${item.skeleton_canvas_visible ? 'ON' : 'OFF'} · E ${item.environment_canvas_visible ? 'ON' : 'OFF'}` },
            { label: 'Video Rect', value: item.rendered_video_rect ? JSON.stringify(item.rendered_video_rect) : '정보 없음' },
            { label: 'Canvas CSS/Backing', value: `${item.canvas_css_size || '정보 없음'} / ${item.canvas_backing_size || '정보 없음'} · DPR ${Number(item.device_pixel_ratio || 1).toFixed(2)}` },
            { label: '청크 ID', value: `${item.source_chunk_id || '-'} / ${item.current_chunk_id || '-'}` },
            { label: '사람 필터 제외', value: String(item.filtered_person_count || 0) },
            { label: '장비 Ghost 제외', value: String(item.ghost_suppressed_count || 0) },
            { label: '객체 필터 제외', value: String(item.filtered_object_count || 0) },
            { label: '오래된 Overlay 폐기', value: String(item.stale_overlay_discard_count || 0) },
        ];
    }

    public realtimeSkeletonExclusionSummary(): string {
        const labels: Record<string, string> = {
            person_filter_excluded: '사람 필터',
            logical_person_pending: '논리 사람 대기',
            alias_track_hidden: '중복 트랙',
            track_too_short: '짧은 트랙',
            pose_missing: 'Pose 없음',
            insufficient_keypoints: '관절 부족',
            low_keypoint_confidence: '관절 신뢰도',
            duplicate_logical_person: '논리 사람 중복',
            local_track_unstable: '로컬 트랙 안정화',
            local_pose_outside_person: '사람 영역 불일치',
            local_pose_insufficient: '로컬 관절 부족',
        };
        const reasons = this.realtimeDiagnostics?.skeleton_exclusion_reasons || {};
        const items = Object.entries(reasons)
            .filter(([, count]) => Number(count || 0) > 0)
            .map(([reason, count]) => `${labels[reason] || reason} ${Number(count)}건`);
        return items.length > 0 ? items.join(' · ') : '없음';
    }

    public realtimeDiagnosticChips(): { label: string; state: string; tone: string }[] {
        const item = this.realtimeDiagnostics || {};
        const lag = Number(item.video_to_result_lag_ms || 0);
        const raw = Number(item.raw_person_track_count || 0);
        const logical = Number(item.logical_person_count || 0);
        const pose = Number(item.pose_detected_person_count || 0);
        const environment = Number(item.detected_environment_object_count || 0);
        const renderedEnvironment = Number(item.rendered_environment_object_count || 0);
        const rawEnvironment = Number(item.raw_environment_candidate_count || 0);
        const overlayDisposition = String(item.overlay_disposition || '');
        const localPoseStale = Number(item.pose_result_age_ms || 0) > POSE_TIMESTAMP_TOLERANCE_MS;
        return [
            {
                label: 'YOLO/Pose',
                state: item.analysis_failed ? '실패' : (raw <= 0 ? '미검출' : (pose <= 0 ? '미검출' : (lag > 1500 ? '지연' : '정상'))),
                tone: !item.analysis_failed && raw > 0 && pose > 0 && lag <= 1500 ? 'normal' : 'review',
            },
            {
                label: 'Logical Tracking',
                state: raw > logical && logical > 0 ? '병합' : (raw > 0 && logical <= 0 ? '보류' : '정상'),
                tone: raw > 0 && logical <= 0 ? 'review' : 'normal',
            },
            {
                label: '환경 객체',
                state: environment <= 0
                    ? (rawEnvironment > 0 ? '필터 제외' : '미검출')
                    : (renderedEnvironment <= 0 ? '필터 제외' : '정상'),
                tone: environment > 0 && renderedEnvironment > 0 ? 'normal' : 'review',
            },
            {
                label: 'Overlay',
                state: overlayDisposition === 'stale_discarded'
                    ? '오래된 결과 폐기'
                    : (Number(item.rendered_skeleton_count || 0) + renderedEnvironment > 0 ? '표시 중' : '숨김'),
                tone: overlayDisposition === 'stale_discarded' ? 'review' : 'normal',
            },
            {
                label: '실시간 지연',
                state: localPoseStale ? 'STALE FRAME' : (lag > 1500 ? '지연' : '정상'),
                tone: localPoseStale || lag > 1500 ? 'review' : 'normal',
            },
        ];
    }

    public realtimeDiagnosticCause(): string {
        const item = this.realtimeDiagnostics || {};
        const lag = Number(item.video_to_result_lag_ms || 0);
        const raw = Number(item.raw_person_track_count || 0);
        const pose = Number(item.pose_detected_person_count || 0);
        const rendered = Number(item.rendered_skeleton_count || 0);
        const environment = Number(item.detected_environment_object_count || 0);
        const renderedEnvironment = Number(item.rendered_environment_object_count || 0);
        const rawEnvironment = Number(item.raw_environment_candidate_count || 0);
        const environmentZeroReason = String(item.environment_zero_reason || '');
        if (item.analysis_failed) return '분석 요청이 실패했습니다. Event Feed의 오류 내용과 서버 상태를 확인하세요.';
        if (String(item.overlay_disposition) === 'stale_discarded') {
            return `분석 결과가 현재 영상보다 ${(lag / 1000).toFixed(1)}초 늦어 오래된 Overlay를 표시하지 않았습니다.`;
        }
        if (this.overlayMode === 'NONE') return '현재 표시 없음 모드이므로 검출 결과를 화면에 그리지 않습니다.';
        if (item.coordinate_transform_valid === false) return '검출 결과가 있지만 좌표 변환 값이 유효하지 않아 Overlay를 표시하지 못했습니다.';
        if (pose > 0 && !item.render_skeleton_called && this.canShowPersonOverlay()) {
            return 'Pose 결과가 있지만 Skeleton 렌더 함수가 호출되지 않았습니다. 표시 모드와 결과 전달 경로를 확인하세요.';
        }
        if (environment > 0 && !item.render_environment_called && this.canShowEnvironmentMaskOverlay()) {
            return '환경 객체 결과가 있지만 환경 렌더 함수가 호출되지 않았습니다. 비동기 결과 전달 경로를 확인하세요.';
        }
        if (pose > 0 && item.render_skeleton_called && item.skeleton_canvas_visible === false) {
            return 'Skeleton은 렌더됐지만 Canvas가 숨김 상태입니다.';
        }
        if (environment > 0 && item.render_environment_called && item.environment_canvas_visible === false) {
            return '환경 객체는 렌더됐지만 Canvas가 숨김 상태입니다.';
        }
        if (raw <= 0) return '사람 검출 자체가 실패했거나 현재 구간에 사람이 없습니다.';
        if (pose <= 0) return '사람은 검출됐지만 관절을 충분히 찾지 못해 스켈레톤을 표시하지 않았습니다.';
        if (rendered <= 0) {
            if (!this.canShowPersonOverlay()) return 'Pose는 검출됐지만 현재 환경 객체 전용 표시 모드에서는 Skeleton을 숨깁니다.';
            if (String(item.person_filter_reason) === 'logical_person_pending') {
                return 'Pose는 검출됐지만 logical person 승인 전이라 화면 표시를 보류했습니다.';
            }
            const exclusion = this.realtimeSkeletonExclusionSummary();
            return `사람과 Pose는 검출됐지만 Skeleton 표시 단계에서 제외됐습니다.${exclusion === '없음' ? '' : ` 제외 사유: ${exclusion}`}`;
        }
        if (environment > 0 && renderedEnvironment <= 0) {
            if (!this.canShowEnvironmentMaskOverlay()) {
                return `환경 객체 ${environment}개를 검출했지만 현재 Skeleton 표시 모드에서는 숨겨져 있습니다.`;
            }
            return `환경 객체 ${environment}개를 검출했지만 표시 안정성 필터를 통과하지 못했습니다.`;
        }
        if (environment <= 0 && rawEnvironment > 0) {
            if (environmentZeroReason === 'no_supported_environment_classes') {
                return `YOLO Raw 박스 ${rawEnvironment}개가 있었지만 지원 환경 클래스가 아니어서 제외했습니다.`;
            }
            if (environmentZeroReason === 'temporal_confirmation_not_met') {
                return `환경 객체 후보 ${rawEnvironment}개가 있었지만 반복 검출 조건을 충족하지 못해 최종 표시에서 제외했습니다.`;
            }
            return `환경 객체 Raw 후보 ${rawEnvironment}개가 있었지만 필터 이후 최종 객체가 남지 않았습니다.`;
        }
        if (environment <= 0 && environmentZeroReason === 'environment_analysis_unavailable') {
            return '환경 객체 분석을 실행하지 못했습니다. 환경 모델 상태와 서버 오류를 확인하세요.';
        }
        if (environment <= 0 && environmentZeroReason === 'environment_aux_pending') {
            return '환경 객체 보조 분석이 비동기로 실행 중입니다. 완료 결과가 도착하면 객체 수와 Overlay를 갱신합니다.';
        }
        if (environment <= 0 && environmentZeroReason === 'environment_aux_not_scheduled_this_chunk') {
            return '환경 객체 분석은 저주기 보조 경로라 현재 청크에는 예약되지 않았습니다. 다음 환경 분석 청크에서 갱신합니다.';
        }
        if (environment <= 0 && environmentZeroReason === 'detector_returned_no_boxes') {
            return '환경 모델 추론은 실행됐지만 0.35 추론 기준을 넘은 Raw 박스가 없었습니다.';
        }
        if (lag > 1500) return `Overlay를 표시 중이지만 분석 결과가 현재 영상보다 ${(lag / 1000).toFixed(1)}초 늦습니다.`;
        return '사람 검출, Pose, logical person 승인과 Overlay 표시가 정상적으로 연결되어 있습니다.';
    }

    public realtimeTrackDiagnosticLines(): string[] {
        const labels: Record<string, string> = {
            confidence: '검출 신뢰도 저하',
            bbox_loss: 'bbox 연결 손실',
            pose_loss: 'Pose 손실',
            tracker_timeout: 'Tracker timeout',
            occlusion: '가림',
            detector_reset: 'Detector reset',
            chunk_end: '청크 종료',
            other: '기타',
        };
        const fragments = Array.isArray(this.realtimeDiagnostics?.track_fragments)
            ? this.realtimeDiagnostics.track_fragments : [];
        return fragments.map((item: any) => {
            const reason = labels[String(item?.end_reason || 'other')] || String(item?.end_reason || '기타');
            const score = item?.tracker_score == null ? '정보 없음' : Number(item.tracker_score).toFixed(2);
            return `P${item?.track_id} · ${item?.creation_frame}~${item?.end_frame}프레임 · ${reason} · Tracker ${score}`;
        });
    }

    public realtimeMergeDiagnosticLines(): string[] {
        const evaluations = Array.isArray(this.realtimeDiagnostics?.merge_evaluations)
            ? this.realtimeDiagnostics.merge_evaluations : [];
        return evaluations.map((item: any) => {
            const status = item?.status === 'AUTO_ALIAS'
                ? '병합 성공'
                : (item?.status === 'POSSIBLE_DUPLICATE_TRACK' ? '병합 후보' : '병합 차단');
            const reasons = Array.isArray(item?.block_reasons) && item.block_reasons.length
                ? item.block_reasons.join(', ') : String(item?.merge_reason || '');
            return `P${item?.left_track_id} ↔ P${item?.right_track_id} · ${status}${reasons ? ` · ${reasons}` : ''}`;
        });
    }

    public realtimeEnvironmentDiagnosticLines(): string[] {
        const candidates = Array.isArray(this.realtimeDiagnostics?.environment_raw_candidates)
            ? this.realtimeDiagnostics.environment_raw_candidates : [];
        return candidates.slice(-12).map((item: any) => {
            const threshold = Number(item?.confirmation_threshold || 0);
            const reason = item?.final_used
                ? '최종 사용'
                : String(item?.drop_reason || item?.disposition || '정보 없음');
            return `${item?.source_label || item?.canonical_label || 'unknown'} · ${Math.round(Number(item?.confidence || 0) * 100)}% · 기준 ${Math.round(threshold * 100)}% · ${reason} · ${item?.coordinate_space || 'unknown'} · mask ${Number(item?.mask_quality || 0).toFixed(2)}${item?.bbox_fallback_reason ? ` · bbox fallback ${item.bbox_fallback_reason}` : ''}`;
        });
    }

    public realtimeGhostSuppressionLines(): string[] {
        const evaluations = Array.isArray(this.realtimeDiagnostics?.ghost_suppression_evaluations)
            ? this.realtimeDiagnostics.ghost_suppression_evaluations : [];
        return evaluations.map((item: any) => {
            const overlap = Math.round(Number(item?.equipment_overlap_iou || 0) * 100);
            const movement = Number(item?.skeleton_movement || 0).toFixed(3);
            const score = Math.round(Number(item?.ghost_score || 0) * 100);
            const keypoints = Number(item?.pose_keypoint_count || item?.keypoint_count || 0);
            const bypassReason = String(item?.ghost_suppression_bypass_reason || item?.bypass_reason || '');
            const disposition = item?.suppressed
                ? 'Excluded'
                : (bypassReason ? `사람 유지 · ${bypassReason}` : (item?.motion_release ? '움직임 감지 · 억제 해제' : '사람 유지'));
            return `P${item?.track_id} · ${item?.equipment_label || '장비 정보 없음'} overlap ${overlap}% · movement ${movement} · keypoints ${keypoints} · Ghost ${score}% · ${disposition}`;
        });
    }

    public isOriginalPrivacyMode(): boolean {
        return this.privacyViewMode === 'original';
    }

    public isSkeletonPrivacyMode(): boolean {
        return this.privacyViewMode === 'skeleton';
    }

    public isSkeletonCompositeMode(): boolean {
        return this.privacyViewMode === 'skeleton-raw';
    }

    public isHighFilterMode(): boolean {
        return this.privacyViewMode === 'high';
    }

    public isLowFilterMode(): boolean {
        return this.privacyViewMode === 'low';
    }

    public isPrivacyPixelFilterMode(): boolean {
        return this.isHighFilterMode() || this.isLowFilterMode();
    }

    private clampPrivacyFilterStrength(mode: 'high' | 'low', value: any): number {
        const fallback = mode === 'high' ? 30 : 35;
        const numeric = Number(value);
        return Math.max(0, Math.min(50, Number.isFinite(numeric) && value !== null && value !== '' ? Math.round(numeric) : fallback));
    }

    public setPrivacyFilterStrength(mode: 'high' | 'low', value: any) {
        const strength = this.clampPrivacyFilterStrength(mode, value);
        if (mode === 'high') this.highPassEdgeStrength = strength;
        else this.lowPassBlurStrength = strength;
        try {
            localStorage.setItem(mode === 'high' ? HIGH_PASS_STRENGTH_STORAGE_KEY : LOW_PASS_STRENGTH_STORAGE_KEY, String(strength));
        } catch (e) { }
    }

    public resetPrivacyFilterStrength(mode: 'high' | 'low') {
        this.setPrivacyFilterStrength(mode, mode === 'high' ? 30 : 35);
    }

    public highPassFilterMix(): number {
        return this.highPassEdgeStrength / 50;
    }

    public highPassEdgeGain(): number {
        return 2 + this.highPassFilterMix() * 12;
    }

    public lowPassFilterStyle(): string {
        if (this.lowPassBlurStrength === 0) return 'none';
        const ratio = this.lowPassBlurStrength / 50;
        const blurRadius = Math.pow(ratio, 0.82) * 48;
        const saturation = Math.max(0.28, 1 - ratio * 0.72);
        return `blur(${blurRadius.toFixed(1)}px) saturate(${saturation.toFixed(2)})`;
    }

    public privacyVideoFilterStyle(): string {
        if (this.isHighFilterMode()) {
            return this.highPassEdgeStrength === 0 ? 'none' : 'url(#fallai-high-pass-filter)';
        }
        if (this.isLowFilterMode()) {
            return this.lowPassFilterStyle();
        }
        return 'none';
    }

    public uploadPrivacyVideoFilterStyle(): string {
        return this.isLowFilterMode() ? 'none' : this.privacyVideoFilterStyle();
    }

    public privacyViewLabel(): string {
        if (this.isHighFilterMode()) return 'High Pass · 윤곽선';
        if (this.isLowFilterMode()) return 'Low Pass · 강한 블러';
        if (this.isCleanOverlayMode()) return this.isSkeletonCompositeMode() ? 'Clean · 스켈원본' : 'Clean · 검은 배경';
        if (this.isOriginalPrivacyMode()) return '원본 전용';
        return this.isSkeletonPrivacyMode() ? '스켈레톤 전용' : '스켈원본';
    }

    public async setPrivacyViewMode(mode: PrivacyViewMode) {
        const previousMode = this.privacyViewMode;
        this.privacyViewMode = mode;
        // 개인정보 보기 필터와 분석 표시 선택은 서로 독립적입니다.
        // 원본 모드에서는 실제 선택값도 반드시 표시 없음으로 고정합니다.
        // 원본을 벗어나면 저장된 Skeleton/환경 표시를 다시 복원합니다.
        if (mode === 'original') {
            this.applyDefaultOverlayModeForPrivacyView(mode);
        } else if (previousMode === 'original') {
            this.overlayMode = this.storedOverlayMode() || this.defaultOverlayModeForPrivacyView(mode);
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                display_mode: this.overlayMode,
                display_mode_label: this.overlayModeLabel(),
            };
        }
        this.clearStoredAgentWeightsForNoOverlay();
        if (this.overlayMode !== 'SKELETON_ENVIRONMENT') this.cleanLegendOpen = false;
        try {
            localStorage.setItem(PRIVACY_VIEW_STORAGE_KEY, this.privacyViewMode);
        } catch (e) { }
        this.latestCleanRenderedObjects = [];
        if (!this.canShowSkeletonOverlay()) this.clearRealtimeServerDetectionOverlay();
        this.drawUploadDetectionAtCurrentTime();
        if (this.inputMode === 'webcam') {
            const cachedOverlay = this.realtimeEnvironmentSnapshotResult || this.analysisResult;
            if (cachedOverlay) this.drawRealtimeServerDetectionOverlay(cachedOverlay);
        }
        await this.service.render();
    }

    public ngOnDestroy() {
        void this.stopRealtimeBenchmark(true);
        this.stopAnalysisProgress();
        this.stopRealtimeAnalysis();
        this.stopWebcamStream();
        this.stopPoseLoop();
        this.disposeMediaPipeModels();
        this.stopDetectionReplay();
        this.stopFeedbackTimer();
        this.stopTrainingJobPolling();
        this.stopContinuousTrainingPolling();
        this.stopPoseStudioPolling();
        this.stopAihubDownloadPolling();
        this.stopFallCandidateTestProgress();
        this.stopFallCandidatePosePlayback(false);
        if (this.fallCandidateTestVideoUrl) {
            try { URL.revokeObjectURL(this.fallCandidateTestVideoUrl); } catch (_e) { }
        }
        if (this.assetVersionCheckTimer) { clearInterval(this.assetVersionCheckTimer); this.assetVersionCheckTimer = null; }
        if (this.logFeedbackTimerHandle) { clearInterval(this.logFeedbackTimerHandle); this.logFeedbackTimerHandle = null; }
        if (this.realtimeOverlayFlashTimer) clearTimeout(this.realtimeOverlayFlashTimer);
        if (this.realtimeUiRenderTimer) clearTimeout(this.realtimeUiRenderTimer);
        if (this.realtimeOverlayRafId) cancelAnimationFrame(this.realtimeOverlayRafId);
        if (this.entityCorrectionMessageTimer) clearTimeout(this.entityCorrectionMessageTimer);
        if (this.mpFpsTimer) clearInterval(this.mpFpsTimer);
        this.cleanupLogBlobUrls();
        this.clearRealtimeUploadedVideoSource();
    }

    public async loadPrototypeInfo() {
        this.loadingInfo = true;
        await this.service.render();
        try {
            const { code, data }: any = await wiz.call('prototype_info');
            if (code === 200) {
                this.prototypeInfo = data;
                const optionKeys = this.analysisModelOptionsForView().map((item: any) => String(item?.key || ''));
                if (optionKeys.length > 0 && !optionKeys.includes(this.selectedModelType)) {
                    this.selectedModelType = String(this.prototypeInfo?.model_options?.default || optionKeys[0] || 'rf-dual');
                }
                const trainingTargetKeys = this.trainingTargetOptionsForView().map((item: any) => String(item?.key || ''));
                if (trainingTargetKeys.length > 0 && !trainingTargetKeys.includes(this.trainingTargetModel)) {
                    this.trainingTargetModel = trainingTargetKeys.includes('rf-dual') ? 'rf-dual' : trainingTargetKeys[0];
                }
            }
            else this.errorMessage = data?.message || '프로토타입 정보를 불러오지 못했습니다.';
        } catch (e: any) {
            this.errorMessage = e?.message || '프로토타입 정보를 불러오지 못했습니다.';
        }
        this.continuousTrainingStatus = this.prototypeInfo?.continuous_training?.aihub82 || this.continuousTrainingStatus;
        this.continuousTrainingItems = this.normalizeContinuousTrainingItems(
            this.prototypeInfo?.continuous_training_items || this.prototypeInfo?.continuous_training
        );
        this.loadingInfo = false;
        await this.service.render();
    }

    public async saveAlertSettings(form: any) {
        try {
            const params: any = { action: 'save_alert_settings', ...form };
            const { code, data } = await wiz.call('prototype_info', params);
            if (code === 200 && data?.alert_settings) this.prototypeInfo.alert_settings = data.alert_settings;
        } catch (e) { }
        await this.service.render();
    }

    public async openRealtimeMode() {
        this.agentWeightManagerOpen = true;
        await this.service.render();
        if (this.uploadWorkflowMode !== 'analyze') {
            await this.setUploadWorkflowMode('analyze');
        }
        await this.switchInputMode('webcam');
    }

    public async openTrainingMode() {
        // 클릭 즉시 화면 상태를 전환합니다. 실시간 분석 정리와 Registry 조회는
        // 첫 paint 이후 수행해 흰 화면으로 기다리는 구간을 만들지 않습니다.
        this.inputMode = 'upload';
        this.uploadWorkflowMode = 'train';
        this.trainingModeEntering = true;
        this.errorMessage = '';
        this.resetFallDetectionDraftSelection();
        this.restorePoseStudioTrainingSettings();
        this.restorePoseStudioDatasetsCache();
        this.restorePoseStudioRegistryCache();
        this.restoreFallDetectionStudioCache();
        await this.service.render();
        this.scrollToFallDetectionStepOne();
        window.setTimeout(() => void this.finishTrainingModeBootstrap(true), 0);
    }

    private resetFallDetectionDraftSelection() {
        this.trainingPipelineType = 'fall_detection';
        this.fallDetectionDatasetIds = [];
        this.fallDetectionDatasetCandidateId = '';
        this.fallDetectionNormalSupplementIds = [];
        this.fallDetectionNormalSupplementCandidateId = '';
        this.fallDetectionNormalSupplementsInitialized = false;
        this.fallDetectionPoseModelId = '';
        this.fallDetectionClassifierTypes = [];
        this.fallDetectionAdvancedSettingsOpen = false;
        this.fallDetectionMessage = '';
        this.fallDetectionMessageType = 'info';
    }

    private scrollToFallDetectionStepOne() {
        window.setTimeout(() => {
            const target = document.getElementById('fall-detection-pipeline');
            if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
            else window.scrollTo({ top: 0, behavior: 'auto' });
        }, 0);
    }

    private async finishTrainingModeBootstrap(cleanupRealtime: boolean = true) {
        if (this.trainingModeBootstrapRunning) return;
        this.trainingModeBootstrapRunning = true;
        try {
            if (cleanupRealtime) {
                await this.deactivateWebcamCamera();
                this.clearRealtimeUploadedVideoSource();
            }
            await Promise.allSettled([
                this.loadFallDetectionTrainingSnapshot(),
                this.loadPoseStudioRegistrySnapshot(),
                this.loadAihubDownloadStatus(),
            ]);
            void this.loadContinuousTrainingStatus(false);
        } finally {
            this.trainingModeBootstrapRunning = false;
            this.trainingModeEntering = false;
            await this.service.render();
        }
    }

    public async switchInputMode(mode: string) {
        const nextMode = mode === 'webcam' ? 'webcam' : 'upload';
        if (this.inputMode === nextMode) {
            if (nextMode !== 'webcam') {
                await this.deactivateWebcamCamera();
                this.clearRealtimeUploadedVideoSource();
            }
            return;
        }
        this.inputMode = nextMode;
        this.errorMessage = '';
        this.clearAnalysisState();
        this.feedbackStatus = '';
        this.feedbackMessage = '';
        if (nextMode === 'webcam') {
            this.clearRealtimeUploadedVideoSource();
            this.realtimeSourceMode = 'camera';
            // 카메라 권한 응답을 기다리기 전에 실시간 레이아웃과
            // 이벤트 피드의 Weight Manager를 먼저 표시합니다.
            await this.service.render();
            await this.ensureWebcamReady();
        } else {
            await this.deactivateWebcamCamera();
            this.clearRealtimeUploadedVideoSource();
        }
        await this.service.render();
    }

    public async ensureWebcamReady() {
        if (this.inputMode !== 'webcam') {
            this.stopWebcamStream();
            return;
        }
        if (this.webcamStream) {
            this.webcamReady = true;
            await this.service.render();
            setTimeout(() => {
                if (this.inputMode !== 'webcam') {
                    this.stopWebcamStream();
                    return;
                }
                const videoEl = this.webcamVideoRef?.nativeElement;
                if (videoEl && this.webcamStream) {
                    videoEl.srcObject = this.webcamStream;
                    videoEl.play().catch(() => { });
                }
                if (!this.mpRunning) this.initMediaPipePose();
            }, 200);
            return;
        }
        this.webcamError = '';
        try {
            const constraints: any = { video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 15, max: 30 } }, audio: false };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (this.inputMode !== 'webcam') {
                stream.getTracks().forEach(t => t.stop());
                this.webcamReady = false;
                return;
            }
            this.webcamStream = stream;
            this.webcamReady = true;
            await this.service.render();
            setTimeout(() => {
                if (this.inputMode !== 'webcam') {
                    this.stopWebcamStream();
                    return;
                }
                const videoEl = this.webcamVideoRef?.nativeElement;
                if (videoEl && this.webcamStream) {
                    videoEl.srcObject = this.webcamStream;
                    videoEl.play().catch(() => { });
                }
                this.initMediaPipePose();
            }, 200);
            return;
        } catch (e: any) {
            this.webcamError = '카메라 접근 실패: ' + (e.message || e);
            this.webcamReady = false;
        }
        await this.service.render();
    }

    private detachWebcamVideoElement() {
        const videoEl = this.webcamVideoRef?.nativeElement;
        if (!videoEl) return;
        try { videoEl.pause(); } catch (e) { }
        try { videoEl.srcObject = null; } catch (e) { }
    }

    private clearRealtimeUploadedVideoSource() {
        this.stopRealtimeEnvironmentSnapshots();
        if (this.realtimeEndFinalizeTimer) {
            clearTimeout(this.realtimeEndFinalizeTimer);
            this.realtimeEndFinalizeTimer = null;
        }
        this.realtimeVideoEnded = false;
        this.realtimeVideoEndedAt = 0;
        this.realtimeFinalAcceptedTimestampMs = 0;
        this.realtimeCancelledPrecisionCount = 0;
        this.realtimeLatePrecisionIgnoredCount = 0;
        this.realtimeEnvironmentSnapshotCount = 0;
        this.realtimeUploadedAnalysisRunId += 1;
        this.realtimeUploadedAnalysisController?.abort();
        this.realtimeUploadedAnalysisController = null;
        if (this.realtimeUploadedVideoUrl) {
            try { URL.revokeObjectURL(this.realtimeUploadedVideoUrl); } catch (e) { }
        }
        this.realtimeUploadedVideoUrl = '';
        this.realtimeUploadedVideoName = '';
        this.realtimeUploadedVideoFile = null;
        this.realtimeUploadedAnalysisRunning = false;
        this.realtimeUploadedAnalysisComplete = false;
        this.realtimePrecisionAnalysisPending = false;
        this.realtimeUploadedAnalysisError = '';
        this.realtimeUploadedFinalizeRunning = false;
        this.resetRealtimeLocalFallCandidate();
    }

    public stopWebcamStream() {
        this.detachWebcamVideoElement();
        if (this.webcamStream) {
            this.webcamStream.getTracks().forEach(t => t.stop());
            this.webcamStream = null;
        }
        this.webcamReady = false;
        this.stopPoseLoop();
    }

    private async deactivateWebcamCamera() {
        await this.stopRealtimeAnalysis();
        this.stopWebcamStream();
        this.clearRealtimeServerDetectionOverlay();
    }

    public async initMediaPipePose() {
        if (this.poseLandmarker) {
            this.mpReady = true;
            if (this.hasRealtimeInputSource() && !this.mpRunning) this.startPoseLoop();
            return;
        }
        if (!this.mpInitializationPromise) {
            this.mpInitializationPromise = this.initializeMediaPipeModels();
        }
        await this.mpInitializationPromise;
        // 페이지 진입 중 시작된 초기화가 업로드 선택과 겹쳐도 업로드 호출자는
        // 같은 Promise를 기다린 뒤 실제 재생 video에 Fast Path를 연결합니다.
        if (this.poseLandmarker && this.hasRealtimeInputSource() && !this.mpRunning) {
            this.mpReady = true;
            this.startPoseLoop();
        }
    }

    private async initializeMediaPipeModels(): Promise<void> {
        this.mpInitializing = true;
        this.faceMeshError = '';
        await this.service.render();
        try {
            const vision = await FilesetResolver.forVisionTasks(MP_VISION_WASM_URL);
            if (!this.poseLandmarker) {
                for (const delegate of ['GPU', 'CPU'] as const) {
                    try {
                        this.poseLandmarker = await this.createPoseLandmarker(vision, delegate);
                        this.mpPoseDelegate = delegate;
                        await this.warmFastPoseLandmarker();
                        this.mpReady = true;
                        if (this.hasRealtimeInputSource() && !this.mpRunning) this.startPoseLoop();
                        break;
                    } catch (error) {
                        console.warn('PoseLandmarker ' + delegate + ' init failed:', error);
                    }
                }
            }
            // 얼굴 모델 실패/지연은 Skeleton Fast Path의 준비 완료를 막지 않습니다.
            if (!this.faceLandmarker) {
                for (const delegate of ['GPU', 'CPU'] as const) {
                    try {
                        this.faceLandmarker = await this.createFaceLandmarker(vision, delegate);
                        this.faceMeshDelegate = delegate;
                        break;
                    } catch (error) {
                        console.warn('FaceLandmarker ' + delegate + ' init failed:', error);
                    }
                }
            }
        } catch (e: any) {
            console.warn('MediaPipe init failed:', e);
        } finally {
            this.mpReady = Boolean(this.poseLandmarker);
            this.faceMeshReady = Boolean(this.faceLandmarker);
            this.faceMeshError = this.faceMeshReady ? '' : '얼굴 메시 모델을 불러오지 못했습니다.';
            this.mpInitializing = false;
            this.mpInitializationPromise = null;
            if (this.poseLandmarker && this.hasRealtimeInputSource() && !this.mpRunning) this.startPoseLoop();
            await this.service.render();
        }
    }

    private async waitForFastPoseReady(timeoutMs: number = 5000): Promise<boolean> {
        const startedAt = performance.now();
        while (!this.poseLandmarker && performance.now() - startedAt < timeoutMs) {
            await new Promise(resolve => setTimeout(resolve, 25));
        }
        return Boolean(this.poseLandmarker);
    }

    private createPoseLandmarker(vision: any, delegate: 'GPU' | 'CPU') {
        return PoseLandmarker.createFromOptions(vision, {
            baseOptions: {
                modelAssetPath: MP_POSE_MODEL_URL,
                delegate,
            },
            runningMode: 'VIDEO',
            numPoses: MP_MAX_POSES,
        });
    }

    private async warmFastPoseLandmarker(): Promise<void> {
        if (!this.poseLandmarker || this.mpPoseWarmed) return;
        try {
            const warmupCanvas = document.createElement('canvas');
            warmupCanvas.width = 64;
            warmupCanvas.height = 64;
            const context = warmupCanvas.getContext('2d');
            if (context) {
                context.fillStyle = '#000';
                context.fillRect(0, 0, warmupCanvas.width, warmupCanvas.height);
            }
            this.poseLandmarker.detectForVideo(warmupCanvas, performance.now());
            this.mpPoseWarmed = true;
        } catch (error) {
            console.debug('[FAST_POSE] warmup-skipped', error);
        }
    }

    private createFaceLandmarker(vision: any, delegate: 'GPU' | 'CPU') {
        return FaceLandmarker.createFromOptions(vision, {
            baseOptions: { modelAssetPath: MP_FACE_MODEL_URL, delegate },
            runningMode: 'VIDEO',
            numFaces: MP_MAX_FACES,
            minFaceDetectionConfidence: 0.50,
            minFacePresenceConfidence: 0.50,
            minTrackingConfidence: 0.50,
            outputFaceBlendshapes: true,
            outputFacialTransformationMatrixes: false,
        });
    }

    private recordPoseFrameDiagnostic(frame: PoseFrameEnvelope, dropReason: PoseDropReason, detail: any = {}) {
        const now = performance.now();
        const entry = {
            videoFrameId: Number(frame?.frameId ?? -1),
            videoTimestampMs: Number(frame?.videoTimestampMs || 0),
            poseRequestStartedAt: Number(detail.poseRequestStartedAt || 0),
            poseResponseReceivedAt: Number(detail.poseResponseReceivedAt || 0),
            poseInferenceMs: Number(detail.poseInferenceMs || 0),
            poseResultAgeMs: Number(detail.poseResultAgeMs || 0),
            poseKeypointCount: Number(detail.poseKeypointCount || 0),
            poseConfidence: Number(detail.poseConfidence || 0),
            personDetected: Boolean(detail.personDetected),
            trackId: Number(detail.trackId || 0),
            trackState: String(detail.trackState || 'LOST'),
            renderCalled: Boolean(detail.renderCalled),
            renderedKeypointCount: Number(detail.renderedKeypointCount || 0),
            dropReason,
            recordedAtRealtimeMs: now,
        };
        this.mpPoseFrameDiagnostics.push(entry);
        if (this.mpPoseFrameDiagnostics.length > 120) this.mpPoseFrameDiagnostics.splice(0, this.mpPoseFrameDiagnostics.length - 120);
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            pose_frame_diagnostic: entry,
            pose_frame_diagnostics: this.mpPoseFrameDiagnostics,
        };
    }

    private fastPoseLog(stage: string, payload: any) {
        if (!this.realtimeDeveloperMode) return;
        console.info(`[FAST_POSE] ${stage}`, payload);
    }

    public realtimeFastPoseDeveloperText(): string {
        const item = this.realtimeFastPoseCounters || {};
        return `Frame ${item.videoFrame || 0} | Pose 요청 ${item.request || 0} | 응답 ${item.response || 0}`
            + ` | 유효 Pose ${item.validResponse || 0} | Track ${item.trackCreated || 0}`
            + ` | Render ${item.render || 0} | Draw ${item.draw || 0} | Clear ${item.clear || 0}`;
    }

    private drainPoseFrameQueue() {
        this.mpInFlightFrame = null;
        if (!this.mpRunning || this.mpProcessing) return;
        const next = this.mpEventCandidateFrame || this.mpNormalPendingFrame;
        if (!next) {
            this.mpPoseQueueLength = 0;
            return;
        }
        if (next === this.mpEventCandidateFrame) this.mpEventCandidateFrame = null;
        else this.mpNormalPendingFrame = null;
        this.mpPoseQueueLength = Number(!!this.mpNormalPendingFrame) + Number(!!this.mpEventCandidateFrame);
        this.mpLastSubmittedFrameId = next.frameId;
        this.mpLastFrameTime = next.capturedAt;
        this.mpInFlightFrame = next;
        queueMicrotask(() => this.drawMpPose(next));
    }

    private skipPoseFrame(frame: PoseFrameEnvelope, reason: PoseDropReason) {
        this.recordPoseFrameDiagnostic(frame, reason);
        this.drainPoseFrameQueue();
    }

    public startPoseLoop() {
        if (!this.hasRealtimeInputSource()) return;
        if (this.mpRunning || (!this.poseLandmarker && !this.faceLandmarker)) return;
        this.mpRunning = true;
        this.mpFrameCount = 0;
        this.faceMeshFrameCount = 0;
        this.mpLastFrameTime = 0;
        this.mpLastSubmittedFrameId = -1;
        this.mpPoseQueueLength = 0;
        this.mpPoseMaxQueueLength = 0;
        this.mpInFlightFrame = null;
        this.mpNormalPendingFrame = null;
        this.mpEventCandidateFrame = null;
        this.mpPriorityTransitionUntilMs = 0;
        this.mpPoseFrameDiagnostics = [];
        this.mpLastRenderedTrackId = 0;
        this.mpTrackIdChangeCount = 0;
        this.mpLastSkeletonRenderedAt = 0;
        this.mpLongestSkeletonGapMs = 0;
        this.mpPersistentRenderPoses = [];
        this.mpSkeletonRenderLastAt = 0;
        this.mpSkeletonCoverageLastVideoMs = -1;
        this.mpSkeletonPersonVisibleMs = 0;
        this.mpSkeletonVisibleMs = 0;
        this.mpSkeletonMissingGapStartedMs = -1;
        this.mpSkeletonMissingGapCount = 0;
        this.mpSkeletonMissingOver700Count = 0;
        this.mpSkeletonLongestMissingMs = 0;
        this.mpSkeletonPredictedDrawCount = 0;
        this.realtimeFastPoseStatus = 'CAPTURE_WAIT';
        this.realtimeFastPoseCounters = {
            videoFrame: 0, request: 0, response: 0, validResponse: 0,
            trackCreated: 0, render: 0, draw: 0, clear: 0,
        };
        this.mpPoseFirstRequestedAt = performance.now();
        this.mpPoseFirstRenderedAt = 0;
        this.mpLastPoseSeenMs = 0;
        this.faceMeshLastInferenceMs = 0;
        this.resetMediaPipeTracks();
        this.initKalmanFilters();
        this.mpFpsTimer = setInterval(() => {
            this.mpFps = this.mpFrameCount;
            this.faceMeshFps = this.faceMeshFrameCount;
            this.mpFrameCount = 0;
            this.faceMeshFrameCount = 0;
        }, 1000);
        const canvasEl = this._mpPoseCanvasEl?.nativeElement || document.querySelector('[data-mp-pose-canvas]') as HTMLCanvasElement;
        if (canvasEl && !this.mpResizeObserver) {
            this.mpResizeObserver = new ResizeObserver((entries: any[]) => {
                for (const entry of entries) {
                    const { width, height } = entry.contentRect;
                    if (width > 0 && height > 0) resizeCanvasForDisplay(canvasEl, width, height);
                }
            });
            this.mpResizeObserver.observe(canvasEl.parentElement || canvasEl);
        }
        const videoEl = this.webcamVideoRef?.nativeElement;
        const submit = (capturedAt: number, videoTimestampMs: number, frameId: number) => {
            if (!this.mpRunning || frameId === this.mpLastSubmittedFrameId) return;
            const frame: PoseFrameEnvelope = {
                capturedAt,
                videoTimestampMs,
                frameId,
                eventCandidate: ['suspected', 'verifying'].includes(this.realtimeLocalFallState)
                    || ['kneel', 'lie', 'transition'].includes(this.realtimeFastPosture)
                    || capturedAt <= this.mpPriorityTransitionUntilMs,
            };
            const analysisIntervalMs = 1000 / this.realtimePresetConfig().analysisFps;
            if (capturedAt - this.mpLastFrameTime < analysisIntervalMs) {
                this.realtimePerfStats.droppedFrames = Number(this.realtimePerfStats.droppedFrames || 0) + 1;
                this.recordPoseFrameDiagnostic(frame, 'POSE_REQUEST_SKIPPED');
                return;
            }
            if (this.mpProcessing) {
                // 일반 최신 프레임과 사건 후보 프레임을 각각 한 개만 보존합니다.
                if (frame.eventCandidate) this.mpEventCandidateFrame = frame;
                else this.mpNormalPendingFrame = frame;
                this.mpPoseQueueLength = Number(!!this.mpNormalPendingFrame) + Number(!!this.mpEventCandidateFrame);
                this.mpPoseMaxQueueLength = Math.max(this.mpPoseMaxQueueLength, this.mpPoseQueueLength);
                this.realtimePerfStats.droppedFrames = Number(this.realtimePerfStats.droppedFrames || 0) + 1;
                this.recordPoseFrameDiagnostic(frame, 'POSE_REQUEST_IN_FLIGHT');
                return;
            }
            this.mpLastSubmittedFrameId = frameId;
            this.mpLastFrameTime = capturedAt;
            this.mpInFlightFrame = frame;
            this.drawMpPose(frame);
        };
        const intervalTick = () => {
            if (!this.mpRunning || !videoEl || videoEl.paused || videoEl.ended) return;
            const capturedAt = performance.now();
            const videoTimestampMs = Math.max(0, Number(videoEl.currentTime || 0) * 1000);
            this.realtimeFastPoseCounters.videoFrame += 1;
            this.fastPoseLog('video-frame', {
                sessionId: this.realtimeSessionId,
                frameId: Math.round(videoTimestampMs),
                mediaTime: Number(videoEl.currentTime || 0),
                videoPaused: videoEl.paused,
                videoEnded: videoEl.ended,
                scheduler: 'interval-fallback',
            });
            submit(capturedAt, videoTimestampMs, Math.round(videoTimestampMs));
        };
        const startIntervalFallback = () => {
            if (this.mpPoseIntervalFallbackTimer) return;
            this.realtimeFastPoseStatus = 'CAPTURE_WAIT';
            this.mpPoseIntervalFallbackTimer = setInterval(intervalTick, 250);
        };
        const requestVideoFrameCallback = (videoEl as any)?.requestVideoFrameCallback;
        if (videoEl && typeof requestVideoFrameCallback === 'function') {
            const onVideoFrame = (now: number, metadata: any) => {
                if (!this.mpRunning) return;
                try {
                    this.mpLastVideoFrameCallbackAt = performance.now();
                    if (this.mpPoseIntervalFallbackTimer) {
                        clearInterval(this.mpPoseIntervalFallbackTimer);
                        this.mpPoseIntervalFallbackTimer = null;
                    }
                    const videoTimestampMs = Math.max(0, Number(metadata?.mediaTime ?? videoEl.currentTime ?? 0) * 1000);
                    const frameId = Math.round(videoTimestampMs);
                    this.realtimeFastPoseCounters.videoFrame += 1;
                    this.fastPoseLog('video-frame', {
                        sessionId: this.realtimeSessionId,
                        frameId,
                        mediaTime: Number(metadata?.mediaTime ?? videoEl.currentTime ?? 0),
                        videoPaused: videoEl.paused,
                        videoEnded: videoEl.ended,
                    });
                    submit(now, videoTimestampMs, frameId);
                } catch (error) {
                    this.recordPoseFrameDiagnostic({ capturedAt: now, videoTimestampMs: Number(videoEl.currentTime || 0) * 1000, frameId: -1, eventCandidate: false }, 'NO_FRAME_CAPTURE');
                } finally {
                    if (this.mpRunning && !videoEl.ended) {
                        this.mpPoseVideoFrameCallbackId = requestVideoFrameCallback.call(videoEl, onVideoFrame);
                    }
                }
            };
            this.mpPoseVideoFrameCallbackId = requestVideoFrameCallback.call(videoEl, onVideoFrame);
            this.mpLastVideoFrameCallbackAt = performance.now();
            this.mpFrameCallbackWatchdogTimer = setInterval(() => {
                if (!this.mpRunning || !videoEl || videoEl.paused || videoEl.ended) return;
                if (performance.now() - this.mpLastVideoFrameCallbackAt >= FRAME_CALLBACK_WATCHDOG_MS) startIntervalFallback();
            }, 250);
        } else {
            startIntervalFallback();
        }
        this.startPersistentSkeletonRenderLoop();
    }

    private persistentPoseHoldMs(track: MpPoseTrack | undefined, nowMs: number): number {
        const transitionActive = ['transition', 'kneel', 'lie'].includes(this.realtimeFastPosture)
            || ['suspected', 'verifying'].includes(this.realtimeLocalFallState)
            || String(track?.recoveryReason || '').includes('POSTURE_CHANGE')
            || nowMs <= this.mpPriorityTransitionUntilMs;
        if (transitionActive) return 2200;
        const detectorPersonPresent = this.approvedLogicalPersonBoxes(this.currentRootAnalysisResult()).length > 0;
        if (detectorPersonPresent) return 1800;
        if (track && nowMs - Number(track.lastSeenMs || 0) <= PERSON_TRACK_TTL_MS) return PERSON_TRACK_TTL_MS;
        return POSE_HOLD_MS;
    }

    private stagedPersistentLandmarks(previous: any[], incoming: any[]): any[] {
        const transitionActive = ['transition', 'kneel', 'lie'].includes(this.realtimeFastPosture)
            || ['suspected', 'verifying'].includes(this.realtimeLocalFallState)
            || performance.now() <= this.mpPriorityTransitionUntilMs;
        const torsoIndexes = new Set([11, 12, 23, 24]);
        const distalIndexes = new Set([15, 16, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32]);
        return (incoming || []).map((landmark: any, index: number) => {
            const old = previous?.[index];
            if (!old) return { ...landmark };
            const visibility = Number(landmark?.visibility ?? 0);
            const presence = Number(landmark?.presence ?? visibility);
            const confidence = Math.max(0, Math.min(1, Math.min(
                Number.isFinite(visibility) ? visibility : 0,
                Number.isFinite(presence) ? presence : visibility,
            )));
            if (confidence >= 0.55) {
                const oldX = Number(old?.x ?? landmark?.x ?? 0);
                const oldY = Number(old?.y ?? landmark?.y ?? 0);
                const nextX = Number(landmark?.x ?? oldX);
                const nextY = Number(landmark?.y ?? oldY);
                const dx = nextX - oldX;
                const dy = nextY - oldY;
                const distance = Math.hypot(dx, dy);
                const maxJump = transitionActive ? (torsoIndexes.has(index) ? 0.18 : 0.24)
                    : torsoIndexes.has(index) ? 0.075 : distalIndexes.has(index) ? 0.15 : 0.105;
                const jumpScale = distance > maxJump ? maxJump / Math.max(distance, 0.0001) : 1;
                const clampedX = oldX + dx * jumpScale;
                const clampedY = oldY + dy * jumpScale;
                const alpha = transitionActive ? 0.82 : torsoIndexes.has(index) ? 0.52 : distalIndexes.has(index) ? 0.72 : 0.62;
                return {
                    ...landmark,
                    x: oldX * (1 - alpha) + clampedX * alpha,
                    y: oldY * (1 - alpha) + clampedY * alpha,
                };
            }
            if (confidence >= 0.25) return {
                ...landmark,
                x: Number(old?.x ?? landmark?.x ?? 0) * 0.55 + Number(landmark?.x ?? 0) * 0.45,
                y: Number(old?.y ?? landmark?.y ?? 0) * 0.55 + Number(landmark?.y ?? 0) * 0.45,
            };
            return { ...old, visibility, presence };
        });
    }

    private updatePersistentSkeletonPoses(renderablePoses: any[], nowMs: number) {
        if (!Array.isArray(renderablePoses) || renderablePoses.length === 0) return;
        const updatedTrackIds = new Set<number>();
        const next = renderablePoses.map((pose: any) => {
            const trackId = Number(pose?.trackId || 0);
            const track = this.mpPoseTracks.find(item => item.id === trackId);
            const previous = this.mpPersistentRenderPoses.find(item => item.trackId === trackId);
            const recovering = Boolean(previous && nowMs - previous.updatedAtMs > POSE_TIMESTAMP_TOLERANCE_MS);
            updatedTrackIds.add(trackId);
            return {
                trackId,
                logicalPersonId: track?.logicalPersonId || `local-person-${trackId}`,
                sourcePoseId: `mp33-${trackId}-${Math.round(nowMs)}`,
                renderEligible: track?.renderEligible !== false && Number(track?.mergedIntoTrackId || 0) <= 0,
                landmarks: this.stagedPersistentLandmarks(previous?.landmarks || [], pose?.landmarks || []),
                poseState: recovering ? 'RECOVERING' : (pose?.poseState || 'LIVE'),
                poseAlpha: Math.max(0.28, Math.min(1, Number(pose?.poseAlpha ?? 1))),
                updatedAtMs: nowMs,
                validUntilMs: nowMs + this.persistentPoseHoldMs(track, nowMs),
                velocityX: Number(track?.velocityX || 0),
                velocityY: Number(track?.velocityY || 0),
                bbox: Array.isArray(track?.bbox) ? [...track!.bbox] : [],
            } as PersistentRenderPose;
        });
        for (const cached of this.mpPersistentRenderPoses) {
            const track = this.mpPoseTracks.find(item => item.id === cached.trackId);
            if (!updatedTrackIds.has(cached.trackId) && cached.validUntilMs > nowMs
                && track?.renderEligible !== false && Number(track?.mergedIntoTrackId || 0) <= 0) next.push(cached);
        }
        this.mpPersistentRenderPoses = this.deduplicateRenderableMediaPipePoses(next);
    }

    private drawPersistentSkeletonCanvas(nowMs: number): number {
        const videoEl = this.webcamVideoRef?.nativeElement;
        const canvasEl = this._mpPoseCanvasEl?.nativeElement || document.querySelector('[data-mp-pose-canvas]') as HTMLCanvasElement;
        if (!videoEl || !canvasEl || !this.canShowPersonOverlay()) return 0;
        const canvasRect = canvasEl.getBoundingClientRect();
        if (canvasRect.width <= 0 || canvasRect.height <= 0) return 0;
        const prepared = resizeCanvasForDisplay(canvasEl, canvasRect.width, canvasRect.height);
        const ctx = prepared.context;
        if (!ctx) return 0;
        const displayRect = this.getVideoContentRect(videoEl);
        const active: PersistentRenderPose[] = [];
        for (const cached of this.mpPersistentRenderPoses) {
            const track = this.mpPoseTracks.find(item => item.id === cached.trackId);
            const dynamicValidUntil = Math.max(cached.validUntilMs, cached.updatedAtMs + this.persistentPoseHoldMs(track, nowMs));
            if (nowMs <= dynamicValidUntil || videoEl.paused || videoEl.ended) active.push({ ...cached, validUntilMs: dynamicValidUntil });
        }
        const renderTracks = this.deduplicateRenderableMediaPipePoses(active);
        this.mpPersistentRenderPoses = renderTracks;
        this.clearCanvasFully(ctx, canvasEl, 'persistent-render-loop');
        const replaceSkeletonFace = this.replaceSkeletonFaceWithExpressionFeatures();
        let renderedSkeletons = 0;
        const renderedLogicalPersonIds = new Set<string>();
        for (const cached of renderTracks) {
            const track = this.mpPoseTracks.find(item => item.id === cached.trackId);
            if (!cached.renderEligible || track?.renderEligible === false || Number(track?.mergedIntoTrackId || 0) > 0) {
                this.mpMergedTrackResidualRenderCount += 1;
                continue;
            }
            const logicalPersonId = cached.logicalPersonId || track?.logicalPersonId || `local-person-${cached.trackId}`;
            if (renderedLogicalPersonIds.has(logicalPersonId)) {
                this.mpDuplicateLogicalRenderCount += 1;
                console.warn('DUPLICATE_LOGICAL_PERSON_RENDER', { logicalPersonId, trackId: cached.trackId });
                continue;
            }
            renderedLogicalPersonIds.add(logicalPersonId);
            const ageMs = Math.max(0, nowMs - cached.updatedAtMs);
            const predictionAgeMs = Math.min(ageMs, this.persistentPoseHoldMs(track, nowMs));
            const predicted = ageMs > Math.max(POSE_TIMESTAMP_TOLERANCE_MS, 450);
            const recovering = cached.poseState === 'RECOVERING' && ageMs < 450;
            const shiftX = predicted ? Math.max(-0.10, Math.min(0.10, cached.velocityX * predictionAgeMs)) : 0;
            const shiftY = predicted ? Math.max(-0.10, Math.min(0.10, cached.velocityY * predictionAgeMs)) : 0;
            const points = (cached.landmarks || []).map((landmark: any, index: number) => {
                const point = this.normalizeMediaPipePoint(landmark);
                const valid = this.mediaPipeLandmarkVisible(landmark, index)
                    || (!MP_HAND_DETAIL_LANDMARKS.has(index) && ageMs <= cached.validUntilMs - cached.updatedAtMs);
                return {
                    x: Math.max(0, Math.min(1, point.x + shiftX)),
                    y: Math.max(0, Math.min(1, point.y + shiftY)),
                    valid,
                };
            });
            const personColor = MP_PERSON_COLORS[(cached.trackId - 1) % MP_PERSON_COLORS.length];
            const lineWidth = Math.max(1.8, Math.min(3.6, Math.min(displayRect.w, displayRect.h) / 240));
            const pointRadius = Math.max(3, Math.min(5.2, Math.min(displayRect.w, displayRect.h) / 120));
            ctx.save();
            ctx.globalAlpha = predicted ? 0.58 : recovering ? 0.82 : cached.poseAlpha;
            ctx.shadowColor = 'rgba(0,0,0,0.92)';
            ctx.shadowBlur = 4;
            for (const [left, right] of MP_POSE_CONNECTIONS) {
                if (MP_HAND_DETAIL_LANDMARKS.has(left) || MP_HAND_DETAIL_LANDMARKS.has(right)) continue;
                if (replaceSkeletonFace && (MP_FACE_LANDMARKS.has(left) || MP_FACE_LANDMARKS.has(right))) continue;
                const a = points[left]; const b = points[right];
                if (!a?.valid || !b?.valid) continue;
                ctx.beginPath();
                ctx.moveTo(displayRect.x + a.x * displayRect.w, displayRect.y + a.y * displayRect.h);
                ctx.lineTo(displayRect.x + b.x * displayRect.w, displayRect.y + b.y * displayRect.h);
                ctx.strokeStyle = this.colorWithAlpha(MP_LANDMARK_COLORS[right] || MP_LANDMARK_COLORS[left] || personColor, predicted ? 0.55 : 0.92);
                ctx.lineWidth = predicted ? Math.max(1.4, lineWidth - 0.6) : lineWidth;
                ctx.setLineDash(predicted ? [4, 3] : []);
                ctx.stroke();
            }
            ctx.setLineDash([]);
            let renderedPoints = 0;
            for (let index = 0; index < points.length; index++) {
                if (MP_HAND_DETAIL_LANDMARKS.has(index) || (replaceSkeletonFace && MP_FACE_LANDMARKS.has(index))) continue;
                const point = points[index];
                if (!point.valid) continue;
                ctx.beginPath();
                ctx.arc(displayRect.x + point.x * displayRect.w, displayRect.y + point.y * displayRect.h, predicted ? pointRadius - 0.7 : pointRadius, 0, Math.PI * 2);
                ctx.fillStyle = this.colorWithAlpha(MP_LANDMARK_COLORS[index] || personColor, predicted ? 0.70 : 0.96);
                ctx.fill();
                renderedPoints += 1;
            }
            ctx.restore();
            if (renderedPoints >= 6) {
                renderedSkeletons += 1;
                if (predicted) this.mpSkeletonPredictedDrawCount += 1;
                this.fastPoseLog('skeleton-draw', {
                    frameId: Math.round(Number(videoEl.currentTime || 0) * 1000),
                    logicalPersonId,
                    trackId: cached.trackId,
                    sourcePoseId: cached.sourcePoseId,
                    poseState: predicted ? 'PREDICTED' : recovering ? 'RECOVERING' : cached.poseState,
                    drawIndex: renderedSkeletons - 1,
                    keypointSchema: 'mediapipe-pose-33',
                });
            }
        }
        return renderedSkeletons;
    }

    private updateSkeletonCoverageMetrics(videoEl: HTMLVideoElement, renderedSkeletons: number, nowMs: number) {
        const videoMs = Math.max(0, Number(videoEl.currentTime || 0) * 1000);
        const activeTrack = this.mpPoseTracks.some(track => nowMs - Number(track.lastSeenMs || 0) <= this.persistentPoseHoldMs(track, nowMs));
        const personPresent = activeTrack || this.approvedLogicalPersonBoxes(this.currentRootAnalysisResult()).length > 0;
        if (this.mpSkeletonCoverageLastVideoMs >= 0) {
            const delta = videoMs - this.mpSkeletonCoverageLastVideoMs;
            if (delta > 0 && delta <= 500) {
                if (personPresent) this.mpSkeletonPersonVisibleMs += delta;
                if (personPresent && renderedSkeletons > 0) this.mpSkeletonVisibleMs += delta;
            }
        }
        this.mpSkeletonCoverageLastVideoMs = videoMs;
        if (personPresent && renderedSkeletons <= 0) {
            if (this.mpSkeletonMissingGapStartedMs < 0) {
                this.mpSkeletonMissingGapStartedMs = videoMs;
                this.mpSkeletonMissingGapCount += 1;
            }
        } else if (this.mpSkeletonMissingGapStartedMs >= 0) {
            const gap = Math.max(0, videoMs - this.mpSkeletonMissingGapStartedMs);
            this.mpSkeletonLongestMissingMs = Math.max(this.mpSkeletonLongestMissingMs, gap);
            if (gap > 700) this.mpSkeletonMissingOver700Count += 1;
            this.mpSkeletonMissingGapStartedMs = -1;
        }
        const coverage = this.mpSkeletonPersonVisibleMs > 0
            ? this.mpSkeletonVisibleMs / this.mpSkeletonPersonVisibleMs : 0;
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            skeleton_person_visible_ms: Math.round(this.mpSkeletonPersonVisibleMs),
            skeleton_visible_ms: Math.round(this.mpSkeletonVisibleMs),
            skeleton_coverage_ratio: coverage,
            skeleton_missing_gap_count: this.mpSkeletonMissingGapCount,
            skeleton_missing_over_700ms_count: this.mpSkeletonMissingOver700Count,
            skeleton_longest_missing_ms: Math.round(this.mpSkeletonLongestMissingMs),
            bbox_prediction_draw_count: this.mpSkeletonPredictedDrawCount,
        };
    }

    private startPersistentSkeletonRenderLoop() {
        if (this.mpSkeletonRenderRafId) return;
        const render = (nowMs: number) => {
            if (!this.mpRunning) { this.mpSkeletonRenderRafId = 0; return; }
            this.mpSkeletonRenderRafId = requestAnimationFrame(render);
            if (nowMs - this.mpSkeletonRenderLastAt < 1000 / 30) return;
            this.mpSkeletonRenderLastAt = nowMs;
            const renderedSkeletons = this.drawPersistentSkeletonCanvas(nowMs);
            const videoEl = this.webcamVideoRef?.nativeElement;
            if (videoEl && !videoEl.paused && !videoEl.ended) this.updateSkeletonCoverageMetrics(videoEl, renderedSkeletons, nowMs);
            if (renderedSkeletons > 0) {
                this.mpDetectedPoseCount = renderedSkeletons;
                this.realtimeFastPoseStatus = 'TRACK_ACTIVE';
            }
        };
        this.mpSkeletonRenderRafId = requestAnimationFrame(render);
    }

    public stopPoseLoop(preserveFrame: boolean = false) {
        this.mpRunning = false;
        if (this.mpRafId) { cancelAnimationFrame(this.mpRafId); this.mpRafId = 0; }
        if (this.mpSkeletonRenderRafId) { cancelAnimationFrame(this.mpSkeletonRenderRafId); this.mpSkeletonRenderRafId = 0; }
        const videoEl = this.webcamVideoRef?.nativeElement as any;
        if (this.mpPoseVideoFrameCallbackId && typeof videoEl?.cancelVideoFrameCallback === 'function') {
            try { videoEl.cancelVideoFrameCallback(this.mpPoseVideoFrameCallbackId); } catch (e) { }
        }
        this.mpPoseVideoFrameCallbackId = 0;
        if (this.mpPoseFallbackTimer) { clearTimeout(this.mpPoseFallbackTimer); this.mpPoseFallbackTimer = null; }
        if (this.mpPoseIntervalFallbackTimer) { clearInterval(this.mpPoseIntervalFallbackTimer); this.mpPoseIntervalFallbackTimer = null; }
        if (this.mpFrameCallbackWatchdogTimer) { clearInterval(this.mpFrameCallbackWatchdogTimer); this.mpFrameCallbackWatchdogTimer = null; }
        this.mpNormalPendingFrame = null;
        this.mpEventCandidateFrame = null;
        if (!preserveFrame) this.mpPersistentRenderPoses = [];
        this.mpPoseQueueLength = 0;
        if (this.mpFpsTimer) { clearInterval(this.mpFpsTimer); this.mpFpsTimer = null; }
        if (this.faceMeshDeferredTimer) { clearTimeout(this.faceMeshDeferredTimer); this.faceMeshDeferredTimer = null; }
        if (this.mpResizeObserver) { this.mpResizeObserver.disconnect(); this.mpResizeObserver = null; }
        if (preserveFrame) return;
        this.mpLastPoseSeenMs = 0;
        this.kalmanFilters = [];
        this.mpPoseKalmanFilters = [];
        this.resetMediaPipeTracks();
        this.mpDetectedPoseCount = 0;
        this.resetLiveFacialState();
        this.clearClientPoseCanvas();
        this.clearFaceMeshCanvas();
    }

    private disposeMediaPipeModels() {
        try { this.poseLandmarker?.close?.(); } catch (e) { }
        try { this.faceLandmarker?.close?.(); } catch (e) { }
        this.poseLandmarker = null;
        this.faceLandmarker = null;
        this.mpReady = false;
        this.faceMeshReady = false;
    }

    private createLandmarkFilters() {
        const filters: { x: Kalman1D; y: Kalman1D; missCount: number }[] = [];
        for (let i = 0; i < 33; i++) filters.push({ x: new Kalman1D(), y: new Kalman1D(), missCount: 0 });
        return filters;
    }

    private initKalmanFilters(maxPoses: number = MP_MAX_POSES) {
        this.mpPoseKalmanFilters = [];
        for (let i = 0; i < maxPoses; i++) this.mpPoseKalmanFilters.push(this.createLandmarkFilters());
        this.kalmanFilters = this.mpPoseKalmanFilters[0] || [];
    }

    private getPoseKalmanFilters(poseIndex: number) {
        while (this.mpPoseKalmanFilters.length <= poseIndex) this.mpPoseKalmanFilters.push(this.createLandmarkFilters());
        this.kalmanFilters = this.mpPoseKalmanFilters[0] || [];
        return this.mpPoseKalmanFilters[poseIndex];
    }

    private resetMediaPipeTracks() {
        this.mpPoseTracks = [];
        this.mpPoseTrackFilters = {};
        this.mpNextPoseTrackId = 1;
        this.mpPrimaryPoseTrackId = 0;
        this.mpDuplicateLogicalRenderCount = 0;
        this.mpMergedTrackResidualRenderCount = 0;
        this.mpDuplicatePoseCandidateCount = 0;
    }

    private getPoseKalmanFiltersForTrack(trackId: number): MpLandmarkFilters {
        const key = Math.max(1, Math.round(Number(trackId || 1)));
        if (!this.mpPoseTrackFilters[key]) this.mpPoseTrackFilters[key] = this.createLandmarkFilters();
        this.kalmanFilters = this.mpPoseTrackFilters[1] || this.mpPoseTrackFilters[key] || [];
        return this.mpPoseTrackFilters[key];
    }

    private mediaPipePoseCenter(landmarks: any[]): { x: number; y: number; visible: number } | null {
        let sx = 0, sy = 0, n = 0;
        for (const lm of landmarks || []) {
            if ((lm?.visibility ?? 0) < this.KF_VIS_THRESHOLD) continue;
            const p = this.normalizeMediaPipePoint(lm);
            sx += p.x;
            sy += p.y;
            n++;
        }
        if (n <= 0) return null;
        return { x: sx / n, y: sy / n, visible: n };
    }

    private mediaPipePoseGeometry(landmarks: any[]): {
        bbox: number[];
        torso: { x: number; y: number };
        shoulder: { x: number; y: number };
        hip: { x: number; y: number };
    } | null {
        const visible = (landmarks || [])
            .map((landmark: any, index: number) => ({ point: this.normalizeMediaPipePoint(landmark), index, valid: this.mediaPipeLandmarkDetected(landmark, index) }))
            .filter(item => item.valid);
        if (visible.length < 4) return null;
        const xs = visible.map(item => item.point.x);
        const ys = visible.map(item => item.point.y);
        const torsoIndexes = new Set([11, 12, 23, 24]);
        const torsoPoints = visible.filter(item => torsoIndexes.has(item.index)).map(item => item.point);
        const torso = torsoPoints.length > 0 ? {
            x: torsoPoints.reduce((sum, point) => sum + point.x, 0) / torsoPoints.length,
            y: torsoPoints.reduce((sum, point) => sum + point.y, 0) / torsoPoints.length,
        } : {
            x: (Math.min(...xs) + Math.max(...xs)) / 2,
            y: (Math.min(...ys) + Math.max(...ys)) / 2,
        };
        const pointCenter = (indexes: number[]) => {
            const points = visible.filter(item => indexes.includes(item.index)).map(item => item.point);
            return points.length > 0 ? {
                x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
                y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
            } : torso;
        };
        return {
            bbox: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)],
            torso,
            shoulder: pointCenter([11, 12]),
            hip: pointCenter([23, 24]),
        };
    }

    private rapidMediaPipePostureChange(previousBbox: number[], nextBbox: number[]): boolean {
        if (previousBbox.length < 4 || nextBbox.length < 4) return false;
        const previousWidth = Math.max(0.001, previousBbox[2] - previousBbox[0]);
        const previousHeight = Math.max(0.001, previousBbox[3] - previousBbox[1]);
        const nextWidth = Math.max(0.001, nextBbox[2] - nextBbox[0]);
        const nextHeight = Math.max(0.001, nextBbox[3] - nextBbox[1]);
        const previousAspect = previousWidth / previousHeight;
        const nextAspect = nextWidth / nextHeight;
        const verticalToHorizontal = previousAspect < 0.75 && nextAspect > 1.05;
        return verticalToHorizontal || Math.abs(Math.log(nextAspect / previousAspect)) >= 0.48;
    }

    private sameLocalLogicalPerson(left: { bbox: number[]; torso?: { x: number; y: number } }, right: { bbox: number[]; torso?: { x: number; y: number } }): boolean {
        if (!Array.isArray(left?.bbox) || !Array.isArray(right?.bbox)) return false;
        const iou = normalizedBboxIou(left.bbox, right.bbox);
        const center = (bbox: number[]) => ({ x: (bbox[0] + bbox[2]) / 2, y: (bbox[1] + bbox[3]) / 2 });
        const a = center(left.bbox);
        const b = center(right.bbox);
        const centerDistance = Math.hypot(a.x - b.x, a.y - b.y);
        const torsoDistance = left.torso && right.torso
            ? Math.hypot(left.torso.x - right.torso.x, left.torso.y - right.torso.y)
            : Number.POSITIVE_INFINITY;
        return iou >= 0.16 || centerDistance <= 0.13 || torsoDistance <= 0.16;
    }

    private mediaPipePoseQuality(landmarks: any[]): number {
        if (!Array.isArray(landmarks)) return 0;
        return landmarks.slice(0, 33).reduce((score, landmark, index) => {
            if (!this.mediaPipeLandmarkDetected(landmark, index)) return score;
            const visibility = Number(landmark?.visibility ?? 0);
            const presence = Number(landmark?.presence ?? visibility);
            return score + 1 + Math.max(0, Math.min(1, Math.min(visibility, presence)));
        }, 0);
    }

    private deduplicateMediaPipePoseCandidates<T extends { landmarks: any[]; bbox: number[]; torso: { x: number; y: number }; sourceIndex: number }>(candidates: T[]): T[] {
        const sorted = [...candidates].sort((a, b) => this.mediaPipePoseQuality(b.landmarks) - this.mediaPipePoseQuality(a.landmarks));
        const unique: T[] = [];
        for (const candidate of sorted) {
            if (unique.some(existing => this.sameLocalLogicalPerson(existing, candidate))) {
                this.mpDuplicatePoseCandidateCount += 1;
                continue;
            }
            unique.push(candidate);
        }
        return unique.sort((a, b) => a.sourceIndex - b.sourceIndex);
    }

    private deduplicateRenderableMediaPipePoses<T extends { trackId: number; poseState: MpPoseState | 'RECOVERING'; sourceIndex?: number }>(poses: T[]): T[] {
        const stateRank: Record<string, number> = { LIVE: 4, RECOVERING: 3, PREDICTED: 2, HELD: 1, LOST: 0 };
        const sorted = [...poses].sort((a, b) => {
            const aPrimary = a.trackId === this.mpPrimaryPoseTrackId ? 1 : 0;
            const bPrimary = b.trackId === this.mpPrimaryPoseTrackId ? 1 : 0;
            if (aPrimary !== bPrimary) return bPrimary - aPrimary;
            const stateDelta = Number(stateRank[b.poseState] || 0) - Number(stateRank[a.poseState] || 0);
            if (stateDelta !== 0) return stateDelta;
            const aTrack = this.mpPoseTracks.find(track => track.id === a.trackId);
            const bTrack = this.mpPoseTracks.find(track => track.id === b.trackId);
            return Number(bTrack?.lastSeenMs || 0) - Number(aTrack?.lastSeenMs || 0);
        });
        const unique: T[] = [];
        for (const pose of sorted) {
            const track = this.mpPoseTracks.find(item => item.id === pose.trackId);
            if (!track || track.renderEligible === false || track.mergedIntoTrackId > 0) continue;
            const duplicate = unique.find(existing => {
                const existingTrack = this.mpPoseTracks.find(item => item.id === existing.trackId);
                return Boolean(existingTrack && this.sameLocalLogicalPerson(
                    { bbox: existingTrack.bbox, torso: { x: existingTrack.torsoX, y: existingTrack.torsoY } },
                    { bbox: track.bbox, torso: { x: track.torsoX, y: track.torsoY } },
                ));
            });
            if (duplicate) {
                track.renderEligible = false;
                track.mergedIntoTrackId = duplicate.trackId;
                track.logicalPersonId = `local-person-${duplicate.trackId}`;
                track.lastLandmarks = [];
                this.mpPersistentRenderPoses = this.mpPersistentRenderPoses.filter(item => item.trackId !== track.id);
                this.mpDuplicateLogicalRenderCount += 1;
                this.fastPoseLog('track-merged', {
                    logicalPersonId: track.logicalPersonId,
                    primaryTrackId: duplicate.trackId,
                    mergedTrackId: track.id,
                });
                continue;
            }
            track.renderEligible = true;
            track.logicalPersonId = track.logicalPersonId || `local-person-${track.id}`;
            unique.push(pose);
        }
        return unique;
    }

    private assignMediaPipeTracks(candidates: {
        landmarks: any[];
        sourceIndex: number;
        center: { x: number; y: number; visible: number };
        bbox: number[];
        torso: { x: number; y: number };
        shoulder: { x: number; y: number };
        hip: { x: number; y: number };
    }[], nowMs: number) {
        const usedTrackIds = new Set<number>();
        this.mpPoseTracks = this.mpPoseTracks.filter(track => {
            const alive = nowMs - Number(track.lastSeenMs || 0) <= Math.max(
                TRACK_RECOVERY_WINDOW_MS,
                this.persistentPoseHoldMs(track, nowMs),
            );
            if (!alive) delete this.mpPoseTrackFilters[track.id];
            return alive;
        });
        if (this.mpPrimaryPoseTrackId && !this.mpPoseTracks.some(track => track.id === this.mpPrimaryPoseTrackId)) {
            this.mpPrimaryPoseTrackId = 0;
        }
        const assigned: { landmarks: any[]; sourceIndex: number; trackId: number; poseState: MpPoseState; poseAlpha: number; recoveryReason: string }[] = [];
        for (const candidate of candidates) {
            let best: MpPoseTrack | null = null;
            let bestScore = Number.POSITIVE_INFINITY;
            let bestBboxIou = 0;
            let bestCenterDistance = Number.POSITIVE_INFINITY;
            let bestIouThreshold = 0.25;
            for (const track of this.mpPoseTracks) {
                if (track.renderEligible === false || track.mergedIntoTrackId > 0) continue;
                if (usedTrackIds.has(track.id)) continue;
                const ageMs = Math.max(0, nowMs - Number(track.lastSeenMs || nowMs));
                const recoveryWindowMs = Math.max(TRACK_RECOVERY_WINDOW_MS, this.persistentPoseHoldMs(track, nowMs));
                const match = scoreRealtimeTrackMatch({
                    bbox: track.bbox,
                    centerX: track.centerX,
                    centerY: track.centerY,
                    torsoX: track.torsoX,
                    torsoY: track.torsoY,
                    shoulderX: track.shoulderX,
                    shoulderY: track.shoulderY,
                    hipX: track.hipX,
                    hipY: track.hipY,
                    velocityX: track.velocityX,
                    velocityY: track.velocityY,
                }, {
                    bbox: candidate.bbox,
                    centerX: candidate.center.x,
                    centerY: candidate.center.y,
                    torsoX: candidate.torso.x,
                    torsoY: candidate.torso.y,
                    shoulderX: candidate.shoulder.x,
                    shoulderY: candidate.shoulder.y,
                    hipX: candidate.hip.x,
                    hipY: candidate.hip.y,
                }, ageMs, recoveryWindowMs);
                const primaryRecovery = track.id === this.mpPrimaryPoseTrackId
                    && candidates.length === 1
                    && ageMs <= recoveryWindowMs
                    && (match.centerDistance <= 0.55 || match.torsoDistance <= 0.45);
                if (!match.admissible && !primaryRecovery) continue;
                // 단일 사람 자세 전환 구간에서는 잠깐 더 가까워진 신규 track보다
                // 최초 primary track의 시간 연속성을 우선합니다.
                const continuityScore = match.score - (track.id === this.mpPrimaryPoseTrackId ? 0.18 : 0);
                if (continuityScore < bestScore) {
                    bestScore = continuityScore;
                    best = track;
                    bestBboxIou = match.bboxIou;
                    bestCenterDistance = match.centerDistance;
                    bestIouThreshold = match.minimumTrackIou;
                }
            }
            if (!best) {
                best = {
                    id: this.mpNextPoseTrackId++,
                    createdAtMs: nowMs,
                    centerX: candidate.center.x,
                    centerY: candidate.center.y,
                    previousCenterX: candidate.center.x,
                    previousCenterY: candidate.center.y,
                    velocityX: 0,
                    velocityY: 0,
                    bbox: candidate.bbox,
                    torsoX: candidate.torso.x,
                    torsoY: candidate.torso.y,
                    shoulderX: candidate.shoulder.x,
                    shoulderY: candidate.shoulder.y,
                    hipX: candidate.hip.x,
                    hipY: candidate.hip.y,
                    lastSeenMs: nowMs,
                    lastPoseSeenMs: nowMs,
                    missed: 0,
                    hits: 1,
                    lastLandmarks: candidate.landmarks,
                    poseState: 'LIVE',
                    recoveryReason: '',
                    lastBboxIou: 1,
                    lastCenterDistance: 0,
                    lastTrackIouThreshold: 0.25,
                    recoveryFromLandmarks: [],
                    recoveryFramesRemaining: 0,
                    logicalPersonId: `local-person-${this.mpNextPoseTrackId - 1}`,
                    mergedIntoTrackId: 0,
                    renderEligible: true,
                };
                this.mpPoseTracks.push(best);
                if (!this.mpPrimaryPoseTrackId) this.mpPrimaryPoseTrackId = best.id;
                this.realtimeFastPoseCounters.trackCreated += 1;
            } else {
                const elapsedMs = Math.max(1, nowMs - Number(best.lastSeenMs || nowMs));
                const gapMs = Math.max(0, nowMs - Number(best.lastPoseSeenMs || nowMs));
                const rapidPostureChange = this.rapidMediaPipePostureChange(best.bbox || [], candidate.bbox || []);
                if (rapidPostureChange) this.mpPriorityTransitionUntilMs = Math.max(this.mpPriorityTransitionUntilMs, nowMs + 700);
                const recovering = best.recoveryFramesRemaining > 0
                    || best.poseState !== 'LIVE'
                    || gapMs > POSE_TIMESTAMP_TOLERANCE_MS;
                const nextVelocityX = (candidate.center.x - best.centerX) / elapsedMs;
                const nextVelocityY = (candidate.center.y - best.centerY) / elapsedMs;
                best.previousCenterX = best.centerX;
                best.previousCenterY = best.centerY;
                best.velocityX = best.velocityX * 0.55 + nextVelocityX * 0.45;
                best.velocityY = best.velocityY * 0.55 + nextVelocityY * 0.45;
                best.centerX = candidate.center.x;
                best.centerY = candidate.center.y;
                best.bbox = candidate.bbox;
                best.torsoX = candidate.torso.x;
                best.torsoY = candidate.torso.y;
                best.shoulderX = candidate.shoulder.x;
                best.shoulderY = candidate.shoulder.y;
                best.hipX = candidate.hip.x;
                best.hipY = candidate.hip.y;
                best.recoveryReason = gapMs > POSE_TIMESTAMP_TOLERANCE_MS
                    ? 'TRACK_RECOVERED_pose_gap'
                    : rapidPostureChange ? 'RAPID_POSTURE_CHANGE' : '';
                best.lastSeenMs = nowMs;
                best.lastPoseSeenMs = nowMs;
                best.missed = 0;
                best.hits = Number(best.hits || 0) + 1;
                if (recovering && Array.isArray(best.lastLandmarks) && best.lastLandmarks.length > 0) {
                    if (best.recoveryFramesRemaining <= 0) {
                        best.recoveryFromLandmarks = best.lastLandmarks.map((landmark: any) => ({ ...landmark }));
                        best.recoveryFramesRemaining = 3;
                    }
                    const alpha = (4 - best.recoveryFramesRemaining) / 3;
                    best.lastLandmarks = candidate.landmarks.map((landmark: any, index: number) => {
                        const previous = best!.recoveryFromLandmarks[index] || landmark;
                        return {
                            ...landmark,
                            x: Number(previous?.x ?? landmark?.x ?? 0) * (1 - alpha) + Number(landmark?.x ?? 0) * alpha,
                            y: Number(previous?.y ?? landmark?.y ?? 0) * (1 - alpha) + Number(landmark?.y ?? 0) * alpha,
                        };
                    });
                    best.recoveryFramesRemaining -= 1;
                } else {
                    best.lastLandmarks = candidate.landmarks;
                    best.recoveryFramesRemaining = 0;
                    best.recoveryFromLandmarks = [];
                }
                best.poseState = 'LIVE';
                best.lastBboxIou = bestBboxIou;
                best.lastCenterDistance = bestCenterDistance;
                best.lastTrackIouThreshold = bestIouThreshold;
            }
            usedTrackIds.add(best.id);
            this.fastPoseLog('track-update', {
                trackId: best.id,
                trackState: best.poseState,
                hasPose: true,
            });
            assigned.push({
                landmarks: best.lastLandmarks,
                sourceIndex: candidate.sourceIndex,
                trackId: best.id,
                poseState: 'LIVE',
                poseAlpha: 1,
                recoveryReason: best.recoveryReason,
            });
        }
        for (const track of this.mpPoseTracks) {
            if (usedTrackIds.has(track.id)) continue;
            track.missed = Number(track.missed || 0) + 1;
            const poseAgeMs = Math.max(0, nowMs - Number(track.lastPoseSeenMs || 0));
            track.poseState = retainedPoseState(poseAgeMs, POSE_HOLD_MS);
        }
        return assigned;
    }

    private heldMediaPipePoses(nowMs: number, usedTrackIds: Set<number>) {
        return this.mpPoseTracks
            .filter(track => track.renderEligible !== false && track.mergedIntoTrackId <= 0)
            .filter(track => !usedTrackIds.has(track.id))
            .filter(track => Array.isArray(track.lastLandmarks) && track.lastLandmarks.length > 0)
            .filter(track => nowMs - Number(track.lastPoseSeenMs || 0) <= POSE_HOLD_MS)
            .map(track => {
                const ageMs = Math.max(0, nowMs - Number(track.lastPoseSeenMs || nowMs));
                const poseState = retainedPoseState(ageMs, POSE_HOLD_MS) as MpPoseState;
                const predictionMs = poseState === 'PREDICTED' ? Math.min(ageMs, POSE_HOLD_MS) : 0;
                const landmarks = (track.lastLandmarks || []).map((landmark: any) => ({
                    ...landmark,
                    x: Math.max(0, Math.min(1, Number(landmark?.x || 0) + track.velocityX * predictionMs)),
                    y: Math.max(0, Math.min(1, Number(landmark?.y || 0) + track.velocityY * predictionMs)),
                }));
                return {
                    landmarks,
                    sourceIndex: -1,
                    trackId: track.id,
                    poseState,
                    poseAlpha: Math.max(0.22, 1 - ageMs / POSE_HOLD_MS * 0.72),
                    recoveryReason: '',
                };
            });
    }

    private getMediaPipeInputSource(videoEl: HTMLVideoElement): any {
        const sourceW = Math.max(1, Math.round(videoEl.videoWidth || videoEl.clientWidth || 0));
        const sourceH = Math.max(1, Math.round(videoEl.videoHeight || videoEl.clientHeight || 0));
        if (!sourceW || !sourceH) return videoEl;
        const side = this.agentComputeBudget().behaviorResolution || MP_INPUT_SIZE;
        const scale = Math.min(side / sourceW, side / sourceH);
        const drawW = Math.max(1, Math.round(sourceW * scale));
        const drawH = Math.max(1, Math.round(sourceH * scale));
        const offsetX = Math.floor((side - drawW) / 2);
        const offsetY = Math.floor((side - drawH) / 2);
        if (!this.mpInputCanvas) {
            this.mpInputCanvas = document.createElement('canvas');
            this.mpInputCtx = this.mpInputCanvas.getContext('2d');
        }
        if (!this.mpInputCanvas || !this.mpInputCtx) return videoEl;
        if (this.mpInputCanvas.width !== side || this.mpInputCanvas.height !== side) {
            this.mpInputCanvas.width = side;
            this.mpInputCanvas.height = side;
        }
        try {
            this.mpInputCtx.fillStyle = '#000';
            this.mpInputCtx.fillRect(0, 0, side, side);
            this.mpInputCtx.drawImage(videoEl, offsetX, offsetY, drawW, drawH);
            this.mpInputTransform = { side, offsetX, offsetY, drawW, drawH };
            return this.mpInputCanvas;
        } catch (e) {
            this.mpInputTransform = null;
            return videoEl;
        }
    }

    private normalizeMediaPipePoint(lm: any): { x: number; y: number } {
        const tr = this.mpInputTransform;
        if (!tr) return { x: Number(lm.x || 0), y: Number(lm.y || 0) };
        const x = ((Number(lm.x || 0) * tr.side) - tr.offsetX) / Math.max(1, tr.drawW);
        const y = ((Number(lm.y || 0) * tr.side) - tr.offsetY) / Math.max(1, tr.drawH);
        return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
    }

    private mediaPipeLandmarkDetected(lm: any, index: number): boolean {
        if (MP_HAND_DETAIL_LANDMARKS.has(index)) return false;
        const visibility = Number(lm?.visibility ?? 0);
        const presenceRaw = lm?.presence;
        const presence = presenceRaw == null ? 1 : Number(presenceRaw || 0);
        const threshold = MP_WRIST_LANDMARKS.has(index) ? this.KF_HAND_VIS_THRESHOLD : this.KF_VIS_THRESHOLD;
        return visibility >= threshold && presence >= threshold;
    }

    private mediaPipeLandmarkVisible(lm: any, index: number): boolean {
        return this.mediaPipeLandmarkDetected(lm, index);
    }

    private cocoKeypointVisible(kp: any, index: number): boolean {
        if (!kp) return false;
        const conf = Number(kp[2] || 0);
        return conf >= (COCO_HAND_KEYPOINTS.has(index) ? this.KF_HAND_VIS_THRESHOLD : 0.30);
    }

    private cocoKeypointAlpha(kp: any): number {
        const conf = Number(kp?.[2] || 0);
        return conf < 0.55 ? 0.38 : Math.min(0.96, 0.58 + conf * 0.38);
    }

    private skeletonLineColor(indexA: number, indexB: number): string {
        const colorA = COCO_KP_COLORS[indexA] || SKELETON_PART_COLORS.torso;
        const colorB = COCO_KP_COLORS[indexB] || colorA;
        return colorA === colorB ? colorA : colorB;
    }

    private drawCocoFacePrivacyVeil(ctx: CanvasRenderingContext2D, det: any, keypoints: any[], scaleX: number, scaleY: number, offX: number, offY: number) {
        if (!this.isCleanOverlayMode() || !this.showPrivacyMaskOverlay()) return;
        const facePoints = keypoints.slice(0, 5).filter((kp: any, index: number) => this.cocoKeypointVisible(kp, index));
        const boxWidth = Math.max(1, (Number(det?.x2 || 0) - Number(det?.x1 || 0)) * scaleX);
        const boxHeight = Math.max(1, (Number(det?.y2 || 0) - Number(det?.y1 || 0)) * scaleY);
        const xs = facePoints.map((kp: any) => Number(kp[0]) * scaleX + offX);
        const ys = facePoints.map((kp: any) => Number(kp[1]) * scaleY + offY);
        const centerX = facePoints.length >= 2
            ? (Math.max(...xs) + Math.min(...xs)) / 2
            : ((Number(det?.x1 || 0) + Number(det?.x2 || 0)) / 2) * scaleX + offX;
        const centerY = facePoints.length >= 2
            ? (Math.max(...ys) + Math.min(...ys)) / 2
            : Number(det?.y1 || 0) * scaleY + offY + boxHeight * 0.13;
        const radiusX = facePoints.length >= 2 ? Math.max(12, (Math.max(...xs) - Math.min(...xs)) * 0.95, boxWidth * 0.11) : Math.max(12, boxWidth * 0.14);
        const radiusY = facePoints.length >= 2 ? Math.max(15, (Math.max(...ys) - Math.min(...ys)) * 1.15, radiusX * 1.18) : Math.max(15, boxHeight * 0.11);
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 8;
        ctx.fillStyle = 'rgba(9,9,11,0.78)';
        ctx.beginPath(); ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
    }

    private drawCleanFacePrivacyVeil(ctx: CanvasRenderingContext2D, landmarks: any[], displayRect: { x: number; y: number; w: number; h: number }) {
        if (!this.isCleanOverlayMode() || !this.showPrivacyMaskOverlay() || !Array.isArray(landmarks) || landmarks.length === 0) return;
        const points = landmarks.map((landmark: any) => this.normalizeMediaPipePoint(landmark));
        const xs = points.map(point => displayRect.x + point.x * displayRect.w);
        const ys = points.map(point => displayRect.y + point.y * displayRect.h);
        const centerX = (Math.max(...xs) + Math.min(...xs)) / 2;
        const centerY = (Math.max(...ys) + Math.min(...ys)) / 2;
        const radiusX = Math.max(14, (Math.max(...xs) - Math.min(...xs)) * 0.58);
        const radiusY = Math.max(18, (Math.max(...ys) - Math.min(...ys)) * 0.62);
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.85)';
        ctx.shadowBlur = 10;
        ctx.fillStyle = 'rgba(9,9,11,0.82)';
        ctx.beginPath(); ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
    }

    private resetRealtimeLocalFallCandidate() {
        this.realtimeRfFallController?.abort();
        this.realtimeRfFallController = null;
        this.realtimeLocalFallState = 'idle';
        this.realtimeLocalFallRiskPercent = 0;
        this.realtimeLocalFallInterval = null;
        this.realtimeLocalFallSamples = [];
        this.realtimeLocalFallConsecutiveHits = 0;
        this.realtimeLocalFallConsecutiveMisses = 0;
        this.realtimeLocalFallLockUntil = 0;
        this.realtimeLocalFallEventLogged = false;
        this.realtimeFallEvidenceSignals.clear();
        this.realtimeFallStrongKinematicEvidence = false;
        this.realtimeFallPeakFastRisk = 0;
        this.realtimeFallPeakDescentTimestampMs = 0;
        this.realtimeFallLowestPointTimestampMs = 0;
        this.realtimeFallTrackChangesAtCandidate = 0;
        this.realtimeFastFallConsecutiveHits = 0;
        this.realtimeFastTransitionSignalHits = 0;
        this.realtimeFastTransitionSignalAtMs = 0;
        this.realtimeFastFallEventKey = '';
        this.realtimeFastFallCreatedAt = 0;
        this.realtimeFastFallLastEvaluatedAt = 0;
        this.realtimeFastPostureFirstAt = 0;
        this.realtimeFastPosture = '';
        this.realtimeFastPostureLabel = '확인 중';
        this.realtimeFastPostureStatus = 'idle';
        this.realtimePostureCandidate = '';
        this.realtimePostureCandidateSinceMs = 0;
        this.realtimeLastPostureEvent = '';
        this.realtimeLastPostureEventAtMs = 0;
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            person_detection_first_ms: 0,
            skeleton_first_display_ms: 0,
            posture_first_display_ms: 0,
            environment_first_display_ms: 0,
            event_feed_render_delay_ms: 0,
            fast_fall_detection_ms: 0,
            provisional_fall_event_status: 'idle',
        };
        this.realtimeRfFallStatus = 'idle';
        this.realtimeRfFallLatencyMs = 0;
        this.realtimeRfFallModel = '';
        this.realtimeRfFallWindowFrameCount = 0;
        this.realtimeRfFallPending = false;
        this.realtimeRfFallQueued = false;
        this.realtimeRfFallSequence = 0;
        this.realtimeRfFallLastDispatchSourceTime = -1;
    }

    private realtimePoseMetric(landmarks: any[], sourceTime: number): any {
        const point = (index: number) => {
            const landmark = landmarks?.[index];
            if (!landmark || !this.mediaPipeLandmarkDetected(landmark, index)) return null;
            return this.normalizeMediaPipePoint(landmark);
        };
        const midpoint = (left: any, right: any) => left && right
            ? { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 }
            : (left || right);
        const shoulder = midpoint(point(11), point(12));
        const hip = midpoint(point(23), point(24));
        if (!shoulder || !hip) return null;
        const visible = (landmarks || [])
            .map((landmark: any, index: number) => this.mediaPipeLandmarkDetected(landmark, index)
                ? this.normalizeMediaPipePoint(landmark) : null)
            .filter(Boolean);
        if (visible.length < 14) return null;
        const xs = visible.map((item: any) => Number(item.x));
        const ys = visible.map((item: any) => Number(item.y));
        const bboxWidth = Math.max(...xs) - Math.min(...xs);
        const bboxHeight = Math.max(...ys) - Math.min(...ys);
        const padX = Math.max(0.015, bboxWidth * 0.08);
        const padY = Math.max(0.02, bboxHeight * 0.06);
        const x1 = Math.max(0, Math.min(...xs) - padX);
        const y1 = Math.max(0, Math.min(...ys) - padY);
        const x2 = Math.min(1, Math.max(...xs) + padX);
        const y2 = Math.min(1, Math.max(...ys) + padY);
        const cocoKeypoints = MP_TO_COCO17.map(index => {
            const landmark = landmarks?.[index];
            const normalized = landmark ? this.normalizeMediaPipePoint(landmark) : { x: 0, y: 0 };
            const visibility = Number(landmark?.visibility ?? 0);
            const presence = Number(landmark?.presence ?? visibility);
            const confidence = this.mediaPipeLandmarkDetected(landmark, index)
                ? Math.max(0, Math.min(1, Math.min(
                    Number.isFinite(visibility) ? visibility : 0,
                    Number.isFinite(presence) ? presence : visibility,
                )))
                : 0;
            return {
                x: Math.max(-0.25, Math.min(1.25, Number(normalized.x || 0))),
                y: Math.max(-0.25, Math.min(1.25, Number(normalized.y || 0))),
                conf: confidence,
            };
        });
        const axisX = Math.abs(Number(hip.x) - Number(shoulder.x));
        const axisY = Math.abs(Number(hip.y) - Number(shoulder.y));
        return {
            sourceTime,
            centerY: (Number(shoulder.y) + Number(hip.y)) / 2,
            torsoAngle: Math.atan2(axisX, Math.max(axisY, 0.001)) * 180 / Math.PI,
            bboxAspect: bboxWidth / Math.max(bboxHeight, 0.001),
            hipY: Number(hip.y),
            bbox: {
                cx: (x1 + x2) / 2,
                cy: (y1 + y2) / 2,
                w: Math.max(0.001, x2 - x1),
                h: Math.max(0.001, y2 - y1),
                conf: cocoKeypoints.reduce((sum, item) => sum + item.conf, 0) / Math.max(cocoKeypoints.length, 1),
            },
            keypoints: cocoKeypoints,
        };
    }

    private updateRealtimeLocalFallCandidate(poses: any[], videoEl: HTMLVideoElement, sourceTimestampMs?: number) {
        if (!Array.isArray(poses) || poses.length === 0 || !this.realtimeActive) return;
        const sourceTime = Number.isFinite(Number(sourceTimestampMs))
            ? Math.max(0, Number(sourceTimestampMs) / 1000)
            : Number(videoEl.currentTime || 0);
        const last = this.realtimeLocalFallSamples[this.realtimeLocalFallSamples.length - 1];
        if (last && sourceTime - last.sourceTime < 0.075) return;
        const metric = this.realtimePoseMetric(poses[0]?.landmarks || [], sourceTime);
        if (!metric) return;
        this.realtimeLocalFallSamples.push(metric);
        this.realtimeLocalFallSamples = this.realtimeLocalFallSamples.filter(
            item => sourceTime - item.sourceTime <= 1.6
        );
        const fastGateStartedAt = performance.now();
        const fastGate = evaluateFastFallGate(this.realtimeLocalFallSamples);
        this.realtimeFastFallLastEvaluatedAt = fastGateStartedAt;
        const postureLabels: Record<string, string> = {
            stand: '서기', sit: '앉기', kneel: '무릎 꿇기', lie: '눕기', transition: '전환 중',
        };
        this.realtimeFastPosture = fastGate.posture;
        this.realtimeFastPostureLabel = postureLabels[fastGate.posture] || '확인 중';
        this.realtimeFastPostureStatus = 'estimated';
        if (this.realtimeFastPostureFirstAt <= 0) this.realtimeFastPostureFirstAt = performance.now();
        // A transition can be classified as "stand" again one sample after the
        // initial geometry jump. Keep that short transition context so the next
        // high-risk sample can confirm a provisional event instead of dropping
        // the actual fall boundary between two 3-4 FPS Pose responses.
        const transitionContextActive = fastGate.posture === 'transition'
            || (this.realtimeFastTransitionSignalAtMs > 0
                && fastGate.evaluatedAtVideoTimeMs - this.realtimeFastTransitionSignalAtMs <= 650);
        const sustainedTransitionSignal = !fastGate.candidate
            && transitionContextActive
            && fastGate.signalCount >= 1
            && fastGate.riskScore >= 0.42;
        if (sustainedTransitionSignal) {
            this.realtimeFastTransitionSignalHits = fastGate.evaluatedAtVideoTimeMs - this.realtimeFastTransitionSignalAtMs <= 650
                ? this.realtimeFastTransitionSignalHits + 1 : 1;
            this.realtimeFastTransitionSignalAtMs = fastGate.evaluatedAtVideoTimeMs;
        } else if (fastGate.evaluatedAtVideoTimeMs - this.realtimeFastTransitionSignalAtMs > 650) {
            this.realtimeFastTransitionSignalHits = 0;
        }
        const provisionalTransitionCandidate = sustainedTransitionSignal && this.realtimeFastTransitionSignalHits >= 2;
        const fastCandidate = fastGate.candidate || provisionalTransitionCandidate;
        for (const signal of fastGate.signals) this.realtimeFallEvidenceSignals.add(signal);
        this.realtimeFallPeakFastRisk = Math.max(this.realtimeFallPeakFastRisk, fastGate.riskScore);
        if (fastGate.signals.includes('rapid_center_drop')) {
            this.realtimeFallPeakDescentTimestampMs = fastGate.evaluatedAtVideoTimeMs;
        }
        if (fastGate.signals.includes('low_posture') || fastGate.signals.includes('hip_floor_approach')) {
            this.realtimeFallLowestPointTimestampMs = fastGate.evaluatedAtVideoTimeMs;
        }
        if (fastGate.candidate && fastGate.signalCount >= 2 && fastGate.riskScore >= 0.62) {
            this.realtimeFallStrongKinematicEvidence = true;
        }
        this.realtimeFastFallConsecutiveHits = fastCandidate ? this.realtimeFastFallConsecutiveHits + 1 : 0;
        const fastFallDetectionMs = Math.max(0, performance.now() - Number(
            this.realtimeDiagnostics?.captured_at_realtime_ms || fastGateStartedAt
        ));
        this.recordRealtimePerf('fastFallGate', Math.max(0, performance.now() - fastGateStartedAt));
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            posture_result_age_ms: Math.max(0, sourceTime * 1000 - fastGate.evaluatedAtVideoTimeMs),
            posture_first_display_ms: Math.max(0, this.realtimeFastPostureFirstAt - this.mpPoseFirstRequestedAt),
            fast_posture: fastGate.posture,
            fast_posture_label: this.realtimeFastPostureLabel,
            fast_fall_detection_ms: fastFallDetectionMs,
            fast_fall_video_timestamp_ms: fastGate.evaluatedAtVideoTimeMs,
            fast_fall_signals: fastGate.signals,
            fast_fall_signal_count: fastGate.signalCount,
        };
        this.fastPoseLog('fast-fall-gate', {
            videoTimestampMs: fastGate.evaluatedAtVideoTimeMs,
            candidate: fastGate.candidate,
            riskScore: fastGate.riskScore,
            posture: fastGate.posture,
            signals: fastGate.signals,
        });
        this.updateRealtimePostureEvent(fastGate.posture, this.realtimeFastPostureLabel, sourceTime);
        this.updateRealtimeFastSignalEvents(fastGate.signals, sourceTime, Math.round(fastGate.riskScore * 100));
        if (fastCandidate) {
            const alreadyConfirmed = this.realtimeLocalFallState === 'confirmed';
            const riskPercent = Math.round(fastGate.riskScore * 100);
            if (!alreadyConfirmed && !['suspected', 'verifying'].includes(this.realtimeLocalFallState)) {
                this.realtimeFallTrackChangesAtCandidate = this.mpTrackIdChangeCount;
            }
            if (!alreadyConfirmed) this.realtimeLocalFallState = 'suspected';
            this.realtimeLocalFallRiskPercent = Math.max(this.realtimeLocalFallRiskPercent, riskPercent);
            this.realtimeLocalFallInterval = {
                startSec: this.realtimeLocalFallInterval?.startSec ?? Math.max(0, sourceTime - 0.5),
                endSec: sourceTime,
            };
            this.realtimeLocalFallLockUntil = Math.max(this.realtimeLocalFallLockUntil, sourceTime + 2);
            if (!alreadyConfirmed) {
                this.upsertRealtimeFallEvent('suspected', riskPercent, {
                    startSec: Math.max(0, sourceTime - 1.5),
                    endSec: sourceTime + 2,
                    eventTimeSec: sourceTime,
                    label: `낙상 후보 · ${Math.max(0, sourceTime - 0.5).toFixed(2)}~${sourceTime.toFixed(2)}초`,
                }, `${provisionalTransitionCandidate ? '급격한 자세 전환 신호가 연속 감지되었습니다.' : '사람의 중심 하강과 자세 변화가 함께 감지되었습니다.'} 근거: ${this.realtimeFallEvidenceLabels().join(' · ') || '자세 급변'}. 정밀 분석으로 확인 중입니다.`);
            }
        } else if (this.realtimeFastPostureStatus === 'estimated') {
            this.requestRealtimeUiRender();
        }
        if (this.realtimeRfFallStatus === 'idle') this.realtimeRfFallStatus = 'warming';
        this.queueRealtimeRfFallWindow(videoEl, sourceTime);
    }

    private updateRealtimePostureEvent(posture: string, label: string, sourceTime: number) {
        const sourceMs = Math.max(0, sourceTime * 1000);
        if (posture !== this.realtimePostureCandidate) {
            this.realtimePostureCandidate = posture;
            this.realtimePostureCandidateSinceMs = sourceMs;
            return;
        }
        if (sourceMs - this.realtimePostureCandidateSinceMs < 300) return;
        if (posture === this.realtimeLastPostureEvent) return;
        const previousLabel = this.realtimeLastPostureEvent
            ? ({ stand: '서기', sit: '앉기', kneel: '무릎 꿇기', lie: '눕기', transition: '전환 중' } as Record<string, string>)[this.realtimeLastPostureEvent]
            : '';
        const entry = this.appendRealtimeFeedEvent({
            type: 'posture_changed',
            tone: 'normal',
            icon: '◉',
            title: '현재 자세 변경',
            description: previousLabel ? `${previousLabel} → ${label}` : `현재 자세: ${label}`,
            showRisk: false,
        }, null, -1, {
            startSec: Math.max(0, sourceTime - 0.3),
            endSec: sourceTime,
            label: `실시간 자세 · ${sourceTime.toFixed(2)}초`,
        });
        entry.eventStatus = 'live';
        entry.createdAtVideoTimeMs = sourceMs;
        entry.createdAtRealtimeMs = performance.now();
        this.realtimeLastPostureEvent = posture;
        this.realtimeLastPostureEventAtMs = sourceMs;
        this.requestRealtimeUiRender(true);
    }

    private updateRealtimeFastSignalEvents(signals: string[], sourceTime: number, riskPercent: number) {
        // Fast 신호는 독립 카드로 쌓지 않고 동일 fall event의 근거로 병합합니다.
        // Debug Raw에서는 아래 누적 필드로 각 신호와 시점을 확인할 수 있습니다.
        for (const signal of signals) this.realtimeFallEvidenceSignals.add(signal);
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            fall_event_evidence_signals: Array.from(this.realtimeFallEvidenceSignals),
            fall_event_evidence_video_timestamp_ms: Math.max(0, sourceTime * 1000),
            fall_event_peak_fast_risk_percent: Math.max(
                Number(this.realtimeDiagnostics?.fall_event_peak_fast_risk_percent || 0),
                Math.round(riskPercent),
            ),
        };
    }

    private realtimeFallEvidenceLabels(): string[] {
        const labels: Record<string, string> = {
            rapid_center_drop: '빠른 중심 하강',
            torso_tilt_change: '몸통 각도 급변',
            vertical_to_horizontal: '수평 자세 전환',
            hip_floor_approach: '바닥 근접',
            low_posture: '낮은 자세 유지',
        };
        return Array.from(this.realtimeFallEvidenceSignals).map(signal => labels[signal] || signal);
    }

    private queueRealtimeRfFallWindow(videoEl: HTMLVideoElement, sourceTime: number) {
        const first = this.realtimeLocalFallSamples[0];
        const span = first ? sourceTime - first.sourceTime : 0;
        if (this.realtimeLocalFallSamples.length < 4 || span < 0.9) return;
        if (this.realtimeRfFallLastDispatchSourceTime >= 0 && sourceTime - this.realtimeRfFallLastDispatchSourceTime < 0.3) return;
        if (this.realtimeRfFallPending) {
            this.realtimeRfFallQueued = true;
            return;
        }
        this.realtimeRfFallLastDispatchSourceTime = sourceTime;
        void this.dispatchRealtimeRfFallWindow(videoEl);
    }

    private async dispatchRealtimeRfFallWindow(videoEl: HTMLVideoElement) {
        if (!this.realtimeActive || this.realtimeRfFallPending) return;
        const samples = this.realtimeLocalFallSamples.slice();
        if (samples.length < 4) return;
        const sequence = this.realtimeRfFallSequence++;
        const sourceStart = Number(samples[0]?.sourceTime || 0);
        const sourceEnd = Number(samples[samples.length - 1]?.sourceTime || sourceStart);
        const sessionId = `${this.realtimeSessionId || 'realtime'}:rf-pose-window`;
        const controller = new AbortController();
        this.realtimeRfFallController = controller;
        this.realtimeRfFallPending = true;
        this.realtimeRfFallQueued = false;
        const startedAt = performance.now();
        try {
            const payload = {
                source_width: Math.max(1, Number(videoEl.videoWidth || videoEl.clientWidth || 640)),
                source_height: Math.max(1, Number(videoEl.videoHeight || videoEl.clientHeight || 480)),
                samples: samples.map(item => ({
                    source_time: item.sourceTime,
                    bbox: item.bbox,
                    keypoints: item.keypoints,
                })),
            };
            const body = new FormData();
            body.append('video', new Blob([JSON.stringify(payload)], { type: 'application/json' }), `pose-window-${sequence}.json`);
            body.append('metadata', JSON.stringify({
                analysis_mode: 'realtime_pose_window',
                input_source: 'realtime',
                model_type: this.selectedModelType,
                realtime_session_id: sessionId,
                request_id: `${sessionId}:${sequence}`,
                chunk_id: sequence,
                source_start_time: sourceStart,
                source_end_time: sourceEnd,
                source_timestamp: sourceEnd,
                ...this.agentWeightMetadata(),
            }));
            const response = await fetch('/wiz/api/page.dashboard/analyze_upload', {
                method: 'POST',
                body,
                signal: controller.signal,
            });
            const json = await this.readJsonResponse(response);
            if (response.status === 409 || json?.code === 409) return;
            if (json?.code !== 200 || !json?.data) {
                throw new Error(json?.data?.message || '실시간 RF 응답이 올바르지 않습니다.');
            }
            const elapsedMs = Math.max(0, performance.now() - startedAt);
            this.recordRealtimePerf('rfFallWindow', elapsedMs);
            this.applyRealtimeRfFallWindow(json.data, elapsedMs);
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                this.realtimeRfFallStatus = 'error';
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    realtime_rf_status: 'error',
                    realtime_rf_error: String(error?.message || error || 'unknown'),
                };
            }
        } finally {
            if (this.realtimeRfFallController === controller) this.realtimeRfFallController = null;
            this.realtimeRfFallPending = false;
            const runLatest = this.realtimeRfFallQueued && this.realtimeActive;
            this.realtimeRfFallQueued = false;
            if (runLatest) {
                this.realtimeRfFallLastDispatchSourceTime = Number(
                    this.realtimeLocalFallSamples[this.realtimeLocalFallSamples.length - 1]?.sourceTime || sourceEnd
                );
                void this.dispatchRealtimeRfFallWindow(videoEl);
            }
        }
    }

    private applyRealtimeRfFallWindow(result: any, elapsedMs: number) {
        const sourceStart = Number(result?.source_start_time || 0);
        const sourceEnd = Number(result?.source_end_time || sourceStart);
        const riskPercent = Math.max(0, Math.min(100, Math.round(Number(result?.risk_score || 0) * 100)));
        const decision = String(result?.decision_state || result?.runtime_inference?.decision_state || '').toLowerCase();
        const candidate = result?.candidate_detected === true
            || result?.fall_detected === true
            || ['fall_suspected', 'fall_confirmed'].includes(decision);
        const frameCount = Number(
            result?.window?.sample_count
            || result?.model_runtime?.extracted_frames
            || result?.runtime_inference?.short_clip?.n_det_frames
            || 0
        );
        this.realtimeRfFallStatus = 'ready';
        this.realtimeRfFallLatencyMs = Math.round(elapsedMs);
        this.realtimeRfFallModel = String(result?.model_runtime?.fall_model_version || result?.runtime_key || 'RF-Fall');
        this.realtimeRfFallWindowFrameCount = frameCount;
        this.realtimeLocalFallConsecutiveHits = candidate ? this.realtimeLocalFallConsecutiveHits + 1 : 0;
        this.realtimeLocalFallConsecutiveMisses = candidate ? 0 : this.realtimeLocalFallConsecutiveMisses + 1;
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            realtime_rf_status: 'ready',
            realtime_rf_model: this.realtimeRfFallModel,
            realtime_rf_latency_ms: this.realtimeRfFallLatencyMs,
            realtime_rf_window_frames: frameCount,
            realtime_rf_source_start: sourceStart,
            realtime_rf_source_end: sourceEnd,
            realtime_rf_candidate_only: true,
        };
        if (this.realtimeLocalFallConsecutiveHits >= 2) {
            const alreadyConfirmed = this.realtimeLocalFallState === 'confirmed';
            if (!alreadyConfirmed) this.realtimeLocalFallState = 'suspected';
            this.realtimeLocalFallRiskPercent = Math.max(this.realtimeLocalFallRiskPercent, riskPercent);
            this.realtimeLocalFallInterval = {
                startSec: this.realtimeLocalFallInterval?.startSec ?? sourceStart,
                endSec: sourceEnd,
            };
            this.realtimeLocalFallLockUntil = Math.max(this.realtimeLocalFallLockUntil, sourceEnd + 2);
            this.realtimeLocalFallEventLogged = true;
            if (!alreadyConfirmed) {
                this.upsertRealtimeFallEvent('confirming', this.realtimeLocalFallRiskPercent, {
                    startSec: Math.max(0, sourceStart - 1.5),
                    endSec: sourceEnd + 2,
                    label: `실시간 RF 낙상 후보 · ${sourceStart.toFixed(2)}~${sourceEnd.toFixed(2)}초`,
                }, `${this.realtimeRfFallModel} 단기 Pose 분석에서도 후보를 확인했습니다. 정밀 분석 중입니다.`);
            }
        } else if (
            ['suspected', 'verifying'].includes(this.realtimeLocalFallState)
            && sourceEnd <= this.realtimeLocalFallLockUntil
        ) {
            this.realtimeLocalFallState = 'verifying';
        } else if (['suspected', 'verifying'].includes(this.realtimeLocalFallState)) {
            // Fast 후보는 RF 단기 창 한 번의 음성 결과로 지우지 않고 정밀 결과까지 유지합니다.
            this.realtimeLocalFallState = 'verifying';
        }
        this.requestRealtimeUiRender();
    }

    private finalizeRealtimeLocalFallCandidate(result: any) {
        const finalFall = result?.fall_detected === true
            || ['fall_suspected', 'fall_confirmed'].includes(String(result?.decision_state || '').toLowerCase());
        const interval = result?.fall_event_interval ?? this.fallEventShadow(result)?.fall_event_interval;
        const runtime = result?.runtime_inference || {};
        const rapidCollapse = runtime?.rapid_collapse_transition || result?.fall_result?.rapid_collapse_transition || {};
        const rfFallScore = Number(runtime?.rf_fall_probability ?? runtime?.fall_score ?? result?.fall_result?.score ?? result?.risk_score ?? 0);
        const rfPipelineScore = Number(runtime?.rf_pipeline_probability ?? runtime?.pipeline_fall_probability ?? result?.rf_pipeline_score ?? 0);
        const fallKinematicScore = Number(
            runtime?.rf_fall_v2?.features?.fall_kinematic_score
            ?? rapidCollapse?.metrics?.fall_kinematic_score
            ?? 0
        );
        const rfFeatureItems = Array.isArray(runtime?.rf_input_features?.items)
            ? runtime.rf_input_features.items : [];
        const rfFeature = (key: string) => Number(
            rfFeatureItems.find((item: any) => String(item?.key || '') === key)?.value ?? 0
        );
        const maxDownSpeed = rfFeature('max_down_speed_norm');
        const downMotionRatio = rfFeature('down_motion_ratio');
        const heightRangeRatio = rfFeature('height_range_ratio');
        const aspectRatioStd = rfFeature('aspect_ratio_std');
        const postureResult = String(result?.posture_label || runtime?.posture_label || result?.behavior_class || 'unknown');
        const poseQuality = Number(runtime?.occlusion?.avg_conf ?? runtime?.input_quality?.pose_quality ?? 0);
        const trackChangedDuringEvent = this.mpTrackIdChangeCount > this.realtimeFallTrackChangesAtCandidate;
        const strongFastKinematics = this.realtimeFallStrongKinematicEvidence
            && this.realtimeFallPeakFastRisk >= 0.62
            && this.realtimeFallEvidenceSignals.size >= 2
            && !trackChangedDuringEvent;
        const combinedEventKinematics = this.realtimeFallPeakFastRisk >= 0.47
            && fallKinematicScore >= 0.60
            && this.realtimeFallEvidenceSignals.size >= 1
            && !trackChangedDuringEvent;
        // 전체 영상 모델의 마지막 posture가 sit이어도 사건 구간에서 급하강과
        // 형태 붕괴가 함께 확인되면 낙상 사건을 유지합니다. 모델 파일/confirm
        // threshold는 바꾸지 않고 기존 RF 입력 특징을 사건 단위로 결합합니다.
        const precisionEventKinematics = fallKinematicScore >= 0.62
            && maxDownSpeed >= 0.55
            && downMotionRatio >= 0.25
            && heightRangeRatio >= 0.45
            && aspectRatioStd >= 0.08;
        const controlledDescent = rapidCollapse?.controlled_descent === true
            || runtime?.controlled_descent === true
            || result?.controlled_descent === true;
        const inputIncomplete = Boolean(runtime?.input_quality?.warning)
            || trackChangedDuringEvent
            || (poseQuality > 0 && poseQuality < 0.35);
        const videoDurationSec = Math.max(0, ...[
            this.webcamVideoRef?.nativeElement?.duration,
            result?.video_metadata?.duration,
            result?.duration,
        ].map(value => Number(value)).filter(Number.isFinite));
        const eventWindow = this.realtimeResultEventWindow(result, this.realtimeLocalFallInterval || {
            startSec: 0,
            endSec: Number.isFinite(videoDurationSec) ? Math.max(0, videoDurationSec) : 0,
            label: Number.isFinite(videoDurationSec) && videoDurationSec > 0
                ? `전체 영상 정밀 분석 · ${videoDurationSec.toFixed(1)}초`
                : '전체 영상 정밀 분석',
        });
        this.fastPoseLog('fall-finalize', {
            finalFall,
            strongFastKinematics,
            combinedEventKinematics,
            precisionEventKinematics,
            peakFastRisk: this.realtimeFallPeakFastRisk,
            fallKinematicScore,
            evidenceSignals: Array.from(this.realtimeFallEvidenceSignals),
            trackChangedDuringEvent,
            controlledDescent,
            inputIncomplete,
        });
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            fall_event_id: this.realtimeFastFallEventKey,
            fall_event_signals: Array.from(this.realtimeFallEvidenceSignals),
            fall_event_peak_fast_score: this.realtimeFallPeakFastRisk,
            fall_event_peak_descent_timestamp_ms: this.realtimeFallPeakDescentTimestampMs,
            fall_event_lowest_point_timestamp_ms: this.realtimeFallLowestPointTimestampMs,
            fall_event_rf_fall_score: rfFallScore,
            fall_event_rf_pipeline_score: rfPipelineScore,
            fall_event_kinematic_score: fallKinematicScore,
            fall_event_max_down_speed: maxDownSpeed,
            fall_event_down_motion_ratio: downMotionRatio,
            fall_event_height_range_ratio: heightRangeRatio,
            fall_event_aspect_ratio_std: aspectRatioStd,
            fall_event_xg_posture: postureResult,
            fall_event_track_changed: trackChangedDuringEvent,
            fall_event_pose_quality: poseQuality,
            fall_dismiss_controlled_descent: controlledDescent,
            fall_dismiss_input_incomplete: inputIncomplete,
        };
        if (finalFall || precisionEventKinematics) {
            this.realtimeLocalFallState = 'confirmed';
            this.realtimeLocalFallRiskPercent = Math.max(
                this.riskScorePercent(result),
                Math.round(fallKinematicScore * 100),
            );
            this.realtimeFastPostureStatus = 'precision';
            this.realtimeFastPostureLabel = this.userCurrentPostureText(result) || this.realtimeFastPostureLabel;
            if (Array.isArray(interval) && interval.length >= 2) {
                this.realtimeLocalFallInterval = { startSec: Number(interval[0]), endSec: Number(interval[1]) };
            }
            this.upsertRealtimeFallEvent(
                'confirmed', this.realtimeLocalFallRiskPercent,
                eventWindow,
                finalFall
                    ? 'RF-Fall과 정밀 자세 변화 분석에서 낙상으로 확인됐습니다.'
                    : `현재 자세는 ${this.userCurrentPostureText(result) || postureResult}이지만, 사건 전체의 급하강·형태 변화·저점 도달 특징이 함께 확인되어 낙상으로 판정했습니다.`,
            );
            return;
        }
        if (['suspected', 'verifying'].includes(this.realtimeLocalFallState)) {
            this.realtimeFastPostureStatus = 'precision';
            this.realtimeFastPostureLabel = this.userCurrentPostureText(result) || this.realtimeFastPostureLabel;
            if (strongFastKinematics || combinedEventKinematics) {
                this.realtimeLocalFallState = 'confirmed';
                this.realtimeLocalFallRiskPercent = Math.max(
                    this.realtimeLocalFallRiskPercent,
                    Math.round(this.realtimeFallPeakFastRisk * 100),
                    Math.round(fallKinematicScore * 100),
                    this.riskScorePercent(result),
                );
                this.upsertRealtimeFallEvent(
                    'confirmed', this.realtimeLocalFallRiskPercent,
                    eventWindow,
                    `현재 자세는 ${this.userCurrentPostureText(result) || postureResult}이지만, 사건 구간의 ${this.realtimeFallEvidenceLabels().join(' · ')} 신호가 연속 확인되어 낙상으로 판정했습니다.`,
                );
            } else if (controlledDescent && !inputIncomplete) {
                this.realtimeLocalFallState = 'cleared';
                this.upsertRealtimeFallEvent(
                    'dismissed', this.riskScorePercent(result),
                    eventWindow,
                    `정밀 분석에서 충격 없는 통제된 하강과 ${this.userCurrentPostureText(result) || postureResult} 자세가 함께 확인되어 후보를 해제했습니다.`,
                );
            } else {
                this.realtimeLocalFallState = 'review_required';
                this.upsertRealtimeFallEvent(
                    'review_required', Math.max(this.realtimeLocalFallRiskPercent, this.riskScorePercent(result)),
                    eventWindow,
                    `Fast 낙상 근거와 정밀 결과가 충돌하거나 해제 근거가 부족해 낙상 가능성 확인이 필요합니다. 현재 자세: ${this.userCurrentPostureText(result) || postureResult}.`,
                );
            }
        }
    }

    private stopRealtimeEnvironmentSnapshots(preserveResult: boolean = false) {
        if (this.realtimeEnvironmentSnapshotTimer) {
            clearTimeout(this.realtimeEnvironmentSnapshotTimer);
            this.realtimeEnvironmentSnapshotTimer = null;
        }
        this.realtimeEnvironmentSnapshotController?.abort();
        this.realtimeEnvironmentSnapshotController = null;
        this.realtimeEnvironmentSnapshotPending = false;
        if (!preserveResult) {
            this.realtimeEnvironmentSnapshotResult = null;
            this.realtimeEnvironmentTracks = [];
            this.realtimeEnvironmentNextTrackId = 1;
            this.realtimeEnvironmentLastSourceTime = -1;
            this.realtimeEnvironmentLatencyEwmaMs = 0;
            this.realtimeEnvironmentAdaptiveResolution = 480;
            this.realtimeEnvironmentTimeoutCount = 0;
        }
    }

    private realtimeEnvironmentSnapshotBlob(videoEl: HTMLVideoElement, side: number): Promise<{ blob: Blob; coordinateMetadata: any } | null> {
        if (videoEl.readyState < 2 || videoEl.videoWidth <= 0 || videoEl.videoHeight <= 0) return Promise.resolve(null);
        side = Math.max(416, Math.min(480, Math.round(Number(side || 480) / 32) * 32));
        const canvas = document.createElement('canvas');
        canvas.width = side;
        canvas.height = side;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return Promise.resolve(null);
        const scale = Math.min(side / videoEl.videoWidth, side / videoEl.videoHeight);
        const drawW = Math.max(1, Math.round(videoEl.videoWidth * scale));
        const drawH = Math.max(1, Math.round(videoEl.videoHeight * scale));
        const offsetX = Math.floor((side - drawW) / 2);
        const offsetY = Math.floor((side - drawH) / 2);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, side, side);
        ctx.drawImage(videoEl, offsetX, offsetY, drawW, drawH);
        return new Promise(resolve => canvas.toBlob(blob => resolve(blob ? {
            blob,
            coordinateMetadata: {
                coordinate_space: 'letterboxed_analysis_input',
                source_width: videoEl.videoWidth,
                source_height: videoEl.videoHeight,
                analysis_width: side,
                analysis_height: side,
                content_offset_x: offsetX,
                content_offset_y: offsetY,
                content_width: drawW,
                content_height: drawH,
                normalized: false,
            },
        } : null), 'image/jpeg', 0.62));
    }

    private updateRealtimeEnvironmentTransport(elapsedMs: number, success: boolean) {
        const measured = Math.max(0, Number(elapsedMs || 0));
        this.realtimeEnvironmentLatencyEwmaMs = this.realtimeEnvironmentLatencyEwmaMs > 0
            ? this.realtimeEnvironmentLatencyEwmaMs * 0.72 + measured * 0.28
            : measured;
        if (!success || this.realtimeEnvironmentLatencyEwmaMs >= 1200) {
            this.realtimeEnvironmentAdaptiveResolution = 416;
        } else if (this.realtimeEnvironmentLatencyEwmaMs >= 650) {
            this.realtimeEnvironmentAdaptiveResolution = 448;
        } else if (this.realtimeEnvironmentLatencyEwmaMs <= 420) {
            this.realtimeEnvironmentAdaptiveResolution = 480;
        }
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            environment_transport_ewma_ms: Math.round(this.realtimeEnvironmentLatencyEwmaMs),
            environment_adaptive_resolution: this.realtimeEnvironmentAdaptiveResolution,
            environment_timeout_count: this.realtimeEnvironmentTimeoutCount,
        };
    }

    private environmentCoordinateTransform(result: any, frame: any): OverlaySourceTransform {
        const metadata = result?.client_coordinate_metadata || result?.coordinate_metadata || {};
        const rawSpace = String(metadata?.coordinate_space || frame?.coordinate_space || 'source_pixel').toLowerCase();
        const coordinateSpace: OverlayCoordinateSpace = rawSpace === 'letterboxed_analysis_input' || rawSpace === 'model_input'
            ? 'model_input'
            : (rawSpace === 'normalized' || rawSpace === 'normalized_source' ? 'normalized_source' : 'source_pixel');
        const sourceWidth = Math.max(1, Number(
            metadata?.source_width
            || frame?.source_frame_width
            || result?.video_meta?.width
            || this.webcamVideoRef?.nativeElement?.videoWidth
            || frame?.coordinate_width
            || 1
        ));
        const sourceHeight = Math.max(1, Number(
            metadata?.source_height
            || frame?.source_frame_height
            || result?.video_meta?.height
            || this.webcamVideoRef?.nativeElement?.videoHeight
            || frame?.coordinate_height
            || 1
        ));
        const coordinateWidth = Math.max(1, Number(
            frame?.coordinate_width || metadata?.analysis_width || sourceWidth
        ));
        const coordinateHeight = Math.max(1, Number(
            frame?.coordinate_height || metadata?.analysis_height || sourceHeight
        ));
        const modelWidth = Math.max(1, Number(metadata?.analysis_width || coordinateWidth));
        const modelHeight = Math.max(1, Number(metadata?.analysis_height || coordinateHeight));
        const fallbackScale = Math.min(modelWidth / sourceWidth, modelHeight / sourceHeight);
        return {
            coordinateSpace,
            sourceWidth,
            sourceHeight,
            coordinateWidth,
            coordinateHeight,
            modelWidth,
            modelHeight,
            letterboxScale: Number(metadata?.letterbox_scale || fallbackScale),
            letterboxPadX: Number(metadata?.content_offset_x ?? metadata?.letterbox_pad_x ?? 0),
            letterboxPadY: Number(metadata?.content_offset_y ?? metadata?.letterbox_pad_y ?? 0),
        };
    }

    private environmentSourcePolygonArea(points: { x: number; y: number }[]): number {
        if (!Array.isArray(points) || points.length < 3) return 0;
        let area = 0;
        for (let index = 0; index < points.length; index++) {
            const next = points[(index + 1) % points.length];
            area += points[index].x * next.y - next.x * points[index].y;
        }
        return Math.abs(area) / 2;
    }

    private normalizeEnvironmentDetectionToSource(
        item: any,
        transform: OverlaySourceTransform,
        sourceTimestampMs: number,
        frameId: number,
        source: EnvironmentDetection['source'],
        videoId: string,
        analysisRunId: string,
    ): any {
        const sourceWidth = transform.sourceWidth;
        const sourceHeight = transform.sourceHeight;
        const rawBbox = this.environmentObjectBbox(item);
        const bboxStart = coordinatePointToSourcePoint({ x: rawBbox[0] || 0, y: rawBbox[1] || 0 }, transform);
        const bboxEnd = coordinatePointToSourcePoint({ x: rawBbox[2] || 0, y: rawBbox[3] || 0 }, transform);
        const bbox = [
            Math.min(bboxStart.x, bboxEnd.x), Math.min(bboxStart.y, bboxEnd.y),
            Math.max(bboxStart.x, bboxEnd.x), Math.max(bboxStart.y, bboxEnd.y),
        ];
        const rawPolygons = Array.isArray(item?.smoothed_segments) && item.smoothed_segments.length > 0
            ? item.smoothed_segments : (Array.isArray(item?.segments) ? item.segments : []);
        let outsidePointCount = 0;
        let sourcePointCount = 0;
        const polygons = rawPolygons.map((polygon: any) => {
            if (!Array.isArray(polygon)) return [];
            const beforeClamp = polygon
                .filter((point: any) => Array.isArray(point) && point.length >= 2)
                .map((point: any) => coordinatePointToSourcePoint({ x: Number(point[0]), y: Number(point[1]) }, transform, false));
            sourcePointCount += beforeClamp.length;
            outsidePointCount += beforeClamp.filter(point => (
                point.x < 0 || point.x > sourceWidth || point.y < 0 || point.y > sourceHeight
            )).length;
            return clampSourcePolygon(beforeClamp, sourceWidth, sourceHeight);
        }).filter((polygon: any[]) => polygon.length >= 3);
        const key = this.normalizeEnvironmentDisplayClass(item);
        const videoArea = Math.max(1, sourceWidth * sourceHeight);
        const polygonArea = polygons.reduce((sum: number, polygon: any[]) => sum + this.environmentSourcePolygonArea(polygon), 0);
        const polygonAreaRatio = polygonArea / videoArea;
        const bboxArea = Math.max(1, (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]));
        const outsideRatio = outsidePointCount / Math.max(1, sourcePointCount);
        const classMaxAreaRatio = ENVIRONMENT_CLASS_MAX_AREA_RATIO[key] ?? 0.58;
        const polygonToBboxRatio = polygonArea / bboxArea;
        const invalidArea = polygonAreaRatio > classMaxAreaRatio || polygonToBboxRatio > 1.35;
        const invalidOutside = outsideRatio > 0.12;
        const maskQuality = polygons.length > 0
            ? Math.max(0, Math.min(1, 1 - outsideRatio * 2.5 - Math.max(0, polygonToBboxRatio - 1) * 0.8))
            : 0;
        const bboxFallbackReason = invalidOutside
            ? 'polygon_outside_source'
            : (invalidArea ? 'polygon_area_outlier' : (maskQuality < 0.45 ? 'low_mask_quality' : ''));
        const safePolygons = bboxFallbackReason ? [] : polygons;
        const stableId = String(item?.stable_object_id ?? item?.track_id ?? `${key}-${frameId}`);
        const contract: EnvironmentDetection = {
            id: stableId,
            classId: String(item?.class_id ?? key),
            label: key,
            confidence: Number(item?.smoothed_confidence ?? item?.confidence ?? 0),
            sourceFrameWidth: sourceWidth,
            sourceFrameHeight: sourceHeight,
            bboxSource: { x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3] },
            polygonSource: safePolygons,
            sourceTimestampMs,
            frameId,
            videoId,
            analysisRunId,
            source,
            transformVersion: OVERLAY_TRANSFORM_VERSION,
            coordinateSpace: 'source_pixel',
        };
        return {
            ...item,
            ...contract,
            stable_object_id: stableId,
            bbox,
            x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3],
            segments: safePolygons.map(polygon => polygon.map(point => [point.x, point.y])),
            smoothed_segments: [],
            coordinate_space: 'source_pixel',
            source_frame_width: sourceWidth,
            source_frame_height: sourceHeight,
            source_timestamp_ms: sourceTimestampMs,
            frame_id: frameId,
            video_id: videoId,
            analysis_run_id: analysisRunId,
            environment_source: source,
            transform_version: OVERLAY_TRANSFORM_VERSION,
            polygon_before_transform: rawPolygons,
            polygon_after_transform: safePolygons.map(polygon => polygon.map(point => [point.x, point.y])),
            polygon_area_ratio: Number(polygonAreaRatio.toFixed(6)),
            polygon_outside_ratio: Number(outsideRatio.toFixed(6)),
            mask_quality: Number(maskQuality.toFixed(4)),
            bbox_fallback_reason: bboxFallbackReason,
            rejected_reason: '',
        };
    }

    private normalizeEnvironmentAnalysis(
        result: any,
        source: EnvironmentDetection['source'],
        fallbackTimestampSec: number = 0,
    ): any {
        const analysis = result?.environment_analysis;
        if (!analysis || !Array.isArray(analysis?.frames)) return result;
        const videoId = String(result?.saved_name || this.realtimeUploadedVideoName || this.realtimeSessionId || 'video');
        const analysisRunId = String(result?.analysis_run_id || this.realtimeUploadedAnalysisRunId || 'analysis');
        const frames = analysis.frames.map((frame: any, index: number) => {
            const transform = this.environmentCoordinateTransform(result, frame);
            const sourceTimestampSec = Number(frame?.time_sec ?? result?.source_timestamp ?? fallbackTimestampSec ?? 0);
            const sourceTimestampMs = Math.max(0, sourceTimestampSec * 1000);
            const frameId = Number(frame?.frame_id ?? frame?.frame_idx ?? index);
            const detections = (Array.isArray(frame?.detections) ? frame.detections : []).map((item: any) => (
                this.normalizeEnvironmentDetectionToSource(
                    item, transform, sourceTimestampMs, frameId, source, videoId, analysisRunId,
                )
            ));
            return {
                ...frame,
                frame_id: frameId,
                time_sec: sourceTimestampSec,
                source_timestamp_ms: sourceTimestampMs,
                coordinate_space: 'source_pixel',
                coordinate_width: transform.sourceWidth,
                coordinate_height: transform.sourceHeight,
                source_frame_width: transform.sourceWidth,
                source_frame_height: transform.sourceHeight,
                transform_version: OVERLAY_TRANSFORM_VERSION,
                detections,
            };
        });
        const rawTransform = frames.length > 0
            ? this.environmentCoordinateTransform(result, analysis.frames[analysis.frames.length - 1])
            : this.environmentCoordinateTransform(result, {});
        const rawCandidates = (Array.isArray(analysis?.raw_diagnostics?.candidates)
            ? analysis.raw_diagnostics.candidates : []).map((item: any, index: number) => (
            this.normalizeEnvironmentDetectionToSource(
                item,
                rawTransform,
                Number(item?.time_sec ?? fallbackTimestampSec ?? 0) * 1000,
                Number(item?.frame_idx ?? index),
                source,
                videoId,
                analysisRunId,
            )
        ));
        return {
            ...result,
            client_coordinate_metadata: {
                coordinate_space: 'source_pixel',
                source_width: Number(frames[0]?.source_frame_width || result?.video_meta?.width || 1),
                source_height: Number(frames[0]?.source_frame_height || result?.video_meta?.height || 1),
                normalized: false,
                transform_version: OVERLAY_TRANSFORM_VERSION,
            },
            environment_analysis: {
                ...analysis,
                coordinate_contract: 'source_pixel',
                transform_version: OVERLAY_TRANSFORM_VERSION,
                frames,
                objects: frames.length > 0 ? frames[frames.length - 1].detections : (analysis.objects || []),
                raw_diagnostics: {
                    ...(analysis?.raw_diagnostics || {}),
                    candidates: rawCandidates,
                    coordinate_space: 'source_pixel',
                    transform_version: OVERLAY_TRANSFORM_VERSION,
                },
            },
        };
    }

    private environmentDetectionGeometryValid(item: any): boolean {
        const bbox = this.environmentObjectBbox(item);
        const sourceWidth = Math.max(1, Number(item?.source_frame_width || item?.sourceFrameWidth || 1));
        const sourceHeight = Math.max(1, Number(item?.source_frame_height || item?.sourceFrameHeight || 1));
        if (bbox.length < 4 || bbox[2] <= bbox[0] || bbox[3] <= bbox[1]) return false;
        if (bbox[0] < 0 || bbox[1] < 0 || bbox[2] > sourceWidth || bbox[3] > sourceHeight) return false;
        const areaRatio = (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]) / Math.max(1, sourceWidth * sourceHeight);
        const classMax = ENVIRONMENT_CLASS_MAX_AREA_RATIO[this.normalizeEnvironmentDisplayClass(item)] ?? 0.72;
        return areaRatio <= Math.min(0.98, classMax * 1.35);
    }

    private mergeRealtimeAndFinalEnvironment(finalResult: any, realtimeResult: any): any {
        const normalizedFinal = this.normalizeEnvironmentAnalysis(finalResult, 'final_analysis');
        const normalizedRealtime = this.normalizeEnvironmentAnalysis(
            realtimeResult, 'realtime', Number(realtimeResult?.source_timestamp || 0),
        );
        const finalFrames = Array.isArray(normalizedFinal?.environment_analysis?.frames)
            ? normalizedFinal.environment_analysis.frames.map((frame: any) => ({ ...frame, detections: [...(frame.detections || [])] })) : [];
        const realtimeFrames = Array.isArray(normalizedRealtime?.environment_analysis?.frames)
            ? normalizedRealtime.environment_analysis.frames : [];
        const realtimeFrame = realtimeFrames.length > 0 ? realtimeFrames[realtimeFrames.length - 1] : null;
        if (!realtimeFrame) return normalizedFinal;
        if (finalFrames.length === 0) {
            // 정밀 분석의 환경 lane이 빈 결과를 반환해도 이미 성공한 실시간
            // snapshot을 지우지 않습니다. 특히 영상 종료 직후 snapshot lane을
            // 정리할 때 이 결과가 환경 객체 표시의 마지막 유효 근거가 됩니다.
            return {
                ...normalizedRealtime,
                environment_analysis: {
                    ...(normalizedRealtime?.environment_analysis || {}),
                    realtime_final_merge: true,
                    merge_policy: 'realtime-fallback-when-final-empty-v1',
                },
            };
        }
        let nearestIndex = 0;
        let nearestDelta = Number.POSITIVE_INFINITY;
        finalFrames.forEach((frame: any, index: number) => {
            const delta = Math.abs(Number(frame?.source_timestamp_ms || frame?.time_sec * 1000 || 0) - Number(realtimeFrame?.source_timestamp_ms || 0));
            if (delta < nearestDelta) { nearestDelta = delta; nearestIndex = index; }
        });
        if (nearestDelta > ENVIRONMENT_TIMESTAMP_TOLERANCE_MS) {
            finalFrames.push({ ...realtimeFrame, detections: (realtimeFrame.detections || []).map((item: any) => ({ ...item, environment_source: 'cached', source: 'cached' })) });
        } else {
            const finalFrame = finalFrames[nearestIndex];
            const liveObjects = [...(realtimeFrame.detections || [])];
            const consumed = new Set<number>();
            const mergedFinal = (finalFrame.detections || []).map((finalObject: any) => {
                const finalBbox = this.environmentObjectBbox(finalObject);
                let bestIndex = -1;
                let bestScore = -1;
                liveObjects.forEach((liveObject: any, index: number) => {
                    if (consumed.has(index) || this.normalizeEnvironmentDisplayClass(liveObject) !== this.normalizeEnvironmentDisplayClass(finalObject)) return;
                    const liveBbox = this.environmentObjectBbox(liveObject);
                    const iou = this.bboxIou(finalBbox, liveBbox);
                    const sourceDiagonal = Math.max(1, Math.hypot(Number(finalObject?.source_frame_width || 1), Number(finalObject?.source_frame_height || 1)));
                    const centerDistance = finalBbox.length >= 4 && liveBbox.length >= 4
                        ? Math.hypot(
                            (finalBbox[0] + finalBbox[2] - liveBbox[0] - liveBbox[2]) / 2,
                            (finalBbox[1] + finalBbox[3] - liveBbox[1] - liveBbox[3]) / 2,
                        ) / sourceDiagonal : 1;
                    const score = iou - centerDistance;
                    if ((iou >= 0.18 || centerDistance <= 0.08) && score > bestScore) { bestIndex = index; bestScore = score; }
                });
                const liveObject = bestIndex >= 0 ? liveObjects[bestIndex] : null;
                if (liveObject) consumed.add(bestIndex);
                if (!this.environmentDetectionGeometryValid(finalObject) && liveObject) {
                    return {
                        ...liveObject,
                        environment_source: 'tracked',
                        source: 'tracked',
                        final_rejected_reason: 'final_geometry_validation_failed',
                    };
                }
                if (!liveObject) return finalObject;
                return {
                    ...finalObject,
                    stable_object_id: liveObject.stable_object_id || finalObject.stable_object_id,
                    environment_source: 'final_analysis',
                    source: 'final_analysis',
                    geometry_transition: {
                        started_at_ms: Date.now(),
                        duration_ms: 300,
                        from_bbox: this.environmentObjectBbox(liveObject),
                        from_segments: liveObject.segments || [],
                    },
                };
            });
            liveObjects.forEach((item: any, index: number) => {
                if (!consumed.has(index)) mergedFinal.push({ ...item, environment_source: 'tracked', source: 'tracked' });
            });
            finalFrames[nearestIndex] = { ...finalFrame, detections: mergedFinal };
        }
        finalFrames.sort((left: any, right: any) => Number(left?.time_sec || 0) - Number(right?.time_sec || 0));
        const currentTimeMs = this.realtimeSourceTime() * 1000;
        const summaryFrame = finalFrames.reduce((best: any, frame: any) => {
            if (!best) return frame;
            return Math.abs(Number(frame?.source_timestamp_ms || 0) - currentTimeMs) < Math.abs(Number(best?.source_timestamp_ms || 0) - currentTimeMs) ? frame : best;
        }, null);
        return {
            ...normalizedFinal,
            client_coordinate_metadata: {
                coordinate_space: 'source_pixel',
                source_width: Number(summaryFrame?.source_frame_width || normalizedFinal?.video_meta?.width || 1),
                source_height: Number(summaryFrame?.source_frame_height || normalizedFinal?.video_meta?.height || 1),
                normalized: false,
                transform_version: OVERLAY_TRANSFORM_VERSION,
            },
            environment_analysis: {
                ...(normalizedFinal?.environment_analysis || {}),
                frames: finalFrames,
                objects: summaryFrame?.detections || [],
                realtime_final_merge: true,
                merge_policy: 'class-spatial-source-pixel-v2',
            },
        };
    }

    private environmentFrameForTimestamp(analysis: any, currentTimeSec: number): any {
        const frames = Array.isArray(analysis?.frames) ? analysis.frames : [];
        if (frames.length === 0) return null;
        const currentMs = Math.max(0, Number(currentTimeSec || 0) * 1000);
        const nearest = frames.reduce((best: any, frame: any) => {
            if (!best) return frame;
            const frameMs = Number(frame?.source_timestamp_ms ?? Number(frame?.time_sec || 0) * 1000);
            const bestMs = Number(best?.source_timestamp_ms ?? Number(best?.time_sec || 0) * 1000);
            return Math.abs(frameMs - currentMs) < Math.abs(bestMs - currentMs) ? frame : best;
        }, null);
        const nearestMs = Number(nearest?.source_timestamp_ms ?? Number(nearest?.time_sec || 0) * 1000);
        const ageMs = Math.abs(currentMs - nearestMs);
        if (ageMs <= ENVIRONMENT_TIMESTAMP_TOLERANCE_MS) return nearest;
        const staticDetections = (nearest?.detections || []).filter((item: any) => (
            ['confirmed', 'tracked', 'cached'].includes(String(item?.object_state || item?.source || '').toLowerCase())
        ));
        if (staticDetections.length === 0) return null;
        return {
            ...nearest,
            detections: staticDetections.map((item: any) => ({
                ...item,
                environment_source: item?.environment_source === 'final_analysis' ? 'final_analysis' : 'cached',
                source: item?.source === 'final_analysis' ? 'final_analysis' : 'cached',
                result_age_ms: ageMs,
            })),
        };
    }

    private stabilizeRealtimeEnvironmentSnapshot(result: any, sourceTime: number): any {
        const normalizedResult = this.normalizeEnvironmentAnalysis(result, 'realtime', sourceTime);
        const frames = Array.isArray(normalizedResult?.environment_analysis?.frames)
            ? normalizedResult.environment_analysis.frames : [];
        const frame = frames.length > 0 ? frames[frames.length - 1] : null;
        const detections = Array.isArray(frame?.detections) ? frame.detections : [];
        this.realtimeEnvironmentTracks = this.realtimeEnvironmentTracks.filter(
            track => (sourceTime - Number(track?.lastSeen || 0)) * 1000 <= ENVIRONMENT_RESULT_TTL_MS
        );
        const matchedTrackIds = new Set<number>();
        const stabilized = detections.map((item: any) => {
            const bbox = this.environmentObjectBbox(item);
            const label = this.normalizeEnvironmentDisplayClass(item);
            const frameDiagonal = Math.max(1, Math.hypot(Number(frame?.coordinate_width || 1), Number(frame?.coordinate_height || 1)));
            let track = this.realtimeEnvironmentTracks
                .filter(candidate => candidate.label === label)
                .map(candidate => {
                    const previous = candidate.bbox || [];
                    const centerDistance = previous.length >= 4 && bbox.length >= 4
                        ? Math.hypot(
                            (previous[0] + previous[2] - bbox[0] - bbox[2]) / 2,
                            (previous[1] + previous[3] - bbox[1] - bbox[3]) / 2,
                        ) / frameDiagonal : Number.POSITIVE_INFINITY;
                    const iou = this.bboxIou(previous, bbox);
                    return { candidate, iou, centerDistance, score: iou - centerDistance };
                })
                .filter(match => match.iou >= 0.18 || match.centerDistance <= 0.10)
                .sort((left, right) => right.score - left.score)[0]?.candidate;
            if (!track) {
                track = {
                    id: this.realtimeEnvironmentNextTrackId++,
                    label,
                    bbox,
                    hits: 0,
                    firstSeen: sourceTime,
                    lastSeen: sourceTime,
                    item,
                };
                this.realtimeEnvironmentTracks.push(track);
            }
            const previousBbox = Array.isArray(track.bbox) ? track.bbox : bbox;
            track.bbox = bbox.map((value: number, index: number) => (
                Number(previousBbox[index] ?? value) * 0.65 + Number(value) * 0.35
            ));
            track.hits += 1;
            track.lastSeen = sourceTime;
            track.item = {
                ...item,
                bbox: track.bbox,
                x1: track.bbox[0], y1: track.bbox[1], x2: track.bbox[2], y2: track.bbox[3],
            };
            matchedTrackIds.add(Number(track.id));
            // 환경 모델을 이미 통과한 첫 정적 객체는 반복 타이머를 기다리지 않고
            // provisional overlay로 표시하고 TTL/후속 결과로 위치를 안정화합니다.
            const confirmed = track.hits >= 1 && Number(item?.confidence || 0) >= 0.28;
            return {
                ...track.item,
                stable_object_id: `live-${track.id}`,
                track_id: `live-${track.id}`,
                object_state: confirmed ? 'confirmed' : 'tentative',
                detection_count: track.hits,
                consecutive_hits: track.hits,
                observation_count: Math.max(2, track.hits),
                stability_score: Math.min(1, track.hits / 2),
                time_sec: sourceTime,
                source_timestamp_ms: sourceTime * 1000,
                sourceTimestampMs: sourceTime * 1000,
                environment_source: 'realtime',
                source: 'realtime',
                result_age_ms: 0,
            };
        });
        for (const track of this.realtimeEnvironmentTracks) {
            if (matchedTrackIds.has(Number(track.id)) || Number(track.hits || 0) < 2 || !track.item) continue;
            const ageMs = Math.max(0, (sourceTime - Number(track.lastSeen || 0)) * 1000);
            if (ageMs > ENVIRONMENT_RESULT_TTL_MS) continue;
            stabilized.push({
                ...track.item,
                stable_object_id: `live-${track.id}`,
                track_id: `live-${track.id}`,
                object_state: 'confirmed',
                detection_count: track.hits,
                consecutive_hits: track.hits,
                observation_count: Math.max(2, track.hits),
                stability_score: Math.min(1, track.hits / 2),
                time_sec: sourceTime,
                source_timestamp_ms: Number(track.lastSeen || sourceTime) * 1000,
                sourceTimestampMs: Number(track.lastSeen || sourceTime) * 1000,
                environment_source: 'tracked',
                source: 'tracked',
                result_age_ms: ageMs,
            });
        }
        return {
            ...normalizedResult,
            source_timestamp: sourceTime,
            source_start_time: sourceTime,
            source_end_time: sourceTime,
            _environment_snapshot_only: true,
            environment_analysis: {
                ...(normalizedResult?.environment_analysis || {}),
                status: 'ready',
                snapshot_mode: true,
                frames: frame ? [{ ...frame, time_sec: sourceTime, detections: stabilized }] : [],
                objects: stabilized,
                object_count: stabilized.filter(item => item.object_state === 'confirmed').length,
            },
        };
    }

    private startRealtimeEnvironmentSnapshots(videoEl: HTMLVideoElement) {
        this.stopRealtimeEnvironmentSnapshots();
        this.realtimeEnvironmentStartedAt = performance.now();
        this.realtimeEnvironmentFirstDisplayedAt = 0;
        this.realtimeEnvironmentFirstRequestDispatchedAt = 0;
        const capture = async () => {
            if (!this.realtimeEnvironmentSourceReady(videoEl)) {
                // 업로드 직후에는 loadedmetadata(readyState=1)만 먼저 도착할 수
                // 있다. 첫 decoded frame이 생길 때까지 lane을 유지해야 paused
                // prewarm snapshot이 실제로 전송된다.
                if (this.realtimeActive && !videoEl.ended) {
                    this.realtimeEnvironmentSnapshotTimer = setTimeout(capture, 100);
                }
                return;
            }
            if (this.agentWeights.environment <= 0) {
                this.realtimeEnvironmentSnapshotTimer = setTimeout(capture, 1000);
                return;
            }
            const sourceTime = this.realtimeSourceTime();
            if (
                (videoEl.paused && this.realtimeEnvironmentFirstRequestDispatchedAt > 0)
                || sourceTime - this.realtimeEnvironmentLastSourceTime < this.realtimePresetConfig().environmentIntervalMs / 1000
                || this.realtimeEnvironmentSnapshotPending
            ) {
                this.realtimeEnvironmentSnapshotTimer = setTimeout(capture, 100);
                return;
            }
            this.realtimeEnvironmentSnapshotPending = true;
            this.realtimeEnvironmentLastSourceTime = sourceTime;
            const environmentStartedAt = performance.now();
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                environment_queue_length: 1,
                environment_inference_status: 'running',
            };
            let requestTimedOut = false;
            let requestTimeout: any = null;
            try {
                const requestedResolution = this.realtimeEnvironmentAdaptiveResolution;
                const snapshot = await this.realtimeEnvironmentSnapshotBlob(videoEl, requestedResolution);
                if (!snapshot) return;
                const controller = new AbortController();
                this.realtimeEnvironmentSnapshotController = controller;
                // 최초 전용 모델 load는 CPU 운영 노드에서 1.5초 이상 걸릴 수 있고,
                // 정밀 분석과 겹치면 더 길어집니다. 첫 결과는 충분히 기다린 뒤
                // warm 요청부터 짧은 timeout과 적응 해상도를 적용합니다.
                const timeoutMs = this.realtimeEnvironmentLatencyEwmaMs <= 0
                    ? 12000
                    : Math.max(3000, Math.round(this.realtimePresetConfig().environmentIntervalMs * 4));
                requestTimeout = setTimeout(() => {
                    requestTimedOut = true;
                    this.realtimeEnvironmentTimeoutCount += 1;
                    controller.abort();
                }, timeoutMs);
                const weightMetadata = this.agentWeightMetadata();
                const body = new FormData();
                body.append('video', snapshot.blob, `environment-${sourceTime.toFixed(3)}.jpg`);
                body.append('metadata', JSON.stringify({
                    analysis_mode: 'environment_snapshot',
                    source_timestamp: sourceTime,
                    realtime_session_id: this.realtimeSessionId,
                    video_id: this.realtimeUploadedVideoName || this.realtimeSessionId,
                    analysis_run_id: String(this.realtimeUploadedAnalysisRunId || 0),
                    frame_id: Math.max(0, Math.round(sourceTime * Math.max(1, this.realtimeVideoFps || 30))),
                    coordinate_metadata: snapshot.coordinateMetadata,
                    ...weightMetadata,
                    compute_budget: {
                        ...(weightMetadata?.compute_budget || {}),
                        environment_resolution: requestedResolution,
                    },
                }));
                if (this.realtimeEnvironmentFirstRequestDispatchedAt <= 0) {
                    this.realtimeEnvironmentFirstRequestDispatchedAt = performance.now();
                }
                const response = await fetch('/wiz/api/page.dashboard/analyze_upload', {
                    method: 'POST',
                    body,
                    signal: controller.signal,
                });
                const json = await this.readJsonResponse(response);
                if (json?.code === 200 && json?.data?.environment_analysis) {
                    const sourceContractReady = json.data.environment_analysis?.coordinate_contract === 'source_pixel';
                    const responseCoordinateMetadata = sourceContractReady ? {
                        coordinate_space: 'source_pixel',
                        source_width: Number(json.data.environment_analysis?.source_frame_size?.width || snapshot.coordinateMetadata.source_width),
                        source_height: Number(json.data.environment_analysis?.source_frame_size?.height || snapshot.coordinateMetadata.source_height),
                        normalized: false,
                        transform_version: OVERLAY_TRANSFORM_VERSION,
                    } : snapshot.coordinateMetadata;
                    const snapshotResult = this.stabilizeRealtimeEnvironmentSnapshot({
                        ...json.data,
                        client_coordinate_metadata: responseCoordinateMetadata,
                    }, sourceTime);
                    this.realtimeEnvironmentSnapshotResult = snapshotResult;
                    this.realtimeEnvironmentSnapshotCount = Number(
                        snapshotResult?.environment_analysis?.object_count || 0
                    );
                    const environmentInferenceMs = Math.max(0, performance.now() - environmentStartedAt);
                    this.updateRealtimeEnvironmentTransport(environmentInferenceMs, true);
                    if (this.realtimeEnvironmentFirstDisplayedAt <= 0) this.realtimeEnvironmentFirstDisplayedAt = performance.now();
                    this.recordRealtimePerf('environmentInference', environmentInferenceMs);
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        environment_queue_length: 0,
                        environment_inference_ms: environmentInferenceMs,
                        environment_result_age_ms: Math.max(0, Number(videoEl.currentTime || 0) * 1000 - sourceTime * 1000),
                        environment_first_display_ms: Math.max(0, this.realtimeEnvironmentFirstDisplayedAt - this.realtimeEnvironmentStartedAt),
                    };
                    const current = this.currentRootAnalysisResult() || {};
                    const merged = Object.keys(current).length > 0 ? {
                        ...current,
                        environment_analysis: snapshotResult.environment_analysis,
                        client_coordinate_metadata: responseCoordinateMetadata,
                        _environment_snapshot_only: true,
                    } : snapshotResult;
                    if (Object.keys(current).length > 0) this.applyAnalysisRoot(merged, true);
                    this.updateRealtimeDiagnosticResult(merged, 0, 'live_environment_snapshot');
                    this.scheduleRealtimeServerDetectionOverlay(merged);
                    this.requestRealtimeUiRender();
                }
            } catch (error: any) {
                if (requestTimedOut) {
                    this.updateRealtimeEnvironmentTransport(performance.now() - environmentStartedAt, false);
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        environment_zero_reason: 'live_environment_snapshot_timeout',
                    };
                } else if (error?.name !== 'AbortError') {
                    this.updateRealtimeEnvironmentTransport(performance.now() - environmentStartedAt, false);
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        environment_zero_reason: 'live_environment_snapshot_failed',
                    };
                }
            } finally {
                if (requestTimeout) clearTimeout(requestTimeout);
                this.realtimeEnvironmentSnapshotController = null;
                this.realtimeEnvironmentSnapshotPending = false;
                this.realtimeDiagnostics = { ...this.realtimeDiagnostics, environment_queue_length: 0 };
                if (this.realtimeEnvironmentSourceReady(videoEl)) {
                    this.realtimeEnvironmentSnapshotTimer = setTimeout(capture, requestTimedOut ? 600 : 100);
                }
            }
        };
        // 첫 정상 프레임은 주기 타이머를 기다리지 않고 즉시 전송합니다.
        void capture();
    }

    private async waitForRealtimeEnvironmentFirstResult(timeoutMs: number = 13000): Promise<boolean> {
        if (this.realtimeDisablePrecision || this.agentWeights.environment <= 0) return false;
        const startedAt = performance.now();
        while (
            this.realtimeActive
            && this.realtimeEnvironmentFirstDisplayedAt <= 0
            && performance.now() - startedAt < timeoutMs
        ) {
            await new Promise(resolve => setTimeout(resolve, 40));
        }
        return this.realtimeEnvironmentFirstDisplayedAt > 0;
    }

    private realtimeEnvironmentSourceReady(videoEl: HTMLVideoElement): boolean {
        if (!this.realtimeActive || videoEl.ended || videoEl.readyState < 2
            || videoEl.videoWidth <= 0 || videoEl.videoHeight <= 0) return false;
        if (this.realtimeSourceMode === 'video') return Boolean(this.realtimeUploadedVideoUrl);
        return this.realtimeSourceMode === 'camera' && this.webcamReady && Boolean(videoEl.srcObject);
    }

    private normalizeFastPoseResponse(rawResponse: any): { landmarks: any[][]; sourceTimestampMs: number } {
        const rawPeople = rawResponse?.landmarks
            ?? rawResponse?.poses
            ?? rawResponse?.persons
            ?? rawResponse?.people
            ?? rawResponse?.detections
            ?? [];
        const list = Array.isArray(rawPeople) ? rawPeople : [];
        const landmarks = list.map((item: any) => {
            const points = Array.isArray(item)
                ? item
                : item?.landmarks ?? item?.keypoints ?? item?.pose_keypoints ?? [];
            return (Array.isArray(points) ? points : []).map((point: any) => {
                if (!Array.isArray(point)) return point;
                return {
                    x: Number(point[0] || 0),
                    y: Number(point[1] || 0),
                    visibility: Number(point[2] ?? 1),
                    presence: Number(point[2] ?? 1),
                };
            });
        }).filter((points: any[]) => points.length > 0);
        return {
            landmarks,
            sourceTimestampMs: Number(
                rawResponse?.sourceTimestamp
                ?? rawResponse?.source_timestamp_ms
                ?? rawResponse?.videoTimestampMs
                ?? 0
            ),
        };
    }

    private drawMpPose(frameMeta?: { capturedAt: number; videoTimestampMs: number; frameId: number }) {
        const frame: PoseFrameEnvelope = {
            capturedAt: Number(frameMeta?.capturedAt || performance.now()),
            videoTimestampMs: Number(frameMeta?.videoTimestampMs || 0),
            frameId: Number(frameMeta?.frameId ?? -1),
            eventCandidate: Boolean((frameMeta as any)?.eventCandidate),
        };
        if (!this.hasRealtimeInputSource()) { this.skipPoseFrame(frame, 'NO_FRAME_CAPTURE'); return; }
        if (this.mpProcessing || (!this.poseLandmarker && !this.faceLandmarker)) {
            this.skipPoseFrame(frame, this.mpProcessing ? 'POSE_REQUEST_IN_FLIGHT' : 'POSE_REQUEST_SKIPPED');
            return;
        }
        const videoEl = this.webcamVideoRef?.nativeElement;
        if (!videoEl || videoEl.readyState < 2) { this.skipPoseFrame(frame, 'NO_FRAME_CAPTURE'); return; }
        if (document.hidden) { this.skipPoseFrame(frame, 'POSE_REQUEST_SKIPPED'); return; }
        const canvasEl = this._mpPoseCanvasEl?.nativeElement || document.querySelector('[data-mp-pose-canvas]') as HTMLCanvasElement;
        if (!canvasEl) { this.skipPoseFrame(frame, 'CANVAS_RENDER_SKIPPED'); return; }
        const canvasRect = canvasEl.getBoundingClientRect();
        const targetW = Math.max(1, Math.round(canvasRect.width || videoEl.clientWidth || videoEl.videoWidth || 1));
        const targetH = Math.max(1, Math.round(canvasRect.height || videoEl.clientHeight || videoEl.videoHeight || 1));
        const preparedCanvas = resizeCanvasForDisplay(canvasEl, targetW, targetH);
        const ctx = preparedCanvas.context;
        if (!ctx) { this.skipPoseFrame(frame, 'CANVAS_RENDER_SKIPPED'); return; }
        this.mpProcessing = true;
        const poseRequestStartedAt = performance.now();
        this.realtimeFastPoseStatus = 'POSE_REQUESTING';
        this.realtimeFastPoseCounters.request += 1;
        this.fastPoseLog('request-start', { frameId: frame.frameId, videoTimestampMs: frame.videoTimestampMs });
        try {
            const preprocessStartedAt = performance.now();
            const mpInput = this.getMediaPipeInputSource(videoEl);
            this.recordRealtimePerf('preprocess', performance.now() - preprocessStartedAt);
            const detectTs = performance.now();
            const poseInferenceStartedAt = performance.now();
            const result = this.poseLandmarker
                ? this.poseLandmarker.detectForVideo(mpInput, detectTs)
                : { landmarks: [] };
            const normalizedPoseResult = this.normalizeFastPoseResponse(result);
            const poseInferenceMs = Math.max(0, performance.now() - poseInferenceStartedAt);
            this.realtimeFastPoseCounters.response += 1;
            this.recordRealtimePerf('poseInference', poseInferenceMs);
            const livePoses = normalizedPoseResult.landmarks.slice(0, MP_MAX_POSES);
            const poseKeypoints = livePoses.flatMap((pose: any[]) => pose || []);
            const visiblePoseKeypoints = poseKeypoints.filter((landmark: any) => Number(landmark?.visibility ?? 0) >= this.KF_VIS_THRESHOLD);
            const poseConfidence = visiblePoseKeypoints.length > 0
                ? visiblePoseKeypoints.reduce((sum: number, landmark: any) => sum + Number(landmark?.visibility || 0), 0) / visiblePoseKeypoints.length
                : 0;
            if (livePoses.length > 0 && visiblePoseKeypoints.length > 0) this.realtimeFastPoseCounters.validResponse += 1;
            this.realtimeFastPoseStatus = livePoses.length > 0 ? 'TRACK_ACTIVE' : 'POSE_EMPTY';
            this.fastPoseLog('response', {
                frameId: frame.frameId,
                personCount: livePoses.length,
                poseCount: livePoses.length,
                keypointCount: visiblePoseKeypoints.length,
                inferenceMs: poseInferenceMs,
                apiUrl: 'local://mediapipe/pose_landmarker',
            });
            if (livePoses.length > 0 && !this.realtimeDiagnostics?.person_detection_first_ms) {
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    person_detection_first_ms: Math.max(0, performance.now() - this.mpPoseFirstRequestedAt),
                };
            }
            const hasHeldPose = this.mpPoseTracks.some(track => (
                Array.isArray(track.lastLandmarks)
                && track.lastLandmarks.length > 0
                && detectTs - Number(track.lastPoseSeenMs || 0) <= POSE_HOLD_MS
            ));
            if (livePoses.length > 0 || hasHeldPose) {
                if (livePoses.length > 0) this.mpLastPoseSeenMs = detectTs;
                const poses = livePoses;
                const displayRect = this.getVideoContentRect(videoEl);
                const offX = displayRect.x; const offY = displayRect.y;
                const w = displayRect.w; const h = displayRect.h;
                if (this.mpPoseKalmanFilters.length === 0) this.initKalmanFilters(MP_MAX_POSES);
                const poseCandidates = poses
                    .map((landmarks: any[], sourceIndex: number) => {
                        if (!Array.isArray(landmarks) || landmarks.length !== 33) {
                            console.warn('UNEXPECTED_KEYPOINT_COUNT', {
                                sourceIndex,
                                expected: 33,
                                actual: Array.isArray(landmarks) ? landmarks.length : 0,
                            });
                        }
                        const normalizedLandmarks = Array.isArray(landmarks) ? landmarks.slice(0, 33) : [];
                        const geometry = this.mediaPipePoseGeometry(normalizedLandmarks);
                        return {
                            landmarks: normalizedLandmarks,
                            sourceIndex,
                            center: this.mediaPipePoseCenter(normalizedLandmarks),
                            bbox: geometry?.bbox,
                            torso: geometry?.torso,
                            shoulder: geometry?.shoulder,
                            hip: geometry?.hip,
                        };
                    })
                    .filter((item: any) => !!item.center && Array.isArray(item.bbox) && !!item.torso && !!item.shoulder && !!item.hip);
                const uniquePoseCandidates = this.deduplicateMediaPipePoseCandidates(poseCandidates as any);
                const liveTrackedPoses = this.assignMediaPipeTracks(uniquePoseCandidates as any, detectTs);
                const usedTrackIds = new Set<number>(liveTrackedPoses.map((item: any) => Number(item.trackId)));
                const trackedPoses = [
                    ...liveTrackedPoses,
                    ...this.heldMediaPipePoses(detectTs, usedTrackIds),
                ];
                const approvedBoxes = this.approvedLogicalPersonBoxes(this.currentRootAnalysisResult());
                const hasFreshServerPerson = approvedBoxes.length > 0;
                const localExclusionReasons: Record<string, number> = {};
                const filteredRenderablePoses = trackedPoses.filter((trackedPose: any) => {
                    if (this.isDebugOverlayMode()) return true;
                    const track = this.mpPoseTracks.find(item => item.id === trackedPose.trackId);
                    const center = this.mediaPipePoseCenter(trackedPose.landmarks || []);
                    const reject = (reason: string) => {
                        localExclusionReasons[reason] = Number(localExclusionReasons[reason] || 0) + 1;
                        return false;
                    };
                    if (!track || !center || center.visible < 8) return reject('local_pose_insufficient');
                    if (['HELD', 'PREDICTED'].includes(trackedPose.poseState)) {
                        return detectTs - Number(track.lastSeenMs || 0) <= PERSON_TRACK_TTL_MS
                            || reject('local_track_ttl_expired');
                    }
                    if (hasFreshServerPerson) {
                        const insideActualPerson = approvedBoxes.some(box => (
                            center.x >= box.x1 && center.x <= box.x2
                            && center.y >= box.y1 && center.y <= box.y2
                        ));
                        // 전체 영상 Precision 결과의 마지막 bbox는 현재 재생 프레임과
                        // timestamp가 다를 수 있습니다. 유효한 로컬 Pose를 과거/미래
                        // 서버 bbox로 제거하지 않고 진단 사유만 남깁니다.
                        if (!insideActualPerson && track) {
                            track.recoveryReason = 'SERVER_PERSON_TIMESTAMP_MISMATCH_BYPASSED';
                            localExclusionReasons.server_person_timestamp_mismatch_bypassed = Number(
                                localExclusionReasons.server_person_timestamp_mismatch_bypassed || 0
                            ) + 1;
                        }
                        // 서버 사람 결과와 무관하게 현재 프레임의 유효 Pose를 보존한다.
                        return Number(track.hits || 0) >= 1;
                    }
                    // 유효 keypoint가 충분한 첫 로컬 Pose부터 표시합니다. Track 지속성은
                    // 이후 프레임의 LIVE/HELD/PREDICTED 상태가 담당합니다.
                    if (Number(track.hits || 0) < 1) return reject('local_track_unstable');
                    return center.visible >= 10 || reject('local_pose_insufficient');
                });
                const renderablePoses = this.deduplicateRenderableMediaPipePoses(filteredRenderablePoses as any);
                const previousRenderedPoseCount = Number(this.mpDetectedPoseCount || 0);
                const preserveRenderedPose = renderablePoses.length === 0
                    && this.mpLastSkeletonRenderedAt > 0
                    && detectTs - this.mpLastSkeletonRenderedAt <= POSE_HOLD_MS;
                const visiblePoseCount = preserveRenderedPose ? previousRenderedPoseCount : renderablePoses.length;
                this.realtimePerfStats.renderedSkeletons = visiblePoseCount;
                this.realtimePerfStats.ghostSkeletonsRemoved = Math.max(0, trackedPoses.length - renderablePoses.length);
                this.mpDetectedPoseCount = visiblePoseCount;
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    video_timestamp_ms: Number(frameMeta?.videoTimestampMs ?? Number(videoEl.currentTime || 0) * 1000),
                    frame_id: Number(frameMeta?.frameId ?? Math.round(Number(videoEl.currentTime || 0) * 1000)),
                    captured_at_realtime_ms: Number(frameMeta?.capturedAt ?? detectTs),
                    inference_finished_at_realtime_ms: performance.now(),
                    capture_delay_ms: Math.max(0, detectTs - Number(frameMeta?.capturedAt ?? detectTs)),
                    pose_queue_length: this.mpPoseQueueLength,
                    pose_queue_max_length: this.mpPoseMaxQueueLength,
                    pose_inference_ms: poseInferenceMs,
                    pose_result_age_ms: Math.max(0, Number(videoEl.currentTime || 0) * 1000 - Number(frameMeta?.videoTimestampMs ?? Number(videoEl.currentTime || 0) * 1000)),
                    pose_detected_person_count: Math.max(
                        Number(this.realtimeDiagnostics?.pose_detected_person_count || 0),
                        trackedPoses.length,
                    ),
                    primary_track_candidate_count: hasFreshServerPerson
                        ? Math.min(trackedPoses.length, approvedBoxes.length)
                        : trackedPoses.length,
                    safe_skeleton_count: visiblePoseCount,
                    rendered_skeleton_count: visiblePoseCount,
                    render_skeleton_called: renderablePoses.length > 0,
                    input_skeleton_count: trackedPoses.length,
                    skeleton_canvas_visible: this.showClientPoseCanvasOverlay(),
                    skeleton_source: 'client-mediapipe',
                    person_detected: trackedPoses.length > 0,
                    pose_detected: liveTrackedPoses.length > 0,
                    track_active: this.mpPoseTracks.some(track => detectTs - Number(track.lastSeenMs || 0) <= PERSON_TRACK_TTL_MS),
                    pose_fresh: liveTrackedPoses.length > 0,
                    pose_predicted: renderablePoses.some((pose: any) => pose.poseState === 'PREDICTED'),
                    pose_state: renderablePoses.find((pose: any) => pose.poseState !== 'LIVE')?.poseState || 'LIVE',
                    pose_hold_remaining_ms: Math.max(0, POSE_HOLD_MS - (detectTs - this.mpLastPoseSeenMs)),
                    person_track_ttl_ms: PERSON_TRACK_TTL_MS,
                    pose_hold_ms: POSE_HOLD_MS,
                    track_recovery_window_ms: TRACK_RECOVERY_WINDOW_MS,
                    track_recovery_reason: liveTrackedPoses.find((pose: any) => pose.recoveryReason)?.recoveryReason || '',
                    local_track_diagnostics: this.mpPoseTracks.map(track => ({
                        frameId: Math.max(0, Math.round(Number(videoEl.currentTime || 0) * 1000)),
                        sourceTimestamp: Math.max(0, Number(videoEl.currentTime || 0) * 1000),
                        trackId: track.id,
                        trackAge: Math.max(0, detectTs - Number(track.createdAtMs || detectTs)),
                        lastSeenMs: Math.max(0, detectTs - Number(track.lastSeenMs || detectTs)),
                        trackState: track.poseState === 'LOST' ? 'LOST' : 'ACTIVE',
                        bboxIou: Number(track.lastBboxIou || 0),
                        centerDistance: Number.isFinite(track.lastCenterDistance) ? track.lastCenterDistance : null,
                        minimumTrackIou: Number(track.lastTrackIouThreshold || 0.25),
                        poseState: track.poseState,
                        poseHoldRemainingMs: Math.max(0, POSE_HOLD_MS - (detectTs - Number(track.lastPoseSeenMs || 0))),
                        trackTtlRemainingMs: Math.max(0, PERSON_TRACK_TTL_MS - (detectTs - Number(track.lastSeenMs || 0))),
                        trackRecoveryReason: track.recoveryReason || '',
                        logicalPersonId: track.logicalPersonId,
                        primaryTrackId: track.mergedIntoTrackId || track.id,
                        renderEligible: track.renderEligible,
                        mergedIntoTrackId: track.mergedIntoTrackId,
                    })),
                    duplicate_pose_candidate_count: this.mpDuplicatePoseCandidateCount,
                    duplicate_logical_render_count: this.mpDuplicateLogicalRenderCount,
                    merged_track_residual_render_count: this.mpMergedTrackResidualRenderCount,
                    keypoint_schema: 'mediapipe-pose-33',
                    expected_keypoint_count: 33,
                    edge_definition_version: 'mediapipe-pose-33-v1',
                    skeleton_exclusion_reasons: localExclusionReasons,
                    person_filter_reason: Object.keys(localExclusionReasons)[0] || '',
                };
                if (previousRenderedPoseCount !== visiblePoseCount) this.requestRealtimeUiRender();
                // HELD/PREDICTED 좌표는 화면 연속성에만 사용하고 낙상 후보 입력에는 넣지 않습니다.
                this.updateRealtimeLocalFallCandidate(
                    renderablePoses.filter((pose: any) => pose.poseState === 'LIVE'),
                    videoEl,
                    Number(frameMeta?.videoTimestampMs ?? Number(videoEl.currentTime || 0) * 1000),
                );
                // Pose 응답은 Persistent Track의 원본 좌표만 갱신합니다. 별도 RAF
                // 렌더 루프가 응답 사이에도 이 좌표를 계속 화면에 그립니다.
                this.updatePersistentSkeletonPoses(renderablePoses, detectTs);
                // 응답 직후 그리는 프레임도 RAF가 사용하는 동일한 안정화 좌표를
                // 사용해 raw Pose와 persistent Pose가 교대로 흔들리는 현상을 막습니다.
                const displayRenderablePoses = renderablePoses.map((pose: any) => {
                    const cached = this.mpPersistentRenderPoses.find(item => item.trackId === Number(pose?.trackId || 0));
                    return cached ? { ...pose, landmarks: cached.landmarks, poseState: cached.poseState } : pose;
                });
                const overlayStartedAt = performance.now();
                const replaceSkeletonFace = this.replaceSkeletonFaceWithExpressionFeatures();
                let renderedKeypointCount = 0;
                if (renderablePoses.length > 0) {
                    this.realtimeFastPoseStatus = 'RENDERING';
                    this.realtimeFastPoseCounters.render += 1;
                    this.clearCanvasFully(ctx, canvasEl, 'pose-render');
                }
                for (let poseIndex = 0; poseIndex < displayRenderablePoses.length; poseIndex++) {
                    const trackedPose = displayRenderablePoses[poseIndex];
                    const landmarks = trackedPose.landmarks || [];
                    const poseState: MpPoseState = trackedPose.poseState || 'LIVE';
                    const poseAlpha = Math.max(0.2, Math.min(1, Number(trackedPose.poseAlpha ?? 1)));
                    const filters = this.getPoseKalmanFiltersForTrack(trackedPose.trackId);
                    const personColor = MP_PERSON_COLORS[(trackedPose.trackId - 1) % MP_PERSON_COLORS.length];
                    const responsiveLineWidth = Math.max(1.8, Math.min(3.6, Math.min(w, h) / 240));
                    const responsivePointRadius = Math.max(3, Math.min(5.2, Math.min(w, h) / 120));
                    ctx.save();
                    ctx.globalAlpha = poseAlpha;
                    ctx.shadowColor = 'rgba(0,0,0,0.92)';
                    ctx.shadowBlur = 4;
                    const pts: { x: number; y: number; predicted: boolean; valid: boolean }[] = [];
                    for (let k = 0; k < landmarks.length; k++) {
                        const lm = landmarks[k];
                        const detected = poseState === 'LIVE' && this.mediaPipeLandmarkDetected(lm, k);
                        const displayVisible = poseState === 'LIVE' && this.mediaPipeLandmarkVisible(lm, k);
                        const kf = filters[k];
                        const p = this.normalizeMediaPipePoint(lm);
                        if (!kf) { pts.push({ x: p.x, y: p.y, predicted: poseState !== 'LIVE', valid: displayVisible || ['HELD', 'PREDICTED'].includes(poseState) }); continue; }
                        kf.x.predict(); kf.y.predict();
                        if (detected) {
                            const fx = kf.x.update(p.x); const fy = kf.y.update(p.y); kf.missCount = 0; pts.push({ x: fx, y: fy, predicted: false, valid: displayVisible });
                        } else {
                            kf.missCount++;
                            if (!MP_WRIST_LANDMARKS.has(k) && !MP_HAND_DETAIL_LANDMARKS.has(k) && kf.x.initialized && (['HELD', 'PREDICTED'].includes(poseState) || kf.missCount < this.KF_MAX_MISS)) {
                                pts.push({ x: kf.x.position, y: kf.y.position, predicted: true, valid: true });
                            }
                            else if (['HELD', 'PREDICTED'].includes(poseState) && !MP_HAND_DETAIL_LANDMARKS.has(k)) pts.push({ x: p.x, y: p.y, predicted: true, valid: true });
                            else pts.push({ x: 0, y: 0, predicted: false, valid: false });
                        }
                    }
                    for (const [i, j] of MP_POSE_CONNECTIONS) {
                        if (MP_HAND_DETAIL_LANDMARKS.has(i) || MP_HAND_DETAIL_LANDMARKS.has(j)) continue;
                        if (replaceSkeletonFace && (MP_FACE_LANDMARKS.has(i) || MP_FACE_LANDMARKS.has(j))) continue;
                        const a = pts[i], b = pts[j];
                        if (!a?.valid || !b?.valid) continue;
                        const pred = a.predicted || b.predicted;
                        ctx.beginPath(); ctx.moveTo(offX + a.x * w, offY + a.y * h); ctx.lineTo(offX + b.x * w, offY + b.y * h);
                        const partColor = MP_LANDMARK_COLORS[j] || MP_LANDMARK_COLORS[i] || personColor;
                        ctx.strokeStyle = this.colorWithAlpha(partColor, pred ? 0.28 : 0.90);
                        ctx.lineWidth = pred ? 1 : responsiveLineWidth; ctx.setLineDash(pred ? [4, 4] : []); ctx.stroke();
                    }
                    ctx.setLineDash([]);
                    let minX = 1, minY = 1, hasPoint = false;
                    for (let k = 0; k < pts.length; k++) {
                        if (MP_HAND_DETAIL_LANDMARKS.has(k)) continue;
                        if (replaceSkeletonFace && MP_FACE_LANDMARKS.has(k)) continue;
                        const pt = pts[k];
                        if (!pt.valid) continue;
                        renderedKeypointCount += 1;
                        minX = Math.min(minX, pt.x);
                        minY = Math.min(minY, pt.y);
                        hasPoint = true;
                        const partColor = MP_LANDMARK_COLORS[k] || personColor;
                        ctx.beginPath(); ctx.arc(offX + pt.x * w, offY + pt.y * h, pt.predicted ? Math.max(1.5, responsivePointRadius - 1) : responsivePointRadius, 0, 2 * Math.PI);
                        if (pt.predicted) { ctx.strokeStyle = this.colorWithAlpha(partColor, 0.45); ctx.lineWidth = 1; ctx.stroke(); }
                        else { ctx.fillStyle = this.colorWithAlpha(partColor, 0.96); ctx.fill(); }
                    }
                    if (hasPoint && this.overlayMode === 'DEBUG_RAW') {
                        const labelX = Math.max(offX + 2, Math.min(offX + minX * w, offX + w - 24));
                        const labelY = Math.max(offY + 14, Math.min(offY + minY * h - 4, offY + h - 4));
                        ctx.font = '11px sans-serif';
                        ctx.fillStyle = this.colorWithAlpha(personColor, 0.88);
                        ctx.fillText(`P${trackedPose.trackId} · ${poseState}`, labelX, labelY);
                    }
                    ctx.restore();
                }
                this.recordRealtimePerf('overlay', performance.now() - overlayStartedAt);
                if (renderedKeypointCount > 0) {
                    this.realtimeFastPoseCounters.draw += 1;
                    this.realtimeFastPoseStatus = 'TRACK_ACTIVE';
                }
                this.fastPoseLog('render', {
                    overlayMode: this.overlayMode,
                    trackCount: trackedPoses.length,
                    renderedSkeletonCount: renderablePoses.length,
                    renderedKeypointCount,
                    canvasWidth: canvasEl.width,
                    canvasHeight: canvasEl.height,
                    canvasRect: canvasEl.getBoundingClientRect(),
                });
                const poseRenderFinishedAt = performance.now();
                if (renderablePoses.length > 0 && this.mpPoseFirstRenderedAt <= 0) this.mpPoseFirstRenderedAt = poseRenderFinishedAt;
                if (renderablePoses.length > 0) {
                    this.mpFrameCount += 1;
                    if (this.mpLastSkeletonRenderedAt > 0) {
                        this.mpLongestSkeletonGapMs = Math.max(this.mpLongestSkeletonGapMs, poseRenderFinishedAt - this.mpLastSkeletonRenderedAt);
                    }
                    this.mpLastSkeletonRenderedAt = poseRenderFinishedAt;
                    const primaryTrackId = Number(renderablePoses[0]?.trackId || 0);
                    if (this.mpLastRenderedTrackId > 0 && primaryTrackId > 0 && primaryTrackId !== this.mpLastRenderedTrackId) this.mpTrackIdChangeCount += 1;
                    if (primaryTrackId > 0) this.mpLastRenderedTrackId = primaryTrackId;
                }
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    pose_render_delay_ms: Math.max(0, poseRenderFinishedAt - Number(frameMeta?.capturedAt ?? poseRenderFinishedAt)),
                    skeleton_first_display_ms: this.mpPoseFirstRenderedAt > 0
                        ? Math.max(0, this.mpPoseFirstRenderedAt - this.mpPoseFirstRequestedAt) : 0,
                    pose_completed_fps: this.mpFps,
                    longest_skeleton_gap_ms: this.mpLongestSkeletonGapMs,
                    track_id_change_count: this.mpTrackIdChangeCount,
                    video_natural_size: `${videoEl.videoWidth}x${videoEl.videoHeight}`,
                    capture_canvas_size: `${this.mpInputCanvas?.width || 0}x${this.mpInputCanvas?.height || 0}`,
                    capture_blob_bytes: 0,
                    captured_video_timestamp_ms: frame.videoTimestampMs,
                    fast_pose_status: this.realtimeFastPoseStatus,
                    fast_pose_counters: { ...this.realtimeFastPoseCounters },
                };
                const poseResultAgeMs = Math.abs(Number(videoEl.currentTime || 0) * 1000 - frame.videoTimestampMs);
                const dropReason: PoseDropReason = renderablePoses.length <= 0
                    ? (livePoses.length <= 0 ? 'POSE_RESPONSE_EMPTY' : poseConfidence < this.KF_VIS_THRESHOLD ? 'POSE_LOW_CONFIDENCE' : 'TRACK_ASSOCIATION_FAILED')
                    : poseResultAgeMs > POSE_HOLD_MS ? 'POSE_RESULT_STALE'
                        : poseResultAgeMs > POSE_TIMESTAMP_TOLERANCE_MS ? 'VIDEO_TIMESTAMP_MISMATCH' : '';
                this.recordPoseFrameDiagnostic(frame, dropReason, {
                    poseRequestStartedAt,
                    poseResponseReceivedAt: poseInferenceStartedAt + poseInferenceMs,
                    poseInferenceMs,
                    poseResultAgeMs,
                    poseKeypointCount: visiblePoseKeypoints.length,
                    poseConfidence,
                    personDetected: trackedPoses.length > 0,
                    trackId: Number(renderablePoses[0]?.trackId || 0),
                    trackState: String(renderablePoses[0]?.poseState || 'LOST'),
                    renderCalled: true,
                    renderedKeypointCount,
                });
                if (renderablePoses.length > 0) this.scheduleDeferredFaceMesh(videoEl);
            } else if (this.kalmanFilters.length > 0) {
                // 업로드 영상의 마지막 프레임이 비어 있어도 마지막 유효 Pose를
                // 보존합니다. 재생 중 miss가 poseHoldMs를 넘을 때만 stale overlay를 지웁니다.
                const preserveLastPoseFrame = (videoEl.ended || videoEl.paused) && this.mpLastPoseSeenMs > 0;
                const persistentTrackVisible = this.mpPersistentRenderPoses.some(pose => pose.validUntilMs > detectTs);
                if (!preserveLastPoseFrame && !persistentTrackVisible && detectTs - this.mpLastPoseSeenMs > POSE_HOLD_MS) {
                    const previousRenderedPoseCount = Number(this.mpDetectedPoseCount || 0);
                    this.mpDetectedPoseCount = 0;
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        safe_skeleton_count: 0,
                        rendered_skeleton_count: 0,
                        skeleton_exclusion_reasons: { pose_missing: 1 },
                        person_filter_reason: 'pose_missing',
                        pose_state: 'LOST',
                        pose_fresh: false,
                        pose_predicted: false,
                        pose_hold_remaining_ms: 0,
                    };
                    if (previousRenderedPoseCount > 0) this.requestRealtimeUiRender();
                }
                for (const filters of this.mpPoseKalmanFilters) {
                    for (const kf of filters) { kf.x.predict(); kf.y.predict(); kf.missCount++; }
                }
                for (const filters of Object.values(this.mpPoseTrackFilters)) {
                    for (const kf of filters) { kf.x.predict(); kf.y.predict(); kf.missCount++; }
                }
                this.recordPoseFrameDiagnostic(frame, 'POSE_RESPONSE_EMPTY', {
                    poseRequestStartedAt,
                    poseResponseReceivedAt: performance.now(),
                    poseInferenceMs,
                    poseKeypointCount: 0,
                    poseConfidence: 0,
                    personDetected: this.mpPoseTracks.length > 0,
                    trackId: Number(this.mpPoseTracks[0]?.id || 0),
                    trackState: String(this.mpPoseTracks[0]?.poseState || 'LOST'),
                    renderCalled: false,
                });
            } else {
                this.recordPoseFrameDiagnostic(frame, 'POSE_RESPONSE_EMPTY', {
                    poseRequestStartedAt,
                    poseResponseReceivedAt: performance.now(),
                    poseInferenceMs,
                    poseKeypointCount: 0,
                    poseConfidence: 0,
                    personDetected: false,
                    trackState: 'LOST',
                    renderCalled: false,
                });
            }
        } catch (e) {
            this.recordPoseFrameDiagnostic(frame, 'POSE_RESPONSE_EMPTY', { poseRequestStartedAt, poseResponseReceivedAt: performance.now() });
        } finally {
            this.mpProcessing = false;
            this.drainPoseFrameQueue();
        }
    }

    private scheduleDeferredFaceMesh(videoEl: HTMLVideoElement) {
        if (this.agentWeights.emotion <= 0) return;
        if (!this.faceLandmarker || this.faceMeshDeferredTimer || videoEl.paused || videoEl.ended) return;
        const elapsed = performance.now() - this.faceMeshLastInferenceMs;
        const delay = Math.max(750, this.faceMeshInferenceIntervalMs - elapsed);
        this.faceMeshDeferredTimer = setTimeout(() => {
            this.faceMeshDeferredTimer = null;
            if (!this.mpRunning || this.mpProcessing || videoEl.paused || videoEl.ended || !this.faceLandmarker) return;
            const detectTs = performance.now();
            try {
                this.detectAndDrawFaceMesh(this.getMediaPipeInputSource(videoEl), detectTs, videoEl);
            } catch (faceError) {
                this.handleFaceMeshMiss(detectTs);
                this.clearFaceMeshCanvas();
            }
        }, delay);
    }

    private detectAndDrawFaceMesh(mpInput: any, detectTs: number, videoEl: HTMLVideoElement) {
        if (!this.faceLandmarker) return;
        const startedAt = performance.now();
        const result = this.faceLandmarker.detectForVideo(mpInput, detectTs);
        const elapsedMs = Math.max(0, performance.now() - startedAt);
        this.faceMeshInferenceIntervalMs = Math.max(
            this.realtimePresetConfig().faceIntervalMs,
            elapsedMs > 42 ? 120 : (elapsedMs > 28 ? 82 : 66),
        );
        this.faceMeshLastInferenceMs = detectTs;
        this.faceMeshFrameCount++;

        const canvasEl = this._mpFaceCanvasEl?.nativeElement || document.querySelector('[data-mp-face-canvas]') as HTMLCanvasElement;
        if (!canvasEl) return;
        const prepared = this.prepareFaceMeshCanvas(canvasEl, videoEl);
        if (!prepared) return;
        const { ctx, width, height } = prepared;

        const faces = Array.isArray(result?.faceLandmarks) ? result.faceLandmarks : [];
        if (faces.length === 0) {
            if (!videoEl.ended && !videoEl.paused) ctx.clearRect(0, 0, width, height);
            this.handleFaceMeshMiss(detectTs);
            return;
        }
        ctx.clearRect(0, 0, width, height);

        let primaryIndex = 0;
        let primaryArea = -1;
        for (let index = 0; index < faces.length; index++) {
            const area = this.faceMeshArea(faces[index] || []);
            if (area > primaryArea) {
                primaryArea = area;
                primaryIndex = index;
            }
        }

        const displayRect = this.getVideoContentRect(videoEl);
        for (let index = 0; index < faces.length; index++) {
            const landmarks = faces[index] || [];
            const primary = index === primaryIndex;
            if (this.showPrivacyMaskOverlay()) {
                this.drawCleanFacePrivacyVeil(ctx, landmarks, displayRect);
                continue;
            }
            if (this.showFacialFeatureOverlay()) {
                this.drawExpressionFacialFeatures(ctx, landmarks, primary, displayRect);
                continue;
            }
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_TESSELATION, primary ? 'rgba(34,211,238,0.20)' : 'rgba(148,163,184,0.10)', primary ? 0.65 : 0.45, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL, primary ? 'rgba(45,212,191,0.92)' : 'rgba(148,163,184,0.45)', primary ? 1.6 : 1.0, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LIPS, primary ? 'rgba(251,113,133,0.95)' : 'rgba(148,163,184,0.42)', primary ? 1.55 : 0.9, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, primary ? 'rgba(167,139,250,0.95)' : 'rgba(148,163,184,0.42)', primary ? 1.45 : 0.9, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE, primary ? 'rgba(167,139,250,0.95)' : 'rgba(148,163,184,0.42)', primary ? 1.45 : 0.9, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYEBROW, primary ? 'rgba(250,204,21,0.92)' : 'rgba(148,163,184,0.42)', primary ? 1.45 : 0.9, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYEBROW, primary ? 'rgba(250,204,21,0.92)' : 'rgba(148,163,184,0.42)', primary ? 1.45 : 0.9, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS, primary ? 'rgba(253,224,71,0.98)' : 'rgba(148,163,184,0.42)', primary ? 1.8 : 1.0, displayRect);
            this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS, primary ? 'rgba(253,224,71,0.98)' : 'rgba(148,163,184,0.42)', primary ? 1.8 : 1.0, displayRect);
        }

        this.faceMeshDetected = true;
        this.faceMeshFaceCount = faces.length;
        this.faceMeshLandmarkCount = Number(faces[primaryIndex]?.length || 0);
        const categories = result?.faceBlendshapes?.[primaryIndex]?.categories || [];
        this.updateLiveFacialEmotion(categories, detectTs);
    }

    private drawExpressionFacialFeatures(
        ctx: CanvasRenderingContext2D,
        landmarks: any[],
        primary: boolean,
        displayRect: { x: number; y: number; w: number; h: number },
    ) {
        const muted = primary ? 1 : 0.48;
        const width = primary ? 2.0 : 1.15;
        // 조밀한 얼굴 삼각망은 숨기고 표정을 읽을 수 있는 윤곽·눈썹·눈·입만 표시한다.
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL, `rgba(45,212,191,${0.94 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LIPS, `rgba(251,113,133,${0.98 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, `rgba(167,139,250,${0.98 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE, `rgba(167,139,250,${0.98 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYEBROW, `rgba(250,204,21,${0.96 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYEBROW, `rgba(250,204,21,${0.96 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS, `rgba(253,224,71,${0.98 * muted})`, width, displayRect);
        this.drawFaceConnectionSet(ctx, landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS, `rgba(253,224,71,${0.98 * muted})`, width, displayRect);
    }

    private prepareFaceMeshCanvas(canvasEl: HTMLCanvasElement, videoEl: HTMLVideoElement): { ctx: CanvasRenderingContext2D; width: number; height: number } | null {
        const rect = canvasEl.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width || videoEl.clientWidth || videoEl.videoWidth || 1));
        const height = Math.max(1, Math.round(rect.height || videoEl.clientHeight || videoEl.videoHeight || 1));
        const dpr = Math.max(1, Math.min(2, Number(window.devicePixelRatio || 1)));
        const backingWidth = Math.max(1, Math.round(width * dpr));
        const backingHeight = Math.max(1, Math.round(height * dpr));
        if (canvasEl.width !== backingWidth || canvasEl.height !== backingHeight) {
            canvasEl.width = backingWidth;
            canvasEl.height = backingHeight;
        }
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return null;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        return { ctx, width, height };
    }

    private drawFaceConnectionSet(ctx: CanvasRenderingContext2D, landmarks: any[], connections: any[], color: string, lineWidth: number, rect: { x: number; y: number; w: number; h: number }) {
        if (!Array.isArray(landmarks) || !Array.isArray(connections) || landmarks.length === 0) return;
        ctx.beginPath();
        let hasLine = false;
        for (const connection of connections) {
            const start = landmarks[Number(connection?.start)];
            const end = landmarks[Number(connection?.end)];
            if (!start || !end) continue;
            const a = this.normalizeMediaPipePoint(start);
            const b = this.normalizeMediaPipePoint(end);
            ctx.moveTo(rect.x + a.x * rect.w, rect.y + a.y * rect.h);
            ctx.lineTo(rect.x + b.x * rect.w, rect.y + b.y * rect.h);
            hasLine = true;
        }
        if (!hasLine) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
    }

    private faceMeshArea(landmarks: any[]): number {
        if (!Array.isArray(landmarks) || landmarks.length === 0) return 0;
        let minX = 1, minY = 1, maxX = 0, maxY = 0;
        for (const landmark of landmarks) {
            const point = this.normalizeMediaPipePoint(landmark);
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        }
        return Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
    }

    private updateLiveFacialEmotion(categories: any[], nowMs: number) {
        const blendshapes: Record<string, number> = {};
        for (const item of categories || []) {
            const name = String(item?.categoryName || '');
            if (name) blendshapes[name] = Math.max(0, Math.min(1, Number(item?.score || 0)));
        }
        const score = (name: string) => Number(blendshapes[name] || 0);
        const average = (...names: string[]) => names.reduce((sum, name) => sum + score(name), 0) / Math.max(names.length, 1);
        const pair = (name: string) => average(name + 'Left', name + 'Right');
        const clamp = (value: number) => Math.max(0.001, Math.min(1, value));

        const smile = pair('mouthSmile');
        const frown = pair('mouthFrown');
        const dimple = pair('mouthDimple');
        const cheekSquint = pair('cheekSquint');
        const eyeWide = pair('eyeWide');
        const eyeSquint = pair('eyeSquint');
        const browDown = pair('browDown');
        const browOuterUp = pair('browOuterUp');
        const browInnerUp = score('browInnerUp');
        const jawOpen = score('jawOpen');
        const mouthPress = pair('mouthPress');
        const mouthStretch = pair('mouthStretch');
        const mouthShrug = (score('mouthShrugLower') + score('mouthShrugUpper')) / 2;
        const upperLip = pair('mouthUpperUp');
        const noseSneer = pair('noseSneer');
        const lowerDown = pair('mouthLowerDown');
        const asymSmile = Math.abs(score('mouthSmileLeft') - score('mouthSmileRight'));
        const asymDimple = Math.abs(score('mouthDimpleLeft') - score('mouthDimpleRight'));
        const asymPress = Math.abs(score('mouthPressLeft') - score('mouthPressRight'));
        const expressiveActivation = Math.max(smile, frown, eyeWide, browDown, browInnerUp, jawOpen, mouthPress, mouthStretch, noseSneer);

        const raw: Record<string, number> = {
            neutral: clamp(1.0 - expressiveActivation * 0.92 - Math.abs(smile - frown) * 0.10),
            happiness: clamp(0.04 + smile * 0.70 + cheekSquint * 0.22 + dimple * 0.14 - frown * 0.22),
            surprise: clamp(0.04 + eyeWide * 0.34 + browOuterUp * 0.24 + browInnerUp * 0.20 + jawOpen * 0.32 - smile * 0.12),
            sadness: clamp(0.04 + frown * 0.48 + browInnerUp * 0.28 + mouthShrug * 0.16 + lowerDown * 0.12 - smile * 0.18),
            anger: clamp(0.04 + browDown * 0.40 + eyeSquint * 0.22 + mouthPress * 0.24 + noseSneer * 0.18 - smile * 0.12),
            disgust: clamp(0.04 + noseSneer * 0.52 + upperLip * 0.24 + mouthShrug * 0.16 + mouthPress * 0.10),
            fear: clamp(0.04 + eyeWide * 0.30 + browInnerUp * 0.26 + mouthStretch * 0.28 + jawOpen * 0.18 - smile * 0.10),
            contempt: clamp(0.03 + asymSmile * 0.50 + asymDimple * 0.24 + asymPress * 0.16 + Math.max(score('mouthSmileLeft'), score('mouthSmileRight')) * 0.12),
        };

        const rawValues = Object.values(raw);
        const maxRaw = rawValues.length ? Math.max(...rawValues) : 0;
        const exponentials: Record<string, number> = {};
        let exponentialSum = 0;
        for (const emotion of LIVE_FACIAL_EMOTIONS) {
            const value = Math.exp((Number(raw[emotion.code] || 0) - maxRaw) / 0.17);
            exponentials[emotion.code] = value;
            exponentialSum += value;
        }

        const hasRecentHistory = Object.keys(this.liveFacialSmoothedScores).length > 0 && nowMs - this.faceMeshLastSeenMs < 900;
        const alpha = hasRecentHistory ? 0.32 : 1;
        let smoothedSum = 0;
        for (const emotion of LIVE_FACIAL_EMOTIONS) {
            const next = Number(exponentials[emotion.code] || 0) / Math.max(exponentialSum, 1e-9);
            const previous = Number(this.liveFacialSmoothedScores[emotion.code] || next);
            const smoothed = previous * (1 - alpha) + next * alpha;
            this.liveFacialSmoothedScores[emotion.code] = smoothed;
            smoothedSum += smoothed;
        }
        for (const emotion of LIVE_FACIAL_EMOTIONS) {
            this.liveFacialSmoothedScores[emotion.code] = Number(this.liveFacialSmoothedScores[emotion.code] || 0) / Math.max(smoothedSum, 1e-9);
        }

        const ranked = LIVE_FACIAL_EMOTIONS
            .map(emotion => ({ ...emotion, score: Number(this.liveFacialSmoothedScores[emotion.code] || 0) }))
            .sort((a, b) => b.score - a.score);
        const top = ranked[0] || { code: 'neutral', label: '중립', color: '#2dd4bf', score: 0 };
        let selectedCode = top.code;
        if (hasRecentHistory && this.liveFacialEmotion && top.code !== this.liveFacialEmotion) {
            const currentScore = Number(this.liveFacialSmoothedScores[this.liveFacialEmotion] || 0);
            if (top.score < currentScore + 0.045) {
                selectedCode = this.liveFacialEmotion;
            } else if (this.liveFacialCandidateCode !== top.code) {
                this.liveFacialCandidateCode = top.code;
                this.liveFacialCandidateSince = nowMs;
                selectedCode = this.liveFacialEmotion;
            } else if (nowMs - this.liveFacialCandidateSince < 260) {
                selectedCode = this.liveFacialEmotion;
            }
        }
        if (selectedCode === top.code) {
            this.liveFacialCandidateCode = '';
            this.liveFacialCandidateSince = 0;
        }

        const selected = ranked.find(item => item.code === selectedCode) || top;
        this.liveFacialEmotion = selected.code;
        this.liveFacialEmotionLabel = selected.label;
        this.liveFacialEmotionConfidence = Math.max(0, Math.min(99, Math.round(selected.score * 100)));
        this.liveFacialTop3 = ranked.slice(0, 3).map(item => ({
            code: item.code,
            label: item.label,
            score: item.score,
            percent: Math.max(0, Math.min(99, Math.round(item.score * 100))),
            color: item.color,
        }));
        this.faceMeshLastSeenMs = nowMs;
        this.scheduleFaceMeshUiRender(nowMs);
    }

    private handleFaceMeshMiss(nowMs: number) {
        if (this.faceMeshLastSeenMs > 0 && nowMs - this.faceMeshLastSeenMs <= 350) return;
        const wasDetected = this.faceMeshDetected;
        this.faceMeshDetected = false;
        this.faceMeshFaceCount = 0;
        this.faceMeshLandmarkCount = 0;
        if (nowMs - this.faceMeshLastSeenMs > 650) {
            this.liveFacialEmotionLabel = '얼굴 찾는 중';
            this.liveFacialEmotionConfidence = 0;
            this.liveFacialTop3 = [];
            this.liveFacialSmoothedScores = {};
            this.liveFacialCandidateCode = '';
            this.liveFacialCandidateSince = 0;
        }
        if (wasDetected || nowMs - this.faceMeshLastUiRenderMs >= 500) this.scheduleFaceMeshUiRender(nowMs);
    }

    private resetLiveFacialState() {
        this.faceMeshDetected = false;
        this.faceMeshFaceCount = 0;
        this.faceMeshLandmarkCount = 0;
        this.faceMeshFps = 0;
        this.faceMeshFrameCount = 0;
        this.liveFacialEmotion = 'neutral';
        this.liveFacialEmotionLabel = '얼굴 찾는 중';
        this.liveFacialEmotionConfidence = 0;
        this.liveFacialTop3 = [];
        this.liveFacialSmoothedScores = {};
        this.liveFacialCandidateCode = '';
        this.liveFacialCandidateSince = 0;
        this.faceMeshLastSeenMs = 0;
        this.faceMeshLastInferenceMs = 0;
        this.faceMeshInferenceIntervalMs = this.realtimePresetConfig().faceIntervalMs;
    }

    private currentLiveFacialSnapshot(): any {
        if (!this.faceMeshDetected || !this.liveFacialEmotionLabel) return null;
        return {
            source: 'mediapipe-face-blendshape-display-only',
            code: this.liveFacialEmotion,
            label: this.liveFacialEmotionLabel,
            confidence: this.liveFacialEmotionConfidence,
            top3: this.liveFacialTop3.map(item => ({ ...item })),
            face_count: this.faceMeshFaceCount,
            landmark_count: this.faceMeshLandmarkCount,
        };
    }

    private scheduleFaceMeshUiRender(nowMs: number) {
        if (nowMs - this.faceMeshLastUiRenderMs < 500) return;
        this.faceMeshLastUiRenderMs = nowMs;
        this.requestRealtimeUiRender();
    }

    private clearFaceMeshCanvas() {
        const canvasEl = this._mpFaceCanvasEl?.nativeElement || document.querySelector('[data-mp-face-canvas]') as HTMLCanvasElement;
        if (!canvasEl) return;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        ctx.restore();
    }

    private clearClientPoseCanvas() {
        const canvasEl = this._mpPoseCanvasEl?.nativeElement || document.querySelector('[data-mp-pose-canvas]') as HTMLCanvasElement;
        if (!canvasEl) return;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        ctx.restore();
        this.realtimeFastPoseCounters.clear += 1;
        if (this.realtimeDeveloperMode) console.debug('[SKELETON_CANVAS] clear', {
            caller: 'clearClientPoseCanvas',
            frameId: this.mpInFlightFrame?.frameId ?? -1,
            time: performance.now(),
        });
        if (this.mpRunning && this.mpLastSkeletonRenderedAt > 0 && this.mpDetectedPoseCount > 0) {
            const videoTimestampMs = Math.max(0, Number(this.webcamVideoRef?.nativeElement?.currentTime || 0) * 1000);
            this.recordPoseFrameDiagnostic({
                capturedAt: performance.now(),
                videoTimestampMs,
                frameId: Math.round(videoTimestampMs),
                eventCandidate: false,
            }, 'CANVAS_CLEARED_AFTER_RENDER');
        }
    }

    private colorWithAlpha(hex: string, alpha: number): string {
        const r = parseInt(hex.slice(1, 3), 16); const g = parseInt(hex.slice(3, 5), 16); const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${alpha})`;
    }

    public async startRealtimeAnalysis() {
        if (this.realtimeSourceMode === 'video' && this.realtimeUploadedVideoFile) {
            await this.startRealtimeUploadedAnalysis();
            return;
        }
        if (!this.hasRealtimeInputSource() || this.realtimeStarting || this.realtimeActive) return;
        this.realtimeStarting = true;
        this.realtimePerfStats = {
            avgRtt: 0, avgServer: 0, currentInterval: 0, discarded: 0, droppedFrames: 0,
            queued: 0, maxQueue: 0, retrying: 0, failed: 0, renderedSkeletons: 0,
            ghostSkeletonsRemoved: 0, faceCacheHits: 0, faceCacheMisses: 0,
            skippedChunks: 0, discardedShadow: 0, ringBufferFrames: 0,
        };
        this.realtimePerfSamples = {};
        this.realtimeLatestAppliedChunkId = -1;
        this.realtimeLatestShadowChunkId = -1;
        this.realtimeDegradedMode = false;
        this.realtimeHealthySince = 0;
        this.realtimeDelayedResultCount = 0;
        this.realtimeDiagnostics = {
            video_current_time: 0,
            analyzed_chunk_start: 0,
            analyzed_chunk_end: 0,
            result_generated_time: null,
            video_to_result_lag_ms: 0,
            raw_person_track_count: 0,
            logical_person_count: 0,
            pose_detected_person_count: 0,
            rendered_skeleton_count: 0,
            detected_environment_object_count: 0,
            rendered_environment_object_count: 0,
            filtered_person_count: 0,
            filtered_object_count: 0,
            stale_overlay_discard_count: 0,
            overlay_disposition: 'waiting',
            person_filter_reason: '',
            analysis_failed: false,
            display_mode: this.overlayMode,
            display_mode_label: this.overlayModeLabel(),
            render_skeleton_called: false,
            render_environment_called: false,
            input_skeleton_count: 0,
            input_environment_count: 0,
        };
        this.errorMessage = '';
        this.resetRealtimeLocalFallCandidate();
        await this.service.render();
        try {
            await this.warmupRealtimeModels();
            if (!this.hasRealtimeInputSource()) return;
            this.realtimeActive = true;
            this.realtimeSessionId = `rt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            this.realtimeSessionStartedAt = Date.now();
            this.realtimeChunkCount = 0;
            this.realtimeScoreHistory = [];
            this.realtimeCumulativeScore = 0;
            this.realtimeLogEntries = [];
            this.realtimeFeedSequence = 0;
            this.realtimeFeedLastTone = '';
            this.realtimeFeedLastRiskPercent = null;
            this.realtimeFeedPersonPresent = null;
            this.realtimeFeedPersonMissCount = 0;
            this.realtimeFeedActiveSignals.clear();
            this.realtimeLastElapsed = 0;
            this.postureTimeline = []; this.postureTransitions = []; this.currentPosture = ''; this.currentPostureLabel = ''; this.postureTransitionText = '';
            this.realtimeOverlayScore = 0; this.realtimeOverlayLevel = 'low'; this.realtimeOverlayLabel = '대기'; this.realtimeOverlayBasis = ''; this.lastNonEmptyOverlayBasis = '';
            this.realtimeRoundTripHistory = []; this.realtimeServerTimeHistory = [];
            this.dispatchQueue = []; this.dispatchQueueSize = 0; this.dispatchWorkerRunning = false; this.nextSlotId = 0; this.recorderSlots = []; this.realtimeWindowKeys.clear(); this.errorMessage = '';
            await this.service.render();
            if (this.realtimeSourceMode === 'video') {
                const videoEl = this.webcamVideoRef?.nativeElement;
                if (!videoEl) throw new Error('업로드 영상 플레이어를 준비하지 못했습니다.');
                videoEl.currentTime = 0;
                try {
                    await videoEl.play();
                } catch (e) {
                    await this.stopRealtimeAnalysis();
                    throw e;
                }
            }
            this.startRealtimeRingCapture();
            // 환경 객체/결합 모드에서는 Skeleton Pose loop와 독립된 저주기
            // snapshot lane을 웹캠에서도 즉시 시작합니다.
            this.ensureRealtimeEnvironmentSnapshotsForOverlay();
            const chunkCfg = this.getChunkConfig();
            if (chunkCfg.bootstrapDurations.length > 0) {
                for (const durationSec of chunkCfg.bootstrapDurations) this.spawnRecorderSlot(durationSec, durationSec);
            } else {
                this.spawnRecorderSlot(chunkCfg.chunkSec, chunkCfg.chunkSec);
            }
            // 첫 4초 창 뒤에는 새 2초분만 전송하고, 서버 rolling tail과 결합해
            // 4초 문맥/2초 stride를 유지한다. 겹치는 2초 프레임을 다시 추론하지 않는다.
            this.recorderSpawnTimer = setTimeout(() => {
                if (!this.realtimeActive) return;
                this.spawnRecorderSlot(chunkCfg.strideSec, chunkCfg.chunkSec);
                this.recorderSpawnTimer = setInterval(() => {
                    if (!this.realtimeActive) return;
                    this.spawnRecorderSlot(chunkCfg.strideSec, chunkCfg.chunkSec);
                }, chunkCfg.spawnMs);
            }, chunkCfg.chunkSec * 1000);
        } finally {
            this.realtimeStarting = false;
            await this.service.render();
        }
    }

    public canStartRealtimeAnalysis(): boolean {
        if (this.realtimeSourceMode === 'video') {
            return Boolean(this.realtimeUploadedVideoFile) && !this.realtimeUploadedAnalysisRunning;
        }
        return this.webcamReady;
    }

    private async startRealtimeUploadedAnalysis() {
        const videoFile = this.realtimeUploadedVideoFile;
        if (!videoFile || this.realtimeStarting || this.realtimeUploadedAnalysisRunning) return;
        this.realtimeStarting = true;
        this.realtimeActive = true;
        this.realtimeUploadedAnalysisError = '';
        this.errorMessage = '';
        await this.service.render();
        try {
            const videoEl = this.webcamVideoRef?.nativeElement;
            if (videoEl) {
                try {
                    if (videoEl.ended) videoEl.currentTime = 0;
                    await videoEl.play();
                } catch (e) { }
                await this.initMediaPipePose();
            }
            await this.analyzeRealtimeUploadedFile(videoFile);
        } finally {
            this.realtimeStarting = false;
            await this.service.render();
        }
    }

    private spawnRecorderSlot(durationSec?: number, analysisDurationSec?: number) {
        if (!this.realtimeActive || !this.hasRealtimeInputSource() || !this.realtimeAnalysisStream) return;
        const chunkCfg = this.getChunkConfig();
        if (this.recorderSlots.length >= chunkCfg.maxSlots) return;
        const actualDurationSec = Number(durationSec || chunkCfg.chunkSec || 5);
        const logicalDurationSec = Number(analysisDurationSec || actualDurationSec);
        try {
            const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8') ? 'video/webm;codecs=vp8' : 'video/webm';
            const recorder = new MediaRecorder(this.realtimeAnalysisStream, {
                mimeType,
                videoBitsPerSecond: 500000,
            });
            const chunks: Blob[] = [];
            const slotId = this.nextSlotId++;
            const slot = {
                recorder,
                chunks,
                timer: null as any,
                slotId,
                durationSec: actualDurationSec,
                startedAtMs: Date.now(),
                sourceStartTime: this.realtimeSourceTime(),
            };
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const captureStartedAt = performance.now();
                const idx = this.recorderSlots.indexOf(slot);
                if (idx >= 0) this.recorderSlots.splice(idx, 1);
                if (!this.realtimeActive) return;
                const chunkId = this.realtimeChunkCount++;
                if (chunks.length > 0) {
                    const encodeStartedAt = performance.now();
                    const blob = new Blob(chunks, { type: 'video/webm' });
                    this.recordRealtimePerf('encode', performance.now() - encodeStartedAt);
                    this.recordRealtimePerf('chunkFinalize', performance.now() - captureStartedAt);
                    const overlapSec = Math.max(0, logicalDurationSec - actualDurationSec);
                    this.enqueueChunk(
                        blob,
                        chunkId,
                        logicalDurationSec,
                        this.buildRealtimeSourceWindow(Math.max(0, slot.sourceStartTime - overlapSec), logicalDurationSec),
                    );
                }
            };
            recorder.start(500);
            slot.timer = setTimeout(() => { if (recorder.state === 'recording') { try { recorder.stop(); } catch (e) { } } }, actualDurationSec * 1000);
            this.recorderSlots.push(slot);
        } catch (e: any) {
            this.errorMessage = '녹화 실패: ' + (e.message || e);
            this.realtimeActive = false;
            this.service.render();
        }
    }

    private realtimeWindowKey(chunkWindow: any): string {
        const start = Math.round(Number(chunkWindow?.start_sec ?? chunkWindow?.startSec ?? 0));
        const end = Math.round(Number(chunkWindow?.end_sec ?? chunkWindow?.endSec ?? start));
        return `${start}-${end}`;
    }

    private enqueueChunk(blob: Blob, chunkId: number, durationSec: number, chunkWindow: any) {
        const key = this.realtimeWindowKey(chunkWindow);
        if (this.realtimeWindowKeys.has(key)) {
            this.realtimePerfStats.discarded = (this.realtimePerfStats.discarded || 0) + 1;
            return;
        }
        this.realtimeWindowKeys.add(key);
        const item = { blob, chunkId, durationSec, chunkWindow, facialSnapshot: this.currentLiveFacialSnapshot(), attempt: 0 };
        if (this.dispatchQueue.length > 0) {
            const dropped = this.dispatchQueue.length;
            this.dispatchQueue = [];
            this.realtimePerfStats.discarded = Number(this.realtimePerfStats.discarded || 0) + dropped;
            this.realtimePerfStats.droppedFrames = Number(this.realtimePerfStats.droppedFrames || 0) + dropped;
        }
        this.dispatchQueue.push(item);
        this.dispatchQueueSize = this.dispatchQueue.length;
        this.realtimePerfStats.queued = this.dispatchQueueSize;
        this.realtimePerfStats.maxQueue = Math.max(Number(this.realtimePerfStats.maxQueue || 0), this.dispatchQueueSize);
        if (!this.dispatchWorkerRunning) this.runDispatchWorker();
    }

    private async runDispatchWorker() {
        if (this.dispatchWorkerRunning) return;
        this.dispatchWorkerRunning = true;
        while (this.dispatchQueue.length > 0) {
            const item = this.dispatchQueue.shift()!;
            this.dispatchQueueSize = this.dispatchQueue.length;
            this.realtimePerfStats.queued = this.dispatchQueueSize;
            const ok = await this.dispatchRealtimeChunk(item.blob, item.chunkId, item.durationSec, item.chunkWindow, item.facialSnapshot);
            if (!ok && this.realtimeActive && Number(item.attempt || 0) < 2 && this.dispatchQueue.length === 0) {
                item.attempt = Number(item.attempt || 0) + 1;
                this.realtimePerfStats.retrying = item.attempt;
                this.dispatchQueue.push(item);
                this.dispatchQueueSize = this.dispatchQueue.length;
                this.realtimePerfStats.queued = this.dispatchQueueSize;
                await new Promise(resolve => setTimeout(resolve, Math.min(3000, 600 * Number(item.attempt || 0))));
            }
        }
        this.dispatchWorkerRunning = false;
        this.dispatchQueueSize = this.dispatchQueue.length;
        this.realtimePerfStats.queued = this.dispatchQueueSize;
    }

    public async stopRealtimeAnalysis() {
        if (this.realtimeBenchmarkRunning) await this.stopRealtimeBenchmark(true);
        this.realtimeStarting = false; this.realtimeActive = false; this.realtimeSessionId = ''; this.realtimeSessionStartedAt = 0;
        if (this.realtimeEndFinalizeTimer) { clearTimeout(this.realtimeEndFinalizeTimer); this.realtimeEndFinalizeTimer = null; }
        this.stopRealtimeEnvironmentSnapshots();
        this.resetRealtimeLocalFallCandidate();
        if (this.realtimeUploadedAnalysisController) {
            this.realtimeUploadedAnalysisRunId += 1;
            this.realtimeUploadedAnalysisController.abort();
            this.realtimeUploadedAnalysisController = null;
            this.realtimeUploadedAnalysisRunning = false;
        }
        this.stopRealtimeRingCapture();
        for (const timer of Object.values(this.realtimeShadowPollTimers)) clearTimeout(timer);
        this.realtimeShadowPollTimers = {};
        if (this.realtimeSourceMode === 'video') {
            try { this.webcamVideoRef?.nativeElement?.pause(); } catch (e) { }
        }
        if (this.recorderSpawnTimer) { clearInterval(this.recorderSpawnTimer); this.recorderSpawnTimer = null; }
        for (const slot of this.recorderSlots) {
            if (slot.timer) clearTimeout(slot.timer);
            if (slot.recorder.state === 'recording') { try { slot.recorder.stop(); } catch (e) { } }
        }
        this.recorderSlots = []; this.dispatchQueue = []; this.dispatchQueueSize = 0; this.realtimeWindowKeys.clear();
        this.realtimeOverlayPendingResult = null;
        if (this.realtimeOverlayRafId) { cancelAnimationFrame(this.realtimeOverlayRafId); this.realtimeOverlayRafId = 0; }
        this.clearRealtimeServerDetectionOverlay();
        if (this.benchmarkReferenceSourceActive) {
            this.stopBenchmarkReferenceSource();
            this.stopWebcamStream();
        }
        await this.service.render();
    }

    private postRealtimeFormData(url: string, body: FormData): Promise<{ status: number; text: string; uploadMs: number; waitingMs: number; networkReturnMs: number }> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const startedAt = performance.now();
            let uploadCompletedAt = startedAt;
            let headersReceivedAt = 0;
            xhr.open('POST', url, true);
            xhr.upload.onloadend = () => {
                uploadCompletedAt = performance.now();
                this.recordRealtimePerf('upload', uploadCompletedAt - startedAt);
            };
            xhr.onreadystatechange = () => {
                if (xhr.readyState >= 2 && headersReceivedAt <= 0) headersReceivedAt = performance.now();
            };
            xhr.onload = () => {
                const completedAt = performance.now();
                const responseAt = headersReceivedAt > 0 ? headersReceivedAt : completedAt;
                const waitingMs = Math.max(0, responseAt - uploadCompletedAt);
                const networkReturnMs = Math.max(0, completedAt - responseAt);
                this.recordRealtimePerf('waiting', waitingMs);
                this.recordRealtimePerf('networkReturn', networkReturnMs);
                resolve({
                    status: xhr.status,
                    text: String(xhr.responseText || ''),
                    uploadMs: Math.max(0, uploadCompletedAt - startedAt),
                    waitingMs,
                    networkReturnMs,
                });
            };
            xhr.onerror = () => reject(new Error('실시간 분석 요청 네트워크 오류'));
            xhr.onabort = () => reject(new Error('실시간 분석 요청이 취소되었습니다.'));
            xhr.send(body);
        });
    }

    private parseRealtimeResponse(text: string): any {
        const trimmed = String(text || '').trim();
        if (!trimmed) throw new SyntaxError('실시간 분석 서버가 빈 응답을 반환했습니다.');
        return JSON.parse(trimmed);
    }

    private updateRealtimeDegradedMode(result: any, sourceDelaySec: number) {
        const serverP95 = this.realtimePerfSummary('serverTotal').p95;
        const queueWaitMs = Number(result?.server_timing?.request_queue_wait_ms || 0);
        const degraded = serverP95 > 1500 || queueWaitMs > 500 || sourceDelaySec > 2;
        if (degraded) {
            this.realtimeDegradedMode = true;
            this.realtimeHealthySince = 0;
            return;
        }
        if (!this.realtimeDegradedMode) return;
        if (!this.realtimeHealthySince) this.realtimeHealthySince = Date.now();
        if (Date.now() - this.realtimeHealthySince >= 10000) {
            this.realtimeDegradedMode = false;
            this.realtimeHealthySince = 0;
        }
    }

    private realtimePoseEvidence(det: any): {
        valid: boolean;
        visible: number;
        averageConfidence: number;
        score: number;
    } {
        const keypoints = Array.isArray(det?.keypoints) ? det.keypoints.slice(0, 17) : [];
        const visible = keypoints.filter((kp: any, index: number) => this.cocoKeypointVisible(kp, index));
        const averageConfidence = visible.length > 0
            ? visible.reduce((sum: number, kp: any) => sum + Number(kp?.[2] || 0), 0) / visible.length
            : 0;
        const bbox = [det?.x1, det?.y1, det?.x2, det?.y2].map(Number);
        const bboxValid = bbox.every(Number.isFinite) && bbox[2] > bbox[0] && bbox[3] > bbox[1];
        const valid = bboxValid && keypoints.length >= 17 && visible.length >= 6 && averageConfidence >= 0.25;
        return {
            valid,
            visible: visible.length,
            averageConfidence,
            score: visible.length + averageConfidence * 10 + Number(det?.conf || 0),
        };
    }

    private realtimeDetectionDecision(det: any, result?: any): { renderable: boolean; reason: string } {
        const pose = this.realtimePoseEvidence(det);
        const classifiedAsPerson = this.detectionEntityType(det, result) === 'person';
        // 유효한 사람 형태의 bbox와 Pose는 장비 Ghost/사람 후처리보다 우선 보존한다.
        if (!classifiedAsPerson && !pose.valid) return { renderable: false, reason: 'person_filter_excluded' };
        if (this.isDebugOverlayMode()) return { renderable: true, reason: 'debug_raw' };
        const logicalPerson = this.matchedLogicalPerson(det, result);
        if (!logicalPerson || !String(logicalPerson?.logical_person_id || '').trim()) {
            return pose.valid
                ? { renderable: true, reason: classifiedAsPerson ? 'valid_pose_fallback' : 'actual_person_pose_override' }
                : { renderable: false, reason: 'logical_person_pending' };
        }
        const detectionTrackId = String(det?.track_id ?? det?.source_track_id ?? '').trim();
        const primaryTrackId = String(logicalPerson?.primary_track_id ?? logicalPerson?.track_id ?? '').trim();
        const logicalTrackIds = new Set([
            primaryTrackId,
            ...(Array.isArray(logicalPerson?.source_track_ids) ? logicalPerson.source_track_ids.map((value: any) => String(value)) : []),
            ...(Array.isArray(logicalPerson?.alias_track_ids) ? logicalPerson.alias_track_ids.map((value: any) => String(value)) : []),
        ].filter(Boolean));
        const frames = Array.isArray(result?.model_runtime?.detection_frames)
            ? result.model_runtime.detection_frames : [];
        const latestFrame = frames.length > 0 ? frames[frames.length - 1] : null;
        const activeLogicalDetections = (Array.isArray(latestFrame?.detections) ? latestFrame.detections : [])
            .filter((candidate: any) => logicalTrackIds.has(
                String(candidate?.track_id ?? candidate?.source_track_id ?? '').trim()
            ))
            .filter((candidate: any) => this.realtimePoseEvidence(candidate).valid)
            .sort((left: any, right: any) => (
                this.realtimePoseEvidence(right).score - this.realtimePoseEvidence(left).score
            ));
        // 원 primary가 현재 프레임에서 유효한 Pose를 잃었으면 같은 logical person의
        // 가장 좋은 활성 alias를 선택해 Skeleton이 통째로 사라지지 않게 한다.
        const preferredDetection = activeLogicalDetections.find((candidate: any) => (
            String(candidate?.track_id ?? candidate?.source_track_id ?? '').trim() === primaryTrackId
        )) || activeLogicalDetections[0];
        const preferredTrackId = String(
            preferredDetection?.track_id ?? preferredDetection?.source_track_id ?? primaryTrackId
        ).trim();
        if (detectionTrackId && preferredTrackId && detectionTrackId !== preferredTrackId) {
            return { renderable: false, reason: 'alias_track_hidden' };
        }
        if (!Array.isArray(det?.keypoints) || det.keypoints.length < 17) return { renderable: false, reason: 'pose_missing' };
        if (pose.visible < 6) return { renderable: false, reason: 'insufficient_keypoints' };
        if (pose.averageConfidence < 0.25) return { renderable: false, reason: 'low_keypoint_confidence' };
        return {
            renderable: true,
            reason: classifiedAsPerson ? 'visible' : 'actual_person_pose_override',
        };
    }

    private updateRealtimeDiagnosticResult(result: any, sourceDelaySec: number, overlayDisposition: string) {
        const environmentSnapshotOnly = result?._environment_snapshot_only === true;
        const frames = result?.model_runtime?.detection_frames || [];
        const frame = Array.isArray(frames) && frames.length > 0 ? frames[frames.length - 1] : null;
        const detections = Array.isArray(frame?.detections) ? frame.detections : [];
        const multi = result?.multi_person || {};
        const normalizedEnvironmentResult = this.normalizeEnvironmentAnalysis(
            result, environmentSnapshotOnly ? 'realtime' : 'final_analysis', Number(result?.source_timestamp || 0),
        );
        const environmentFrame = this.environmentFrameForTimestamp(
            normalizedEnvironmentResult?.environment_analysis, this.realtimeSourceTime(),
        );
        const environmentDetections = Array.isArray(environmentFrame?.detections) ? environmentFrame.detections : [];
        const environmentRaw = normalizedEnvironmentResult?.environment_analysis?.raw_diagnostics || {};
        const environmentPerformance = normalizedEnvironmentResult?.environment_analysis?.performance || {};
        const environmentTimestampMs = Number(environmentFrame?.source_timestamp_ms ?? Number(environmentFrame?.time_sec || 0) * 1000);
        const environmentResultAgeMs = Math.max(0, this.realtimeSourceTime() * 1000 - environmentTimestampMs);
        const trackDiagnostics = multi?.track_diagnostics || {};
        const mergeDiagnostics = multi?.merge_diagnostics || {};
        const poseTrackIds = new Set(
            detections
                .filter((det: any) => Array.isArray(det?.keypoints) && det.keypoints.length >= 17)
                .map((det: any, index: number) => String(det?.track_id ?? det?.source_track_id ?? `pose-${index}`)),
        );
        const decisions = detections.map((det: any) => this.realtimeDetectionDecision(det, result));
        const firstHidden = decisions.find(item => !item.renderable);
        const exclusionReasons = decisions
            .filter(item => !item.renderable)
            .reduce((counts: Record<string, number>, item: any) => {
                counts[item.reason] = Number(counts[item.reason] || 0) + 1;
                return counts;
            }, {});
        const primaryCandidates = new Set(
            detections
                .filter((det: any) => this.realtimePoseEvidence(det).valid)
                .map((det: any, index: number) => {
                    const person = this.matchedLogicalPerson(det, result);
                    return String(
                        person?.logical_person_id
                        || person?.primary_track_id
                        || det?.track_id
                        || det?.source_track_id
                        || `pose-${index}`
                    );
                }),
        );
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            video_current_time: this.realtimeSourceTime(),
            analyzed_chunk_start: Number(result?.source_start_time || 0),
            analyzed_chunk_end: Number(result?.source_end_time || 0),
            result_generated_time: result?.result_generated_time || null,
            source_timestamp: Number(result?.source_timestamp ?? result?.source_end_time ?? 0),
            inference_started_at: Number(result?.inference_started_at || 0),
            completed_at: Number(result?.completed_at || result?.result_generated_time || 0),
            overlay_age_ms: Math.max(0, sourceDelaySec * 1000),
            video_to_result_lag_ms: Math.max(0, sourceDelaySec * 1000),
            raw_person_track_count: Number(multi?.raw_track_count ?? multi?.raw_person_track_count ?? detections.length ?? 0),
            logical_person_count: Number(multi?.logical_person_count ?? multi?.person_count ?? 0),
            pose_detected_person_count: poseTrackIds.size,
            primary_track_candidate_count: primaryCandidates.size,
            safe_skeleton_count: decisions.filter(item => item.renderable).length,
            rendered_skeleton_count: environmentSnapshotOnly
                ? Number(this.realtimeDiagnostics?.rendered_skeleton_count || 0) : 0,
            skeleton_exclusion_reasons: exclusionReasons,
            detected_environment_object_count: environmentDetections.length,
            environment_inference_status: String(normalizedEnvironmentResult?.environment_analysis?.status || 'waiting'),
            environment_inference_fps: Number(environmentPerformance?.effective_environment_fps || normalizedEnvironmentResult?.environment_analysis?.sample_fps || 0),
            environment_inference_latency_ms: Number(environmentPerformance?.total_pipeline_ms || 0),
            environment_result_age_ms: environmentResultAgeMs,
            environment_tracked_count: environmentDetections.filter((item: any) => ['tracked', 'cached'].includes(String(item?.source || item?.environment_source || ''))).length,
            environment_source: String(environmentDetections[0]?.source || environmentDetections[0]?.environment_source || (environmentSnapshotOnly ? 'realtime' : 'final_analysis')),
            environment_coordinate_space: String(environmentFrame?.coordinate_space || 'source_pixel'),
            environment_source_frame_size: environmentFrame ? `${environmentFrame?.source_frame_width || environmentFrame?.coordinate_width}x${environmentFrame?.source_frame_height || environmentFrame?.coordinate_height}` : '',
            raw_environment_candidate_count: Number(environmentRaw?.model_box_count || 0),
            environment_filter_drop_count: Number(environmentRaw?.filter_drop_count || 0),
            environment_zero_reason: String(environmentRaw?.zero_result_reason || ''),
            environment_raw_candidates: Array.isArray(environmentRaw?.candidates) ? environmentRaw.candidates : [],
            track_fragments: Array.isArray(trackDiagnostics?.fragments) ? trackDiagnostics.fragments : [],
            merge_evaluations: Array.isArray(mergeDiagnostics?.overlapping_duplicate_evaluations)
                ? mergeDiagnostics.overlapping_duplicate_evaluations : [],
            ghost_suppression_evaluations: Array.isArray(result?.ghost_suppression?.evaluations)
                ? result.ghost_suppression.evaluations : [],
            ghost_suppressed_count: Number(result?.ghost_suppression?.suppressed_count || 0),
            rendered_environment_object_count: environmentSnapshotOnly
                ? Number(this.realtimeDiagnostics?.rendered_environment_object_count || 0) : 0,
            filtered_person_count: decisions.filter(item => !item.renderable).length,
            filtered_object_count: environmentDetections.length,
            stale_overlay_discard_count: this.realtimeDelayedResultCount,
            overlay_disposition: overlayDisposition,
            person_filter_reason: firstHidden?.reason || '',
            analysis_failed: Boolean(result?.runtime_warning || result?.error),
        };
    }

    private mergeRealtimeUploadShadowResult(shadowResult: any): any {
        const current = this.currentRootAnalysisResult() || {};
        const merged = {
            ...current,
            ...(shadowResult || {}),
            runtime_inference: {
                ...(current?.runtime_inference || {}),
                ...(shadowResult?.runtime_inference || {}),
            },
            model_runtime: {
                ...(current?.model_runtime || {}),
                ...(shadowResult?.model_runtime || {}),
            },
        };
        if (shadowResult?.environment_analysis && current?.environment_analysis) {
            const environmentMerged = this.mergeRealtimeAndFinalEnvironment(shadowResult, current);
            merged.environment_analysis = environmentMerged.environment_analysis;
            merged.client_coordinate_metadata = environmentMerged.client_coordinate_metadata;
        }
        if (!shadowResult?.environment_analysis && current?.environment_analysis) {
            merged.environment_analysis = current.environment_analysis;
        }
        if (!shadowResult?.chunk_analysis && current?.chunk_analysis) {
            merged.chunk_analysis = current.chunk_analysis;
        }
        const shadow = this.fallEventShadow(merged) || {};
        const fallActive = shadow?.fall_event_detected === true
            || shadow?.fall_event_candidate_detected === true
            || merged?.fall_detected === true
            || ['FALL', 'FALL_CANDIDATE', 'DESCENT_CANDIDATE'].includes(String(shadow?.decision_state || ''));
        if (fallActive) {
            const promotedRisk = Math.max(
                Number(current?.displayed_risk_score ?? current?.event_risk_score ?? current?.risk_score ?? 0),
                Number(merged?.displayed_risk_score ?? merged?.event_risk_score ?? merged?.risk_score ?? 0),
                Number(merged?.fall_risk_score ?? shadow?.fall_risk_score ?? 0),
            );
            merged.displayed_risk_score = promotedRisk;
            merged.event_risk_score = Math.max(Number(merged?.event_risk_score || 0), promotedRisk);
            merged.fall_risk_score = Math.max(Number(merged?.fall_risk_score || 0), Number(shadow?.fall_risk_score || 0));
            merged.fall_detected = true;
            if (!merged.decision_state || ['safe', 'posture_only'].includes(String(merged.decision_state))) {
                merged.decision_state = 'fall_suspected';
            }
            if (!merged.final_event_state || ['SAFE', 'POSTURE_ONLY'].includes(String(merged.final_event_state))) {
                merged.final_event_state = 'FALL_SUSPECTED';
            }
            if (!merged.behavior_label || merged.behavior_label === '정상') merged.behavior_label = '낙상';
        }
        return merged;
    }

    private realtimeResultEventWindow(result: any, fallbackWindow: any): any {
        const shadow = this.fallEventShadow(result) || {};
        const interval = result?.fall_event_interval ?? shadow?.fall_event_interval;
        let start = Number(Array.isArray(interval) ? interval[0] : interval?.start_time ?? interval?.start_sec);
        let end = Number(Array.isArray(interval) ? interval[1] : interval?.end_time ?? interval?.end_sec);
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return fallbackWindow;
        const eventTimeSec = start;
        start = Math.max(0, start - 1.5);
        end = Math.max(start, end + 2.0);
        return {
            startSec: start,
            endSec: end,
            eventTimeSec,
            label: `낙상 사건 · ${start.toFixed(2)}~${end.toFixed(2)}초`,
        };
    }

    private pollRealtimeShadow(job: any, chunkId: number, chunkWindow: any, facialSnapshot: any) {
        const jobId = String(job?.job_id || '');
        if (!jobId) return;
        const analysisRunId = this.realtimeUploadedAnalysisRunId;
        const videoSessionId = this.realtimeSessionId;
        const startedAt = performance.now();
        const pollTimeoutMs = this.realtimeSourceMode === 'video' ? 120000 : 12000;
        const poll = async () => {
            if (!this.realtimeActive && this.realtimeSourceMode !== 'video') return;
            if (this.realtimeSourceMode === 'video' && !this.canAcceptRealtimePrecisionResult(analysisRunId, videoSessionId)) {
                delete this.realtimeShadowPollTimers[jobId];
                return;
            }
            try {
                const query = new URLSearchParams({
                    action: 'realtime_shadow_status',
                    realtime_session_id: String(job?.session_id || this.realtimeSessionId),
                    chunk_id: String(chunkId),
                    request_id: String(job?.request_id || ''),
                });
                const response = await fetch(`/wiz/api/page.dashboard/prototype_info?${query.toString()}`);
                const json = await this.readJsonResponse(response);
                if (this.realtimeSourceMode === 'video' && !this.canAcceptRealtimePrecisionResult(analysisRunId, videoSessionId)) {
                    delete this.realtimeShadowPollTimers[jobId];
                    return;
                }
                const data = json?.data || {};
                if (data?.status === 'completed') {
                    delete this.realtimeShadowPollTimers[jobId];
                    this.realtimePrecisionAnalysisPending = false;
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        precision_analysis_status: 'completed',
                        precision_analysis_elapsed_ms: Math.max(0, performance.now() - startedAt),
                    };
                    if (this.realtimeSourceMode === 'video') this.realtimeUploadedAnalysisComplete = true;
                    this.recordRealtimePerf('shadow', Number(data?.shadow_ms || 0));
                    this.recordRealtimePerf('emotionAux', Number(data?.emotion_aux_ms || 0));
                    this.recordRealtimePerf('auxiliaryWorker', Number(data?.auxiliary_worker_ms || 0));
                    this.recordRealtimePerf('environmentAux', Number(data?.environment_aux_ms || 0));
                    // 업로드 원본 정밀 결과는 source pixel 좌표이므로 480px 캡처의
                    // letterbox 메타데이터를 붙이지 않습니다.
                    const shadowResult = this.realtimeSourceMode === 'video'
                        ? this.normalizeEnvironmentAnalysis(data?.result || {}, 'final_analysis')
                        : this.attachRealtimeCoordinateMetadata(data?.result || {});
                    const result = this.realtimeSourceMode === 'video'
                        ? this.mergeRealtimeUploadShadowResult(shadowResult)
                        : shadowResult;
                    if (chunkId < this.realtimeLatestAppliedChunkId) {
                        if (result?.environment_analysis) {
                            const current = this.currentRootAnalysisResult() || {};
                            const merged = {
                                ...current,
                                environment_analysis: result.environment_analysis,
                                _environment_snapshot_only: true,
                            };
                            this.applyAnalysisRoot(merged, true);
                            this.updateRealtimeDiagnosticResult(merged, 0, 'environment_snapshot');
                            this.scheduleRealtimeServerDetectionOverlay(merged);
                            this.requestRealtimeUiRender();
                        }
                        this.realtimePerfStats.discardedShadow = Number(this.realtimePerfStats.discardedShadow || 0) + 1;
                        return;
                    }
                    this.realtimeLatestShadowChunkId = chunkId;
                    this.applyAnalysisRoot(result, true);
                    if (this.realtimeSourceMode === 'video') {
                        if (Array.isArray(result?.environment_analysis?.frames)
                            && result.environment_analysis.frames.length > 0) {
                            this.realtimeEnvironmentSnapshotResult = result;
                        }
                        this.stopRealtimeEnvironmentSnapshots(true);
                        this.realtimeEnvironmentSnapshotCount = Number(
                            result?.environment_analysis?.object_count
                            ?? result?.environment_analysis?.objects?.length
                            ?? 0
                        );
                        this.finalizeRealtimeLocalFallCandidate(result);
                    }
                    if (this.realtimeSourceMode === 'video') this.syncUploadChunkLogs(result);
                    this.updateRealtimeOverlay();
                    const sourceDelaySec = Math.max(0, this.realtimeSourceTime() - Number(result?.source_end_time ?? chunkWindow?.endSec ?? 0));
                    if (sourceDelaySec <= 2) {
                        this.updateRealtimeDiagnosticResult(result, sourceDelaySec, sourceDelaySec > 1.5 ? 'delayed_visible' : 'scheduled');
                        this.scheduleRealtimeServerDetectionOverlay(result);
                    } else {
                        this.realtimeDelayedResultCount += 1;
                        this.updateRealtimeDiagnosticResult(result, sourceDelaySec, 'stale_discarded');
                        this.clearRealtimeSkeletonOverlay();
                    }
                    const logStartedAt = performance.now();
                    const eventWindow = this.realtimeResultEventWindow(result, chunkWindow);
                    this.pushRealtimeLog(null, chunkId, eventWindow, facialSnapshot);
                    this.recordRealtimePerf('log', performance.now() - logStartedAt);
                    this.requestRealtimeUiRender();
                    return;
                }
                if (['failed', 'discarded'].includes(String(data?.status || ''))) {
                    delete this.realtimeShadowPollTimers[jobId];
                    this.realtimePrecisionAnalysisPending = false;
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        precision_analysis_status: String(data?.status || 'failed'),
                        precision_analysis_elapsed_ms: Math.max(0, performance.now() - startedAt),
                    };
                    if (this.realtimeSourceMode === 'video') this.realtimeUploadedAnalysisComplete = true;
                    if (data?.status === 'failed' && this.realtimeFeedSignalStarted('shadow_error', true)) {
                        this.appendRealtimeFeedEvent({
                            type: 'shadow_error', tone: 'review', icon: '◇', title: 'Shadow 분석 지연',
                            description: '보조 Shadow 분석에 실패했지만 기본 낙상 분석은 계속됩니다.',
                        }, null, chunkId, chunkWindow, facialSnapshot);
                        this.requestRealtimeUiRender();
                    }
                    return;
                }
                // 다중 런타임 프로세스에서 queued 상태 파일이 보이기 전의
                // 일시적인 not_found는 정밀 분석 실패가 아니므로 계속 조회합니다.
                if (data?.status === 'not_found' && performance.now() - startedAt >= pollTimeoutMs) {
                    delete this.realtimeShadowPollTimers[jobId];
                    this.realtimePrecisionAnalysisPending = false;
                    return;
                }
            } catch (e) {
                if (performance.now() - startedAt > pollTimeoutMs) {
                    delete this.realtimeShadowPollTimers[jobId];
                    this.realtimePrecisionAnalysisPending = false;
                    return;
                }
            }
            if (performance.now() - startedAt <= pollTimeoutMs) {
                this.realtimeShadowPollTimers[jobId] = setTimeout(poll, 250);
            }
        };
        this.realtimeShadowPollTimers[jobId] = setTimeout(poll, 100);
    }

    private async dispatchRealtimeChunk(blob: Blob, chunkId: number, durationSec: number, chunkWindow: any, facialSnapshot: any = null): Promise<boolean> {
        this.realtimeDispatching = true;
        const startTime = performance.now();
        const requestId = `${this.realtimeSessionId}:${chunkId}:${Date.now()}`;
        try {
            const fd = new FormData();
            fd.append('video', blob, `chunk_${chunkId}.webm`);
            fd.append('metadata', JSON.stringify({
                analysis_profile: this.analysisProfile,
                input_source: 'webcam-live',
                model_type: this.selectedModelType,
                duration: durationSec,
                chunk_id: chunkId,
                request_id: requestId,
                realtime_session_id: this.realtimeSessionId,
                chunk_window: chunkWindow,
                source_start_time: Number(chunkWindow?.startSec ?? chunkWindow?.start_sec ?? 0),
                source_end_time: Number(chunkWindow?.endSec ?? chunkWindow?.end_sec ?? 0),
                ring_buffer_frames: this.realtimeRingBuffer.length,
                coordinate_metadata: this.realtimeAnalysisCoordinateMeta,
                defer_shadow: true,
                degraded_mode: this.realtimeDegradedMode,
                ...this.agentWeightMetadata(),
            }));
            const transport = await this.postRealtimeFormData(`/wiz/api/page.dashboard/analyze_upload`, fd);
            const parseStartedAt = performance.now();
            const json = this.parseRealtimeResponse(transport.text);
            this.recordRealtimePerf('parse', performance.now() - parseStartedAt);
            if (transport.status === 409 || json?.code === 409) {
                this.realtimePerfStats.skippedChunks = Number(this.realtimePerfStats.skippedChunks || 0) + 1;
                return true;
            }
            if (json.code === 200 && json.data) {
                const result = this.attachRealtimeCoordinateMetadata(json.data);
                this.realtimeFeedSignalStarted('analysis_error', false);
                if (chunkId <= this.realtimeLatestAppliedChunkId) {
                    this.realtimePerfStats.discarded = Number(this.realtimePerfStats.discarded || 0) + 1;
                    this.realtimeDispatching = false;
                    return true;
                }
                this.realtimeLatestAppliedChunkId = chunkId;
                this.applyAnalysisRoot(result, true);
                this.analysisSavedName = result.saved_name || '';
                const timing = result.server_timing || {};
                this.realtimeLastElapsed = Number(timing.total_server_latency_ms || 0) / 1000;
                const score = result.risk_score || 0;
                this.realtimeScoreHistory.push(score);
                if (this.realtimeScoreHistory.length > 3) this.realtimeScoreHistory.shift();
                const weights = [1, 2, 3];
                const n = this.realtimeScoreHistory.length;
                let wSum = 0, wTotal = 0;
                for (let i = 0; i < n; i++) { const w = weights[weights.length - n + i] || 1; wSum += this.realtimeScoreHistory[i] * w; wTotal += w; }
                this.realtimeCumulativeScore = wSum / wTotal;
                this.updateRealtimeOverlay();
                const sourceEnd = Number(result?.source_end_time ?? chunkWindow?.endSec ?? chunkWindow?.end_sec ?? 0);
                const sourceDelaySec = Math.max(0, this.realtimeSourceTime() - sourceEnd);
                const overlayAgeMs = sourceDelaySec * 1000;
                result.client_received_at = Date.now() / 1000;
                result.overlay_age_ms = overlayAgeMs;
                this.recordRealtimePerf('sourceDelay', overlayAgeMs);
                this.recordRealtimePerf('overlayAge', overlayAgeMs);
                this.updateRealtimeDegradedMode(result, sourceDelaySec);
                if (sourceDelaySec <= 2) {
                    this.updateRealtimeDiagnosticResult(result, sourceDelaySec, sourceDelaySec > 1.5 ? 'delayed_visible' : 'scheduled');
                    this.scheduleRealtimeServerDetectionOverlay(result);
                } else {
                    this.realtimeDelayedResultCount += 1;
                    this.updateRealtimeDiagnosticResult(result, sourceDelaySec, 'stale_discarded');
                    this.clearRealtimeSkeletonOverlay();
                    if (this.realtimeFeedSignalStarted(`analysis_delay_${chunkId}`, true)) {
                        this.appendRealtimeFeedEvent({
                            type: 'analysis_delay',
                            tone: 'review',
                            icon: '⌛',
                            title: '분석 지연',
                            description: `영상보다 ${sourceDelaySec.toFixed(1)}초 늦은 오버레이는 표시하지 않았습니다.`,
                        }, null, chunkId, chunkWindow, facialSnapshot);
                    }
                }
                const logStartedAt = performance.now();
                this.pushRealtimeLog(blob, chunkId, chunkWindow, facialSnapshot);
                this.recordRealtimePerf('log', performance.now() - logStartedAt);
                const clientLatencyMs = performance.now() - startTime;
                this.recordRealtimePerf('total', clientLatencyMs);
                this.realtimeRoundTripHistory.push(clientLatencyMs);
                if (this.realtimeRoundTripHistory.length > 10) this.realtimeRoundTripHistory.shift();
                const stageMap: [string, string][] = [
                    ['request_queue_wait_ms', 'queueWait'],
                    ['inference_sec', 'inferenceTime'],
                    ['decode_ms', 'decode'],
                    ['person_detection_ms', 'personDetection'],
                    ['tracking_ms', 'tracking'],
                    ['logical_person_analysis_ms', 'logicalPersonAnalysis'],
                    ['rf_fall_ms', 'rfFall'],
                    ['posture_ms', 'posture'],
                    ['person_object_filter_ms', 'personObjectFilter'],
                    ['shadow_ms', 'shadow'],
                    ['emotion_aux_ms', 'emotionAux'],
                    ['environment_aux_ms', 'environmentAux'],
                    ['response_build_ms', 'responseBuild'],
                    ['total_server_latency_ms', 'serverTotal'],
                ];
                for (const [sourceKey, perfKey] of stageMap) {
                    if (timing[sourceKey] == null) continue;
                    const rawValue = Number(timing[sourceKey]);
                    this.recordRealtimePerf(perfKey, sourceKey.endsWith('_sec') ? rawValue * 1000 : rawValue);
                }
                const st = Number(timing.total_server_latency_ms || 0);
                this.realtimeServerTimeHistory.push(st);
                if (this.realtimeServerTimeHistory.length > 10) this.realtimeServerTimeHistory.shift();
                this.realtimePerfStats.avgRtt = Math.round(this.realtimeRoundTripHistory.reduce((a: number, b: number) => a + b, 0) / this.realtimeRoundTripHistory.length);
                this.realtimePerfStats.avgServer = Math.round(this.realtimeServerTimeHistory.reduce((a: number, b: number) => a + b, 0) / this.realtimeServerTimeHistory.length);
                this.realtimePerfStats.currentInterval = this.getChunkConfig().spawnMs;
                this.realtimePerfStats.ringBufferFrames = this.realtimeRingBuffer.length;
                const facialCacheHit = result?.facial_analysis?.cache_hit ?? result?.runtime_inference?.facial_cache_hit;
                if (facialCacheHit === true) this.realtimePerfStats.faceCacheHits = Number(this.realtimePerfStats.faceCacheHits || 0) + 1;
                else if (facialCacheHit === false) this.realtimePerfStats.faceCacheMisses = Number(this.realtimePerfStats.faceCacheMisses || 0) + 1;
                if (result?.shadow_job) this.pollRealtimeShadow(result.shadow_job, chunkId, chunkWindow, facialSnapshot);
                this.requestRealtimeUiRender();
                return true;
            }
            this.realtimePerfStats.failed = (this.realtimePerfStats.failed || 0) + 1;
            this.requestRealtimeUiRender();
            return false;
        } catch (e: any) {
            if (e instanceof SyntaxError) console.error('Dispatch error (JSON parse failed — server may have returned NaN):', e.message);
            else console.error('Dispatch error:', e);
            if (this.realtimeFeedSignalStarted('analysis_error', true)) {
                this.appendRealtimeFeedEvent({
                    type: 'analysis_error',
                    tone: 'review',
                    icon: '!',
                    title: '분석 오류',
                    description: '실시간 분석 응답을 처리하지 못했습니다. 영상 재생은 계속됩니다.',
                }, null, chunkId, chunkWindow, facialSnapshot);
            }
            this.realtimePerfStats.failed = (this.realtimePerfStats.failed || 0) + 1;
            this.requestRealtimeUiRender();
            return false;
        } finally {
            this.realtimeDispatching = false;
        }
    }

    private updateRealtimeOverlay() {
        if (!this.analysisResult) return;
        const displayScore = this.isPersonAnalysisSelected() ? Number(this.analysisResult?.risk_score || 0) : this.realtimeCumulativeScore;
        this.realtimeOverlayScore = Math.round(displayScore * 100);
        const level = this.analysisResult?.risk_level || 'low';
        this.realtimeOverlayLevel = level;
        const decisionTone = this.decisionStageTone(this.analysisResult);
        this.realtimeOverlayLabel = this.realtimePendingConfirmation
            ? '확인 대기'
            : decisionTone === 'confirmed'
                ? '낙상 확정'
                : (decisionTone === 'suspected' || this.analysisResult?.fall_detected)
                    ? '낙상 의심'
                    : this.nonFallRealtimeLabel(this.analysisResult);
        const basis: string[] = [];
        const ri = this.analysisResult?.runtime_inference;
        const runtimeKey = this.analysisResult?.runtime_key || '';
        const isXgb = runtimeKey === 'person-feature-runtime';
        const fallDetected = this.analysisResult?.fall_detected;
        if (fallDetected) basis.push('⚠ 낙상');
        else if (ri?.fall_score != null || ri?.fall_probability != null) basis.push('비낙상');
        const fallProb = ri?.fall_probability;
        if (fallProb != null) basis.push(`확률 ${(fallProb * 100).toFixed(0)}%`);
        else { const rawProb = ri?.raw_fall_probability; if (rawProb != null) basis.push(`Raw ${(rawProb * 100).toFixed(0)}%`); }
        if (isXgb) {
            const tf = ri?.top_features || {};
            const mds = tf['max_down_speed']; const cdy = tf['center_dy']; const fp = tf['floor_proximity']; const hr = tf['height_ratio'];
            if (mds != null) basis.push(`하강속도 ${Number(mds).toFixed(2)}`);
            if (cdy != null && Number(cdy) > 0.01) basis.push(`수직이동 ${Number(cdy).toFixed(2)}`);
            if (fp != null && Number(fp) > 0.5) basis.push(`바닥근접 ${(Number(fp) * 100).toFixed(0)}%`);
            if (hr != null && Number(hr) < -0.03) basis.push(`높이↓ ${(Number(hr) * 100).toFixed(0)}%`);
        } else {
            const feats = ri?.features || {};
            const dym = feats['delta_y_max']; const fhr = feats['final_height_ratio'];
            if (dym != null) basis.push(`△Y ${Number(dym).toFixed(1)}px`);
            if (fhr != null) basis.push(`자세 ${(Number(fhr) * 100).toFixed(0)}%`);
        }
        const nFrames = ri?.short_clip?.n_det_frames || ri?.windows;
        if (nFrames != null) basis.push(`${nFrames}${isXgb ? 'w' : 'f'} 검출`);
        const postureLabel = ri?.posture_label; const decisionState = ri?.decision_state;
        if (postureLabel) {
            if (postureLabel === 'unknown') {
                const topCode = this.topNonFallPostureCode(this.analysisResult);
                const probs = this.displayPostureProbs(this.analysisResult);
                const topPct = Math.round(Number(probs?.[topCode] || 0) * 100);
                basis.push(topCode && topCode !== 'unknown' ? `추정(${this.POSTURE_LABEL_MAP[topCode] || topCode} ${topPct}%)` : '추정(서기)');
            } else {
                basis.push(this.POSTURE_LABEL_MAP[postureLabel] || postureLabel);
            }
        }
        if (decisionState && decisionState !== 'safe') { const dsMeta = this.DECISION_STATE_META[decisionState]; if (dsMeta) basis.push(dsMeta.label); }
        const llmReview = this.analysisResult?.llm_behavior_review || ri?.llm_behavior_review || this.analysisResult?.llm_interpretation;
        if (llmReview?.status === 'generated') basis.push('LLM검토');
        else if (llmReview?.status === 'cached') basis.push('LLM캐시');
        const facial = ri?.facial_state || this.analysisResult?.facial_state;
        if (facial?.applied) basis.push(`표정보조 +${Math.round(Number(facial.support_score || 0) * 100)}%p`);
        else if (facial?.available && Number(facial?.support_score || 0) > 0) basis.push('표정보조 검토');
        else if (facial?.reason === 'face_not_detected' && fallDetected) basis.push('얼굴미검출');
        if (isXgb) { if (ri?.motion_gate && !ri.motion_gate.passed) basis.push('Gate억제'); }
        else { if (ri?.motion_guard?.applied) basis.push('Guard'); }
        if (ri?.short_clip?.is_short_clip) basis.push('Short-clip');
        if (this.realtimeLastElapsed > 0) basis.push(`${this.realtimeLastElapsed.toFixed(1)}초`);
        const newBasis = basis.slice(0, 7).join(' · ') || '';
        if (newBasis) { this.realtimeOverlayBasis = newBasis; this.lastNonEmptyOverlayBasis = newBasis; }
        else if (this.lastNonEmptyOverlayBasis) this.realtimeOverlayBasis = this.lastNonEmptyOverlayBasis;
        this.realtimeOverlayRuntime = '';
        if (this.analysisResult?.fall_detected && !this.analysisResult?._dedup_suppressed) {
            this.realtimeOverlayFlash = true;
            if (this.realtimeOverlayFlashTimer) clearTimeout(this.realtimeOverlayFlashTimer);
            this.realtimeOverlayFlashTimer = setTimeout(() => { this.realtimeOverlayFlash = false; }, 2000);
        }
    }

    private isUploadVideoFile(file: File): boolean {
        const name = String(file?.name || '').toLowerCase();
        return Boolean(file?.type?.startsWith('video/')) || /\.(mp4|mov|avi|mkv|webm|m4v)$/.test(name);
    }

    private revokeSelectedVideoUrl() {
        if (this.selectedVideoUrl) {
            try { URL.revokeObjectURL(this.selectedVideoUrl); } catch (e) { }
        }
        this.selectedVideoUrl = '';
    }

    private fileIdentity(file: File): string {
        const anyFile: any = file as any;
        return [
            anyFile.webkitRelativePath || file.name || '',
            file.size || 0,
            file.lastModified || 0,
        ].join('|');
    }

    private inferTrainingFileAssignment(file: File): TrainingFileAssignment {
        const relativePath = String((file as any)?.webkitRelativePath || '').replace(/\\/g, '/');
        const pathParts = relativePath.split('/').slice(0, -1).filter(Boolean);
        const upperParts = pathParts.map(part => part.toUpperCase());
        const lowerParts = pathParts.map(part => part.toLowerCase());
        let fallLabel = String(this.trainingUploadLabel || 'N').toUpperCase() === 'Y' ? 'Y' : 'N';
        let postureClass = String(this.trainingUploadPostureClass || '') as TrainingPostureClass;
        const yTagged = upperParts.includes('Y');
        const nTagged = upperParts.includes('N');
        if (yTagged !== nTagged) fallLabel = yTagged ? 'Y' : 'N';
        const posture = ['stand', 'walk', 'run', 'sit', 'lie'].find(code => lowerParts.includes(code));
        if (posture) postureClass = posture as TrainingPostureClass;
        if (lowerParts.includes('fall')) {
            fallLabel = 'Y';
            postureClass = '';
        }
        return { fallLabel, postureClass };
    }

    private syncTrainingFileAssignments(files: File[]) {
        const next: Record<string, TrainingFileAssignment> = {};
        for (const file of files || []) {
            const key = this.fileIdentity(file);
            next[key] = this.trainingFileAssignments[key] || this.inferTrainingFileAssignment(file);
        }
        this.trainingFileAssignments = next;
        const maxPage = Math.max(0, Math.ceil(files.length / this.trainingFilePageSize) - 1);
        this.trainingFilePage = Math.min(this.trainingFilePage, maxPage);
    }

    public trainingAssignmentForFile(file: File): TrainingFileAssignment {
        const key = this.fileIdentity(file);
        if (!this.trainingFileAssignments[key]) {
            this.trainingFileAssignments[key] = this.inferTrainingFileAssignment(file);
        }
        return this.trainingFileAssignments[key];
    }

    public async applyTrainingDefaultsToFiles() {
        const fallLabel: TrainingFallLabel = String(this.trainingUploadLabel || 'N').toUpperCase() === 'Y' ? 'Y' : 'N';
        const rawPosture = String(this.trainingUploadPostureClass || '');
        const postureClass = (['stand', 'walk', 'run', 'sit', 'lie'].includes(rawPosture) ? rawPosture : '') as TrainingPostureClass;
        for (const file of this.selectedFiles) {
            this.trainingFileAssignments[this.fileIdentity(file)] = { fallLabel, postureClass };
        }
        this.trainingUploadMessage = `선택 파일 ${this.selectedFiles.length}개의 기본 라벨을 적용했습니다.`;
        this.trainingUploadMessageType = 'info';
        await this.service.render();
    }

    public trainingPostureOptions(): any[] {
        return this.postureClasses.filter(item => item.code !== 'fall');
    }

    public trainingFallLabelSummary(): string {
        const counts: Record<string, number> = { Y: 0, N: 0 };
        for (const file of this.selectedFiles) counts[this.trainingAssignmentForFile(file).fallLabel]++;
        if (this.selectedFiles.length === 0) return '-';
        if (counts.Y === 0) return `N ${counts.N}건`;
        if (counts.N === 0) return `Y ${counts.Y}건`;
        return `Y ${counts.Y}건 · N ${counts.N}건`;
    }

    public trainingPostureLabelSummary(): string {
        const counts: Record<string, number> = {};
        for (const file of this.selectedFiles) {
            const label = this.trainingAssignmentForFile(file).postureClass || '미지정';
            counts[label] = (counts[label] || 0) + 1;
        }
        const entries = Object.entries(counts);
        if (entries.length === 0) return '-';
        return entries.slice(0, 3).map(([label, count]) => `${label} ${count}건`).join(' · ') + (entries.length > 3 ? ` 외 ${entries.length - 3}종` : '');
    }

    public selectedUploadPageCount(): number {
        return Math.max(1, Math.ceil(this.selectedFiles.length / this.trainingFilePageSize));
    }

    public async moveTrainingFilePage(delta: number) {
        const maxPage = Math.max(0, this.selectedUploadPageCount() - 1);
        this.trainingFilePage = Math.max(0, Math.min(maxPage, this.trainingFilePage + delta));
        await this.service.render();
    }

    public trackBySelectedUploadItem(_index: number, item: any): string {
        return String(item?.key || item?.name || _index);
    }

    private dedupeUploadFiles(files: File[]): File[] {
        const seen = new Set<string>();
        const out: File[] = [];
        for (const file of files || []) {
            if (!file) continue;
            const key = this.fileIdentity(file);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(file);
        }
        return out;
    }

    private uploadFolderKey(file: File): string {
        const rel = String((file as any)?.webkitRelativePath || '');
        if (!rel || !rel.includes('/')) return '';
        return rel.split('/').slice(0, -1).join('/');
    }

    private uploadDisplayName(file: File): string {
        const rel = String((file as any)?.webkitRelativePath || '');
        return rel || file.name || '파일';
    }

    private async syncSelectedUploadPreview(files: File[]) {
        const cleanFiles = this.dedupeUploadFiles(files);
        this.revokeSelectedVideoUrl();
        this.selectedFiles = cleanFiles;
        this.syncTrainingFileAssignments(cleanFiles);
        const firstVideo = cleanFiles.find(file => this.isUploadVideoFile(file)) || null;
        this.selectedFile = firstVideo;
        this.selectedVideoUrl = firstVideo ? URL.createObjectURL(firstVideo) : '';
    }

    private async setSelectedUploadFiles(files: File[]) {
        if (this.trainingUploading || this.trainingJobStarting) return;
        const cleanFiles = (files || []).filter(Boolean);
        if (cleanFiles.length === 0) return;
        await this.syncSelectedUploadPreview(cleanFiles);
        this.clearAnalysisState();
        this.feedbackStatus = '';
        this.feedbackMessage = '';
        this.trainingUploadMessage = '';
        this.trainingUploadDone = 0;
        this.trainingUploadTotal = 0;
        this.errorMessage = '';
        this.uploadLogEntries = [];
        this.uploadChunkSummary = null;
        await this.service.render();
    }

    public async onFileSelected(event: any) {
        const files = Array.from(event?.target?.files || []) as File[];
        await this.setSelectedUploadFiles(files);
        if (event?.target) event.target.value = '';
    }
    public async onRealtimeUploadSelected(event: any) {
        const files = Array.from(event?.target?.files || []) as File[];
        if (event?.target) event.target.value = '';
        const videoFile = files.find(file => this.isUploadVideoFile(file)) || null;
        if (!videoFile) {
            this.errorMessage = '분석할 동영상 파일을 선택해주세요.';
            await this.service.render();
            return;
        }
        await this.stopRealtimeAnalysis();
        this.stopWebcamStream();
        this.clearRealtimeUploadedVideoSource();
        this.clearAnalysisState();
        this.realtimeSourceMode = 'video';
        this.realtimeUploadedVideoUrl = URL.createObjectURL(videoFile);
        this.realtimeUploadedVideoName = videoFile.name;
        this.realtimeUploadedVideoFile = videoFile;
        this.realtimeUploadedAnalysisRunning = true;
        this.realtimePrecisionAnalysisPending = false;
        this.realtimeUploadedAnalysisComplete = false;
        this.realtimeUploadedAnalysisError = '';
        this.errorMessage = '';
        // 환경 표시를 선택한 경우 첫 환경 스냅샷을 전체 정밀 분석보다 먼저 서버에
        // 전달합니다. 단일 GPU/worker에서도 짧은 실시간 요청이 긴 정밀 작업 뒤에
        // 막히지 않게 하면서, 플레이어 준비 실패 시에는 원본 분석을 바로 시작합니다.
        const preparedRunId = ++this.realtimeUploadedAnalysisRunId;
        const preparedSessionId = `upload-file-${Date.now()}-${preparedRunId}-${Math.random().toString(36).slice(2, 8)}`;
        this.realtimeSessionId = preparedSessionId;
        let analysisTask: Promise<void> | null = null;
        const ensureAnalysisTask = () => {
            if (!analysisTask) {
                analysisTask = this.analyzeRealtimeUploadedFile(videoFile, {
                    runId: preparedRunId,
                    sessionId: preparedSessionId,
                });
            }
            return analysisTask;
        };
        if (this.realtimeDisablePrecision) {
            this.realtimeSessionId = `developer-fast-pose-${Date.now()}`;
            this.realtimeUploadedAnalysisRunning = false;
            this.realtimePrecisionAnalysisPending = false;
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                precision_analysis_status: 'disabled_developer',
            };
        }
        // Pose 모델 준비도 정밀 업로드와 병렬로 시작해 재생 후 초기화를 기다리지 않습니다.
        const poseInitTask = this.initMediaPipePose();
        await this.service.render();
        const videoEl = this.webcamVideoRef?.nativeElement;
        if (!videoEl) {
            this.errorMessage = '영상 미리보기를 준비하지 못했지만 서버 분석은 계속 진행됩니다.';
            await this.service.render();
            await ensureAnalysisTask();
            return;
        }
        try {
            videoEl.srcObject = null;
            videoEl.src = this.realtimeUploadedVideoUrl;
            videoEl.load();
            await this.waitForRealtimeUploadedVideo(videoEl);
            await this.waitForRealtimeUploadedVideoFrame(videoEl);
            // 모델 준비 전 재생을 시작하면 첫 프레임들이 Pose 없이 지나가므로 Fast Pose
            // 준비만 짧게 기다립니다. Precision/Face 결과는 이 경로를 막지 않습니다.
            let poseReady = await this.waitForFastPoseReady();
            if (!poseReady) {
                // 사전 로딩이 네트워크/GPU 초기화 경쟁으로 실패했으면 현재 업로드
                // video가 연결된 상태에서 한 번 재시도합니다.
                await this.initMediaPipePose();
                poseReady = await this.waitForFastPoseReady(3000);
            }
            videoEl.currentTime = 0;
            // 화면 Pose는 video element를 직접 읽고, 정밀 분석은 업로드 원본 파일을
            // 서버에 바로 전송합니다. captureStream/MediaRecorder 지원 여부에 의존하지 않습니다.
            videoEl.pause();
            videoEl.currentTime = 0;
            this.webcamReady = true;
            this.realtimeActive = true;
            this.realtimeSessionStartedAt = Date.now();
            await this.service.render();
            // 업로드 영상은 첫 프레임에서 환경 모델을 먼저 준비합니다. 긴 원본
            // 정밀 분석이 GPU/worker를 점유하기 전에 환경 결과를 받아 Canvas에
            // 그린 뒤 재생해야 Skeleton과 환경 객체가 같은 시작점부터 보입니다.
            if (!this.realtimeDisablePrecision) {
                this.startRealtimeEnvironmentSnapshots(videoEl);
                await this.waitForRealtimeEnvironmentFirstResult();
            }
            void ensureAnalysisTask();
            try {
                await videoEl.play();
                if (poseReady && !this.mpRunning) this.startPoseLoop();
            } catch (playError) {
                this.errorMessage = '브라우저가 자동 재생을 차단했습니다. 재생 버튼을 눌러주세요. 서버 분석은 계속 진행됩니다.';
                await this.service.render();
            }
            void poseInitTask.catch(() => undefined);
        } catch (e: any) {
            this.errorMessage = `${e?.message || '업로드 영상을 재생하지 못했습니다.'} 서버 분석은 계속 진행됩니다.`;
            this.realtimeActive = false;
            this.webcamReady = false;
            await this.service.render();
        }
        await ensureAnalysisTask();
    }
    private canAcceptRealtimePrecisionResult(runId: number, sessionId: string): boolean {
        if (runId !== this.realtimeUploadedAnalysisRunId || sessionId !== this.realtimeSessionId) {
            this.realtimeLatePrecisionIgnoredCount += 1;
            return false;
        }
        // 영상 재생 종료는 화면 Pose 입력의 종료일 뿐, 이미 제출된 원본 정밀
        // 분석의 취소 사유가 아니다. 결과 수락은 run/session identity로만 막고
        // 실제 제한 시간은 poll/종료 flush 타이머에서 관리한다.
        return true;
    }

    private async analyzeRealtimeUploadedFile(
        videoFile: File,
        prepared?: { runId: number; sessionId: string },
    ) {
        if (this.realtimeDisablePrecision) return;
        const runId = prepared?.runId ?? ++this.realtimeUploadedAnalysisRunId;
        const realtimeSessionId = prepared?.sessionId
            || `upload-file-${Date.now()}-${runId}-${Math.random().toString(36).slice(2, 8)}`;
        if (prepared && runId !== this.realtimeUploadedAnalysisRunId) return;
        const requestId = `${realtimeSessionId}:0`;
        this.realtimeSessionId = realtimeSessionId;
        this.realtimeSessionStartedAt = Date.now();
        this.realtimeVideoEnded = false;
        this.realtimeVideoEndedAt = 0;
        this.realtimeFinalAcceptedTimestampMs = 0;
        const controller = new AbortController();
        this.realtimeUploadedAnalysisController?.abort();
        this.realtimeUploadedAnalysisController = controller;
        this.realtimeUploadedAnalysisRunning = true;
        this.realtimeUploadedAnalysisComplete = false;
        this.realtimeUploadedAnalysisError = '';
        const startedAt = performance.now();
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            precision_analysis_status: 'running',
            precision_analysis_elapsed_ms: 0,
            precision_queue_length: 1,
            oldest_precision_task_age_ms: 0,
            precision_lag_behind_playback_ms: 0,
        };
        try {
            const fd = new FormData();
            fd.append('video', videoFile, videoFile.name);
            fd.append('metadata', JSON.stringify({
                analysis_profile: this.analysisProfile,
                input_source: 'realtime',
                model_type: this.selectedModelType,
                realtime_display: true,
                realtime_session_id: realtimeSessionId,
                request_id: requestId,
                chunk_id: 0,
                source_start_time: 0,
                // 저장 영상을 실시간 화면에서 재생할 때는 원본 전체 구간의
                // 후속 자세까지 확인한 최종 판정만 화면에 반영합니다.
                defer_shadow: false,
                precision_shadow: true,
                original_filename: videoFile.name,
                original_size: Number(videoFile.size || 0),
                original_last_modified: Number(videoFile.lastModified || 0),
                source_timestamp: 0,
                ...this.agentWeightMetadata(),
            }));
            // 업로드 원본은 브라우저 녹화 청크를 거치지 않습니다.
            // 브라우저 지원 여부와 무관하게 서버 정밀 분석을 1회 시작하고,
            // 재생 중 화면 Pose는 MediaPipe가 독립적으로 담당합니다.
            const response = await fetch(`/wiz/api/page.dashboard/analyze_upload`, {
                method: 'POST',
                body: fd,
                signal: controller.signal,
            });
            const json = await this.readJsonResponse(response);
            if (!this.canAcceptRealtimePrecisionResult(runId, realtimeSessionId)) return;
            if (json.code !== 200 || !json.data) {
                throw new Error(json.data?.message || '업로드 영상 분석에 실패했습니다.');
            }
            let result = json.data;
            if (this.realtimeEnvironmentSnapshotResult?.environment_analysis) {
                result = this.mergeRealtimeAndFinalEnvironment(result, this.realtimeEnvironmentSnapshotResult);
            } else result = this.normalizeEnvironmentAnalysis(result, 'final_analysis');
            this.applyAnalysisRoot(result, false);
            if (Array.isArray(result?.environment_analysis?.frames)
                && result.environment_analysis.frames.length > 0) {
                this.realtimeEnvironmentSnapshotResult = result;
                this.realtimeEnvironmentSnapshotCount = Number(
                    result?.environment_analysis?.object_count
                    ?? result?.environment_analysis?.objects?.length
                    ?? 0
                );
                if (this.canShowEnvironmentMaskOverlay()) {
                    this.scheduleRealtimeServerDetectionOverlay(result);
                }
            }
            this.analysisSavedName = result.saved_name || '';
            this.alertWorkflow = result.alert_workflow || null;
            this.syncUploadChunkLogs(result);
            this.realtimeLastElapsed = Math.max(0, (performance.now() - startedAt) / 1000);
            this.realtimeScoreHistory = [Number(result.risk_score || 0)];
            this.realtimeCumulativeScore = Number(result.risk_score || 0);
            this.updateRealtimeOverlay();
            this.finalizeRealtimeLocalFallCandidate(result);
            this.updateRealtimeDiagnosticResult(result, 0, 'upload_analysis_completed');
            const durationSec = Math.max(0, ...[
                result?.video_metadata?.duration,
                result?.duration,
                result?.model_runtime?.duration,
                this.webcamVideoRef?.nativeElement?.duration,
            ].map(value => Number(value)).filter(Number.isFinite));
            const chunkWindow = {
                startSec: 0,
                endSec: Math.max(0, durationSec),
                label: durationSec > 0 ? `전체 영상 · ${durationSec.toFixed(1)}초` : '전체 영상',
            };
            this.appendRealtimeFeedEvent({
                type: 'upload_analysis_completed',
                tone: this.realtimeFeedTone(result),
                icon: '✓',
                title: '영상 분석 완료',
                description: `원본 영상 분석이 완료되었습니다. 현재 위험도 ${this.riskScorePercent(result)}%`,
                riskPercent: this.riskScorePercent(result),
                showRisk: true,
            }, null, 0, chunkWindow);
            this.pushRealtimeLog(null, 0, chunkWindow);
            if (result?.shadow_job) {
                this.realtimePrecisionAnalysisPending = true;
                this.pollRealtimeShadow(result.shadow_job, 0, chunkWindow, null);
            }
            this.realtimeUploadedAnalysisComplete = !result?.shadow_job;
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                precision_analysis_status: result?.shadow_job ? 'running' : 'completed',
                precision_analysis_elapsed_ms: Math.max(0, performance.now() - startedAt),
                precision_queue_length: result?.shadow_job ? 1 : 0,
            };
            this.realtimeUploadedAnalysisError = '';
            this.errorMessage = '';
        } catch (e: any) {
            if (runId !== this.realtimeUploadedAnalysisRunId) return;
            if (e?.name !== 'AbortError') {
                const message = this.requestErrorMessage(e, '실시간 업로드 분석');
                this.realtimeUploadedAnalysisError = message;
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    precision_analysis_status: 'failed',
                    precision_analysis_elapsed_ms: Math.max(0, performance.now() - startedAt),
                    precision_queue_length: 0,
                };
                this.errorMessage = message;
                this.appendRealtimeFeedEvent({
                    type: 'upload_analysis_failed',
                    tone: 'review',
                    icon: '!',
                    title: '영상 분석 실패',
                    description: message,
                }, null, 0, { startSec: 0, endSec: 0, label: '업로드 요청' });
            }
        } finally {
            if (runId === this.realtimeUploadedAnalysisRunId) {
                this.realtimeUploadedAnalysisController = null;
                this.realtimeUploadedAnalysisRunning = false;
                await this.service.render();
            }
        }
    }
    private waitForRealtimeUploadedVideo(videoEl: HTMLVideoElement): Promise<void> {
        if (videoEl.readyState >= 1) return Promise.resolve();
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                cleanup();
                reject(new Error('업로드 영상 정보를 불러오는 시간이 초과되었습니다.'));
            }, 10000);
            const cleanup = () => {
                clearTimeout(timeout);
                videoEl.removeEventListener('loadedmetadata', onLoaded);
                videoEl.removeEventListener('error', onError);
            };
            const onLoaded = () => { cleanup(); resolve(); };
            const onError = () => { cleanup(); reject(new Error('선택한 동영상을 재생할 수 없습니다.')); };
            videoEl.addEventListener('loadedmetadata', onLoaded);
            videoEl.addEventListener('error', onError);
        });
    }
    private waitForRealtimeUploadedVideoFrame(videoEl: HTMLVideoElement): Promise<void> {
        if (videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
            return Promise.resolve();
        }
        return new Promise(resolve => {
            const timeout = setTimeout(() => {
                cleanup();
                resolve();
            }, 5000);
            const cleanup = () => {
                clearTimeout(timeout);
                videoEl.removeEventListener('loadeddata', onReady);
                videoEl.removeEventListener('canplay', onReady);
            };
            const onReady = () => { cleanup(); resolve(); };
            videoEl.addEventListener('loadeddata', onReady);
            videoEl.addEventListener('canplay', onReady);
            // 일부 브라우저는 paused 상태의 0초 프레임을 디코딩하지 않으므로
            // 눈에 보이지 않는 최소 seek으로 첫 source frame을 준비한다.
            try { videoEl.currentTime = 0.001; } catch (e) { }
        });
    }
    public async onRealtimeUploadedVideoEnded() {
        if (this.realtimeSourceMode !== 'video' || !this.realtimeActive || this.realtimeUploadedFinalizeRunning) return;
        if (this.realtimeUploadedVideoFile) {
            this.realtimeUploadedFinalizeRunning = true;
            this.realtimeVideoEnded = true;
            this.realtimeVideoEndedAt = performance.now();
            this.realtimeFinalAcceptedTimestampMs = Math.max(0, Number(this.webcamVideoRef?.nativeElement?.currentTime || 0) * 1000);
            this.realtimeActive = false;
            // 영상 재생이 끝나도 마지막 환경 모델 결과는 유지합니다. 서버 정밀
            // 결과가 뒤이어 도착하면 같은 캐시를 새 결과로 교체합니다.
            this.stopRealtimeEnvironmentSnapshots(true);
            // 정밀 결과가 후보보다 먼저 도착한 경우에도 종료 시 같은 후보 카드를
            // 확정/해제로 마무리하고 새 이벤트를 만들지 않습니다.
            if (this.realtimeFastFallEventKey && this.currentRootAnalysisResult()) {
                this.finalizeRealtimeLocalFallCandidate(this.currentRootAnalysisResult());
            }
            // 정밀 결과가 늦게 도착해도 마지막 영상/스켈레톤 프레임을 유지합니다.
            // 여기서 캔버스를 지우면 정상 분석 후에도 화면이 검게 보입니다.
            this.webcamReady = true;
            this.stopPoseLoop(true);
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                precision_analysis_status: 'flushing',
                precision_queue_length: Number(this.realtimeUploadedAnalysisRunning || this.realtimePrecisionAnalysisPending),
                realtime_end_status: '마지막 구간 정리 중',
                final_accepted_timestamp_ms: this.realtimeFinalAcceptedTimestampMs,
            };
            if (!this.realtimeLogEntries.some(entry => entry?.eventType === 'realtime_video_ended')) {
                const endedEntry = this.appendRealtimeFeedEvent({
                    type: 'realtime_video_ended', tone: 'normal', icon: '✓',
                    title: '영상 실시간 분석 종료',
                    description: '영상 재생 구간의 실시간 자세 추적을 종료했습니다.',
                }, null, -1, {
                    startSec: Math.max(0, this.realtimeFinalAcceptedTimestampMs / 1000 - 0.1),
                    endSec: this.realtimeFinalAcceptedTimestampMs / 1000,
                    label: '영상 종료',
                });
                endedEntry.createdAtVideoTimeMs = this.realtimeFinalAcceptedTimestampMs;
                endedEntry.createdAtRealtimeMs = performance.now();
            }
            await this.service.render();
            const finalizeWhenPrecisionSettles = () => {
                const precisionWasPending = this.realtimeUploadedAnalysisRunning
                    || this.realtimePrecisionAnalysisPending
                    || Object.keys(this.realtimeShadowPollTimers).length > 0;
                const elapsedMs = Math.max(0, performance.now() - this.realtimeVideoEndedAt);
                if (precisionWasPending && elapsedMs < this.REALTIME_END_PRECISION_FLUSH_MS) {
                    this.realtimeDiagnostics = {
                        ...this.realtimeDiagnostics,
                        precision_analysis_status: 'flushing',
                        precision_queue_length: Number(this.realtimeUploadedAnalysisRunning || this.realtimePrecisionAnalysisPending)
                            + Object.keys(this.realtimeShadowPollTimers).length,
                        realtime_end_status: '환경 객체·정밀 결과 반영 대기 중',
                        video_end_cleanup_ms: elapsedMs,
                    };
                    this.realtimeEndFinalizeTimer = setTimeout(finalizeWhenPrecisionSettles, 500);
                    void this.service.render();
                    return;
                }
                if (precisionWasPending) {
                    this.realtimeCancelledPrecisionCount += Number(this.realtimeUploadedAnalysisRunning)
                        + Number(this.realtimePrecisionAnalysisPending)
                        + Object.keys(this.realtimeShadowPollTimers).length;
                    this.realtimeUploadedAnalysisRunId += 1;
                    this.realtimeUploadedAnalysisController?.abort();
                    this.realtimeUploadedAnalysisController = null;
                    this.realtimeRfFallController?.abort();
                    this.realtimeRfFallController = null;
                    for (const timer of Object.values(this.realtimeShadowPollTimers)) clearTimeout(timer);
                    this.realtimeShadowPollTimers = {};
                }
                this.realtimeUploadedAnalysisRunning = false;
                this.realtimePrecisionAnalysisPending = false;
                this.realtimeUploadedAnalysisComplete = true;
                this.realtimeUploadedFinalizeRunning = false;
                this.realtimeDiagnostics = {
                    ...this.realtimeDiagnostics,
                    precision_analysis_status: precisionWasPending ? 'partial' : 'completed',
                    precision_queue_length: 0,
                    realtime_end_status: '실시간 자세 분석 완료',
                    video_end_cleanup_ms: elapsedMs,
                    cancelled_precision_count: this.realtimeCancelledPrecisionCount,
                    late_precision_ignored_count: this.realtimeLatePrecisionIgnoredCount,
                };
                this.realtimeEndFinalizeTimer = null;
                void this.service.render();
            };
            this.realtimeEndFinalizeTimer = setTimeout(
                finalizeWhenPrecisionSettles,
                this.REALTIME_END_GRACEFUL_FLUSH_MS,
            );
            return;
        }
        this.realtimeUploadedFinalizeRunning = true;
        if (this.recorderSpawnTimer) {
            clearInterval(this.recorderSpawnTimer);
            this.recorderSpawnTimer = null;
        }
        for (const slot of [...this.recorderSlots]) {
            if (slot.timer) clearTimeout(slot.timer);
            if (slot.recorder.state === 'recording') {
                try { slot.recorder.stop(); } catch (e) { }
            }
        }
        const deadline = Date.now() + 120000;
        while (
            Date.now() < deadline
            && (this.recorderSlots.length > 0 || this.dispatchQueue.length > 0 || this.dispatchWorkerRunning)
        ) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        const shadowDeadline = Date.now() + 12000;
        while (Date.now() < shadowDeadline && Object.keys(this.realtimeShadowPollTimers).length > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        await this.stopRealtimeAnalysis();
        if (this.webcamStream) {
            this.webcamStream.getTracks().forEach(track => track.stop());
            this.webcamStream = null;
        }
        this.webcamReady = false;
        this.stopPoseLoop();
        this.realtimeUploadedAnalysisComplete = true;
        this.realtimeUploadedFinalizeRunning = false;
        await this.service.render();
    }
    public async onFolderSelected(event: any) {
        const files = Array.from(event?.target?.files || []) as File[];
        await this.setSelectedUploadFiles(files);
        if (event?.target) event.target.value = '';
    }
    public async onDrop(event: DragEvent) {
        event.preventDefault();
        this.dragover = false;
        const files = Array.from(event.dataTransfer?.files || []) as File[];
        await this.setSelectedUploadFiles(files);
    }
    public onDragOver(event: DragEvent) { event.preventDefault(); this.dragover = true; }
    public onDragLeave(event: DragEvent) { event.preventDefault(); this.dragover = false; }

    public async setUploadWorkflowMode(mode: 'analyze' | 'train') {
        this.uploadWorkflowMode = ['analyze', 'train'].includes(mode) ? mode : 'analyze';
        if (this.uploadWorkflowMode === 'train') {
            this.inputMode = 'upload';
            this.trainingModeEntering = true;
            this.resetFallDetectionDraftSelection();
            this.trainingTargetModel = 'pose-backbone';
            this.restorePoseStudioTrainingSettings();
            this.restorePoseStudioDatasetsCache();
            this.restorePoseStudioRegistryCache();
            this.restoreFallDetectionStudioCache();
            await this.service.render();
            this.scrollToFallDetectionStepOne();
            window.setTimeout(() => void this.finishTrainingModeBootstrap(true), 0);
            return;
        }
        await this.service.render();
    }

    public selectedUploadCount(): number {
        return this.selectedFiles.length;
    }

    public selectedUploadSummary(): string {
        const count = this.selectedUploadCount();
        if (count === 0) return '선택된 파일 없음';
        const firstNames = this.selectedFiles.slice(0, 3).map(file => file.name).join(', ');
        const extra = count > 3 ? ` 외 ${count - 3}개` : '';
        return `${count}개 선택 · ${firstNames}${extra}`;
    }

    public selectedUploadItems(): any[] {
        const isTrainingView = this.uploadWorkflowMode === 'train';
        const pageSize = isTrainingView ? this.trainingFilePageSize : 12;
        const start = isTrainingView ? this.trainingFilePage * pageSize : 0;
        return this.selectedFiles.slice(start, start + pageSize).map((file, offset) => ({
            index: start + offset,
            key: this.fileIdentity(file),
            name: this.uploadDisplayName(file),
            folder: this.uploadFolderKey(file),
            size_mb: file.size ? (file.size / (1024 * 1024)).toFixed(1) : '0.0',
            type: this.isUploadVideoFile(file) ? '영상' : (this._isArchiveName(file.name) ? '압축' : '파일'),
            assignment: this.trainingAssignmentForFile(file),
        }));
    }

    public selectedUploadHiddenCount(): number {
        if (this.uploadWorkflowMode === 'train') return 0;
        return Math.max(0, this.selectedFiles.length - 12);
    }

    public selectedUploadFolderGroups(): any[] {
        const groups: Record<string, number> = {};
        for (const file of this.selectedFiles) {
            const folder = this.uploadFolderKey(file);
            if (!folder) continue;
            groups[folder] = (groups[folder] || 0) + 1;
        }
        return Object.keys(groups).sort().map(key => ({ key, label: key.split('/')[0] || key, count: groups[key] }));
    }

    private _isArchiveName(name: string): boolean {
        return /\.(zip|tar|tar\.gz|tgz)$/i.test(String(name || ''));
    }

    public async removeSelectedUploadFile(index: number, event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (index < 0 || index >= this.selectedFiles.length) return;
        const next = this.selectedFiles.filter((_, i) => i !== index);
        await this.syncSelectedUploadPreview(next);
        if (next.length === 0) this.clearSelectedFile();
        else {
            this.clearAnalysisState();
            this.uploadLogEntries = [];
            this.uploadChunkSummary = null;
            this.trainingUploadMessage = '';
            this.errorMessage = '';
        }
        await this.service.render();
    }

    public async removeSelectedUploadFolder(folder: string, event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const key = String(folder || '');
        if (!key) return;
        const next = this.selectedFiles.filter(file => this.uploadFolderKey(file) !== key);
        await this.syncSelectedUploadPreview(next);
        if (next.length === 0) this.clearSelectedFile();
        else {
            this.clearAnalysisState();
            this.uploadLogEntries = [];
            this.uploadChunkSummary = null;
            this.trainingUploadMessage = '';
            this.errorMessage = '';
        }
        await this.service.render();
    }

    public selectedUploadTrainingHint(): string {
        if (this.selectedUploadCount() === 0) return '영상 여러 개 또는 zip/tar 압축 파일을 한 번에 선택할 수 있습니다.';
        if (!this.selectedFile) return '압축 파일은 학습 등록만 가능하고, 분석 미리보기는 영상 파일에서만 동작합니다.';
        if (this.selectedUploadCount() > 1) return '분석은 첫 번째 영상으로 실행하고, 학습 등록은 선택된 전체 파일을 순차 저장합니다.';
        return '선택한 영상을 분석하거나 학습 데이터로 등록할 수 있습니다.';
    }

    public trainingTargetOptionsForView(): any[] {
        const options = this.prototypeInfo?.training_target_options;
        return Array.isArray(options) && options.length > 0 ? options : this.fallbackTrainingTargetOptions;
    }

    public trainingTargetOption(key?: string): any {
        const target = String(key || this.trainingTargetModel || 'rf-dual');
        return this.trainingTargetOptionsForView().find((item: any) => item.key === target) || this.trainingTargetOptionsForView()[0] || {};
    }

    public trainingTargetLabel(): string {
        return this.trainingTargetOption().label || this.trainingTargetModel || '모델';
    }

    public trainingTargetDescription(): string {
        return this.trainingTargetOption().description || '선택한 모델 학습 자료로 metadata를 저장합니다.';
    }

    public trainingTargetRegistrationSupported(key?: string): boolean {
        return this.trainingTargetOption(key)?.registration_supported !== false;
    }

    public trainingTargetRequiresPostureLabel(key?: string): boolean {
        return this.trainingTargetOption(key)?.requires_posture_label === true;
    }

    public trainingTargetStatusActionLabel(): string {
        return this.trainingTargetSupportsDashboardJob()
            ? '이미 등록된 데이터로 학습 시작'
            : '전용 학습 현황 확인';
    }

    public uploadSettingsTitle(): string {
        return this.uploadWorkflowMode === 'train' ? '등록 기준' : '분석 설정';
    }

    public uploadSelectionModeText(): string {
        if (this.uploadWorkflowMode === 'train') return `${this.trainingTargetLabel()} 학습 자료`;
        return this.selectedFile ? 'RF-Dual 업로드 분석' : '분석할 영상 대기';
    }

    public llmModeHint(): string {
        const settings = this.prototypeInfo?.llm_settings || {};
        if (!settings.enabled) return 'LLM 설명 꺼짐';
        return '기본 local_fast 요약 · 동기 OpenAI 호출 없음';
    }

    public trainingTargetJobType(): string {
        return String(this.trainingTargetOption().job_type || 'full');
    }

    public trainingTargetSupportsDashboardJob(): boolean {
        return ['full', 'rf', 'posture'].includes(this.trainingTargetJobType());
    }

    public async onTrainingTargetModelChanged() {
        if (this.trainingTargetModel === 'pose-backbone') {
            this.restorePoseStudioRegistryCache();
            await this.loadPoseStudioRegistrySnapshot();
            if (this.trainingPipelineType === 'fall_detection') {
                await this.loadFallDetectionTrainingSnapshot();
            }
            this.trainingUploadMessage = '';
            await this.service.render();
            return;
        }
        if (!this.trainingTargetRegistrationSupported()) {
            await this.loadContinuousTrainingStatus(false);
            const status = this.selectedContinuousTrainingStatus();
            this.trainingUploadMessage = `${this.trainingTargetLabel()}은 전용 데이터셋·라벨 계약의 연속 학습 대상으로, 이 화면에서는 파일 등록 대신 학습 현황과 재개 상태를 확인합니다. ${this.continuousTrainingStageText(status)}`;
            this.trainingUploadMessageType = 'info';
        } else if (this.trainingTargetRequiresPostureLabel() && this.selectedFiles.some(file => !this.trainingAssignmentForFile(file).postureClass)) {
            this.trainingUploadMessage = `${this.trainingTargetLabel()} 자료는 모든 파일에 행동 라벨이 필요합니다.`;
            this.trainingUploadMessageType = 'info';
        } else {
            this.trainingUploadMessage = '';
        }
        await this.service.render();
    }

    public isPoseTrainingTarget(): boolean {
        return this.trainingTargetModel === 'pose-backbone';
    }

    public async setTrainingPipelineType(type: 'dataset_management' | 'fall_detection') {
        this.trainingPipelineType = type === 'fall_detection' ? 'fall_detection' : 'dataset_management';
        if (this.trainingPipelineType === 'fall_detection') await this.loadFallDetectionTrainingSnapshot();
        await this.service.render();
    }

    private applyFallDetectionStudio(payload: any) {
        if (!payload || typeof payload !== 'object') return;
        const previousJobId = String(this.fallDetectionJob?.job_id || '');
        const previousJobStatus = String(this.fallDetectionJob?.status || '').toLowerCase();
        this.fallDetectionStudio = payload;
        this.cacheFallDetectionStudio(payload);
        const defaults = payload?.temporal_defaults || {};
        const availableDatasetIds = new Set(this.fallDetectionDatasets().map((item: any) => String(item?.dataset_id || '')));
        this.fallDetectionDatasetIds = this.fallDetectionDatasetIds.filter((datasetId: string) => availableDatasetIds.has(datasetId));
        if (this.fallDetectionDatasetCandidateId && !availableDatasetIds.has(this.fallDetectionDatasetCandidateId)) {
            this.fallDetectionDatasetCandidateId = '';
        }
        const availableSupplementIds = new Set(this.fallDetectionNormalSupplements().map((item: any) => String(item?.dataset_id || '')));
        this.fallDetectionNormalSupplementIds = this.fallDetectionNormalSupplementIds.filter((datasetId: string) => availableSupplementIds.has(datasetId));
        if (!this.fallDetectionNormalSupplementsInitialized && Array.isArray(payload?.normal_supplements)) {
            this.fallDetectionNormalSupplementIds = this.fallDetectionNormalSupplements()
                .filter((item: any) => item?.ready === true)
                .map((item: any) => String(item?.dataset_id || ''));
            this.fallDetectionNormalSupplementsInitialized = true;
        }
        if (this.fallDetectionNormalSupplementCandidateId && !availableSupplementIds.has(this.fallDetectionNormalSupplementCandidateId)) {
            this.fallDetectionNormalSupplementCandidateId = '';
        }
        if (this.fallDetectionPoseModelId && !this.selectedFallDetectionPoseExtractor()?.model_id) {
            this.fallDetectionPoseModelId = '';
        }
        const jobs = this.fallDetectionJobs();
        const runningJob = jobs.find((item: any) => ['queued', 'running'].includes(String(item?.status || '').toLowerCase()));
        const currentJob = this.fallDetectionJob?.job_id
            ? jobs.find((item: any) => String(item?.job_id || '') === String(this.fallDetectionJob.job_id || ''))
            : null;
        // The server-side job survives a browser refresh. Always reconnect the
        // screen to the newest active job instead of keeping a stale cached job.
        this.fallDetectionJob = runningJob || currentJob || jobs[0] || null;
        const nextJobStatus = String(this.fallDetectionJob?.status || '').toLowerCase();
        if (
            !this.trainingModeEntering
            &&
            previousJobId
            && previousJobId === String(this.fallDetectionJob?.job_id || '')
            && ['queued', 'running'].includes(previousJobStatus)
            && nextJobStatus === 'completed'
        ) {
            setTimeout(() => this.scrollToTrainingSection('fall-detection-completed'), 30);
        }
        if (this.fallCandidateTestJobId && !this.selectedFallCandidateTestJob()?.job_id) {
            this.fallCandidateTestJobId = '';
            this.fallCandidateTestResult = null;
            this.fallCandidateTestProgress = 0;
            this.fallCandidateTestMessage = '';
        }
        if (this.fallCandidateServerVideoId && !this.selectedFallCandidateServerVideo()?.video_id) {
            this.clearFallCandidateTestVideo();
        }
        if (!Number.isFinite(this.fallDetectionFps)) this.fallDetectionFps = Number(defaults.fps || 6);
        if (!Number.isFinite(this.fallDetectionWindowSize)) this.fallDetectionWindowSize = Number(defaults.window_size || 1);
        if (!Number.isFinite(this.fallDetectionStride)) this.fallDetectionStride = Number(defaults.stride || 0.5);
        const runningBatch = this.fallCandidateBatchTests().find(
            (item: any) => ['queued', 'running'].includes(String(item?.status || '').toLowerCase()),
        );
        if (runningJob) this.startPoseStudioPolling();
        if (runningBatch) this.startPoseStudioPolling();
    }

    private applyPoseStudioRegistry(payload: any) {
        if (!payload || typeof payload !== 'object' || !Array.isArray(payload?.items)) return;
        this.poseStudioRegistry = payload;
        this.applyFallDetectionStudio(payload?.fall_detection_studio);
        const datasets = Array.isArray(payload?.pose_studio?.datasets) ? payload.pose_studio.datasets : [];
        if (datasets.length) this.cachePoseStudioDatasets(datasets);
        const jobs = Array.isArray(payload?.pose_studio?.jobs) ? payload.pose_studio.jobs : [];
        const evaluationJobs = Array.isArray(payload?.pose_studio?.evaluation_jobs) ? payload.pose_studio.evaluation_jobs : [];
        if (this.poseStudioJob?.job_id) {
            this.poseStudioJob = jobs.find((item: any) => item.job_id === this.poseStudioJob.job_id) || this.poseStudioJob;
        } else if (jobs.length) {
            this.poseStudioJob = jobs[0];
        }
        let requestedModelId = '';
        try { requestedModelId = String(localStorage.getItem(POSE_TRAINING_MODEL_KEY) || ''); } catch (_e) { }
        const requestedModel = requestedModelId
            ? this.poseTrainableModels().find((item: any) => String(item?.model_id || '') === requestedModelId)
            : null;
        if (requestedModel) {
            this.poseStudioBaseModelId = requestedModelId;
            this.poseStudioModelCategory = this.poseModelCategoryKey(requestedModel);
            try { localStorage.removeItem(POSE_TRAINING_MODEL_KEY); } catch (_e) { }
        } else if (!this.poseStudioBaseModelId && this.poseTrainableModels().length) {
            this.poseStudioBaseModelId = String(this.poseTrainableModels()[0].model_id || '');
        }
        if (!this.poseStudioDatasetId && this.poseReadyDatasets().length) {
            this.selectPoseStudioDataset(String(this.poseReadyDatasets()[0].dataset_id || ''));
        }
        if (!this.poseHistoryCompareLeftId && jobs.length) this.poseHistoryCompareLeftId = String(jobs[0]?.job_id || '');
        if (!this.poseHistoryCompareRightId && jobs.length > 1) this.poseHistoryCompareRightId = String(jobs[1]?.job_id || '');
        const running = [...jobs, ...evaluationJobs, ...this.fallDetectionJobs(), ...this.fallCandidateBatchTests()].some(
            (item: any) => ['queued', 'running'].includes(String(item?.status || '').toLowerCase()),
        );
        if (running) this.startPoseStudioPolling();
        else this.stopPoseStudioPolling();
    }

    public async loadFallDetectionTrainingSnapshot(force: boolean = false) {
        const forceDatasetIds = force
            ? (this.fallDetectionDatasetIds.length > 0
                ? [...this.fallDetectionDatasetIds]
                : (this.fallDetectionDatasetCandidateId ? [this.fallDetectionDatasetCandidateId] : []))
            : [];
        if (force && forceDatasetIds.length === 0) {
            this.fallDetectionMessage = '다시 검사할 Dataset을 먼저 선택하거나 추가하세요.';
            this.fallDetectionMessageType = 'info';
            await this.service.render();
            return;
        }
        if (force) {
            this.fallDetectionValidationBusy = true;
            this.fallDetectionMessage = `선택한 Dataset ${forceDatasetIds.length}개를 다시 검사하고 있습니다.`;
            this.fallDetectionMessageType = 'info';
            await this.service.render();
        }
        try {
            const targets = force ? forceDatasetIds : [''];
            for (const datasetId of targets) {
                const endpoint = force ? 'validate_fall_detection_dataset' : 'fall_detection_training_snapshot';
                const params = new URLSearchParams();
                if (datasetId) params.set('dataset_id', datasetId);
                const query = params.toString() ? `?${params.toString()}` : '';
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), 30000);
                try {
                    const res = await fetch(`/wiz/api/page.models/${endpoint}${query}`, {
                        method: force ? 'POST' : 'GET',
                        signal: controller.signal,
                    });
                    const json = await this.readJsonResponse(res);
                    const payload = json?.data && typeof json.data === 'object' ? json.data : json;
                    if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Fall Detection 학습 정보를 불러오지 못했습니다.');
                    this.applyFallDetectionStudio(payload);
                } finally {
                    window.clearTimeout(timeoutId);
                }
            }
            if (force) {
                this.fallDetectionMessage = `선택한 Dataset ${forceDatasetIds.length}개의 구조와 라벨을 다시 확인했습니다.`;
                this.fallDetectionMessageType = 'success';
            }
        } catch (error: any) {
            this.fallDetectionMessage = error?.name === 'AbortError'
                ? 'Dataset 검사 시간이 30초를 넘었습니다. 대용량 Dataset은 하나씩 선택해 다시 검사하세요.'
                : (error?.message || 'Fall Detection 학습 정보를 불러오지 못했습니다.');
            this.fallDetectionMessageType = 'error';
        } finally {
            if (force) this.fallDetectionValidationBusy = false;
        }
        await this.service.render();
    }

    public fallDetectionDatasets(): any[] {
        return Array.isArray(this.fallDetectionStudio?.datasets) ? this.fallDetectionStudio.datasets : [];
    }

    public fallDetectionNormalSupplements(): any[] {
        return Array.isArray(this.fallDetectionStudio?.normal_supplements) ? this.fallDetectionStudio.normal_supplements : [];
    }

    public selectedFallDetectionNormalSupplements(): any[] {
        const selected = new Set(this.fallDetectionNormalSupplementIds);
        return this.fallDetectionNormalSupplements().filter((item: any) => selected.has(String(item?.dataset_id || '')));
    }

    public isFallDetectionNormalSupplementSelected(item: any): boolean {
        return this.fallDetectionNormalSupplementIds.includes(String(item?.dataset_id || ''));
    }

    public addFallDetectionNormalSupplement() {
        const item = this.fallDetectionNormalSupplements().find(
            (row: any) => String(row?.dataset_id || '') === this.fallDetectionNormalSupplementCandidateId,
        );
        if (!item || item?.ready !== true || this.isFallDetectionNormalSupplementSelected(item) || this.fallDetectionBusy) return;
        this.fallDetectionNormalSupplementIds = [...this.fallDetectionNormalSupplementIds, String(item.dataset_id)];
        this.fallDetectionNormalSupplementCandidateId = '';
        this.service.render();
    }

    public canAddFallDetectionNormalSupplement(): boolean {
        const item = this.fallDetectionNormalSupplements().find(
            (row: any) => String(row?.dataset_id || '') === this.fallDetectionNormalSupplementCandidateId,
        );
        return !this.fallDetectionBusy && item?.ready === true && !this.isFallDetectionNormalSupplementSelected(item);
    }

    public toggleFallDetectionNormalSupplement(item: any) {
        const datasetId = String(item?.dataset_id || '');
        if (!datasetId || this.fallDetectionBusy) return;
        this.fallDetectionNormalSupplementIds = this.isFallDetectionNormalSupplementSelected(item)
            ? this.fallDetectionNormalSupplementIds.filter((value: string) => value !== datasetId)
            : [...this.fallDetectionNormalSupplementIds, datasetId];
        this.service.render();
    }

    public fallDetectionPoseExtractors(): any[] {
        return Array.isArray(this.fallDetectionStudio?.pose_extractors) ? this.fallDetectionStudio.pose_extractors : [];
    }

    public fallDetectionClassifiers(): any[] {
        return Array.isArray(this.fallDetectionStudio?.classifiers) ? this.fallDetectionStudio.classifiers : [];
    }

    public selectedFallDetectionClassifiers(): any[] {
        const selected = new Set(this.fallDetectionClassifierTypes);
        return this.fallDetectionClassifiers().filter((item: any) => selected.has(String(item?.key || '')));
    }

    public isFallDetectionClassifierSelected(classifier: any): boolean {
        return this.fallDetectionClassifierTypes.includes(String(classifier?.key || classifier || ''));
    }

    public toggleFallDetectionClassifier(classifier: any) {
        if (this.fallDetectionBusy) return;
        const classifierType = String(classifier?.key || classifier || '');
        if (!classifierType) return;
        this.fallDetectionClassifierTypes = this.isFallDetectionClassifierSelected(classifierType)
            ? this.fallDetectionClassifierTypes.filter((value: string) => value !== classifierType)
            : [...this.fallDetectionClassifierTypes, classifierType];
        this.fallDetectionMessage = '';
        this.service.render();
    }

    public fallDetectionJobClassifierLabel(job: any = this.fallDetectionJob): string {
        const classifierType = String(job?.classifier_type || '');
        const classifier = this.fallDetectionClassifiers().find((item: any) => String(item?.key || '') === classifierType);
        return String(job?.classifier_label || classifier?.label || classifierType || '낙상 모델');
    }

    public toggleFallDetectionAdvancedSettings() {
        this.fallDetectionAdvancedSettingsOpen = !this.fallDetectionAdvancedSettingsOpen;
    }

    public useRecommendedFallDetectionSettings() {
        const defaults = this.fallDetectionStudio?.temporal_defaults || {};
        this.fallDetectionFps = Number(defaults.fps || 6);
        this.fallDetectionWindowSize = Number(defaults.window_size || 1);
        this.fallDetectionStride = Number(defaults.stride || 0.5);
        this.fallDetectionTracking = defaults.tracking !== false;
        this.fallDetectionKeypointConfidence = Number(defaults.keypoint_confidence ?? 0.25);
        this.fallDetectionPersonVisibility = Number(defaults.person_visibility ?? 0);
    }

    public fallDetectionJobs(): any[] {
        return Array.isArray(this.fallDetectionStudio?.jobs) ? this.fallDetectionStudio.jobs : [];
    }

    public fallDetectionCandidates(): any[] {
        return this.fallDetectionJobs().filter((item: any) => (
            String(item?.status || '').toLowerCase() === 'completed'
            && Boolean(item?.primary_model_path)
        ));
    }

    public activeFallDetectionJob(): any {
        const status = String(this.fallDetectionJob?.status || '').toLowerCase();
        return ['queued', 'running'].includes(status) ? this.fallDetectionJob : {};
    }

    public activeFallDetectionJobs(): any[] {
        return this.fallDetectionJobs().filter((item: any) => (
            ['queued', 'running'].includes(String(item?.status || '').toLowerCase())
        ));
    }

    public fallDetectionCandidateMetric(job: any, key: string): string {
        const value = Number(job?.metrics?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallDetectionCandidateTitle(job: any): string {
        const classifierType = String(job?.classifier_type || '').toLowerCase();
        const classifier = classifierType === 'rf-pipeline' ? 'RF-Pipeline'
            : (classifierType === 'rf-fall-v2' ? 'RF-Fall'
                : (classifierType === 'rf-fusion' ? 'RF-Fusion' : String(job?.classifier_label || '낙상 모델')));
        const pose = String(job?.pose_extractor?.label || 'Pose 모델')
            .replace(/^Pose 추출 모델\s*·\s*/i, '')
            .trim();
        const dataset = String(job?.dataset_name || '').trim();
        return [classifier, pose, dataset].filter((value: string) => Boolean(value)).join(' · ');
    }

    public fallDetectionNormalBehaviorRows(): any[] {
        const labels: Record<string, string> = {
            lying: '눕기', sitting: '앉기', picking_up: '물건 줍기', bed_transition: '침대 오르내리기',
        };
        const counts: Record<string, number> = { lying: 0, sitting: 0, picking_up: 0, bed_transition: 0 };
        for (const dataset of this.selectedFallDetectionDatasets()) {
            const values = dataset?.normal_behavior_coverage || dataset?.fall_detection_validation?.normal_behavior_coverage || {};
            for (const key of Object.keys(counts)) counts[key] += Number(values?.[key] || 0);
        }
        for (const dataset of this.selectedFallDetectionNormalSupplements()) {
            const values = dataset?.normal_behavior_coverage || {};
            for (const key of Object.keys(counts)) counts[key] += Number(values?.[key] || 0);
        }
        return Object.keys(counts).map((key: string) => ({ key, label: labels[key], count: counts[key], ready: counts[key] > 0 }));
    }

    public selectCompletedFallCandidate(jobId: string) {
        this.stopFallCandidatePosePlayback(false);
        this.fallCandidateTestJobId = String(jobId || '');
        this.fallCandidateDetailView = 'final';
        this.fallCandidateTestResult = null;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestMessage = '';
        void this.service.render();
        setTimeout(() => this.scrollToTrainingSection('fall-candidate-test'), 0);
    }

    public async deleteCompletedFallCandidate(job: any) {
        const candidateId = String(job?.job_id || '');
        if (!candidateId || this.fallDetectionDeletingJobId) return;
        const confirmed = await this.service.modal.show({
            title: '학습 완료 모델 삭제',
            message: `${job?.candidate_label || '선택한 후보 모델'}을 학습 완료 목록에서 삭제하시겠습니까? 현재 운영 모델은 변경되지 않습니다.`,
            action: '삭제',
            cancel: '취소',
            status: 'warning',
            actionBtn: 'warning',
        });
        if (!confirmed) return;
        this.fallDetectionDeletingJobId = candidateId;
        this.fallDetectionCompletedMessage = '';
        await this.service.render();
        try {
            const params = new URLSearchParams({ candidate_id: candidateId });
            const res = await fetch(`/wiz/api/page.models/delete_fall_detection_candidate?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || '학습 완료 모델 삭제 실패');
            if (this.fallCandidateTestJobId === candidateId) {
                this.stopFallCandidatePosePlayback(false);
                this.fallCandidateTestJobId = '';
                this.fallCandidateTestResult = null;
                this.fallCandidateTestProgress = 0;
                this.fallCandidateTestMessage = '';
            }
            this.applyFallDetectionStudio(payload?.fall_detection_studio);
            this.fallDetectionCompletedMessage = payload?.message || '학습 완료 모델을 삭제했습니다.';
        } catch (error: any) {
            this.fallDetectionCompletedMessage = error?.message || '학습 완료 모델 삭제 실패';
        }
        this.fallDetectionDeletingJobId = '';
        await this.service.render();
    }

    public selectedFallCandidateTestJob(): any {
        return this.fallDetectionCandidates().find((item: any) => String(item?.job_id || '') === this.fallCandidateTestJobId) || {};
    }

    public fallCandidateServerVideos(): any[] {
        return Array.isArray(this.fallDetectionStudio?.test_videos) ? this.fallDetectionStudio.test_videos : [];
    }

    public fallCandidateBatchTests(): any[] {
        return Array.isArray(this.fallDetectionStudio?.candidate_batch_tests)
            ? this.fallDetectionStudio.candidate_batch_tests : [];
    }

    public selectedFallCandidateBatchTest(): any {
        return this.fallCandidateBatchTests().find(
            (item: any) => String(item?.candidate_id || '') === this.fallCandidateTestJobId,
        ) || {};
    }

    public fallCandidateBatchIsRunning(): boolean {
        return ['queued', 'running'].includes(String(this.selectedFallCandidateBatchTest()?.status || '').toLowerCase());
    }

    public fallCandidateBatchProgress(): number {
        const value = Number(this.selectedFallCandidateBatchTest()?.progress || 0);
        return Math.max(0, Math.min(100, Math.round(value * 100)));
    }

    public fallCandidateBatchMetric(key: string): string {
        const value = Number(this.selectedFallCandidateBatchTest()?.metrics?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateBatchRecentResults(): any[] {
        const results = Array.isArray(this.selectedFallCandidateBatchTest()?.results)
            ? this.selectedFallCandidateBatchTest().results : [];
        return [...results].sort((left: any, right: any) => {
            const leftWrong = left?.status === 'failed' || String(left?.expected_label || '') !== String(left?.predicted_label || '');
            const rightWrong = right?.status === 'failed' || String(right?.expected_label || '') !== String(right?.predicted_label || '');
            return Number(rightWrong) - Number(leftWrong);
        }).slice(0, 12);
    }

    public async startFallCandidateBatchTest() {
        if (!this.fallCandidateTestJobId || this.fallCandidateBatchStarting || this.fallCandidateBatchIsRunning()) return;
        this.fallCandidateBatchStarting = true;
        this.fallCandidateTestMessage = `서버 Test 영상 ${this.fallCandidateServerVideos().length}개 전체 분석을 시작하고 있습니다.`;
        await this.service.render();
        try {
            const params = new URLSearchParams({
                training_type: 'fall_detection_batch_test',
                candidate_id: this.fallCandidateTestJobId,
            });
            const response = await fetch(`/wiz/api/page.models/start_pose_model_training?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(response);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || '전체 영상 분석을 시작하지 못했습니다.');
            this.fallDetectionStudio = {
                ...(this.fallDetectionStudio || {}),
                candidate_batch_tests: [payload, ...this.fallCandidateBatchTests().filter((item: any) => item?.batch_id !== payload?.batch_id)],
            };
            this.fallCandidateTestMessage = payload?.message || '전체 영상 분석을 백그라운드에서 시작했습니다.';
            this.startPoseStudioPolling();
        } catch (error: any) {
            this.fallCandidateTestMessage = error?.message || '전체 영상 분석 시작에 실패했습니다.';
        } finally {
            this.fallCandidateBatchStarting = false;
            await this.service.render();
        }
    }

    public selectedFallCandidateServerVideo(): any {
        return this.fallCandidateServerVideos().find(
            (item: any) => String(item?.video_id || '') === this.fallCandidateServerVideoId,
        ) || {};
    }

    public fallCandidateServerVideoOption(item: any): string {
        const label = String(item?.expected_label || '').toUpperCase() === 'FALL' ? '낙상 폴더'
            : (String(item?.expected_label || '').toUpperCase() === 'NORMAL' ? '정상 폴더' : '미분류');
        const size = Number(item?.size_bytes || 0) / (1024 * 1024);
        return `${item?.filename || 'Test 영상'} · ${label} · ${size.toFixed(1)}MB`;
    }

    public onFallCandidateServerVideoChanged(videoId: string) {
        this.stopFallCandidatePosePlayback(false);
        if (this.fallCandidateTestVideoUrl) {
            try { URL.revokeObjectURL(this.fallCandidateTestVideoUrl); } catch (_e) { }
        }
        this.fallCandidateServerVideoId = String(videoId || '');
        this.fallCandidateTestFile = null;
        this.fallCandidateTestVideoUrl = this.fallCandidateServerVideoId
            ? `/wiz/api/page.models/fall_candidate_test_video?video_id=${encodeURIComponent(this.fallCandidateServerVideoId)}`
            : '';
        this.fallCandidateTestResult = null;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestVideoDurationSec = 0;
        this.fallCandidateTestMessage = this.fallCandidateServerVideoId
            ? '서버 Test 영상을 선택했습니다. 후보 모델을 선택한 뒤 분석하세요.' : '';
        void this.service.render();
    }

    public setFallCandidateDetailView(view: 'raw' | 'final' | 'improvement') {
        this.fallCandidateDetailView = ['raw', 'improvement'].includes(view) ? view : 'final';
        if (this.fallCandidateDetailView !== 'final') this.stopFallCandidatePosePlayback(false);
        this.service.render();
    }

    public fallCandidateDetailViewClass(view: 'raw' | 'final' | 'improvement'): string {
        return this.fallCandidateDetailView === view
            ? 'border-violet-600 bg-violet-600 text-white shadow-sm'
            : 'border-zinc-200 bg-white text-zinc-600 hover:border-violet-300 hover:text-violet-700';
    }

    public onFallCandidateTestJobChanged(jobId: string) {
        this.stopFallCandidatePosePlayback(false);
        this.fallCandidateTestJobId = String(jobId || '');
        this.fallCandidateDetailView = 'final';
        this.fallCandidateTestResult = null;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestVideoDurationSec = 0;
        this.fallCandidateTestMessage = '';
        this.service.render();
    }

    public selectedFallCandidateTrainingSummary(): any {
        const job = this.selectedFallCandidateTestJob();
        return job?.training_summary && typeof job.training_summary === 'object' ? job.training_summary : {};
    }

    public fallCandidatePerformanceMetric(key: string): string {
        const job = this.selectedFallCandidateTestJob();
        const summary = this.selectedFallCandidateTrainingSummary();
        const value = Number(job?.metrics?.[key] ?? summary?.metrics?.[key] ?? summary?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateConfusionCounts(): { tn: number; fp: number; fn: number; tp: number } {
        const job = this.selectedFallCandidateTestJob();
        const summary = this.selectedFallCandidateTrainingSummary();
        const matrix = job?.metrics?.confusion_matrix ?? summary?.confusion_matrix ?? summary?.metrics?.confusion_matrix;
        const tn = Number(matrix?.[0]?.[0]);
        const fp = Number(matrix?.[0]?.[1]);
        const fn = Number(matrix?.[1]?.[0]);
        const tp = Number(matrix?.[1]?.[1]);
        return {
            tn: Number.isFinite(tn) ? tn : 0,
            fp: Number.isFinite(fp) ? fp : 0,
            fn: Number.isFinite(fn) ? fn : 0,
            tp: Number.isFinite(tp) ? tp : 0,
        };
    }

    public fallCandidatePerformanceRate(kind: 'false_alarm' | 'miss_detection' | 'specificity'): string {
        const counts = this.fallCandidateConfusionCounts();
        let numerator = 0;
        let denominator = 0;
        if (kind === 'false_alarm') { numerator = counts.fp; denominator = counts.tn + counts.fp; }
        if (kind === 'miss_detection') { numerator = counts.fn; denominator = counts.tp + counts.fn; }
        if (kind === 'specificity') { numerator = counts.tn; denominator = counts.tn + counts.fp; }
        return denominator > 0 ? `${(numerator / denominator * 100).toFixed(1)}%` : '-';
    }

    public fallCandidatePerformanceSampleCount(): number {
        const counts = this.fallCandidateConfusionCounts();
        return counts.tn + counts.fp + counts.fn + counts.tp;
    }

    public fallCandidatePerformanceThreshold(): string {
        const summary = this.selectedFallCandidateTrainingSummary();
        const value = Number(summary?.threshold ?? summary?.validation?.threshold);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateEventPostprocess(): any {
        const value = this.fallCandidateTestResult?.event_postprocess;
        return value && typeof value === 'object' ? value : {};
    }

    public fallCandidateEventRiskPercent(): string {
        const value = Number(this.fallCandidateEventPostprocess()?.event_risk_score);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateShadowRiskPercent(): string {
        const value = Number(this.fallCandidateEventPostprocess()?.shadow_peak_risk_score);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateEventIntervalText(): string {
        const interval = this.fallCandidateEventPostprocess()?.event_interval;
        if (!Array.isArray(interval) || interval.length < 2) return '확정 구간 없음';
        return `${Number(interval[0]).toFixed(3)}초 ~ ${Number(interval[1]).toFixed(3)}초`;
    }

    public fallCandidateRawPeakIntervalText(): string {
        const interval = this.fallCandidateEventPostprocess()?.raw_peak_interval;
        if (!Array.isArray(interval) || interval.length < 2) return '-';
        return `${Number(interval[0]).toFixed(3)}초 ~ ${Number(interval[1]).toFixed(3)}초`;
    }

    public fallCandidatePostprocessRiskDeltaText(): string {
        const value = Number(this.fallCandidateEventPostprocess()?.risk_delta);
        if (!Number.isFinite(value)) return '-';
        const percentagePoint = value * 100;
        if (Math.abs(percentagePoint) < 0.05) return '변화 없음';
        return `${Math.abs(percentagePoint).toFixed(1)}%p ${percentagePoint > 0 ? '높아짐' : '낮아짐'}`;
    }

    public fallCandidatePostprocessPeakShiftText(): string {
        const value = Number(this.fallCandidateEventPostprocess()?.peak_start_shift_sec);
        if (!Number.isFinite(value)) return '-';
        if (Math.abs(value) < 0.0005) return '변화 없음';
        return `${Math.abs(value).toFixed(3)}초 ${value > 0 ? '뒤로 이동' : '앞으로 이동'}`;
    }

    public fallCandidateFinalStateLabel(): string {
        const state = String(this.fallCandidateEventPostprocess()?.final_state || '').toUpperCase();
        const labels: Record<string, string> = {
            FALL_CONFIRMED: '낙상 확정',
            FALL_SUSPECTED: '낙상 의심 · 확인 필요',
            NEAR_FALL_RECOVERED: '넘어질 뻔했지만 회복',
            NON_FALL: '정상 행동',
            UNCERTAIN: '판단 어려움 · 확인 필요',
            INSUFFICIENT_DETECTION: '사람 인식 부족',
        };
        return labels[state] || (state ? state.replace(/_/g, ' ') : '분석 상태 없음');
    }

    public fallCandidateFinalDecisionLabel(): string {
        const decision = String(
            this.fallCandidateEventPostprocess()?.final_decision
            || this.fallCandidateTestResult?.result_label
            || ''
        ).toUpperCase();
        if (decision === 'FALL') return '낙상';
        if (decision === 'SUSPECTED') return '낙상 의심';
        if (decision === 'NORMAL' || decision === 'NON_FALL') return '정상';
        return '확인 필요';
    }

    public fallCandidateAppliedRuleLabel(): string {
        const rule = String(this.fallCandidateEventPostprocess()?.applied_rule || 'MODEL_DECISION_PRESERVED');
        const labels: Record<string, string> = {
            MODEL_DECISION_PRESERVED: '모델 판단을 그대로 유지했습니다.',
            SHADOW_HIGHER_WITH_TWO_EVENT_FEATURES: '움직임 근거가 충분해 낙상 의심으로 보완했습니다.',
            PRECISION_FULL_TIMELINE_FALSE_POSITIVE_GUARD: '영상 전체를 확인해 잘못된 낙상 알림을 줄였습니다.',
            ZERO_PERSON_FRAMES_INSUFFICIENT_DETECTION: '사람이 보이지 않아 자동 판정을 보류했습니다.',
            SPARSE_DETECTION_WITH_NEIGHBOR_EVENT_EVIDENCE: '사람 인식은 적지만 주변 움직임을 근거로 낙상을 의심했습니다.',
            SPARSE_DETECTION_REVIEW_REQUIRED: '사람 인식이 부족해 직접 확인이 필요합니다.',
        };
        return labels[rule] || '모델 판단과 움직임 정보를 함께 확인했습니다.';
    }

    public fallCandidateDecisionChangeText(): string {
        const result = this.fallCandidateEventPostprocess();
        const decisionLabel = (value: any) => String(value || '').toUpperCase() === 'FALL' ? '낙상'
            : (String(value || '').toUpperCase() === 'SUSPECTED' ? '낙상 의심' : '정상');
        const raw = decisionLabel(result?.raw_decision);
        const final = decisionLabel(result?.final_decision);
        return result?.decision_changed ? `${raw} → ${final}` : `${final} 유지`;
    }

    public fallCandidateEventReasonLabels(): string {
        const labels: Record<string, string> = {
            RAPID_VERTICAL_DROP: '빠른 중심 하강',
            ABRUPT_TORSO_ROTATION: '급격한 몸통 기울기',
            IMPACT_CANDIDATE: '충격 후보',
            LOW_POSITION_PERSISTENCE: '낮은 자세 지속',
            LATERAL_COLLAPSE_GEOMETRY: '측면 붕괴 형태',
            CONTROLLED_CONTACT_UNVERIFIED: '접촉 여부 확인 필요',
            OCCLUSION_LIMITED: '가림으로 판단 제한',
            INSUFFICIENT_VALID_KEYPOINTS: '관절점 부족',
            SHADOW_ANALYSIS_ERROR: 'Event 분석 오류',
        };
        const codes = Array.isArray(this.fallCandidateEventPostprocess()?.reason_codes)
            ? this.fallCandidateEventPostprocess().reason_codes : [];
        return codes.length ? codes.map((code: string) => labels[code] || code).join(' · ') : '추가 Event 근거 없음';
    }

    public onFallCandidateTestVideoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        if (!file) return;
        if (!String(file.type || '').startsWith('video/') && !/\.(mp4|mov|avi|mkv|webm)$/i.test(file.name)) {
            this.fallCandidateTestMessage = '동영상 파일을 선택하세요.';
            return;
        }
        if (this.fallCandidateTestVideoUrl) {
            try { URL.revokeObjectURL(this.fallCandidateTestVideoUrl); } catch (_e) { }
        }
        this.fallCandidateTestFile = file;
        this.fallCandidateServerVideoId = '';
        this.fallCandidateTestVideoUrl = URL.createObjectURL(file);
        this.fallCandidateTestResult = null;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestVideoDurationSec = 0;
        this.fallCandidateTestMessage = '';
        void this.service.render();
    }

    public clearFallCandidateTestVideo() {
        this.stopFallCandidatePosePlayback(false);
        if (this.fallCandidateTestVideoUrl) {
            try { URL.revokeObjectURL(this.fallCandidateTestVideoUrl); } catch (_e) { }
        }
        this.fallCandidateTestFile = null;
        this.fallCandidateServerVideoId = '';
        this.fallCandidateTestVideoUrl = '';
        this.fallCandidateTestResult = null;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestMessage = '';
        void this.service.render();
    }

    private resetFallCandidateTestAnalysis() {
        this.stopFallCandidateTestProgress();
        this.stopFallCandidatePosePlayback(false);
        if (this.fallCandidateTestVideoUrl) {
            try { URL.revokeObjectURL(this.fallCandidateTestVideoUrl); } catch (_e) { }
        }
        this.fallCandidateTestJobId = '';
        this.fallCandidateTestFile = null;
        this.fallCandidateServerVideoId = '';
        this.fallCandidateTestVideoUrl = '';
        this.fallCandidateTestBusy = false;
        this.fallCandidateTestProgress = 0;
        this.fallCandidateTestResult = null;
        this.fallCandidateTestVideoDurationSec = 0;
        this.fallCandidateTestMessage = '';
        this.fallCandidateDetailView = 'final';
    }

    public canTestFallCandidate(): boolean {
        return Boolean(
            (this.fallCandidateTestFile || this.selectedFallCandidateServerVideo()?.video_id)
            && this.selectedFallCandidateTestJob()?.job_id
            && !this.fallCandidateTestBusy
        );
    }

    private startFallCandidateTestProgress() {
        this.stopFallCandidateTestProgress();
        this.fallCandidateTestProgress = 3;
        this.fallCandidateTestProgressHandle = setInterval(async () => {
            this.fallCandidateTestProgress = Math.min(92, this.fallCandidateTestProgress + Math.max(1, Math.round((92 - this.fallCandidateTestProgress) * 0.08)));
            await this.service.render();
        }, 700);
    }

    private stopFallCandidateTestProgress() {
        if (this.fallCandidateTestProgressHandle) clearInterval(this.fallCandidateTestProgressHandle);
        this.fallCandidateTestProgressHandle = null;
    }

    public async testFallDetectionCandidate() {
        if (!this.canTestFallCandidate()) return;
        this.fallCandidateTestBusy = true;
        this.fallCandidateDetailView = 'final';
        this.fallCandidateTestResult = null;
        this.fallCandidateTestMessage = '후보 모델로 Pose와 낙상 구간을 분석하고 있습니다.';
        this.startFallCandidateTestProgress();
        await this.service.render();
        try {
            const form = new FormData();
            if (this.fallCandidateTestFile) form.append('video', this.fallCandidateTestFile);
            const params = new URLSearchParams({ candidate_id: this.fallCandidateTestJobId });
            if (this.fallCandidateServerVideoId) params.set('test_video_id', this.fallCandidateServerVideoId);
            let response = await fetch(`/wiz/api/page.models/test_fall_detection_candidate?${params.toString()}`, {
                method: 'POST', body: form,
            });
            let json = await this.readJsonResponse(response);
            if (Number(json?.code) === 404) {
                params.set('training_type', 'fall_detection_test');
                response = await fetch(`/wiz/api/page.models/start_pose_model_training?${params.toString()}`, {
                    method: 'POST', body: form,
                });
                json = await this.readJsonResponse(response);
            }
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || '후보 모델 테스트에 실패했습니다.');
            this.fallCandidateTestResult = payload;
            this.fallCandidateTestProgress = 100;
            this.fallCandidateTestMessage = payload?.message || '후보 모델 테스트를 완료했습니다.';
        } catch (error: any) {
            this.fallCandidateTestMessage = error?.message || '후보 모델 테스트에 실패했습니다.';
            this.fallCandidateTestProgress = 0;
        } finally {
            this.stopFallCandidateTestProgress();
            this.fallCandidateTestBusy = false;
            await this.service.render();
            setTimeout(() => this.drawFallCandidateTestPose(), 0);
        }
    }

    public fallCandidateTestIsCompleted(): boolean {
        const resultStatus = String(this.fallCandidateTestResult?.candidate_test?.status || '').toLowerCase();
        const jobStatus = String(this.selectedFallCandidateTestJob()?.candidate_test?.status || '').toLowerCase();
        return resultStatus === 'completed' || jobStatus === 'completed';
    }

    public async completeFallCandidateTest() {
        if (!this.fallCandidateTestResult || !this.fallCandidateTestJobId || this.fallCandidateTestCompleting || this.fallCandidateTestIsCompleted()) return;
        this.fallCandidateTestCompleting = true;
        this.fallCandidateTestMessage = 'Test 완료 모델로 등록하고 자동 호환성 검사를 진행하고 있습니다.';
        await this.service.render();
        try {
            const params = new URLSearchParams({
                training_type: 'fall_detection_test',
                candidate_id: this.fallCandidateTestJobId,
                complete: 'true',
            });
            const response = await fetch(`/wiz/api/page.models/start_pose_model_training?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(response);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || 'Test 완료 처리에 실패했습니다.');
            if (payload?.candidate_test) this.fallCandidateTestResult.candidate_test = payload.candidate_test;
            if (payload?.fall_detection_studio) this.applyFallDetectionStudio(payload.fall_detection_studio);
            try {
                localStorage.setItem('fallai-model-library-category', 'tested');
                sessionStorage.removeItem('fallai:model-registry:v2');
            } catch (_e) { }
            this.resetFallCandidateTestAnalysis();
            window.location.assign('/models#model-library');
        } catch (error: any) {
            this.fallCandidateTestMessage = error?.message || 'Test 완료 처리에 실패했습니다.';
        } finally {
            this.fallCandidateTestCompleting = false;
            await this.service.render();
        }
    }

    public openTestCompletedModelLibrary() {
        try { localStorage.setItem('fallai-model-library-category', 'tested'); } catch (_e) { }
        window.location.assign('/models#model-library');
    }

    public fallCandidateTestRiskPercent(): string {
        const value = Number(this.fallCandidateTestResult?.fall_probability);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateTestThresholdPercent(): string {
        const value = Number(this.fallCandidateTestResult?.threshold);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateTestCoveragePercent(): string {
        const value = Number(this.fallCandidateTestResult?.tracking_summary?.coverage_ratio);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateTestDetectionPercent(): string {
        const value = Number(this.fallCandidateTestResult?.tracking_summary?.pose_detection_rate);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateTestAverageRiskPercent(): string {
        const value = Number(this.fallCandidateTestResult?.decision_summary?.average_fall_probability);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public fallCandidateTestDecisionMarginText(): string {
        const value = Number(this.fallCandidateTestResult?.decision_summary?.margin_to_threshold);
        if (!Number.isFinite(value)) return '-';
        const points = Math.abs(value * 100).toFixed(1);
        return value >= 0 ? `기준보다 ${points}%p 높음` : `기준보다 ${points}%p 낮음`;
    }

    public fallCandidateTestTopWindows(): any[] {
        return Array.isArray(this.fallCandidateTestResult?.top_risk_windows)
            ? this.fallCandidateTestResult.top_risk_windows : [];
    }

    public fallCandidateWindowRiskPercent(window: any): string {
        const value = Number(window?.fall_probability);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public startFallCandidatePosePlayback() {
        this.stopFallCandidatePosePlayback(false);
        const tick = () => {
            this.drawFallCandidateTestPose();
            const video = this.fallCandidateTestVideoRef?.nativeElement;
            if (video && !video.paused && !video.ended) {
                this.fallCandidatePoseAnimationHandle = requestAnimationFrame(tick);
            } else {
                this.fallCandidatePoseAnimationHandle = null;
            }
        };
        this.fallCandidatePoseAnimationHandle = requestAnimationFrame(tick);
    }

    public onFallCandidateTestVideoLoaded() {
        const video = this.fallCandidateTestVideoRef?.nativeElement;
        const duration = Number(video?.duration || 0);
        this.fallCandidateTestVideoDurationSec = Number.isFinite(duration) ? duration : 0;
        this.fallCandidateTestMessage = this.fallCandidateServerVideoId
            ? `서버 Test 영상 준비 완료 · 원본 길이 ${this.fallCandidateTestVideoDurationText()}`
            : this.fallCandidateTestMessage;
        this.drawFallCandidateTestPose();
        void this.service.render();
    }

    public onFallCandidateTestVideoError() {
        this.stopFallCandidatePosePlayback(false);
        this.fallCandidateTestMessage = '영상을 불러오지 못했습니다. 서버 구간 전송 상태를 확인한 뒤 다시 선택하세요.';
        void this.service.render();
    }

    public fallCandidateTestVideoDurationText(): string {
        const seconds = Number(this.fallCandidateTestVideoDurationSec || 0);
        if (!Number.isFinite(seconds) || seconds <= 0) return '-';
        const minutes = Math.floor(seconds / 60);
        const remainder = seconds - (minutes * 60);
        return minutes > 0 ? `${minutes}분 ${remainder.toFixed(1)}초` : `${remainder.toFixed(1)}초`;
    }

    public stopFallCandidatePosePlayback(draw: boolean = true) {
        if (this.fallCandidatePoseAnimationHandle != null) {
            cancelAnimationFrame(this.fallCandidatePoseAnimationHandle);
            this.fallCandidatePoseAnimationHandle = null;
        }
        if (draw) this.drawFallCandidateTestPose();
    }

    public drawFallCandidateTestPose() {
        const video = this.fallCandidateTestVideoRef?.nativeElement;
        const canvas = this.fallCandidateTestCanvasRef?.nativeElement;
        if (!video || !canvas) return;
        const width = Math.max(1, Math.round(video.clientWidth));
        const height = Math.max(1, Math.round(video.clientHeight));
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.clearRect(0, 0, width, height);
        const sourceWidth = Math.max(1, Number(video.videoWidth || this.fallCandidateTestResult?.video?.width || width));
        const sourceHeight = Math.max(1, Number(video.videoHeight || this.fallCandidateTestResult?.video?.height || height));
        const scale = Math.min(width / sourceWidth, height / sourceHeight);
        const drawWidth = sourceWidth * scale;
        const drawHeight = sourceHeight * scale;
        const offsetX = (width - drawWidth) / 2;
        const offsetY = (height - drawHeight) / 2;
        const pointX = (value: any) => offsetX + Number(value || 0) * drawWidth;
        const pointY = (value: any) => offsetY + Number(value || 0) * drawHeight;
        const frames = Array.isArray(this.fallCandidateTestResult?.pose_frames) ? this.fallCandidateTestResult.pose_frames : [];
        if (!frames.length) return;
        const currentTime = Number(video.currentTime || 0);
        let nearest = frames[0];
        for (const frame of frames) {
            if (Math.abs(Number(frame?.timestamp || 0) - currentTime) < Math.abs(Number(nearest?.timestamp || 0) - currentTime)) nearest = frame;
        }
        const sampleFps = Math.max(1, Number(this.fallCandidateTestResult?.tracking_summary?.sample_fps || 6));
        const maximumPoseAge = Math.max(0.35, 2 / sampleFps);
        if (Math.abs(Number(nearest?.timestamp || 0) - currentTime) > maximumPoseAge) return;
        const frameIndex = Number(nearest?.frame_index || 0);
        const visible = frames.filter((frame: any) => Number(frame?.frame_index || 0) === frameIndex);
        const fall = this.fallCandidateTestResult?.fall_detected === true;
        context.lineCap = 'round';
        for (const frame of visible) {
            const bbox = frame?.bbox || {};
            const x = pointX(bbox.x1);
            const y = pointY(bbox.y1);
            const boxWidth = (Number(bbox.x2 || 0) - Number(bbox.x1 || 0)) * drawWidth;
            const boxHeight = (Number(bbox.y2 || 0) - Number(bbox.y1 || 0)) * drawHeight;
            context.strokeStyle = fall ? '#fb7185' : '#34d399';
            context.lineWidth = 2;
            context.strokeRect(x, y, boxWidth, boxHeight);
            const keypoints = Array.isArray(frame?.keypoints) ? frame.keypoints : [];
            context.strokeStyle = '#67e8f9';
            context.lineWidth = 3;
            for (const [start, end] of COCO17_CONNECTIONS) {
                const left = keypoints[start]; const right = keypoints[end];
                if (!left || !right || Number(left.confidence || 0) < 0.15 || Number(right.confidence || 0) < 0.15) continue;
                context.beginPath();
                context.moveTo(pointX(left.x), pointY(left.y));
                context.lineTo(pointX(right.x), pointY(right.y));
                context.stroke();
            }
            context.fillStyle = '#f8fafc';
            for (const point of keypoints) {
                if (Number(point?.confidence || 0) < 0.15) continue;
                context.beginPath();
                context.arc(pointX(point.x), pointY(point.y), 3, 0, Math.PI * 2);
                context.fill();
            }
        }
    }

    public selectedFallDetectionDataset(): any {
        return this.selectedFallDetectionDatasets()[0] || {};
    }

    public selectedFallDetectionDatasets(): any[] {
        const selected = new Set(this.fallDetectionDatasetIds);
        return this.fallDetectionDatasets().filter((item: any) => selected.has(String(item?.dataset_id || '')));
    }

    public isFallDetectionDatasetSelected(dataset: any): boolean {
        return this.fallDetectionDatasetIds.includes(String(dataset?.dataset_id || ''));
    }

    public toggleFallDetectionDataset(dataset: any) {
        const datasetId = String(dataset?.dataset_id || '');
        if (!datasetId || dataset?.ready !== true || this.fallDetectionBusy) return;
        this.fallDetectionDatasetIds = this.isFallDetectionDatasetSelected(dataset)
            ? this.fallDetectionDatasetIds.filter((value: string) => value !== datasetId)
            : [...this.fallDetectionDatasetIds, datasetId];
        this.fallDetectionMessage = '';
        this.service.render();
    }

    public addFallDetectionDataset() {
        const dataset = this.fallDetectionDatasets().find(
            (item: any) => String(item?.dataset_id || '') === this.fallDetectionDatasetCandidateId,
        );
        if (!dataset || dataset?.ready !== true || this.isFallDetectionDatasetSelected(dataset) || this.fallDetectionBusy) return;
        this.fallDetectionDatasetIds = [...this.fallDetectionDatasetIds, String(dataset.dataset_id)];
        this.fallDetectionDatasetCandidateId = '';
        this.fallDetectionMessage = '';
        this.service.render();
    }

    public canAddFallDetectionDataset(): boolean {
        const dataset = this.fallDetectionDatasets().find(
            (item: any) => String(item?.dataset_id || '') === this.fallDetectionDatasetCandidateId,
        );
        return !this.fallDetectionBusy && dataset?.ready === true && !this.isFallDetectionDatasetSelected(dataset);
    }

    public clearFallDetectionDatasets() {
        if (this.fallDetectionBusy) return;
        this.fallDetectionDatasetIds = [];
        this.fallDetectionMessage = '';
        this.service.render();
    }

    public selectedFallDetectionPoseExtractor(): any {
        return this.fallDetectionPoseExtractors().find((item: any) => String(item?.model_id || '') === this.fallDetectionPoseModelId) || {};
    }

    public canStartFallDetectionTraining(): boolean {
        const datasets = this.selectedFallDetectionDatasets();
        const extractor = this.selectedFallDetectionPoseExtractor();
        return !this.fallDetectionBusy
            && datasets.length > 0
            && datasets.every((dataset: any) => dataset?.ready === true)
            && extractor?.ready === true
            && this.fallDetectionClassifierTypes.length > 0
            && Number(this.fallDetectionFps) > 0
            && Number(this.fallDetectionWindowSize) > 0
            && Number(this.fallDetectionStride) > 0
            && Number(this.fallDetectionStride) <= Number(this.fallDetectionWindowSize);
    }

    public fallDetectionBlockReason(): string {
        const datasets = this.selectedFallDetectionDatasets();
        const extractor = this.selectedFallDetectionPoseExtractor();
        if (!datasets.length) return 'Fall Detection Dataset을 하나 이상 선택하세요.';
        const blocked = datasets.find((dataset: any) => dataset?.ready !== true);
        if (blocked) return blocked?.message || '선택한 Dataset의 영상·FALL/NORMAL·annotation을 확인하세요.';
        if (!extractor?.model_id) return 'Pose extractor를 선택하세요.';
        if (extractor?.ready !== true) return extractor?.message || 'Pose artifact를 사용할 수 없습니다.';
        if (!this.fallDetectionClassifierTypes.length) return '낙상 판단 방법을 하나 이상 선택하세요.';
        if (Number(this.fallDetectionStride) > Number(this.fallDetectionWindowSize)) return 'Stride는 Window size보다 작아야 합니다.';
        return '';
    }

    public async startFallDetectionTraining() {
        if (!this.canStartFallDetectionTraining()) {
            this.fallDetectionMessage = this.fallDetectionBlockReason() || 'Dataset, Pose extractor, Temporal 설정, classifier를 모두 선택하세요.';
            this.fallDetectionMessageType = 'error';
            await this.service.render();
            return;
        }
        this.fallDetectionBusy = true;
        this.fallDetectionMessage = `선택한 ${this.fallDetectionClassifierTypes.length}개 낙상 판단 방법의 학습을 준비하고 있습니다.`;
        this.fallDetectionMessageType = 'info';
        let trainingStarted = false;
        await this.service.render();
        try {
            const classifierTypes = [...this.fallDetectionClassifierTypes];
            const startedJobs: any[] = [];
            const failedClassifiers: string[] = [];
            for (const classifierType of classifierTypes) {
                const params = new URLSearchParams({
                    training_type: 'fall_detection',
                    dataset_id: this.fallDetectionDatasetIds[0] || '',
                    dataset_ids: JSON.stringify(this.fallDetectionDatasetIds),
                    normal_supplement_ids: JSON.stringify(this.fallDetectionNormalSupplementIds),
                    pose_model_id: this.fallDetectionPoseModelId,
                    classifier_type: classifierType,
                    fps: String(this.fallDetectionFps),
                    window_size: String(this.fallDetectionWindowSize),
                    stride: String(this.fallDetectionStride),
                    tracking: String(this.fallDetectionTracking),
                    keypoint_confidence: String(this.fallDetectionKeypointConfidence),
                    person_visibility: String(this.fallDetectionPersonVisibility),
                });
                let res = await fetch(`/wiz/api/page.models/start_fall_detection_training?${params.toString()}`, { method: 'POST' });
                let json = await this.readJsonResponse(res);
                if (Number(json?.code) === 404) {
                    res = await fetch(`/wiz/api/page.models/start_pose_model_training?${params.toString()}`, { method: 'POST' });
                    json = await this.readJsonResponse(res);
                }
                const payload = json?.data && typeof json.data === 'object' ? json.data : json;
                if (Number(json?.code) === 200) startedJobs.push(payload);
                else failedClassifiers.push(this.fallDetectionClassifiers().find((item: any) => item?.key === classifierType)?.label || classifierType);
            }
            if (!startedJobs.length) throw new Error(`선택한 ${classifierTypes.length}개 낙상 판단 방법의 학습을 시작하지 못했습니다.`);
            const startedIds = new Set(startedJobs.map((job: any) => String(job?.job_id || '')));
            this.fallDetectionStudio = {
                ...(this.fallDetectionStudio || {}),
                jobs: [...startedJobs, ...this.fallDetectionJobs().filter((job: any) => !startedIds.has(String(job?.job_id || '')))],
            };
            this.fallDetectionJob = startedJobs[0];
            this.fallDetectionMessage = failedClassifiers.length
                ? `${startedJobs.length}개 학습을 시작했고 ${failedClassifiers.join(', ')} 시작은 실패했습니다.`
                : `선택한 ${startedJobs.length}개 방법의 학습을 각각 시작했습니다. 완료 결과도 각각 Candidate로 등록됩니다.`;
            this.fallDetectionMessageType = 'success';
            trainingStarted = true;
            this.startPoseStudioPolling();
        } catch (error: any) {
            this.fallDetectionMessage = error?.message || 'Fall Detection 학습 시작 실패';
            this.fallDetectionMessageType = 'error';
        }
        this.fallDetectionBusy = false;
        await this.service.render();
        if (trainingStarted) setTimeout(() => this.scrollToTrainingSection('fall-detection-running'), 0);
    }

    public async stopFallDetectionTraining(job: any) {
        const jobId = String(job?.job_id || '').trim();
        const status = String(job?.status || '').toLowerCase();
        if (!jobId || !['queued', 'running'].includes(status) || this.fallDetectionStoppingJobId) return;
        const confirmed = await this.service.modal.show({
            title: '학습 중단',
            message: `${this.fallDetectionCandidateTitle(job)} 학습을 중단하시겠습니까? 완료되지 않은 결과는 후보 모델로 등록되지 않습니다.`,
            action: '중단',
            cancel: '취소',
            status: 'warning',
            actionBtn: 'warning',
        });
        if (!confirmed) return;
        this.fallDetectionStoppingJobId = jobId;
        await this.service.render();
        try {
            const params = new URLSearchParams({ job_id: jobId });
            const res = await fetch(`/wiz/api/page.models/stop_fall_detection_training?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || '학습 중단 실패');
            if (payload?.fall_detection_studio) this.applyFallDetectionStudio(payload.fall_detection_studio);
            this.fallDetectionMessage = payload?.message || '학습을 중단했습니다.';
            this.fallDetectionMessageType = 'info';
        } catch (error: any) {
            this.fallDetectionMessage = error?.message || '학습 중단 실패';
            this.fallDetectionMessageType = 'error';
        }
        this.fallDetectionStoppingJobId = '';
        await this.service.render();
    }

    public fallDetectionProgress(job: any = this.fallDetectionJob): number {
        const progress = Number(job?.progress || 0);
        return Math.max(0, Math.min(100, Number.isFinite(progress) ? Math.round(progress * 100) : 0));
    }

    public fallDetectionStageText(job: any = this.fallDetectionJob): string {
        const stages: Record<string, string> = {
            QUEUED: '대기', RUNNING: '준비', EXTRACTING_POSE: 'Pose extraction',
            BUILDING_FEATURES: 'Feature 생성', TRAINING: 'Classifier 학습',
            EVALUATING: 'Test 평가', REGISTERING: 'Candidate 등록', COMPLETED: '완료', FAILED: '실패', STOPPED: '중단',
        };
        return stages[String(job?.stage || 'QUEUED').toUpperCase()] || String(job?.stage || '대기');
    }

    public fallDetectionMetric(key: string, job: any = this.fallDetectionJob): string {
        const value = Number(job?.metrics?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    private restorePoseStudioRegistryCache() {
        if (this.poseStudioRegistry?.items?.length) return;
        try {
            const cached = JSON.parse(String(sessionStorage.getItem(MODEL_REGISTRY_CACHE_KEY) || 'null'));
            if (cached?.registry && Array.isArray(cached.registry.items)) this.applyPoseStudioRegistry(cached.registry);
        } catch (_e) { }
    }

    private restoreFallDetectionStudioCache() {
        try {
            const cached = JSON.parse(String(sessionStorage.getItem(FALL_DETECTION_STUDIO_CACHE_KEY) || 'null'));
            if (cached && typeof cached === 'object') this.applyFallDetectionStudio(cached);
        } catch (_e) { }
    }

    private cacheFallDetectionStudio(payload: any) {
        if (!payload || typeof payload !== 'object') return;
        try {
            sessionStorage.setItem(FALL_DETECTION_STUDIO_CACHE_KEY, JSON.stringify(payload));
        } catch (_e) { }
    }

    private cachePoseStudioDatasets(datasets: any[]) {
        try {
            sessionStorage.setItem(POSE_DATASETS_CACHE_KEY, JSON.stringify(datasets));
        } catch (_e) { }
    }

    private applyPoseStudioDatasets(datasets: any[]) {
        if (!Array.isArray(datasets)) return;
        datasets = datasets.filter((item: any) => !this.poseStudioDeletedDatasetIds.has(String(item?.dataset_id || '')));
        this.poseStudioRegistry = this.poseStudioRegistry || { items: [], pose_studio: {} };
        this.poseStudioRegistry.pose_studio = this.poseStudioRegistry.pose_studio || {};
        this.poseStudioRegistry.pose_studio.datasets = datasets;
        this.cachePoseStudioDatasets(datasets);
    }

    private restorePoseStudioDatasetsCache() {
        try {
            const datasets = JSON.parse(String(sessionStorage.getItem(POSE_DATASETS_CACHE_KEY) || '[]'));
            if (Array.isArray(datasets) && datasets.length) this.applyPoseStudioDatasets(datasets);
        } catch (_e) { }
    }

    private async loadPoseStudioDatasetsSnapshot() {
        try {
            const res = await fetch('/wiz/api/page.models/pose_datasets_snapshot');
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code === 200 && payload?.available === true && Array.isArray(payload?.datasets)) {
                this.applyPoseStudioDatasets(payload.datasets);
                await this.service.render();
            }
        } catch (_e) { }
    }

    public async loadPoseStudioRegistrySnapshot() {
        if (this.poseStudioSnapshotLoading) return;
        this.poseStudioSnapshotLoading = true;
        void this.loadPoseStudioDatasetsSnapshot();
        let loaded = false;
        try {
            const res = await fetch('/wiz/api/page.models/pose_training_snapshot');
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code === 200 && payload?.available === true) {
                this.applyPoseStudioRegistry(payload);
                loaded = true;
            }
        } catch (_e) {
            // The cached list remains usable; manual refresh can request the full registry.
        }
        this.poseStudioSnapshotLoading = false;
        await this.service.render();
        // Snapshot paints immediately; the persistent Registry then replaces stale Dataset data.
        void this.loadPoseStudioRegistry(true);
    }

    public async loadPoseStudioRegistry(silent: boolean = false) {
        try {
            const res = await fetch('/wiz/api/page.models/model_registry');
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Pose 학습 정보를 불러오지 못했습니다.');
            this.applyPoseStudioRegistry(payload || {});
        } catch (error: any) {
            if (!silent) {
                this.poseStudioMessage = error?.message || 'Pose 학습 정보를 불러오지 못했습니다.';
                this.poseStudioMessageType = 'error';
            }
        }
        await this.service.render();
    }

    public poseRegisteredModels(): any[] {
        const items = Array.isArray(this.poseStudioRegistry?.items) ? this.poseStudioRegistry.items : [];
        const catalog = Array.isArray(this.poseStudioRegistry?.pose_studio?.catalog) ? this.poseStudioRegistry.pose_studio.catalog : [];
        return items.filter((item: any) => {
            const source = String(item?.source || '').toLowerCase();
            const path = String(item?.primary_model_path || '');
            return String(item?.family || '') === 'pose-backbone'
                && !!path
                && !item?.missing
                && source !== 'active'
                && source !== 'spec';
        }).map((item: any) => {
            const variantId = String(item?.pose_variant_id || item?.registration_metadata?.pose_variant_id || item?.summary?.pose_variant_id || '');
            const variant = catalog.find((row: any) => String(row?.key || '') === variantId) || {};
            const checkpointReady = /\.pt$/i.test(String(item?.primary_model_path || ''));
            const adapterReady = variant?.training_supported === true;
            const trainingReady = checkpointReady && adapterReady;
            return {
                ...item,
                variant,
                training_ready: trainingReady,
                adapter_required: !adapterReady,
                training_block_reason: !adapterReady
                    ? (variant?.training_note || 'Adapter Required')
                    : (!checkpointReady ? '현재 학습은 .pt 체크포인트만 지원합니다.' : ''),
                training_option_label: `${item.display_name || item.label || variant?.label || item.model_id}${trainingReady ? '' : ' · Adapter Required'}`,
            };
        });
    }

    public poseModelCategoryKey(item: any): string {
        const group = String(item?.variant?.group || '').toLowerCase();
        const variantId = String(item?.pose_variant_id || item?.registration_metadata?.pose_variant_id || item?.summary?.pose_variant_id || '').toLowerCase();
        if (group.startsWith('yolo') || variantId.startsWith('yolo')) return 'yolo-pose';
        if (group === 'rtmpose' || variantId.startsWith('rtmpose')) return 'rtmpose';
        if (group === 'vitpose' || variantId.startsWith('vitpose')) return 'vitpose';
        if (group === 'hrnet' || variantId.startsWith('hrnet')) return 'hrnet';
        if (group === 'rtmo' || variantId === 'rtmo') return 'rtmo';
        return 'other';
    }

    public poseTrainingModelCategories(): any[] {
        const models = this.poseRegisteredModels();
        const definitions = [
            { key: 'all', label: '전체' },
            { key: 'yolo-pose', label: 'YOLO Pose' },
            { key: 'rtmpose', label: 'RTMPose' },
            { key: 'vitpose', label: 'ViTPose' },
            { key: 'hrnet', label: 'HRNet' },
            { key: 'rtmo', label: 'RTMO' },
            { key: 'other', label: '기타 Pose' },
        ];
        return definitions.map((definition: any) => ({
            ...definition,
            count: definition.key === 'all'
                ? models.length
                : models.filter((item: any) => this.poseModelCategoryKey(item) === definition.key).length,
        }));
    }

    public poseModelsForSelectedCategory(): any[] {
        const models = this.poseRegisteredModels();
        if (this.poseStudioModelCategory === 'all') return models;
        return models.filter((item: any) => this.poseModelCategoryKey(item) === this.poseStudioModelCategory);
    }

    public selectPoseStudioModelCategory(category: string) {
        const next = String(category || 'all');
        const option = this.poseTrainingModelCategories().find((item: any) => item.key === next);
        if (!option || (next !== 'all' && option.count === 0)) return;
        this.poseStudioModelCategory = next;
        const selectedVisible = this.poseModelsForSelectedCategory().some(
            (item: any) => String(item?.model_id || '') === this.poseStudioBaseModelId,
        );
        if (!selectedVisible) this.poseStudioBaseModelId = '';
        this.service.render();
    }

    public poseStudioModelCategoryClass(category: any): string {
        if (this.poseStudioModelCategory === category?.key) return 'border-sky-600 bg-sky-600 text-white';
        if (Number(category?.count || 0) === 0 && category?.key !== 'all') return 'border-zinc-200 bg-zinc-100 text-zinc-400';
        return 'border-zinc-200 bg-white text-zinc-700 hover:border-sky-300 hover:bg-sky-50';
    }

    public poseTrainableModels(): any[] {
        return this.poseRegisteredModels().filter((item: any) => item?.training_ready === true);
    }

    public poseStudioDatasets(): any[] {
        return Array.isArray(this.poseStudioRegistry?.pose_studio?.datasets)
            ? this.poseStudioRegistry.pose_studio.datasets : [];
    }

    public postureReadyDatasets(): any[] {
        return this.poseStudioDatasets().filter((item: any) => item?.posture_ready === true);
    }

    public poseManagementOtherDatasets(): any[] {
        return this.poseStudioDatasets().filter((item: any) => item?.posture_ready !== true);
    }

    public poseReadyDatasets(): any[] {
        return this.poseStudioDatasets().filter((item: any) => item?.ready === true);
    }

    public selectedPoseStudioModel(): any {
        return this.poseRegisteredModels().find((item: any) => String(item?.model_id || '') === this.poseStudioBaseModelId) || {};
    }

    public selectPoseStudioModel(modelId: string) {
        const model = this.poseRegisteredModels().find((item: any) => String(item?.model_id || '') === String(modelId || ''));
        if (!modelId) {
            this.poseStudioBaseModelId = '';
            this.service.render();
            return;
        }
        if (!model?.training_ready) return;
        this.poseStudioBaseModelId = String(model.model_id || '');
        this.service.render();
    }

    public scrollToTrainingSection(sectionId: string) {
        if (typeof document === 'undefined') return;
        const target = document.getElementById(String(sectionId || ''));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    public async openTrainingDatasetRegistration() {
        const returnToFall = this.trainingPipelineType === 'fall_detection';
        if (returnToFall) {
            this.trainingPipelineType = 'dataset_management';
            await this.service.render();
        }
        this.openPoseStudioDatasetRegistration(returnToFall);
    }

    public openPoseStudioDatasetRegistration(returnToFall: boolean = false) {
        this.poseStudioDatasetRegistrationReturnToFall = returnToFall;
        this.poseStudioDatasetEditingId = '';
        this.poseStudioDatasetName = '';
        this.poseStudioDatasetPath = '';
        this.poseStudioDatasetSourceType = 'aihub-fall';
        this.poseStudioDatasetFile = null;
        this.poseStudioDatasetRegistrationMode = 'reference';
        this.poseStudioDatasetFormOpen = true;
        this.service.render();
        setTimeout(() => this.scrollToTrainingSection('training-dataset-management'), 0);
    }

    public selectedPoseStudioDataset(): any {
        return this.poseStudioDatasets().find((item: any) => String(item?.dataset_id || '') === this.poseStudioDatasetId) || {};
    }

    public selectPoseStudioDataset(datasetId: string) {
        this.poseStudioDatasetId = String(datasetId || '');
    }

    public togglePoseStudioDatasetForm() {
        this.poseStudioDatasetFormOpen = !this.poseStudioDatasetFormOpen;
        if (this.poseStudioDatasetFormOpen) {
            this.poseStudioDatasetEditingId = '';
            this.poseStudioDatasetName = '';
            this.poseStudioDatasetPath = '';
            this.poseStudioDatasetSourceType = 'aihub-fall';
            this.poseStudioDatasetFile = null;
            this.poseStudioDatasetRegistrationMode = 'reference';
        }
        this.service.render();
    }

    public editPoseStudioDataset(dataset: any) {
        const zipUpload = String(dataset?.source_type || '') === 'zip-upload';
        this.poseStudioDatasetEditingId = String(dataset?.dataset_id || '');
        this.poseStudioDatasetName = String(dataset?.name || '');
        this.poseStudioDatasetPath = String(zipUpload ? dataset?.extracted_path : dataset?.manifest_path || '');
        this.poseStudioDatasetSourceType = String(dataset?.source_type || 'user');
        this.poseStudioDatasetFile = null;
        this.poseStudioDatasetRegistrationMode = zipUpload ? 'zip' : 'reference';
        this.poseStudioDatasetFormOpen = true;
        this.service.render();
    }

    public async cancelPoseStudioDatasetForm() {
        const returnToFall = this.poseStudioDatasetRegistrationReturnToFall;
        this.poseStudioDatasetRegistrationReturnToFall = false;
        this.poseStudioDatasetFormOpen = false;
        this.poseStudioDatasetEditingId = '';
        this.poseStudioDatasetFile = null;
        if (returnToFall) {
            this.trainingPipelineType = 'fall_detection';
            await this.loadFallDetectionTrainingSnapshot();
            return;
        }
        await this.service.render();
    }

    public onPoseStudioDatasetFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        this.poseStudioDatasetFile = input.files?.[0] || null;
        this.poseStudioDatasetPath = '';
        this.poseStudioDatasetRegistrationMode = 'zip';
        this.service.render();
    }

    public setPoseStudioDatasetRegistrationMode(mode: 'reference' | 'video-reference' | 'zip' | 'aihub-download') {
        this.poseStudioDatasetRegistrationMode = mode;
        if (mode !== 'zip') this.poseStudioDatasetFile = null;
        this.poseStudioDatasetPath = '';
        if (mode === 'aihub-download') void this.loadAihubDownloadStatus();
        this.service.render();
    }

    public canRegisterPoseStudioDataset(): boolean {
        if (this.poseStudioBusy || !String(this.poseStudioDatasetName || '').trim()) return false;
        if (this.poseStudioDatasetRegistrationMode === 'aihub-download') {
            return /^\d+$/.test(String(this.aihubDatasetKey || '').trim())
                && (Boolean(String(this.aihubApiKey || '').trim()) || this.aihubDownloadConfig?.api_key_configured === true);
        }
        if (this.poseStudioDatasetEditingId) {
            if (this.poseStudioDatasetRegistrationMode === 'zip') return true;
            return !!String(this.poseStudioDatasetPath || '').trim();
        }
        if (this.poseStudioDatasetRegistrationMode === 'zip') return !!this.poseStudioDatasetFile;
        return !!String(this.poseStudioDatasetPath || '').trim();
    }

    public setAihub71641Bundle(bundle: 'training' | 'validation' | 'test') {
        this.aihubDatasetKey = '71641';
        this.aihubFileKeys = bundle === 'training'
            ? '531131,531132,531133,531134,531135,531136'
            : (bundle === 'validation' ? '531137,531138' : '531131');
        if (bundle === 'test' && !String(this.poseStudioDatasetName || '').trim()) {
            this.poseStudioDatasetName = 'AI-Hub Training 원천 Test 영상';
        }
        this.service.render();
    }

    public poseDatasetStorageSize(dataset: any): string {
        const fallDataset = this.fallDetectionDatasetFor(dataset);
        const bytes = Number(dataset?.validation?.total_bytes || fallDataset?.fall_detection_validation?.total_bytes || 0);
        if (!Number.isFinite(bytes) || bytes <= 0) return '0 GB';
        if (bytes >= 1024 ** 3) return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
        return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
    }

    public aihubDownloadedSize(): string {
        const bytes = Number(this.aihubDownloadJob?.downloaded_bytes || 0);
        if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
        if (bytes >= 1024 ** 3) return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
        return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
    }

    public aihubDownloadSpeed(): string {
        const bytes = Number(this.aihubDownloadJob?.download_speed_bps || 0);
        if (!Number.isFinite(bytes) || bytes <= 0) return this.aihubDownloadRunning() ? '측정 중' : '종료됨';
        if (bytes >= 1024 ** 2) return `${(bytes / (1024 ** 2)).toFixed(1)} MB/s`;
        return `${(bytes / 1024).toFixed(1)} KB/s`;
    }

    public aihubDownloadElapsed(): string {
        const seconds = Math.max(0, Number(this.aihubDownloadJob?.elapsed_seconds || 0));
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`;
    }

    public aihubDownloadStatusLabel(): string {
        if (String(this.aihubDownloadJob?.status || '') === 'completed'
            && (this.aihubDownloadJob?.test_videos_registered === true
                || this.aihubDownloadJob?.bundle?.purpose === 'candidate_test_only')) {
            return 'Test 영상 준비 완료';
        }
        const labels: Record<string, string> = { queued: '대기 중', running: this.aihubDownloadJob?.stage === 'PREPARING' ? '복원·검증 중' : '다운로드 중', stopped: '중단됨', completed: '학습 준비 완료', failed: '실패' };
        return labels[String(this.aihubDownloadJob?.status || '')] || '상태 확인 중';
    }

    public aihubDownloadRunning(): boolean {
        return ['queued', 'running'].includes(String(this.aihubDownloadJob?.status || ''));
    }

    public poseDatasetCanSelect(dataset: any): boolean {
        return dataset?.ready === true || dataset?.posture_ready === true || dataset?.fall_ready === true || this.fallDetectionDatasetReady(dataset);
    }

    public poseDatasetStatusLabel(dataset: any): string {
        if (dataset?.ready === true && this.fallDetectionDatasetReady(dataset)) return 'POSE · FALL READY';
        if (dataset?.ready === true) return 'READY';
        if (dataset?.posture_ready === true) return 'POSTURE READY';
        if (dataset?.fall_ready === true) return 'FALL READY';
        if (this.fallDetectionDatasetReady(dataset)) return 'FALL READY';
        return 'BLOCKED';
    }

    public poseDatasetUsageLabel(dataset: any): string {
        if (dataset?.ready === true && this.fallDetectionDatasetReady(dataset)) return 'Pose·낙상 모델 학습 가능';
        if (dataset?.ready === true) return 'Pose 학습 가능';
        if (dataset?.posture_ready === true) return 'XG-Posture 학습 가능';
        if (dataset?.fall_ready === true) return '낙상 모델 학습 가능';
        if (this.fallDetectionDatasetReady(dataset)) return '낙상 모델 학습 가능';
        return '구조 확인 필요';
    }

    public poseDatasetAnnotationLabel(dataset: any): string {
        if (dataset?.fall_ready === true || this.fallDetectionDatasetReady(dataset)) return '낙상 영상 + 시간 라벨';
        return String(dataset?.annotation_type || 'Dataset 구조 확인 필요');
    }

    public fallDetectionDatasetFor(dataset: any): any {
        const datasetId = String(dataset?.dataset_id || dataset || '');
        return this.fallDetectionDatasets().find(
            (item: any) => String(item?.dataset_id || '') === datasetId,
        ) || {};
    }

    public fallDetectionDatasetReady(dataset: any): boolean {
        return this.fallDetectionDatasetFor(dataset)?.ready === true;
    }

    public fallDetectionIndependentEventLabel(dataset: any): string {
        const item = this.fallDetectionDatasetFor(dataset);
        const counts = item?.class_group_count || item?.fall_detection_validation?.class_group_count || {};
        return `독립 사건 · 낙상 ${Number(counts?.FALL || 0)}건 · 정상 ${Number(counts?.NORMAL || 0)}건`;
    }

    public fallDetectionEvaluationLabel(dataset: any): string {
        const item = this.fallDetectionDatasetFor(dataset);
        return item?.evaluation_ready === true ? '독립 평가 가능' : '평가 데이터 보강 필요';
    }

    public poseDatasetFileLabel(dataset: any): string {
        const sourceType = String(dataset?.source_type || '');
        if (sourceType === 'zip-upload') return 'ZIP Dataset';
        if (sourceType === 'archive-reference') return 'AI-Hub 원본 압축';
        if (sourceType === 'video-reference') return '동영상 폴더';
        const path = String(dataset?.manifest_path || '');
        return path ? (path.split('/').pop() || 'pose.yaml') : 'Dataset 파일';
    }

    public poseDatasetLocationLabel(dataset: any): string {
        const path = String(dataset?.extracted_path || dataset?.manifest_path || dataset?.source_path || '');
        if (path.startsWith('/mnt/data/wiz/')) return 'Season 저장소';
        if (path.startsWith('/mnt/')) return '연결 저장소';
        return path ? '서버 저장소' : '경로 확인 필요';
    }

    private uploadPoseStudioDataset(url: string, body: FormData): Promise<any> {
        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open('POST', url, true);
            request.upload.onprogress = (event: ProgressEvent) => {
                if (!event.lengthComputable) return;
                this.poseStudioDatasetUploadProgress = Math.min(90, Math.round((event.loaded / event.total) * 90));
                this.poseStudioDatasetUploadStage = `ZIP 업로드 중 · ${Math.round((event.loaded / event.total) * 100)}%`;
                void this.service.render();
            };
            request.upload.onload = () => {
                this.poseStudioDatasetUploadProgress = 92;
                this.poseStudioDatasetUploadStage = '업로드 완료 · 압축 해제 및 데이터셋 검사 중';
                void this.service.render();
            };
            request.onerror = () => reject(new Error('ZIP 파일을 서버에 업로드하지 못했습니다.'));
            request.onabort = () => reject(new Error('ZIP 업로드가 취소되었습니다.'));
            request.onload = () => {
                let payload: any = null;
                try { payload = JSON.parse(request.responseText || '{}'); } catch (_e) { payload = null; }
                if (request.status < 200 || request.status >= 300) {
                    const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
                    reject(new Error(data?.message || payload?.message || `데이터셋 업로드 실패 (${request.status})`));
                    return;
                }
                resolve(payload || {});
            };
            request.send(body);
        });
    }

    private applyPoseStudioDatasetUpload(payload: any) {
        const datasets = Array.isArray(payload?.datasets)
            ? payload.datasets
            : (payload?.dataset ? [payload.dataset] : []);
        if (!datasets.length) return;
        this.applyPoseStudioDatasets(datasets);
        this.poseStudioDatasetId = String(payload?.dataset?.dataset_id || this.poseStudioDatasetId || '');
    }

    public async registerPoseStudioDataset() {
        if (this.poseStudioBusy) return;
        this.poseStudioBusy = true;
        this.poseStudioDatasetUploadProgress = 0;
        this.poseStudioDatasetUploadStage = this.poseStudioDatasetFile ? 'ZIP 업로드 준비 중' : '데이터셋 확인 중';
        this.poseStudioMessage = this.poseStudioDatasetUploadStage;
        this.poseStudioMessageType = 'info';
        await this.service.render();
        try {
            const params = new URLSearchParams({
                name: String(this.poseStudioDatasetName || '').trim(),
                source_type: String(this.poseStudioDatasetSourceType || 'user'),
            });
            let json: any;
            if (this.poseStudioDatasetRegistrationMode === 'aihub-download') {
                params.set('dataset_key', String(this.aihubDatasetKey || '').trim());
                params.set('file_keys', String(this.aihubFileKeys || '').trim());
                const body = new FormData();
                const oneTimeApiKey = String(this.aihubApiKey || '').trim();
                if (oneTimeApiKey) body.append('api_key', new Blob([oneTimeApiKey], { type: 'text/plain' }), 'api-key.txt');
                const res = await fetch(`/wiz/api/page.models/start_aihub_dataset_download?${params.toString()}`, { method: 'POST', body });
                json = await this.readJsonResponse(res);
            } else if (this.poseStudioDatasetEditingId) {
                params.set('dataset_id', this.poseStudioDatasetEditingId);
                params.set('manifest_path', String(this.poseStudioDatasetPath || '').trim());
                const res = await fetch(`/wiz/api/page.models/update_pose_dataset?${params.toString()}`, { method: 'POST' });
                json = await this.readJsonResponse(res);
            } else if (this.poseStudioDatasetRegistrationMode === 'video-reference') {
                params.set('source_path', String(this.poseStudioDatasetPath || '').trim());
                params.set('sample_fps', String(this.poseStudioVideoSampleFps || 2));
                const res = await fetch(`/wiz/api/page.models/register_pose_video_source?${params.toString()}`, { method: 'POST' });
                json = await this.readJsonResponse(res);
            } else if (this.poseStudioDatasetFile) {
                const body = new FormData();
                body.append('dataset', this.poseStudioDatasetFile);
                json = await this.uploadPoseStudioDataset(`/wiz/api/page.models/upload_pose_dataset?${params.toString()}`, body);
            } else {
                params.set('manifest_path', String(this.poseStudioDatasetPath || '').trim());
                const res = await fetch(`/wiz/api/page.models/register_pose_dataset?${params.toString()}`, { method: 'POST' });
                json = await this.readJsonResponse(res);
            }
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || '데이터셋 추가 실패');
            if (this.poseStudioDatasetRegistrationMode === 'aihub-download') {
                this.aihubDownloadJob = payload;
                this.aihubApiKey = '';
                this.aihubApiKeyVisible = false;
                this.poseStudioMessage = payload?.message || 'AI-Hub 다운로드를 시작했습니다.';
                this.poseStudioMessageType = 'success';
                this.poseStudioBusy = false;
                this.startAihubDownloadPolling();
                await this.service.render();
                return;
            }
            this.applyPoseStudioDatasetUpload(payload);
            this.poseStudioDatasetUploadProgress = 100;
            this.poseStudioDatasetUploadStage = '데이터셋 등록 완료';
            this.poseStudioDatasetId = String(payload?.dataset?.dataset_id || '');
            this.poseStudioDatasetFormOpen = false;
            this.poseStudioDatasetFile = null;
            this.poseStudioDatasetName = '';
            this.poseStudioDatasetPath = '';
            this.poseStudioDatasetEditingId = '';
            this.poseStudioMessage = payload?.message || 'Dataset 정보를 저장했습니다.';
            this.poseStudioMessageType = 'success';
            await this.loadPoseStudioRegistry(true);
            this.applyPoseStudioDatasetUpload(payload);
            if (this.poseStudioDatasetRegistrationReturnToFall) {
                this.poseStudioDatasetRegistrationReturnToFall = false;
                this.trainingPipelineType = 'fall_detection';
                await this.loadFallDetectionTrainingSnapshot();
            }
        } catch (error: any) {
            this.poseStudioDatasetUploadStage = '데이터셋 등록 실패';
            this.poseStudioMessage = error?.message || '데이터셋 추가 실패';
            this.poseStudioMessageType = 'error';
        }
        this.poseStudioBusy = false;
        await this.service.render();
    }

    public async loadAihubDownloadStatus() {
        try {
            const params = new URLSearchParams();
            const jobId = String(this.aihubDownloadJob?.job_id || '').trim();
            if (jobId) params.set('job_id', jobId);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`/wiz/api/page.models/aihub_dataset_download_status${suffix}`);
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || 'AI-Hub 상태 조회 실패');
            this.aihubDownloadConfig = payload?.config || null;
            if (payload?.job) this.aihubDownloadJob = payload.job;
            const status = String(this.aihubDownloadJob?.status || '');
            if (['queued', 'running'].includes(status)) this.startAihubDownloadPolling();
            if (status === 'completed') {
                this.poseStudioMessage = this.aihubDownloadJob?.message || 'AI-Hub 다운로드가 완료됐습니다.';
                this.poseStudioMessageType = 'success';
                this.stopAihubDownloadPolling();
                await this.loadPoseStudioRegistry(true);
            } else if (status === 'failed') {
                this.poseStudioMessage = this.aihubDownloadJob?.message || 'AI-Hub 다운로드에 실패했습니다.';
                this.poseStudioMessageType = 'error';
                this.stopAihubDownloadPolling();
            }
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'AI-Hub 상태 조회 실패';
            this.poseStudioMessageType = 'error';
        }
        await this.service.render();
    }

    private startAihubDownloadPolling() {
        if (this.aihubDownloadPollHandle) return;
        this.aihubDownloadPollHandle = setInterval(() => { void this.loadAihubDownloadStatus(); }, 5000);
    }

    private stopAihubDownloadPolling() {
        if (!this.aihubDownloadPollHandle) return;
        clearInterval(this.aihubDownloadPollHandle);
        this.aihubDownloadPollHandle = null;
    }

    public async stopAihubDownload() {
        const jobId = String(this.aihubDownloadJob?.job_id || '').trim();
        if (!jobId || !this.aihubDownloadRunning() || this.aihubDownloadActionBusy) return;
        const confirmed = await this.service.modal.show({
            title: 'AI-Hub 다운로드 중단',
            message: '현재 다운로드를 중단하시겠습니까? 지금까지 받은 파일은 서버에 보존됩니다.',
            action: '중단',
            cancel: '취소',
            status: 'warning',
            actionBtn: 'warning',
        });
        if (!confirmed) return;
        this.aihubDownloadActionBusy = true;
        await this.service.render();
        try {
            const params = new URLSearchParams({ job_id: jobId });
            const res = await fetch(`/wiz/api/page.models/stop_aihub_dataset_download?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || '다운로드 중단 실패');
            this.aihubDownloadJob = payload;
            this.stopAihubDownloadPolling();
            this.poseStudioMessage = payload?.message || 'AI-Hub 다운로드를 중단했습니다.';
            this.poseStudioMessageType = 'info';
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'AI-Hub 다운로드 중단 실패';
            this.poseStudioMessageType = 'error';
        }
        this.aihubDownloadActionBusy = false;
        await this.service.render();
    }

    public async deleteAihubDownload() {
        const jobId = String(this.aihubDownloadJob?.job_id || '').trim();
        if (!jobId || this.aihubDownloadActionBusy) return;
        const confirmed = await this.service.modal.show({
            title: 'AI-Hub 다운로드 삭제',
            message: '이 작업을 삭제하시겠습니까? 다운로드 중이면 먼저 중단되며, 지금까지 받은 파일과 작업 로그는 복구할 수 없습니다.',
            action: '삭제',
            cancel: '취소',
            status: 'warning',
            actionBtn: 'warning',
        });
        if (!confirmed) return;
        this.aihubDownloadActionBusy = true;
        await this.service.render();
        try {
            const params = new URLSearchParams({ job_id: jobId });
            const res = await fetch(`/wiz/api/page.models/delete_aihub_dataset_download?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (Number(json?.code) !== 200) throw new Error(payload?.message || json?.message || '다운로드 삭제 실패');
            this.stopAihubDownloadPolling();
            this.aihubDownloadJob = payload?.job || null;
            this.poseStudioMessage = payload?.message || 'AI-Hub 다운로드 작업을 삭제했습니다.';
            this.poseStudioMessageType = 'success';
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'AI-Hub 다운로드 삭제 실패';
            this.poseStudioMessageType = 'error';
        }
        this.aihubDownloadActionBusy = false;
        await this.service.render();
    }

    public async deletePoseStudioDataset(dataset: any) {
        if (this.poseStudioBusy || !dataset?.dataset_id) return;
        const confirmed = await this.service.modal.show({
            title: 'Dataset 삭제',
            message: `${dataset.name || '선택한 Dataset'}을 학습 목록에서 삭제하시겠습니까? 기존 학습 이력과 원본 파일은 보존됩니다.`,
            action: '삭제',
            cancel: '취소',
            status: 'warning',
            actionBtn: 'warning',
        });
        if (!confirmed) return;
        this.poseStudioBusy = true;
        await this.service.render();
        try {
            const params = new URLSearchParams({ dataset_id: String(dataset.dataset_id) });
            const res = await fetch(`/wiz/api/page.models/delete_pose_dataset?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Dataset 삭제 실패');
            this.poseStudioDeletedDatasetIds.add(String(dataset.dataset_id));
            if (this.poseStudioDatasetId === String(dataset.dataset_id)) this.poseStudioDatasetId = '';
            if (this.poseStudioDatasetEditingId === String(dataset.dataset_id)) this.cancelPoseStudioDatasetForm();
            if (String(this.poseStudioDatasetPreview?.dataset?.dataset_id || '') === String(dataset.dataset_id)) {
                this.poseStudioDatasetPreview = null;
            }
            if (Array.isArray(payload?.datasets)) this.applyPoseStudioDatasets(payload.datasets);
            this.poseStudioMessage = payload?.message || 'Dataset을 삭제했습니다.';
            this.poseStudioMessageType = 'success';
            void this.loadPoseStudioRegistry(true);
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'Dataset 삭제 실패';
            this.poseStudioMessageType = 'error';
        }
        this.poseStudioBusy = false;
        await this.service.render();
    }

    public async previewPoseStudioDataset(dataset: any) {
        if (!dataset?.dataset_id || this.poseStudioDatasetPreviewBusy) return;
        this.poseStudioDatasetPreviewBusy = true;
        await this.service.render();
        try {
            const params = new URLSearchParams({ dataset_id: String(dataset.dataset_id), limit: '12' });
            const res = await fetch(`/wiz/api/page.models/pose_dataset_preview?${params.toString()}`);
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Dataset 미리보기 실패');
            this.poseStudioDatasetPreview = payload;
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'Dataset 미리보기 실패';
            this.poseStudioMessageType = 'error';
        }
        this.poseStudioDatasetPreviewBusy = false;
        await this.service.render();
    }

    public closePoseStudioDatasetPreview() {
        this.poseStudioDatasetPreview = null;
        this.service.render();
    }

    public poseDatasetPreviewImageUrl(sample: any): string {
        const params = new URLSearchParams({
            dataset_id: String(this.poseStudioDatasetPreview?.dataset?.dataset_id || ''),
            split: String(sample?.split || 'train'),
            index: String(sample?.index ?? 0),
        });
        return `/wiz/api/page.models/pose_dataset_preview_image?${params.toString()}`;
    }

    public applyPoseStudioPreset(preset: 'fast' | 'balanced' | 'accuracy') {
        this.poseStudioPreset = preset;
        const values = {
            fast: { epochs: 30, batch: 32, imageSize: 512, learningRate: 0.001, optimizer: 'AdamW' },
            balanced: { epochs: 100, batch: 16, imageSize: 640, learningRate: 0.001, optimizer: 'AdamW' },
            accuracy: { epochs: 200, batch: 8, imageSize: 960, learningRate: 0.0005, optimizer: 'AdamW' },
        }[preset];
        this.poseStudioEpochs = values.epochs;
        this.poseStudioBatch = values.batch;
        this.poseStudioImageSize = values.imageSize;
        this.poseStudioLearningRate = values.learningRate;
        this.poseStudioOptimizer = values.optimizer;
        this.poseStudioTrainingSettingsSignature = '';
        this.service.render();
    }

    public poseStudioPresetClass(preset: string): string {
        return this.poseStudioPreset === preset
            ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
            : 'border-zinc-200 bg-white text-zinc-600 hover:border-emerald-300';
    }

    public markPoseStudioPresetCustom() {
        this.poseStudioPreset = 'custom';
        this.poseStudioTrainingSettingsSignature = '';
    }

    private poseStudioTrainingSettingsPayload() {
        return {
            preset: this.poseStudioPreset,
            epochs: Number(this.poseStudioEpochs || 0),
            batch: Number(this.poseStudioBatch || 0),
            imageSize: Number(this.poseStudioImageSize || 0),
            learningRate: Number(this.poseStudioLearningRate || 0),
            optimizer: String(this.poseStudioOptimizer || ''),
            device: String(this.poseStudioDevice || 'auto'),
        };
    }

    private poseStudioCurrentSettingsSignature(): string {
        return JSON.stringify(this.poseStudioTrainingSettingsPayload());
    }

    public poseStudioTrainingSettingsSaved(): boolean {
        return Boolean(this.poseStudioTrainingSettingsSignature)
            && this.poseStudioTrainingSettingsSignature === this.poseStudioCurrentSettingsSignature();
    }

    public savePoseStudioTrainingSettings() {
        const settings = this.poseStudioTrainingSettingsPayload();
        if (settings.epochs < 1 || settings.batch < 1 || settings.imageSize < 320 || settings.learningRate <= 0) {
            this.poseStudioMessage = '학습 설정 값을 확인하세요.';
            this.poseStudioMessageType = 'error';
            this.service.render();
            return;
        }
        this.poseStudioTrainingSettingsSignature = JSON.stringify(settings);
        this.poseStudioTrainingSettingsSavedAt = new Date().toISOString();
        try {
            localStorage.setItem(POSE_TRAINING_SETTINGS_KEY, JSON.stringify({
                ...settings,
                savedAt: this.poseStudioTrainingSettingsSavedAt,
            }));
        } catch (_e) { }
        this.poseStudioMessage = '학습 설정을 저장했습니다.';
        this.poseStudioMessageType = 'success';
        this.service.render();
    }

    private restorePoseStudioTrainingSettings() {
        try {
            const settings = JSON.parse(String(localStorage.getItem(POSE_TRAINING_SETTINGS_KEY) || 'null'));
            if (!settings || typeof settings !== 'object') return;
            this.poseStudioPreset = ['fast', 'balanced', 'accuracy', 'custom'].includes(String(settings.preset))
                ? settings.preset : 'custom';
            this.poseStudioEpochs = Number(settings.epochs || 100);
            this.poseStudioBatch = Number(settings.batch || 16);
            this.poseStudioImageSize = Number(settings.imageSize || 640);
            this.poseStudioLearningRate = Number(settings.learningRate || 0.001);
            this.poseStudioOptimizer = String(settings.optimizer || 'AdamW');
            this.poseStudioDevice = String(settings.device || 'auto');
            this.poseStudioTrainingSettingsSignature = this.poseStudioCurrentSettingsSignature();
            this.poseStudioTrainingSettingsSavedAt = String(settings.savedAt || '');
        } catch (_e) { }
    }

    public canStartPoseStudioTraining(): boolean {
        const model = this.selectedPoseStudioModel();
        const dataset = this.selectedPoseStudioDataset();
        return !this.poseStudioBusy
            && model?.training_ready === true
            && Boolean(model?.model_id)
            && dataset?.ready === true
            && Boolean(dataset?.dataset_id)
            && Boolean(dataset?.manifest_path)
            && this.poseStudioTrainingSettingsSaved();
    }

    public poseStudioTrainingBlockReason(): string {
        const model = this.selectedPoseStudioModel();
        const dataset = this.selectedPoseStudioDataset();
        if (!model?.model_id) return '먼저 학습할 Pose 모델을 선택하세요.';
        if (model?.training_ready !== true) return model?.training_block_reason || '선택한 모델은 아직 학습 Adapter가 준비되지 않았습니다.';
        if (!dataset?.dataset_id) return '학습할 Dataset을 선택하세요.';
        if (dataset?.posture_ready === true && dataset?.ready !== true) {
            return '선택한 AI-Hub 행동 Dataset은 XG-Posture 학습용입니다. Pose Backbone 학습에는 pose.yaml Dataset을 선택하세요.';
        }
        if (dataset?.ready !== true || !dataset?.manifest_path) {
            return '선택한 Dataset은 ZIP 보관만 완료된 상태입니다. pose.yaml, train/valid 이미지, COCO-17 라벨을 준비해야 학습할 수 있습니다.';
        }
        if (!this.poseStudioTrainingSettingsSaved()) return '학습 설정 우측 상단의 설정 저장을 눌러주세요.';
        return '';
    }

    public async startPoseStudioTraining() {
        if (!this.canStartPoseStudioTraining()) {
            this.poseStudioMessage = '모델, 사용 가능한 Dataset, 저장된 학습 설정을 모두 준비하세요.';
            this.poseStudioMessageType = 'error';
            await this.service.render();
            return;
        }
        const model = this.selectedPoseStudioModel();
        const dataset = this.selectedPoseStudioDataset();
        this.poseStudioBusy = true;
        this.poseStudioMessage = '학습을 준비하고 있습니다.';
        this.poseStudioMessageType = 'info';
        await this.service.render();
        try {
            const params = new URLSearchParams();
            params.set('base_model_id', String(model.model_id));
            params.set('dataset_id', String(dataset.dataset_id || ''));
            params.set('variant_id', String(model?.pose_variant_id || model?.registration_metadata?.pose_variant_id || model?.summary?.pose_variant_id || ''));
            params.set('dataset_manifest', String(dataset.manifest_path));
            params.set('epochs', String(this.poseStudioEpochs || 100));
            params.set('imgsz', String(this.poseStudioImageSize || 640));
            params.set('batch', String(this.poseStudioBatch || 16));
            params.set('lr0', String(this.poseStudioLearningRate || 0.001));
            params.set('optimizer', String(this.poseStudioOptimizer || 'AdamW'));
            params.set('device', String(this.poseStudioDevice || 'auto'));
            params.set('workers', '2');
            params.set('benchmark_manifest', String(this.poseStudioBenchmarkManifest || '').trim());
            params.set('note', '학습 모드에서 등록 모델 기반 학습');
            const res = await fetch(`/wiz/api/page.models/start_pose_model_training?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Pose 학습 시작 실패');
            this.poseStudioJob = payload;
            this.poseStudioMessage = this.poseStudioBenchmarkManifest.trim()
                ? '학습을 시작했습니다. Pose 평가 후 Fall AI End-to-End 평가가 자동 실행됩니다.'
                : '학습을 시작했습니다. Pose 평가는 자동 실행되며 Fall E2E 평가는 benchmark 등록 후 실행할 수 있습니다.';
            this.poseStudioMessageType = 'success';
            this.startPoseStudioPolling();
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'Pose 학습 시작 실패';
            this.poseStudioMessageType = 'error';
        }
        this.poseStudioBusy = false;
        await this.service.render();
    }

    private startPoseStudioPolling() {
        if (this.poseStudioPollHandle) return;
        this.poseStudioPollHandle = setInterval(() => { void this.loadPoseStudioRegistry(true); }, 5000);
    }

    private stopPoseStudioPolling() {
        if (!this.poseStudioPollHandle) return;
        clearInterval(this.poseStudioPollHandle);
        this.poseStudioPollHandle = null;
    }

    public poseStudioProgress(): number {
        const progress = Number(this.poseStudioJob?.progress || 0);
        return Math.max(0, Math.min(100, Number.isFinite(progress) ? Math.round(progress * 100) : 0));
    }

    public poseStudioMetric(key: string): string {
        const value = Number(this.poseStudioJob?.pose_evaluation?.metrics?.[key] ?? this.poseStudioJob?.metrics?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public poseTrainingJobs(): any[] {
        return Array.isArray(this.poseStudioRegistry?.pose_studio?.jobs) ? this.poseStudioRegistry.pose_studio.jobs : [];
    }

    public poseEvaluationJobs(): any[] {
        return Array.isArray(this.poseStudioRegistry?.pose_studio?.evaluation_jobs) ? this.poseStudioRegistry.pose_studio.evaluation_jobs : [];
    }

    public poseStudioJobHistory(job: any = this.poseStudioJob): any[] {
        const rows = Array.isArray(job?.history) ? job.history : [];
        return rows.slice(-12).reverse();
    }

    public poseStudioCurrentEpoch(job: any = this.poseStudioJob): string {
        return `${Number(job?.current_epoch || 0)} / ${Number(job?.epochs || this.poseStudioEpochs || 0)}`;
    }

    public poseStudioLoss(job: any = this.poseStudioJob): string {
        const value = Number(job?.loss ?? this.poseStudioJobHistory(job)?.[0]?.loss);
        return Number.isFinite(value) ? value.toFixed(4) : '-';
    }

    public poseStudioMap(job: any = this.poseStudioJob): string {
        const rows = this.poseStudioJobHistory(job);
        const value = Number(rows?.[0]?.map50 ?? job?.metrics?.map50);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public poseStudioGpu(job: any = this.poseStudioJob): string {
        const value = Number(job?.gpu?.utilization_pct);
        if (Number.isFinite(value)) return `${value.toFixed(0)}%`;
        return job?.gpu?.available === false ? 'CPU' : String(job?.gpu?.name || job?.device || '-');
    }

    public poseStudioEta(job: any = this.poseStudioJob): string {
        const seconds = Number(job?.eta_sec || 0);
        if (!Number.isFinite(seconds) || seconds <= 0) return String(job?.status || '').toLowerCase() === 'completed' ? '완료' : '-';
        const minutes = Math.ceil(seconds / 60);
        return minutes < 60 ? `약 ${minutes}분` : `약 ${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
    }

    public poseStudioJobStatusText(job: any): string {
        const status = String(job?.status || '').toLowerCase();
        if (status === 'completed') return '완료';
        if (status === 'failed') return '실패';
        if (status === 'blocked') return '차단';
        if (status === 'running') return String(job?.stage || '') === 'evaluating' ? 'Pose 평가 중' : '학습 중';
        return '대기';
    }

    public poseStudioJobStatusClass(job: any): string {
        const status = String(job?.status || '').toLowerCase();
        if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
        if (status === 'failed' || status === 'blocked') return 'bg-rose-100 text-rose-700';
        if (status === 'running') return 'bg-sky-100 text-sky-700';
        return 'bg-zinc-100 text-zinc-600';
    }

    public poseEvaluationForJob(job: any): any {
        const evaluationId = String(job?.automatic_evaluation_job_id || '');
        const modelId = String(job?.registered_model_id || '');
        return this.poseEvaluationJobs().find((item: any) => evaluationId && String(item?.job_id || '') === evaluationId)
            || this.poseEvaluationJobs().find((item: any) => modelId && String(item?.model_id || '') === modelId)
            || {};
    }

    public poseFallMetric(job: any, key: string, percent: boolean = true): string {
        const evaluation = this.poseEvaluationForJob(job);
        const value = Number(evaluation?.report?.candidate?.[key]);
        if (!Number.isFinite(value)) return '-';
        return percent ? `${(value * 100).toFixed(1)}%` : `${value.toFixed(1)}ms`;
    }

    public poseOverallEvaluationStatus(job: any): string {
        const poseStatus = String(job?.pose_evaluation?.status || '').toUpperCase();
        const evaluation = this.poseEvaluationForJob(job);
        const fallStatus = String(evaluation?.report?.status || '').toUpperCase();
        if (poseStatus === 'PASSED' && fallStatus === 'PASSED') return 'PASS';
        if (poseStatus === 'FAILED' || ['FAILED', 'BLOCKED'].includes(fallStatus)) return 'FAIL';
        return 'WAITING';
    }

    public async startPoseStudioEvaluation(job: any = this.poseStudioJob) {
        if (this.poseStudioEvaluationBusy || !job?.registered_model_id || !this.poseStudioBenchmarkManifest.trim()) return;
        this.poseStudioEvaluationBusy = true;
        this.poseStudioMessage = 'Fall AI End-to-End 평가를 준비하고 있습니다.';
        this.poseStudioMessageType = 'info';
        await this.service.render();
        try {
            const params = new URLSearchParams({
                model_id: String(job.registered_model_id),
                benchmark_manifest: this.poseStudioBenchmarkManifest.trim(),
            });
            const res = await fetch(`/wiz/api/page.models/start_pose_model_evaluation?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const payload = json?.data && typeof json.data === 'object' ? json.data : json;
            if (json.code !== 200) throw new Error(payload?.message || json?.message || 'Fall E2E 평가 시작 실패');
            this.poseStudioMessage = 'Fall AI End-to-End 평가를 시작했습니다.';
            this.poseStudioMessageType = 'success';
            this.startPoseStudioPolling();
            await this.loadPoseStudioRegistry(true);
        } catch (error: any) {
            this.poseStudioMessage = error?.message || 'Fall E2E 평가 시작 실패';
            this.poseStudioMessageType = 'error';
        }
        this.poseStudioEvaluationBusy = false;
        await this.service.render();
    }

    public poseHistoryJob(jobId: string): any {
        return this.poseTrainingJobs().find((item: any) => String(item?.job_id || '') === String(jobId || '')) || {};
    }

    public poseHistoryDatasetName(job: any): string {
        if (job?.dataset_name) return String(job.dataset_name);
        const manifest = String(job?.dataset_manifest || '');
        return this.poseStudioDatasets().find((item: any) => String(item?.manifest_path || '') === manifest)?.name || manifest.split('/').pop() || '-';
    }

    public poseHistoryDate(job: any): string {
        return String(job?.completed_at || job?.created_at || '-').replace('T', ' ').replace('Z', '').slice(0, 16);
    }

    public poseHistoryMetric(job: any, key: string): string {
        const value = Number(job?.pose_evaluation?.metrics?.[key] ?? job?.metrics?.[key]);
        return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';
    }

    public poseHistoryDelta(key: string): string {
        const left = this.poseHistoryJob(this.poseHistoryCompareLeftId);
        const right = this.poseHistoryJob(this.poseHistoryCompareRightId);
        const leftValue = Number(left?.pose_evaluation?.metrics?.[key] ?? left?.metrics?.[key]);
        const rightValue = Number(right?.pose_evaluation?.metrics?.[key] ?? right?.metrics?.[key]);
        if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) return '-';
        const delta = (leftValue - rightValue) * 100;
        return `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%p`;
    }

    private validateTrainingFileAssignments(files: File[]): string {
        for (const file of files) {
            const assignment = this.trainingAssignmentForFile(file);
            if (!['Y', 'N'].includes(assignment.fallLabel)) return `${file.name}: 낙상 라벨 Y/N을 선택해주세요.`;
            if (this.trainingTargetRequiresPostureLabel() && !assignment.postureClass) {
                return `${file.name}: ${this.trainingTargetLabel()} 학습용 행동 라벨을 선택해주세요.`;
            }
        }
        return '';
    }

    public async analyze() {
        if (!this.selectedFile || this.analyzing) {
            if (!this.selectedFile && this.selectedUploadCount() > 0) this.errorMessage = '분석은 영상 파일 하나가 필요합니다. 압축 파일은 학습 데이터 등록으로 처리해주세요.';
            return;
        }
        const requestKey = `${this.selectedFile.name}:${this.selectedFile.size}:${this.selectedFile.lastModified}:${this.analysisProfile}:${this.selectedModelType}:${JSON.stringify(this.agentWeights)}`;
        if (this.uploadAnalysisRequestKey === requestKey) return;
        this.uploadAnalysisRequestKey = requestKey;
        this.analyzing = true;
        this.errorMessage = '';
        this.feedbackStatus = '';
        this.feedbackMessage = '';
        this.uploadProgress = 0;
        this.startAnalysisProgress();
        await this.service.render();
        let analysisSucceeded = false;
        try {
            this.analysisAbortController = new AbortController();
            const fd = new FormData();
            fd.append('video', this.selectedFile);
            fd.append('metadata', JSON.stringify({ analysis_profile: this.analysisProfile, input_source: 'upload', model_type: this.selectedModelType, ...this.agentWeightMetadata() }));
            const res = await fetch(`/wiz/api/page.dashboard/analyze_upload`, { method: 'POST', body: fd, signal: this.analysisAbortController.signal });
            const json = await this.readJsonResponse(res);
            if (json.code === 200 && json.data) {
                this.applyAnalysisRoot(json.data, false);
                this.analysisSavedName = json.data.saved_name || '';
                this.alertWorkflow = json.data.alert_workflow || null;
                this.syncUploadChunkLogs(json.data);
                analysisSucceeded = true;
            } else {
                this.errorMessage = json.data?.message || '분석 실패';
            }
        } catch (e: any) {
            this.errorMessage = e?.name === 'AbortError' ? '분석이 취소되었습니다. 선택한 영상으로 다시 분석할 수 있습니다.' : this.requestErrorMessage(e, '분석 요청');
        }
        await this.finishAnalysisProgress(analysisSucceeded);
        this.analyzing = false;
        this.analysisAbortController = null;
        this.uploadAnalysisRequestKey = '';
        await this.service.render();
    }

    public cancelAnalysis() {
        if (!this.analyzing) return;
        this.analysisAbortController?.abort();
        this.stopAnalysisProgress();
    }

    private analysisProgressEstimateMs(): number {
        const durationSec = Math.max(0, Number(this.videoMeta?.duration || 0));
        const fileSizeMb = Math.max(0, Number(this.selectedFile?.size || 0) / (1024 * 1024));
        return Math.max(8000, Math.min(45000, 6000 + (durationSec * 550) + (fileSizeMb * 120)));
    }

    private startAnalysisProgress() {
        this.stopAnalysisProgress();
        this.uploadProgress = 1;
        const startedAt = Date.now();
        const estimatedMs = this.analysisProgressEstimateMs();
        this.analysisProgressTimer = setInterval(async () => {
            const elapsedRatio = Math.max(0, (Date.now() - startedAt) / estimatedMs);
            const easedProgress = 1 - Math.exp(-2.4 * elapsedRatio);
            const nextProgress = Math.min(95, Math.round(1 + (94 * easedProgress)));
            if (nextProgress <= this.uploadProgress) return;
            this.uploadProgress = nextProgress;
            await this.service.render();
        }, 400);
    }

    private stopAnalysisProgress() {
        if (!this.analysisProgressTimer) return;
        clearInterval(this.analysisProgressTimer);
        this.analysisProgressTimer = null;
    }

    private async finishAnalysisProgress(success: boolean) {
        this.stopAnalysisProgress();
        if (!success) {
            this.uploadProgress = 0;
            return;
        }
        this.uploadProgress = 100;
        await this.service.render();
        await new Promise(resolve => setTimeout(resolve, 350));
    }

    public async registerSelectedFileForTraining(): Promise<boolean> {
        const files = this.selectedFiles.length > 0 ? this.selectedFiles : (this.selectedFile ? [this.selectedFile] : []);
        if (files.length === 0 || this.trainingUploading) return false;
        const registrationTargetModel = String(this.trainingTargetModel || 'rf-dual');
        const registrationTargetOption = this.trainingTargetOption(registrationTargetModel);
        const registrationTargetLabel = registrationTargetOption?.label || registrationTargetModel;
        if (!this.trainingTargetRegistrationSupported(registrationTargetModel)) {
            this.trainingUploadMessage = `${registrationTargetLabel}은 전용 데이터셋·라벨 계약의 연속 학습 대상이라 이 화면의 Y/N 영상 등록을 사용하지 않습니다. 전용 학습 현황을 확인해주세요.`;
            this.trainingUploadMessageType = 'info';
            await this.loadContinuousTrainingStatus(false);
            await this.service.render();
            return false;
        }
        const requiresPostureLabel = this.trainingTargetRequiresPostureLabel(registrationTargetModel);
        const registrationNote = String(this.trainingUploadNote || '');
        const analysisSavedNameSnapshot = String(this.analysisSavedName || '');
        const assignments = files.map(file => ({
            file,
            assignment: { ...this.trainingAssignmentForFile(file) } as TrainingFileAssignment,
        }));
        const invalidAssignment = assignments.find(item => (
            !['Y', 'N'].includes(item.assignment.fallLabel)
            || (requiresPostureLabel && !item.assignment.postureClass)
        ));
        const assignmentError = invalidAssignment
            ? `${invalidAssignment.file.name}: ${requiresPostureLabel && !invalidAssignment.assignment.postureClass ? `${registrationTargetLabel} 학습용 행동 라벨을 선택해주세요.` : '낙상 라벨 Y/N을 선택해주세요.'}`
            : '';
        if (assignmentError) {
            this.trainingUploadMessage = assignmentError;
            this.trainingUploadMessageType = 'error';
            await this.service.render();
            return false;
        }
        this.lastTrainingRegistrationTargetModel = registrationTargetModel;
        this.trainingUploading = true;
        this.trainingUploadDone = 0;
        this.trainingUploadTotal = files.length;
        this.trainingUploadMessage = '';
        this.trainingUploadMessageType = 'info';
        await this.service.render();
        let savedTotal = 0;
        let duplicateTotal = 0;
        const errors: string[] = [];
        let requestFailed = false;
        try {
            for (let i = 0; i < assignments.length; i++) {
                const file = assignments[i].file;
                const assignment = assignments[i].assignment;
                let stopAfterCurrent = false;
                const metadata: any = {
                    input_source: 'upload-mode-bulk-training-registration',
                    analysis_profile: this.analysisProfile,
                    analysis_model_snapshot: this.selectedModelType,
                    target_model: registrationTargetModel,
                    training_target_model: registrationTargetModel,
                    training_target_label: registrationTargetLabel,
                    posture_class: assignment.postureClass || '',
                    bulk_index: i + 1,
                    bulk_total: files.length,
                    include_intake_summary: i === files.length - 1,
                };
                if (analysisSavedNameSnapshot) metadata.analysis_saved_name = analysisSavedNameSnapshot;
                const fd = new FormData();
                fd.append('video', file);
                fd.append('label', assignment.fallLabel);
                fd.append('note', registrationNote);
                fd.append('metadata', JSON.stringify(metadata));
                const res = await fetch(`/wiz/api/page.dashboard/submit_training_sample`, { method: 'POST', body: fd });
                const json = await this.readJsonResponse(res);
                if (json.code === 200 && json.data) {
                    savedTotal += Number(json.data.saved_count ?? 1);
                    duplicateTotal += Number(json.data.duplicate_count || 0);
                } else {
                    errors.push(`${file.name}: ${json.data?.message || '등록 실패'}`);
                    stopAfterCurrent = true;
                }
                this.trainingUploadDone = i + 1;
                this.trainingUploadMessage = `등록 중 ${this.trainingUploadDone}/${this.trainingUploadTotal} · 신규 ${savedTotal}건 · 중복 ${duplicateTotal}건`;
                await this.service.render();
                if (stopAfterCurrent) break;
            }
            if (errors.length === 0) {
                const yCount = assignments.filter(item => item.assignment.fallLabel === 'Y').length;
                const nCount = assignments.length - yCount;
                const fallSummary = yCount && nCount ? `Y ${yCount}건 · N ${nCount}건` : (yCount ? `Y ${yCount}건` : `N ${nCount}건`);
                const postureCounts: Record<string, number> = {};
                for (const item of assignments) {
                    const key = item.assignment.postureClass || '미지정';
                    postureCounts[key] = (postureCounts[key] || 0) + 1;
                }
                const postureSummary = Object.entries(postureCounts).map(([key, count]) => `${key} ${count}건`).join(' · ');
                this.trainingUploadMessage = `학습 데이터 등록 완료: ${registrationTargetLabel} · 신규 ${savedTotal}건 · 중복 ${duplicateTotal}건 · ${fallSummary}${requiresPostureLabel ? ` · ${postureSummary}` : ''}`;
                this.trainingUploadMessageType = 'success';
                this.trainingUploadNote = '';
            } else {
                this.trainingUploadMessage = `등록 중단: 신규 ${savedTotal}건, 중복 ${duplicateTotal}건, 실패 ${errors.length}건 · ${errors.slice(0, 2).join(' / ')}`;
                this.trainingUploadMessageType = 'error';
            }
        } catch (e: any) {
            requestFailed = true;
            this.trainingUploadMessage = this.requestErrorMessage(e, '학습 데이터 등록');
            this.trainingUploadMessageType = 'error';
        }
        this.trainingUploading = false;
        await this.service.render();
        return !requestFailed && errors.length === 0;
    }

    public async registerAndStartBackgroundTraining() {
        if (this.trainingUploading || this.trainingJobStarting) return;
        const ok = await this.registerSelectedFileForTraining();
        if (!ok) return;
        await this.startBackgroundTraining('full', this.lastTrainingRegistrationTargetModel || this.trainingTargetModel);
    }

    private stopTrainingJobPolling() {
        if (this.trainingJobPollHandle) {
            clearInterval(this.trainingJobPollHandle);
            this.trainingJobPollHandle = null;
        }
    }

    private startTrainingJobPolling() {
        this.stopTrainingJobPolling();
        this.trainingJobPollHandle = setInterval(() => { this.refreshTrainingJobStatus(); }, 5000);
    }

    private stopContinuousTrainingPolling() {
        if (this.continuousTrainingPollHandle) {
            clearInterval(this.continuousTrainingPollHandle);
            this.continuousTrainingPollHandle = null;
        }
    }

    private startContinuousTrainingPolling() {
        this.stopContinuousTrainingPolling();
        this.continuousTrainingPollHandle = setInterval(() => { this.loadContinuousTrainingStatus(false); }, 20000);
    }

    public async toggleBackgroundTrainingPanel() {
        this.backgroundTrainingExpanded = !this.backgroundTrainingExpanded;
        await this.service.render();
    }

    public async loadContinuousTrainingStatus(render: boolean = true) {
        try {
            const res = await fetch('/wiz/api/page.dashboard/continuous_training_status?name=all');
            const json = await this.readJsonResponse(res);
            if (json.code === 200 && json.data) {
                const items = this.normalizeContinuousTrainingItems(json.data.items || json.data);
                this.continuousTrainingItems = items;
                this.continuousTrainingStatus = this.selectedContinuousTrainingStatus() || json.data;
                if (this.prototypeInfo) {
                    this.prototypeInfo.continuous_training = this.prototypeInfo.continuous_training || {};
                    for (const item of items) {
                        const name = String(item?.name || '').trim();
                        if (name) this.prototypeInfo.continuous_training[name] = item;
                    }
                    this.prototypeInfo.continuous_training_items = items;
                }
            }
        } catch (e) { }
        if (render) await this.service.render();
    }

    private continuousTrainingResumeTarget(status: any): string {
        const name = String(status?.name || '').trim();
        if (name.startsWith('dashboard-')) return this.trainingTargetModel || 'all';
        if (name === 'rf-fall-v2') return 'rf-fall-v2';
        if (name === 'xg-posture') return 'xg-posture';
        if (name === 'xg-posture-occlusion-aux') return 'xg-posture-occlusion-aux';
        if (name === 'aihub82') return 'aihub82';
        if (name === 'aihub173') return 'aihub173';
        return name || this.trainingTargetContinuousStatusName() || 'all';
    }

    public isContinuousTrainingResuming(status: any): boolean {
        return !!this.continuousTrainingResuming[this.continuousTrainingResumeTarget(status)];
    }

    public continuousTrainingResumeButtonText(status: any): string {
        if (this.isContinuousTrainingResuming(status)) return '요청 중';
        const stage = this.continuousTrainingStageValue(status);
        if (stage === 'running') return '점검';
        if (stage === 'blocked' || stage === 'stale' || stage === 'failed') return '재개';
        if (stage === 'queued' || stage === 'preparing') return '점검';
        return '재개';
    }

    public async resumeContinuousTraining(status?: any) {
        const target = this.continuousTrainingResumeTarget(status || {});
        if (this.continuousTrainingResuming[target]) return;
        this.continuousTrainingResuming[target] = true;
        this.trainingUploadMessage = `${this.continuousTrainingCardTitle(status || {})} 재개/점검 요청 중입니다.`;
        this.trainingUploadMessageType = 'info';
        await this.service.render();
        try {
            const params = new URLSearchParams();
            params.set('target', target);
            const res = await fetch(`/wiz/api/page.dashboard/resume_continuous_training?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            if (json.code === 200 && json.data?.ok !== false) {
                const items = this.normalizeContinuousTrainingItems(json.data?.status?.items || json.data?.status || []);
                if (items.length > 0) {
                    this.continuousTrainingItems = items;
                    this.continuousTrainingStatus = this.selectedContinuousTrainingStatus() || json.data.status;
                }
                this.trainingUploadMessage = json.data?.message || '학습 재개/점검 요청을 보냈습니다.';
                this.trainingUploadMessageType = 'success';
            } else {
                this.trainingUploadMessage = json.data?.message || '학습 재개 요청 실패';
                this.trainingUploadMessageType = 'error';
            }
        } catch (e: any) {
            this.trainingUploadMessage = this.requestErrorMessage(e, '학습 재개');
            this.trainingUploadMessageType = 'error';
        }
        this.continuousTrainingResuming[target] = false;
        await this.loadContinuousTrainingStatus(false);
        await this.service.render();
    }

    public async startBackgroundTraining(jobType: string = 'full', targetModel: string = '') {
        if (this.trainingJobStarting) return;
        const resolvedTargetModel = String(targetModel || this.trainingTargetModel || 'rf-dual');
        const targetOption = this.trainingTargetOption(resolvedTargetModel);
        const resolvedJobType = jobType === 'full' ? String(targetOption?.job_type || 'full') : jobType;
        if (!['full', 'rf', 'posture'].includes(resolvedJobType)) {
            await this.loadContinuousTrainingStatus(false);
            const status = this.selectedContinuousTrainingStatus();
            const eta = this.continuousTrainingEtaText(status);
            this.trainingUploadMessage = `${this.trainingTargetLabel()}은 대시보드 HITL job 대신 별도 연속 학습/전용 학습 파이프라인으로 관리됩니다. ${this.continuousTrainingStageText(status)}${eta && eta !== '-' ? ` · ETA ${eta}` : ''}`;
            this.trainingUploadMessageType = 'info';
            await this.service.render();
            return;
        }
        this.trainingJobStarting = true;
        this.trainingUploadMessage = '백그라운드 학습 job을 시작합니다.';
        this.trainingUploadMessageType = 'info';
        await this.service.render();
        try {
            const params = new URLSearchParams();
            params.set('job_type', resolvedJobType || 'full');
            params.set('apply_mode', 'manual');
            params.set('note', this.trainingUploadNote || '');
            params.set('target_model', resolvedTargetModel);
            const res = await fetch(`/wiz/api/page.dashboard/start_training_job?${params.toString()}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            const stage = String(json?.data?.stage || json?.data?.status || '').trim();
            if (json.code === 200 && json.data && json.data.ok !== false && stage !== 'blocked' && stage !== 'failed') {
                this.trainingJob = json.data;
                this.trainingUploadMessage = `학습 job 시작: ${json.data.job_id || ''}`;
                this.trainingUploadMessageType = 'success';
                this.startTrainingJobPolling();
            } else {
                if (json.data) this.trainingJob = json.data;
                this.trainingUploadMessage = json.data?.message || '학습 job 시작 실패';
                this.trainingUploadMessageType = 'error';
            }
        } catch (e: any) {
            this.trainingUploadMessage = this.requestErrorMessage(e, '학습 job 요청');
            this.trainingUploadMessageType = 'error';
        }
        this.trainingJobStarting = false;
        await this.service.render();
    }

    public async refreshTrainingJobStatus() {
        const jobId = this.trainingJob?.job_id || 'latest';
        try {
            const res = await fetch(`/wiz/api/page.dashboard/training_job_status?job_id=${encodeURIComponent(jobId)}`);
            const json = await this.readJsonResponse(res);
            if (json.code === 200 && json.data) {
                this.trainingJob = json.data;
                const status = String(this.trainingJob?.status || '');
                if (!['queued', 'running'].includes(status)) {
                    this.stopTrainingJobPolling();
                    const metric = this.trainingJobCandidateMetricText();
                    const sample = this.trainingJobTrainingSampleText();
                    if (status === 'completed_pending_apply' || status === 'completed') {
                        this.trainingUploadMessage = `학습 완료: ${metric} · 샘플 ${sample}`;
                        this.trainingUploadMessageType = 'success';
                    } else if (status === 'applied') {
                        this.trainingUploadMessage = `학습 결과 적용 완료: ${metric}`;
                        this.trainingUploadMessageType = 'success';
                    } else if (status === 'blocked' || status === 'failed') {
                        this.trainingUploadMessage = this.trainingJob?.message || this.trainingJobStatusText();
                        this.trainingUploadMessageType = 'error';
                    }
                }
                await this.service.render();
            }
        } catch (e) { }
    }

    public async applyTrainingJob() {
        if (!this.trainingJob?.job_id || this.trainingJobApplying) return;
        this.trainingJobApplying = true;
        await this.service.render();
        try {
            const res = await fetch(`/wiz/api/page.dashboard/apply_training_job?job_id=${encodeURIComponent(this.trainingJob.job_id)}`, { method: 'POST' });
            const json = await this.readJsonResponse(res);
            if (json.code === 200 && json.data) {
                this.trainingJob = json.data;
                await this.loadPrototypeInfo();
            } else {
                this.trainingUploadMessage = json.data?.message || '학습 결과 적용 실패';
                this.trainingUploadMessageType = 'error';
            }
        } catch (e: any) {
            this.trainingUploadMessage = this.requestErrorMessage(e, '학습 적용');
            this.trainingUploadMessageType = 'error';
        }
        this.trainingJobApplying = false;
        await this.service.render();
    }

    public trainingJobProgressPercent(): number {
        return Math.max(0, Math.min(100, Math.round(Number(this.trainingJob?.progress || 0) * 100)));
    }

    public trainingJobStatusText(): string {
        const status = String(this.trainingJob?.status || '');
        if (status === 'queued') return '대기 중';
        if (status === 'running') return '학습 중';
        if (status === 'completed_pending_apply') return '학습 완료';
        if (status === 'completed') return '학습 완료';
        if (status === 'applied') return '적용 완료';
        if (status === 'blocked') return '데이터 필요';
        if (status === 'failed') return '실패';
        return status || '상태 없음';
    }

    public trainingJobEtaText(): string {
        const status = String(this.trainingJob?.status || '');
        if (status === 'completed_pending_apply' || status === 'completed' || status === 'applied') return '완료';
        if (status === 'blocked') return '데이터 필요';
        if (status === 'failed') return '실패';
        const eta = Number(this.trainingJob?.eta_sec);
        if (!Number.isFinite(eta) || eta <= 0) return '계산 중';
        if (eta < 60) return `${Math.round(eta)}초`;
        const min = Math.floor(eta / 60);
        const sec = Math.round(eta % 60);
        return `${min}분 ${sec}초`;
    }

    public trainingJobVersionText(): string {
        const jobId = String(this.trainingJob?.job_id || '').trim();
        if (!jobId) return '-';
        const version = String(this.trainingJob?.training_run_label || this.trainingJob?.version_badge || '').trim();
        const target = this.trainingJob?.target_model ? String(this.trainingJob.target_model) : '';
        const shortId = jobId.length > 13 ? jobId.slice(0, 12) : jobId;
        if (version) return target ? `${version} · ${target} · ${shortId}` : `${version} · ${shortId}`;
        return target ? `${target} · ${shortId}` : shortId;
    }

    public trainingJobCanApply(): boolean {
        return this.trainingJob?.status === 'completed_pending_apply';
    }

    private jobNumber(value: any): number {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    private jobPct(value: any, digits: number = 1): string {
        const n = this.jobNumber(value);
        return n > 0 ? `${(n * 100).toFixed(digits)}%` : '';
    }

    private dashboardJobRfSummary(jobArg?: any): any {
        const job = jobArg || this.trainingJob || {};
        const result = job.result || {};
        if (result.rf_pipeline_training && typeof result.rf_pipeline_training === 'object') return result.rf_pipeline_training;
        const steps = Array.isArray(job.steps) ? job.steps : [];
        const rfStep = steps.find((step: any) => String(step?.name || '') === 'rf_pipeline');
        const summary = rfStep?.result?.summary;
        return summary && typeof summary === 'object' ? summary : {};
    }

    private dashboardJobCvMetrics(jobArg?: any): any {
        const job = jobArg || this.trainingJob || {};
        const rf = this.dashboardJobRfSummary(job);
        if (rf.cv && typeof rf.cv === 'object') return rf.cv;
        const model = job?.result?.model_comparison?.models?.['rf-pipeline'] || {};
        return {
            f1: model?.f1?.cv ?? job?.candidate_f1,
            accuracy: model?.accuracy?.cv ?? job?.candidate_accuracy,
            precision: model?.precision?.cv ?? job?.candidate_precision,
            recall: model?.recall?.cv ?? job?.candidate_recall,
            roc_auc: model?.roc_auc?.cv ?? job?.candidate_roc_auc,
        };
    }

    public trainingJobTrainingSampleText(jobArg?: any): string {
        const job = jobArg || this.trainingJob || {};
        const rf = this.dashboardJobRfSummary(job);
        const samples = this.jobNumber(rf.training_samples ?? job?.training_samples ?? job?.intake_summary?.total ?? job?.processed);
        return samples > 0 ? samples.toLocaleString() : '-';
    }

    public trainingJobCandidateMetricText(jobArg?: any): string {
        const cv = this.dashboardJobCvMetrics(jobArg);
        const bits = [];
        const f1 = this.jobPct(cv.f1);
        const acc = this.jobPct(cv.accuracy);
        const recall = this.jobPct(cv.recall);
        if (f1) bits.push(`CV F1 ${f1}`);
        if (acc) bits.push(`Acc ${acc}`);
        if (recall) bits.push(`Recall ${recall}`);
        return bits.join(' · ') || '-';
    }

    public trainingJobClassBalanceText(jobArg?: any): string {
        const job = jobArg || this.trainingJob || {};
        const rf = this.dashboardJobRfSummary(job);
        const dist = rf.class_distribution || job.intake_summary || {};
        const y = this.jobNumber(dist.Y);
        const n = this.jobNumber(dist.N);
        if (y > 0 || n > 0) return `Y ${y.toLocaleString()} / N ${n.toLocaleString()}`;
        return '-';
    }

    public normalizeContinuousTrainingItems(raw: any): any[] {
        if (Array.isArray(raw)) return raw.filter(Boolean);
        if (raw?.items && Array.isArray(raw.items)) return raw.items.filter(Boolean);
        if (raw && typeof raw === 'object') {
            if (raw.aihub82 || raw.aihub173 || raw['xg-posture-occlusion-aux']) {
                return Object.keys(raw).map(key => ({ name: key, ...(raw[key] || {}) })).filter(Boolean);
            }
            return [raw];
        }
        return [];
    }

    private trainingTargetContinuousStatusName(): string {
        const option = this.trainingTargetOption();
        const key = String(option?.key || this.trainingTargetModel || '').trim();
        const jobType = String(option?.job_type || '').trim();
        if (jobType === 'aihub82' || key.includes('aihub82')) return 'aihub82';
        if (jobType === 'aihub173' || key.includes('aihub173') || key.includes('driver')) return 'aihub173';
        if (key === 'rf-fall-v2') return 'rf-fall-v2';
        if (key === 'xg-posture') return 'xg-posture';
        if (key.includes('occlusion')) return 'xg-posture-occlusion-aux';
        return '';
    }

    public selectedContinuousTrainingStatus(): any {
        const selectedName = this.trainingTargetContinuousStatusName();
        const items = this.normalizeContinuousTrainingItems(this.continuousTrainingItems);
        if (selectedName) {
            const selected = items.find((item: any) => String(item?.name || '') === selectedName);
            if (selected) return selected;
        }
        const running = items.find((item: any) => ['queued', 'running'].includes(this.continuousTrainingStageValue(item)));
        return running || items.find((item: any) => String(item?.name || '') === 'aihub82') || this.continuousTrainingStatus || {};
    }

    private continuousTrainingStatusValues(): any[] {
        const byName: Record<string, any> = {};
        const items = this.normalizeContinuousTrainingItems(this.continuousTrainingItems);
        for (let index = 0; index < items.length; index += 1) {
            const item = items[index];
            const key = String(item?.name || item?.label || item?.job_id || `item-${index}`);
            byName[key] = item;
        }
        if (this.continuousTrainingStatus && Object.keys(byName).length === 0) {
            byName['aihub82'] = { name: 'aihub82', label: 'AI-Hub 82 표정', ...this.continuousTrainingStatus };
        }
        if (this.trainingJob?.status) {
            const jobKey = 'dashboard-' + String(this.trainingJob.job_id || 'latest');
            if (!byName[jobKey]) {
                byName[jobKey] = {
                    name: jobKey,
                    label: this.trainingTargetLabel() || '대시보드 학습',
                    model_family: 'dashboard-job',
                    ...this.trainingJob,
                };
            }
        }
        return Object.values(byName);
    }

    private isMainContinuousTrainingStage(stage: string): boolean {
        return ['queued', 'running', 'blocked', 'failed', 'stale', 'preparing', 'completed_pending_apply'].includes(stage);
    }

    private isCompletedContinuousTrainingStage(stage: string): boolean {
        return stage === 'completed' || stage === 'applied';
    }

    public continuousTrainingCards(): any[] {
        const selectedName = this.trainingTargetContinuousStatusName();
        const values = this.continuousTrainingStatusValues().filter((item: any) => {
            const stage = this.continuousTrainingStageValue(item);
            if (!stage || stage === 'missing' || stage === 'not-started') return false;
            if (stage === 'queued' || stage === 'running') return true;
            if (this.uploadWorkflowMode !== 'train') return false;
            return this.isMainContinuousTrainingStage(stage);
        });
        values.sort((a: any, b: any) => {
            const rank = (item: any) => {
                const stage = this.continuousTrainingStageValue(item);
                if (stage === 'running') return 0;
                if (stage === 'queued') return 1;
                if (String(item?.name || '') === selectedName) return 2;
                if (stage === 'stale' || stage === 'failed' || stage === 'blocked') return 3;
                if (stage === 'preparing') return 4;
                return 5;
            };
            const rankDiff = rank(a) - rank(b);
            if (rankDiff !== 0) return rankDiff;
            return String(b?.updated_at || b?.created_at || '').localeCompare(String(a?.updated_at || a?.created_at || ''));
        });
        return values.slice(0, 6);
    }

    public completedTrainingLogCards(): any[] {
        const values = this.continuousTrainingStatusValues().filter((item: any) => {
            const stage = this.continuousTrainingStageValue(item);
            return this.isCompletedContinuousTrainingStage(stage);
        });
        values.sort((a: any, b: any) => {
            return String(b?.updated_at || b?.created_at || '').localeCompare(String(a?.updated_at || a?.created_at || ''));
        });
        return values.slice(0, 6);
    }

    public completedTrainingLogCount(): number {
        return this.continuousTrainingStatusValues().filter((item: any) => {
            return this.isCompletedContinuousTrainingStage(this.continuousTrainingStageValue(item));
        }).length;
    }

    public trainingStatusMainCountText(): string {
        const count = this.continuousTrainingCards().length;
        return count > 0 ? `${count}개 진행/조치 상태 표시` : '진행/조치 상태 없음';
    }

    public isTrainingJobActive(): boolean {
        const status = String(this.trainingJob?.status || '');
        return status === 'queued' || status === 'running';
    }

    public continuousTrainingGridClass(): string {
        const count = this.continuousTrainingCards().length;
        if (count >= 2) return 'xl:grid-cols-2';
        return 'xl:grid-cols-1';
    }

    public continuousTrainingCardTitle(status: any): string {
        return String(status?.label || status?.title || status?.name || '학습 상태');
    }

    public continuousTrainingStageBadgeText(status: any): string {
        const stage = this.continuousTrainingStageValue(status);
        if (stage === 'running' && this.isSourceRecoveryTrainingStatus(status)) return '재수신 중';
        if (stage === 'running') return '학습 중';
        if (stage === 'queued') return '대기';
        if (stage === 'blocked') return '데이터 대기';
        if (stage === 'preparing') return '준비 필요';
        if (stage === 'stale') return '중단';
        if (stage === 'completed_pending_apply') return '적용 대기';
        if (stage === 'applied') return '적용 완료';
        if (stage === 'completed') return '완료';
        if (stage === 'failed') return '실패';
        return stage || '-';
    }

    private continuousTrainingStageValue(status: any): string {
        const rawStage = String(status?.stage || '').trim();
        const rawStatus = String(status?.status || '').trim();
        if (rawStage === 'finished' && rawStatus) return rawStatus;
        if (rawStage === 'blocked' || rawStatus === 'blocked') return 'blocked';
        if (rawStage === 'stale' || rawStatus === 'stale') return 'stale';
        return rawStage || rawStatus;
    }

    public continuousTrainingCardClass(status: any): string {
        const stage = this.continuousTrainingStageValue(status);
        if (stage === 'failed') return 'border-rose-200 bg-rose-50';
        if (stage === 'stale' || stage === 'blocked') return 'border-amber-200 bg-amber-50';
        if (stage === 'preparing') return 'border-zinc-200 bg-zinc-50';
        if (stage === 'queued' || stage === 'running') return 'border-emerald-200 bg-emerald-50';
        if (stage === 'completed' || stage === 'completed_pending_apply' || stage === 'applied') return 'border-blue-200 bg-blue-50';
        return 'border-zinc-200 bg-white';
    }

    public continuousTrainingStageText(statusArg?: any): string {
        const status = statusArg || this.continuousTrainingStatus || {};
        const stage = this.continuousTrainingStageValue(status);
        const version = String(status.candidate_version_text || status.training_version_text || '').trim()
            || this.compactExperimentVersion(String(status.experiment || '').trim());
        const suffix = version && version !== '-' ? ` · ${version}` : '';
        const label = this.continuousTrainingCardTitle(status);
        if (stage === 'running' && this.isSourceRecoveryTrainingStatus(status)) return `${label} 데이터 재수신 중${suffix}`;
        if (stage === 'running') return `${label} 학습 중${suffix}`;
        if (stage === 'queued') return `${label} 대기 중${suffix}`;
        if (stage === 'blocked') return `${label} 데이터 대기${suffix}`;
        if (stage === 'preparing') return `${label} 준비 필요${suffix}`;
        if (stage === 'completed_pending_apply') return `${label} 학습 완료 · 적용 대기`;
        if (stage === 'applied') return `${label} 학습 결과 적용 완료`;
        if (stage === 'completed') return status.promoted ? `${label} 운영 성능 충족` : `${label} 학습 완료`;
        if (stage === 'failed') return `${label} 학습 실패`;
        if (stage === 'stale') return `${label} 중단됨 · 상태 확인 필요`;
        if (stage === 'already-running') return '이미 실행 중';
        if (stage === 'not-started' || stage === 'missing') return '대기 중';
        return stage || '상태 확인 중';
    }

    public continuousTrainingMetricText(statusArg?: any): string {
        const status = statusArg || this.continuousTrainingStatus || {};
        if (status?.model_family === 'dashboard-job') {
            const stage = this.continuousTrainingStageValue(status);
            const metric = this.trainingJobCandidateMetricText(status);
            const samples = this.trainingJobTrainingSampleText(status);
            const balance = this.trainingJobClassBalanceText(status);
            if (metric !== '-') {
                const bits = [metric];
                if (samples !== '-') bits.push(`샘플 ${samples}`);
                if (balance !== '-') bits.push(balance);
                return bits.join(' · ');
            }
            if (stage === 'blocked') return status?.message || '학습 차단 · 데이터 필요';
            if (stage === 'failed') return status?.message || '학습 실패';
            const progress = Math.round(Number(status.progress || 0) * 100);
            const processed = Number(status.processed || 0);
            const total = Number(status.total || 0);
            return total > 0 ? `진행 ${progress}% · 처리 ${processed}/${total}` : `진행 ${progress}%`;
        }
        const candidateSeqF1 = Number(status?.candidate_sequence_macro_f1 || 0);
        const candidateF1 = Number(status?.candidate_macro_f1 || 0);
        if (candidateSeqF1 > 0 || candidateF1 > 0) {
            const activeSeqF1 = Number(status?.sequence_macro_f1 || 0);
            const activeF1 = Number(status?.active_macro_f1 || status?.macro_f1 || 0);
            const bits = [];
            if (activeSeqF1 > 0) bits.push(`active sequence F1 ${(activeSeqF1 * 100).toFixed(1)}%`);
            else if (activeF1 > 0) bits.push(`active F1 ${(activeF1 * 100).toFixed(1)}%`);
            if (candidateSeqF1 > 0) bits.push(`후보 sequence F1 ${(candidateSeqF1 * 100).toFixed(1)}%`);
            else if (candidateF1 > 0) bits.push(`후보 F1 ${(candidateF1 * 100).toFixed(1)}%`);
            if (status?.candidate_saved === false) bits.push('미승격');
            const target = Number(status?.target_macro_f1 || 0.95);
            bits.push(`목표 ${(target * 100).toFixed(0)}%`);
            return bits.join(' · ');
        }
        const seqF1 = Number(status?.sequence_macro_f1 || 0);
        const seqAcc = Number(status?.sequence_accuracy || 0);
        if (seqF1 > 0 || seqAcc > 0) {
            const group = Number(status?.active_macro_f1 || status?.macro_f1 || 0);
            const bits = [];
            if (seqF1 > 0) bits.push(`sequence F1 ${(seqF1 * 100).toFixed(1)}%`);
            if (seqAcc > 0) bits.push(`sequence Acc ${(seqAcc * 100).toFixed(1)}%`);
            if (group > 0) bits.push(`window F1 ${(group * 100).toFixed(1)}%`);
            return bits.join(' · ');
        }
        const active = Number(status?.active_macro_f1 || status?.macro_f1 || 0);
        const candidate = Number(status?.candidate_macro_f1 || 0);
        const target = Number(status?.target_macro_f1 || 0.9);
        const stretch = Number(status?.stretch_macro_f1 || 0.95);
        const activeVersion = String(status?.active_version_text || '').trim();
        const activePrefix = activeVersion ? `active ${activeVersion} ` : 'active ';
        const activeText = active > 0 ? `${activePrefix}macro F1 ${(active * 100).toFixed(1)}%` : `${activePrefix}F1 확인 중`;
        const targetText = `목표 ${(target * 100).toFixed(0)}%→${(stretch * 100).toFixed(0)}%`;
        return candidate > 0 ? `${activeText} · 학습 결과 ${(candidate * 100).toFixed(1)}% · ${targetText}` : `${activeText} · ${targetText}`;
    }

    private compactExperimentVersion(label: string): string {
        const raw = String(label || '').trim();
        const match = raw.match(/^(\d+)_([A-Za-z0-9_.-]+?)(?:_\d{8}_\d{6})?$/);
        if (!match) return raw || '-';
        return `#${match[1]} ${match[2].replace(/_/g, ' ')}`;
    }

    private isSourceRecoveryTrainingStatus(status: any): boolean {
        const text = [
            status?.latest_log,
            status?.message,
            status?.eta_text,
        ].map((value: any) => String(value || '').toLowerCase()).join(' ');
        return text.includes('[source]')
            || text.includes('download')
            || text.includes('다운로드')
            || text.includes('재수신')
            || text.includes('split zip')
            || text.includes('assembly');
    }

    public continuousTrainingVersionText(statusArg?: any): string {
        const status = statusArg || this.continuousTrainingStatus || {};
        const readyText = String(status.training_version_text || '').trim();
        if (readyText) return readyText;
        const candidate = String(status.candidate_version_text || '').trim();
        const active = String(status.active_version_text || '').trim();
        const experiment = String(status.experiment || '').trim();
        const bits = [];
        if (candidate) bits.push(`진행 ${candidate}`);
        else if (experiment) bits.push(`진행 ${this.compactExperimentVersion(experiment)}`);
        if (active) bits.push(`active ${active}`);
        const total = Number(status.total_candidate_versions || 0);
        if (Number.isFinite(total) && total > 0) bits.push(`누적 후보 ${total}개`);
        return bits.length > 0 ? bits.join(' · ') : '-';
    }

    public continuousTrainingUpdatedText(): string {
        return String(this.continuousTrainingStatus?.updated_at || '-');
    }

    public continuousTrainingUpdatedTextFor(statusArg?: any): string {
        const status = statusArg || {};
        return String(status.updated_at || status.created_at || '-');
    }

    public continuousTrainingEtaText(statusArg?: any): string {
        const status = statusArg || this.continuousTrainingStatus || {};
        const text = String(status.eta_text || '').trim();
        if (text) return text;
        const eta = Number(status.eta_sec);
        if (!Number.isFinite(eta) || eta <= 0) {
            const stage = this.continuousTrainingStageValue(status);
            if (stage === 'completed' || stage === 'completed_pending_apply' || stage === 'applied') return '완료';
            if (stage === 'stale') return '중단';
            if (stage === 'blocked') return '데이터 필요';
            return '-';
        }
        if (eta < 60) return `${Math.round(eta)}초`;
        const min = Math.floor(eta / 60);
        const sec = Math.round(eta % 60);
        if (min < 60) return `${min}분 ${sec}초`;
        const hour = Math.floor(min / 60);
        const remMin = min % 60;
        return `${hour}시간 ${remMin}분`;
    }

    public continuousTrainingDataProgressText(statusArg?: any): string {
        const status = statusArg || this.continuousTrainingStatus || {};
        const completed = Number(status.completed_files);
        const required = Number(status.required_files);
        const current = Number(status.redownload_current);
        const total = Number(status.redownload_total);
        const progress = Number(status.redownload_current_progress);
        const speed = Number(status.redownload_current_speed_mbps);
        const bits: string[] = [];
        if (Number.isFinite(completed) && Number.isFinite(required) && required > 0) {
            bits.push(`확보 ${completed}/${required}`);
        }
        if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
            bits.push(`현재 ${current}/${total}`);
        }
        if (Number.isFinite(progress) && progress > 0) {
            bits.push(`파일 ${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%`);
        }
        if (Number.isFinite(speed) && speed > 0) {
            bits.push(`${speed.toFixed(1)}MB/s`);
        }
        if (bits.length > 0) return bits.join(' · ');
        const purpose = String(status.data_purpose || '').trim();
        if (purpose) return purpose;
        return this.continuousTrainingUpdatedTextFor(status);
    }

    public continuousTrainingDownloadProgressPercent(statusArg?: any): number {
        const progress = Number((statusArg || {}).redownload_current_progress);
        if (!Number.isFinite(progress) || progress <= 0) return 0;
        return Math.round(Math.max(0, Math.min(1, progress)) * 100);
    }

    public modelTrainingStatCards(): any[] {
        const stats = this.prototypeInfo?.dataset_summary?.model_training_stats;
        if (Array.isArray(stats) && stats.length > 0) return stats.slice(0, 6);
        return [];
    }

    public analysisModelOptionsForView(): any[] {
        const options = this.prototypeInfo?.model_options?.options;
        if (Array.isArray(options) && options.length > 0) {
            return options.filter((item: any) => item?.available !== false);
        }
        return [{ key: 'rf-dual', label: 'RF-Dual (Fall+Posture)', description: '현재 운영 모델' }];
    }

    public selectedAnalysisModelOption(): any {
        return this.analysisModelOptionsForView().find((item: any) => item?.key === this.selectedModelType) || this.analysisModelOptionsForView()[0] || {};
    }

    public selectedAnalysisModelLabel(): string {
        return this.selectedAnalysisModelOption()?.label || 'RF-Dual (Fall+Posture)';
    }

    public plannedRuntimeFilterItems(): any[] {
        const registry = this.prototypeInfo?.model_registry || {};
        const selection = registry?.runtime_selection || {};
        const items = Array.isArray(registry?.items) ? registry.items : [];
        const modelId = String(selection?.['static-object-filter'] || '').trim();
        const planned: any[] = [];
        if (modelId) {
            const item = items.find((candidate: any) => String(candidate?.model_id || '') === modelId) || {};
            planned.push({
                family: 'static-object-filter',
                model_id: modelId,
                label: item?.family_label || '정지 사물 필터 v1',
                version: item?.display_version || item?.version_text || '',
            });
        }
        const poseModelId = String(selection?.['pose-entity-filter'] || '').trim();
        const poseItem = items.find((candidate: any) => (
            poseModelId
                ? String(candidate?.model_id || '') === poseModelId
                : candidate?.family === 'pose-entity-filter' && candidate?.source === 'active'
        )) || {};
        planned.push({
            family: 'pose-entity-filter',
            model_id: poseItem?.model_id || poseModelId || 'active:pose-entity-filter',
            label: poseItem?.family_label || '사람/사물 판정 필터 v1',
            version: poseItem?.display_version || poseItem?.version_text || 'v1',
        });
        return planned;
    }

    public plannedRuntimeFilterText(): string {
        const items = this.plannedRuntimeFilterItems();
        if (items.length === 0) return '';
        const labels = items.map((item: any) => {
            const version = String(item?.version || '').trim();
            const label = String(item?.label || '운영 필터').trim();
            return version && !label.includes(version) ? `${label} ${version}` : label;
        });
        return `적용 필터: ${labels.join(' / ')} · 모든 모델 공통`;
    }

    public async onAnalysisModelChanged() {
        this.uploadAnalysisRequestKey = '';
        this.realtimePerfStats.warmupMs = 0;
        if (this.realtimeActive) {
            this.errorMessage = '모델 변경은 다음 청크부터 반영됩니다.';
        }
        await this.service.render();
    }

    public modelMetricText(item: any): string {
        const bits = [];
        if (item?.algorithm) bits.push(String(item.algorithm));
        const f1 = Number(item?.macro_f1 ?? item?.f1 ?? 0);
        const acc = Number(item?.accuracy ?? 0);
        const recall = Number(item?.recall ?? 0);
        if (Number.isFinite(f1) && f1 > 0) bits.push(`F1 ${(f1 * 100).toFixed(1)}%`);
        if (Number.isFinite(acc) && acc > 0) bits.push(`Acc ${(acc * 100).toFixed(1)}%`);
        if (Number.isFinite(recall) && recall > 0) bits.push(`Recall ${(recall * 100).toFixed(1)}%`);
        return bits.join(' · ') || item?.metric_note || '-';
    }

    public modelVersionBadge(item: any): string {
        const direct = String(item?.version_badge || '').trim();
        if (direct) return direct;
        const values = [
            item?.training_run_label,
            item?.version_text,
            item?.active_version_text,
            item?.model_version,
            item?.version,
        ];
        for (const value of values) {
            const text = String(value || '').trim();
            if (!text) continue;
            const longMatch = text.match(/(?:^|[^A-Za-z0-9])v\s*([0-9]{8,20})(?:[^A-Za-z0-9]|$)/i);
            if (longMatch) return `v${longMatch[1]}`;
            const match = text.match(/(?:^|[^A-Za-z0-9])v\s*([0-9]{1,4})(?:[^A-Za-z0-9]|$)/i);
            if (match) return `v${Number(match[1])}`;
            const cycle = text.match(/(?:^|[^A-Za-z0-9])cycle\s*0*([1-9][0-9]{0,5})(?:[^0-9]|$)/i);
            if (cycle) return `v${Number(cycle[1])}`;
        }
        return '';
    }

    public modelVersionText(item: any): string {
        const label = String(item?.training_run_label || '').trim();
        const text = String(item?.version_text || '').trim();
        if (label && text && text !== '-') return `${label} · ${text}`;
        if (label) return label;
        if (text && text !== '-') return `active 버전: ${text}`;
        const source = String(item?.model_path || '').split('/').pop() || '';
        return source ? `active 버전: ${source}` : 'active 버전: -';
    }

    public modelContinuousTrainingText(item: any): string {
        const badge = String(item?.current_training_badge || '').trim();
        const text = String(item?.continuous_training_version_text || '').trim();
        if (badge && text && text !== '-') return `${badge} · ${text}`;
        if (badge) return badge;
        if (!text || text === '-') return '';
        const stage = String(item?.continuous_training_stage || '').trim();
        const prefix = stage === 'running' ? '연속 학습' : '연속 상태';
        return `${prefix}: ${text}`;
    }

    public clearSelectedFile() {
        this.revokeSelectedVideoUrl();
        this.selectedFile = null;
        this.selectedFiles = [];
        this.clearAnalysisState();
        this.feedbackStatus = '';
        this.trainingUploadMessage = '';
        this.trainingUploadDone = 0;
        this.trainingUploadTotal = 0;
        this.trainingFileAssignments = {};
        this.trainingFilePage = 0;
        this.trainingJob = null;
        this.stopTrainingJobPolling();
        this.errorMessage = '';
        this.uploadLogEntries = [];
        this.uploadChunkSummary = null;
    }

    public riskClass(level: string): string { if (level === 'high') return 'text-rose-600'; if (level === 'medium') return 'text-amber-600'; return 'text-emerald-600'; }
    public riskBgClass(level: string): string { if (level === 'high') return 'bg-rose-50 border-rose-200'; if (level === 'medium') return 'bg-amber-50 border-amber-200'; return 'bg-emerald-50 border-emerald-200'; }
    public riskBadgeClass(level: string): string { if (level === 'high') return 'bg-rose-100 text-rose-700'; if (level === 'medium') return 'bg-amber-100 text-amber-700'; return 'bg-emerald-100 text-emerald-700'; }
    public riskScorePercent(result?: any): number {
        const res = result || this.analysisResult || {};
        return Math.round(Number(res?.displayed_risk_score ?? res?.event_risk_score ?? res?.raw_model_risk_score ?? res?.risk_score ?? 0) * 100);
    }
    public rawModelRiskPercent(result?: any): number {
        const res = result || this.analysisResult || {};
        return Math.round(Number(res?.raw_model_risk_score ?? res?.model_output?.risk_score ?? res?.risk_score ?? 0) * 100);
    }
    public formatRisk(score: number): string { return (score * 100).toFixed(0) + '%'; }
    public environmentObjectAnalysis(): any {
        const root = this.rootAnalysisResult || this.analysisResult || {};
        return root?.environment_analysis || null;
    }
    public environmentObjects(): any[] {
        const objects = this.environmentObjectAnalysis()?.objects;
        return Array.isArray(objects) ? objects : [];
    }
    public cleanOverlayClassOptions(): any[] {
        const labels: Record<string, string> = {
            facility_bed: '시설 침대', hospital_bed: '병원 침대', bed: '침대', wheelchair: '휠체어',
            walker: '보행기', chair: '의자', curtain: '커튼', privacy_curtain: '프라이버시 커튼',
            door: '문', floor_mat: '바닥 매트', floor_mat_candidate: '매트 후보', obstacle: '장애물',
            camera_tripod: '카메라 삼각대', camera: '카메라', iv_pole: '수액 거치대', monitor: '모니터',
            equipment_cart: '장비 카트', medical_cart: '의료 카트', cart: '카트',
            cable_on_floor: '바닥 전선', blocked_path: '통로 막힘',
        };
        return DEFAULT_CLEAN_OVERLAY_CLASSES.map(value => ({ value, label: labels[value] || value }));
    }
    public cleanOverlayClassEnabled(value: string): boolean { return this.cleanOverlayShowClasses.includes(value); }
    public async toggleCleanOverlayClass(value: string) {
        this.cleanOverlayShowClasses = this.cleanOverlayClassEnabled(value)
            ? this.cleanOverlayShowClasses.filter(item => item !== value)
            : [...this.cleanOverlayShowClasses, value];
        try { localStorage.setItem(CLEAN_OVERLAY_CLASSES_STORAGE_KEY, JSON.stringify(this.cleanOverlayShowClasses)); } catch (e) { }
        this.drawUploadDetectionAtCurrentTime();
        await this.service.render();
    }
    private normalizeEnvironmentDisplayClass(item: any): string {
        const value = String(item?.class_name || item?.label || item?.class || '').trim().toLowerCase().replace(/[ -]+/g, '_');
        const aliases: Record<string, string> = {
            mat: 'floor_mat', floor_mat_candidate: 'floor_mat', rug: 'floor_mat', carpet: 'floor_mat',
            bed: 'facility_bed', hospital_bed: 'facility_bed', privacy_curtain: 'curtain',
            tripod: 'camera_tripod', sofa: 'couch',
            cable: 'cable_on_floor', electrical_cord: 'cable_on_floor', blocked_walkway: 'blocked_path',
        };
        return aliases[value] || value;
    }
    private environmentStableId(item: any): string {
        return String(item?.stable_object_id ?? item?.track_id ?? item?.object_id ?? '');
    }
    private resolvedEnvironmentDisplayClass(item: any): string {
        const current = this.normalizeEnvironmentDisplayClass(item);
        const stableId = this.environmentStableId(item);
        if (!stableId || !['floor_mat', 'facility_bed'].includes(current)) return current;
        const previous = this.cleanStableCanonicalClass[stableId];
        if (previous && previous !== current && ['floor_mat', 'facility_bed'].includes(previous)) {
            const confidence = Number(item?.smoothed_confidence ?? item?.confidence ?? 0);
            const stability = this.environmentTemporalStability(item);
            if (confidence < 0.80 || stability < 0.80) return previous;
        }
        if (String(item?.object_state || item?.state || '').toLowerCase() === 'confirmed') {
            this.cleanStableCanonicalClass[stableId] = current;
        }
        return current;
    }
    public environmentClassColor(itemOrClass: any): string {
        const key = typeof itemOrClass === 'string'
            ? this.normalizeEnvironmentDisplayClass({ label: itemOrClass })
            : this.resolvedEnvironmentDisplayClass(itemOrClass);
        return ENVIRONMENT_CLASS_COLORS[key] || '#38bdf8';
    }
    private environmentTemporalStability(item: any): number {
        const direct = Number(item?.stability_score ?? item?.temporal_stability_score ?? item?.detection_continuity_ratio);
        if (Number.isFinite(direct) && direct >= 0) return Math.max(0, Math.min(1, direct));
        const hits = Number(item?.detection_count ?? item?.hit_count ?? item?.consecutive_hits ?? 0);
        const observations = Number(item?.observation_count ?? item?.sampled_frames ?? 5);
        return Math.max(0, Math.min(1, hits / Math.max(1, observations)));
    }
    private isStaticEnvironmentFixture(item: any): boolean {
        return ['facility_bed', 'curtain', 'door', 'air_conditioner', 'cabinet', 'refrigerator', 'sink', 'toilet']
            .includes(this.resolvedEnvironmentDisplayClass(item));
    }
    private environmentDisplayScore(item: any): number {
        const confidence = Number(item?.smoothed_confidence ?? item?.confidence ?? 0);
        const stability = this.environmentTemporalStability(item);
        const temporalBoost = Math.min(0.12, stability * 0.12);
        const staticBoost = this.isStaticEnvironmentFixture(item) && stability >= CLEAN_OVERLAY_CONFIG.stableFixtureMinStability ? 0.05 : 0;
        return Math.max(0, Math.min(1, confidence + temporalBoost + staticBoost));
    }
    private cleanDisplayDecision(item: any): { enabled: boolean; reason: string; opacity: number; score: number } {
        const state = String(item?.object_state || item?.state || '').toLowerCase();
        const confidence = Number(item?.smoothed_confidence ?? item?.confidence ?? 0);
        const stability = this.environmentTemporalStability(item);
        const hits = Number(item?.detection_count ?? item?.hit_count ?? item?.consecutive_hits ?? 0);
        const score = this.environmentDisplayScore(item);
        const realtime = this.inputMode === 'webcam';
        if (state === 'confirmed') {
            const stableFixture = this.isStaticEnvironmentFixture(item)
                && stability >= CLEAN_OVERLAY_CONFIG.stableFixtureMinStability
                && confidence >= CLEAN_OVERLAY_CONFIG.stableFixtureMinConfidence;
            const realtimeConfirmed = realtime && confidence >= 0.25;
            if (confidence >= CLEAN_OVERLAY_CONFIG.confirmedMinConfidence || stableFixture || score >= 0.46 || realtimeConfirmed) {
                return { enabled: true, reason: realtimeConfirmed ? 'realtime_confirmed' : 'visible', opacity: 1, score };
            }
            return { enabled: false, reason: 'low_stability', opacity: 0, score };
        }
        const tentativeMinHits = realtime ? 2 : CLEAN_OVERLAY_CONFIG.repeatedTentativeMinHits;
        const tentativeMinStability = realtime ? 0.45 : CLEAN_OVERLAY_CONFIG.repeatedTentativeMinStability;
        const tentativeMinConfidence = realtime ? 0.28 : 0.35;
        if (state === 'tentative' && hits >= tentativeMinHits
            && stability >= tentativeMinStability && confidence >= tentativeMinConfidence) {
            return { enabled: true, reason: 'repeated_tentative', opacity: realtime ? 0.58 : 0.48, score };
        }
        return { enabled: false, reason: 'low_stability', opacity: 0, score };
    }
    public environmentDisplayEnabled(item: any): boolean {
        if (this.overlayMode === 'DEBUG_RAW') return true;
        if (!['SKELETON_ENVIRONMENT', 'ENVIRONMENT'].includes(this.overlayMode)) return false;
        return this.cleanDisplayDecision(item).enabled;
    }
    private environmentObjectBbox(item: any): number[] {
        const bbox = Array.isArray(item?.bbox) ? item.bbox.map(Number) : [];
        if (bbox.length >= 4 && bbox.slice(0, 4).every(Number.isFinite)) return bbox.slice(0, 4);
        const values = [item?.x1, item?.y1, item?.x2, item?.y2].map(Number);
        return values.every(Number.isFinite) ? values : [];
    }
    private bboxIou(a: number[], b: number[]): number {
        if (a.length < 4 || b.length < 4) return 0;
        const left = Math.max(a[0], b[0]); const top = Math.max(a[1], b[1]);
        const right = Math.min(a[2], b[2]); const bottom = Math.min(a[3], b[3]);
        const intersection = Math.max(0, right - left) * Math.max(0, bottom - top);
        const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
        const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
        return intersection / Math.max(1, areaA + areaB - intersection);
    }
    private bboxContainment(a: number[], b: number[]): number {
        if (a.length < 4 || b.length < 4) return 0;
        const intersection = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]))
            * Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
        const areaA = Math.max(1, (a[2] - a[0]) * (a[3] - a[1]));
        const areaB = Math.max(1, (b[2] - b[0]) * (b[3] - b[1]));
        return intersection / Math.min(areaA, areaB);
    }
    private environmentDisplayObjects(objects: any[], mode: OverlayMode = this.overlayMode): any[] {
        const candidates = (Array.isArray(objects) ? objects : [])
            .filter(item => mode === 'DEBUG_RAW' ? true : this.cleanDisplayDecision(item).enabled)
            .sort((a, b) => this.environmentDisplayScore(b) - this.environmentDisplayScore(a));
        if (mode === 'DEBUG_RAW') return candidates;
        const accepted: any[] = [];
        const stableIds = new Set<string>();
        for (const item of candidates) {
            const stableId = this.environmentStableId(item);
            if (stableId && stableIds.has(stableId)) continue;
            const key = this.resolvedEnvironmentDisplayClass(item);
            const bbox = this.environmentObjectBbox(item);
            const duplicate = accepted.some(existing => this.resolvedEnvironmentDisplayClass(existing) === key
                && this.bboxIou(this.environmentObjectBbox(existing), bbox) >= CLEAN_OVERLAY_CONFIG.duplicateIou);
            if (duplicate) continue;
            const surfaceConflict = accepted.find(existing => {
                const existingKey = this.resolvedEnvironmentDisplayClass(existing);
                if (!['floor_mat', 'facility_bed'].includes(key) || !['floor_mat', 'facility_bed'].includes(existingKey) || key === existingKey) return false;
                const existingBbox = this.environmentObjectBbox(existing);
                const centerDistance = existingBbox.length >= 4 && bbox.length >= 4
                    ? Math.hypot((existingBbox[0] + existingBbox[2] - bbox[0] - bbox[2]) / 2, (existingBbox[1] + existingBbox[3] - bbox[1] - bbox[3]) / 2)
                    : Number.POSITIVE_INFINITY;
                const referenceDiagonal = existingBbox.length >= 4
                    ? Math.max(1, Math.hypot(existingBbox[2] - existingBbox[0], existingBbox[3] - existingBbox[1])) : 1;
                return this.bboxIou(existingBbox, bbox) >= 0.55
                    || (this.bboxContainment(existingBbox, bbox) >= 0.90 && centerDistance / referenceDiagonal <= 0.08);
            });
            if (surfaceConflict) continue;
            accepted.push(item);
            if (stableId) stableIds.add(stableId);
        }
        return accepted;
    }
    public overlayComparisonStats(objects: any[] = this.environmentObjects(), renderedObjects?: any[]): any {
        const source = Array.isArray(objects) ? objects : [];
        const eligible = source.filter(item => this.cleanDisplayDecision(item).enabled);
        const cleanInput = this.environmentDisplayObjects(source, 'SKELETON_ENVIRONMENT');
        const rendered = Array.isArray(renderedObjects) ? renderedObjects : cleanInput;
        const hasMask = (item: any) => {
            const polygons = Array.isArray(item?.smoothed_segments) && item.smoothed_segments.length > 0 ? item.smoothed_segments : item?.segments;
            return Array.isArray(polygons) && polygons.some((polygon: any) => Array.isArray(polygon) && polygon.length >= 3);
        };
        const cleanRendered = this.overlayMode === 'DEBUG_RAW' && Array.isArray(renderedObjects)
            ? cleanInput.filter(hasMask) : rendered;
        return {
            inference_object_count: source.length,
            clean_input_object_count: source.length,
            debug_input_object_count: source.length,
            clean_rendered_object_count: cleanRendered.length,
            hidden_by_min_area: Math.max(0, cleanInput.filter(hasMask).length - cleanRendered.length),
            hidden_by_invalid_mask: cleanInput.filter(item => !hasMask(item)).length,
            hidden_by_duplicate: Math.max(0, eligible.length - cleanInput.length),
            hidden_by_low_stability: source.length - eligible.length,
        };
    }
    private logOverlayComparison(objects: any[], renderedObjects?: any[]) {
        const stats = this.overlayComparisonStats(objects, renderedObjects);
        const signature = JSON.stringify(stats);
        if (signature === this.lastOverlayComparisonSignature) return;
        this.lastOverlayComparisonSignature = signature;
        console.debug('[environment-overlay-comparison]', stats);
    }
    public cleanObjectMaskOpacity(item: any): number { return this.cleanDisplayDecision(item).opacity; }
    public cleanOverlaySummaryItems(): any[] {
        const grouped: Record<string, any> = {};
        for (const item of this.environmentDisplayObjects(this.environmentObjects())) {
            const key = this.resolvedEnvironmentDisplayClass(item);
            const confidence = Number(item?.smoothed_confidence ?? item?.confidence ?? 0);
            if (!grouped[key]) grouped[key] = { key, label: item?.label_ko || item?.label || key, confidence, count: 0, color: this.environmentClassColor(key), risk: this.isEnvironmentHazard(item) };
            grouped[key].count += 1;
            grouped[key].confidence = Math.max(grouped[key].confidence, confidence);
            grouped[key].risk = grouped[key].risk || this.isEnvironmentHazard(item);
        }
        return Object.values(grouped).sort((a: any, b: any) => Number(b.risk) - Number(a.risk) || b.confidence - a.confidence);
    }
    public cleanOverlaySummaryVisibleItems(): any[] { return this.cleanOverlaySummaryItems().slice(0, CLEAN_OVERLAY_CONFIG.maxSummaryItems); }
    public cleanOverlaySummaryHiddenCount(): number { return Math.max(0, this.cleanOverlaySummaryItems().length - CLEAN_OVERLAY_CONFIG.maxSummaryItems); }
    public showCleanOverlaySummary(): boolean { return this.overlayMode === 'SKELETON_ENVIRONMENT' && this.cleanOverlaySummaryItems().length > 0; }
    public environmentLegendItems(): any[] {
        const source = this.latestCleanRenderedObjects.length > 0 ? this.latestCleanRenderedObjects : this.environmentDisplayObjects(this.environmentObjects(), 'SKELETON_ENVIRONMENT');
        const grouped: Record<string, any> = {};
        for (const item of source) {
            const key = this.resolvedEnvironmentDisplayClass(item);
            if (!grouped[key]) grouped[key] = { key, label: this.environmentShortLabel(item), color: this.environmentClassColor(item) };
        }
        return Object.values(grouped);
    }
    public environmentRiskObjectCount(): number { return this.cleanOverlaySummaryItems().filter(item => item.risk).length; }
    private isEnvironmentHazard(item: any): boolean {
        return ['obstacle', 'cable_on_floor', 'blocked_path'].includes(this.resolvedEnvironmentDisplayClass(item)) || Number(item?.risk_score || 0) >= 0.7;
    }
    public environmentObjectConfidence(item: any): string {
        return `${Math.round(Number(item?.confidence || 0) * 100)}%`;
    }
    public environmentObjectStatusText(): string {
        const analysis = this.environmentObjectAnalysis();
        if (!analysis) return '';
        if (analysis.status === 'unavailable') return `사물 인식 실행 실패${analysis.error ? ` · ${analysis.error}` : ''}`;
        if (analysis.status === 'no_frames') return '사물 인식용 프레임을 읽지 못했습니다.';
        const temporal = analysis?.temporal_stabilization?.enabled ? ' · ByteTrack · 객체별 메모리' : '';
        return `${analysis.model || 'YOLO'} · ${analysis.sampled_frames || 0}프레임 확인 · ${this.environmentObjects().length}종 인식${temporal}`;
    }
    public environmentMaskMemoryStatusText(): string {
        return this.environmentObjectAnalysis()?.video_memory_refinement_ready
            ? 'SAM2 비디오 메모리 적용'
            : 'SAM2 이미지 대체 경로';
    }
    public environmentObjectStateText(item: any): string {
        return item?.object_state === 'confirmed' ? '확정' : item?.object_state === 'tentative' ? '검토 중' : '상태 확인';
    }
    public environmentLimitations(): string[] {
        const values = this.environmentObjectAnalysis()?.limitations;
        return Array.isArray(values) ? values : [];
    }
    public currentEngineLabel(): string {
        const key = this.analysisResult?.runtime_key || '';
        const selectedVersion = this.analysisResult?.selected_model_version?.label || '';
        if (selectedVersion) return selectedVersion;
        const runtimeLabel = this.prototypeInfo?.analysis_engine_summary?.current_runtime?.label || '';
        if (this.currentModelStatusLabel() === 'ready' && (!key || key === 'heuristic-fallback')) {
            return runtimeLabel || 'RF-Dual (Fall+Posture)';
        }
        if (key === 'person-feature-runtime') return 'XGBoost v2 (사람 추적)';
        if (key === 'rf-pipeline-runtime') return 'RF 보조 (RandomForest)';
        if (key === 'rf-dual') return 'RF-Dual (Fall+Posture)';
        if (key === 'rf-dual-person') return 'RF-Dual 사람별 분석';
        if (key === 'heuristic-fallback') return '휴리스틱 Fallback';
        if (runtimeLabel) return runtimeLabel;
        if (this.selectedModelType === 'rf-dual') {
            return this.prototypeInfo?.trained_model?.runtime_ready ? 'RF-Dual (Fall+Posture)' : '모델 없음 (Fallback)';
        }
        return this.selectedAnalysisModelLabel() || key || '알 수 없음';
    }
    public runtimeFilterItems(result?: any): any[] {
        const res = result || this.analysisResult || {};
        const items = res?.runtime_filters?.items;
        if (Array.isArray(items)) return items;
        const legacy = res?.static_object_filter || res?.runtime_inference?.static_object_filter;
        if (!legacy?.enabled) return [];
        return [{
            family: legacy.family || 'static-object-filter',
            label: '정지 사물 필터 v1',
            model_id: legacy.model_id || '',
            version: legacy.version || '',
            applied: legacy.applied === true,
            suppressed: legacy.suppressed === true,
        }];
    }
    public runtimeFilterSummaryText(result?: any): string {
        const items = this.runtimeFilterItems(result);
        if (items.length === 0) return '';
        return items.map((item: any) => {
            const version = String(item?.version || '').trim();
            const label = String(item?.label || '운영 필터').trim();
            const name = version && !label.includes(version) ? `${label} ${version}` : label;
            if (item?.applied !== true) return `적용 필터: ${name} · 적용 실패`;
            const status = String(item?.status_label || '').trim() || (item?.suppressed ? '적용 · 제외' : '적용 · 유지');
            return `적용 필터: ${name} · ${status}`;
        }).join(' / ');
    }
    public runtimeFilterSummaryClass(result?: any): string {
        const items = this.runtimeFilterItems(result);
        if (items.some((item: any) => item?.applied !== true)) return 'border-rose-200 bg-rose-50 text-rose-700';
        if (items.some((item: any) => item?.suppressed === true)) return 'border-zinc-300 bg-zinc-100 text-zinc-700';
        return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }
    public currentModelStatusLabel(): string {
        const trainedReady = this.prototypeInfo?.trained_model?.runtime_ready === true;
        const runtimeReady = this.prototypeInfo?.analysis_engine_summary?.current_runtime?.key === 'rf-dual-runtime';
        const baseReady = this.prototypeInfo?.baseline_model_ready === true && this.prototypeInfo?.behavior_model_ready === true;
        if (trainedReady || runtimeReady || baseReady) return 'ready';
        if (this.analysisResult?.runtime_key === 'heuristic-fallback') return 'fallback';
        return 'missing';
    }
    public currentModelStatusText(): string {
        const status = this.currentModelStatusLabel();
        if (status === 'ready') return '연결됨';
        if (status === 'fallback') return 'fallback 동작 중';
        return '운영 모델 파일 없음';
    }
    public currentModelWeightsPath(): string {
        return this.prototypeInfo?.trained_model?.weights_path || '';
    }
    public runtimeRfModelPath(): string {
        return this.analysisResult?.model_runtime?.rf_model_path || '';
    }
    public runtimePostureClassifierPath(): string {
        return this.analysisResult?.model_runtime?.posture_classifier || '';
    }
    public modelSpeedHint(): string {
        const key = this.selectedModelType || '';
        if (key.startsWith('registry:')) return '선택한 모델 버전으로 RF-Dual 분석';
        if (this.inputMode === 'webcam') {
            if (key === 'person-feature') return '~8초/청크';
            if (key === 'rf-dual') return '4초 청크 / 누락 없는 순차 큐';
            return '~3초/청크';
        }
        if (key === 'person-feature') return '5~15초';
        if (key === 'rf-dual') return '단일 분석 + 실시간은 4초 청크';
        return '3~8초';
    }
    public getRiskScoreFormula(): string {
        const key = this.analysisResult?.runtime_key || '';
        const guide = this.analysisResult?.risk_score_guide;
        if (guide?.formula) return guide.formula;
        if (key === 'rf-dual' || key === 'rf-dual-person') return 'RF predict_proba → motion guard → XG-Posture 5-class';
        if (key === 'person-feature-runtime') return 'max(window_probs) × fall_ratio';
        return 'fall_probability × motion_factor';
    }
    public sortedAnalysisBasis(): any[] { const basis = this.analysisResult?.analysis_basis || []; return [...basis].sort((a: any, b: any) => (a.contribution_rank || 99) - (b.contribution_rank || 99)); }
    public basisCardClass(item: any): string { const lvl = item.level || 'normal'; if (lvl === 'danger' || lvl === 'high') return 'border-rose-200 bg-rose-50'; if (lvl === 'warning' || lvl === 'medium') return 'border-amber-200 bg-amber-50'; return 'border-zinc-200 bg-zinc-50'; }
    public facialStateAux(result?: any): any {
        const res = result || this.analysisResult || {};
        return res?.facial_state || res?.runtime_inference?.facial_state || null;
    }
    public hasFacialStateAux(result?: any): boolean {
        if (this.isObjectResult(result)) return false;
        const face = this.facialStateAux(result);
        return Boolean(face);
    }
    public facialStateAuxText(result?: any): string {
        const face = this.facialStateAux(result);
        if (!face) return '';
        const actualFaceRatio = Number(face.actual_face_ratio || 0);
        const poseFallbackRatio = Number(face.pose_head_roi_ratio || 0);
        if (face.reason === 'face_not_detected' || face.face_visible === false || (!face.face_detected && poseFallbackRatio >= 0.5)) {
            return `얼굴/표정 미검출 · 실제얼굴 ${Math.round(actualFaceRatio * 100)}% · pose대체 ${Math.round(poseFallbackRatio * 100)}% · 감정 점수 미사용`;
        }
        const emotion = face.emotion_display_label || face.emotion_top_label || this.facialEmotionLabel(face.emotion_top);
        const emotionConfidence = Math.round(Number(face.emotion_confidence || 0) * 100);
        const emotionMargin = Math.round(Number(face.emotion_margin || 0) * 100);
        const emotionConsistency = Math.round(Number(face.emotion_consistency || 0) * 100);
        const qualityMap: Record<string, string> = { high: '높음', medium: '보통', low: '낮음', unavailable: '불가' };
        const quality = qualityMap[String(face.emotion_quality || 'unavailable')] || '낮음';
        const rawDistress = Math.round(Number(face.distress_score ?? 0) * 100);
        const trustedDistress = Math.round(Number(face.trusted_distress_score ?? face.distress_score ?? 0) * 100);
        const faceRatio = Math.round(Number(face.actual_face_ratio || 0) * 100);
        const driver = face.driver_state_top_label || this.facialDriverStateLabel(face.driver_state_top);
        const driverConfidence = Math.round(Number(face.driver_state_confidence || 0) * 100);
        const driverRisk = Math.round(Number(face.driver_state_risk || 0) * 100);
        const modelMode = String(face.emotion_output_mode || '') === 'distress_signal' ? '불편신호 모델' : '표정분류 모델';
        const suffix = face.emotion_top && face.emotion_top !== 'unavailable'
            ? `${modelMode} · ${emotion} · 모델확신 ${emotionConfidence}% · margin ${emotionMargin}% · 일치 ${emotionConsistency}% · 신뢰 ${quality} · 불편 raw ${rawDistress}% / 신뢰보정 ${trustedDistress}% · 실제얼굴 ${faceRatio}%`
            : '';
        const driverSuffix = face.driver_state_top && face.driver_state_top !== 'unavailable'
            ? `상태 ${driver} ${driverConfidence}% · 주의저하 ${driverRisk}%${face.driver_state_reliable ? '' : ' · 저신뢰'}`
            : '';
        const combined = [suffix, driverSuffix].filter(Boolean).join(' · ');
        if (face.applied) return `${combined || face.label || '표정 보조'} · +${Math.round(Number(face.support_score || 0) * 100)}%p 반영`;
        if (face.available) return `${combined || face.label || '표정 분석'} · 점수 미반영`;
        if (face.reason === 'face_not_detected') return '얼굴 미검출 · 점수 미반영';
        if (face.reason === 'body_not_suspected') return '낙상 의심 점수 미만 · 표시 대기';
        return face.description || '표정 보조 분석 불가';
    }
    public facialEmotionLabel(code: string): string {
        const labels: Record<string, string> = {
            neutral: '중립',
            happiness: '기쁨',
            embarrassed: '당황',
            surprise: '놀람',
            anxiety: '불안',
            distress: '불편/고통',
            non_distress: '비불편',
            hurt: '상처',
            sadness: '슬픔',
            anger: '분노',
            disgust: '불쾌',
            fear: '공포',
            contempt: '경멸',
            unavailable: '분석 불가',
        };
        return labels[code || ''] || code || '분석 불가';
    }
    public facialDriverStateLabel(code: string): string {
        const labels: Record<string, string> = {
            normal_focus: '정상/집중',
            drowsy: '졸림',
            yawn: '하품',
            phone_call: '통화',
            smoking: '흡연',
            unavailable: '상태 분석 불가',
        };
        return labels[code || ''] || code || '상태 분석 불가';
    }
    public facialStateLabel(result?: any): string {
        if (this.isObjectResult(result)) return '사물 · 분석 제외';
        const face = this.facialStateAux(result);
        if (!face) return '대기';
        if (face.reason === 'face_not_detected' || face.face_visible === false || (!face.face_detected && Number(face.pose_head_roi_ratio || 0) >= 0.5)) return '얼굴/표정 미검출';
        if (face.applied) return face.label || '표정 보조 반영';
        if (face.available) {
            if (face.label) return face.label;
            if (face.emotion_top && face.emotion_top !== 'unavailable') return face.emotion_display_label || face.emotion_top_label || this.facialEmotionLabel(face.emotion_top);
            if (face.driver_state_top && face.driver_state_top !== 'unavailable') return face.driver_state_top_label || this.facialDriverStateLabel(face.driver_state_top);
            return face.label || '표정 분석';
        }
        if (face.reason === 'face_not_detected') return '얼굴 미검출';
        if (face.reason === 'body_not_suspected') return '표시 대기';
        if (face.reason === 'no_frames') return '프레임 없음';
        return '분석 불가';
    }
    public facialStatePercent(result?: any): number {
        const face = this.facialStateAux(result);
        if (!face) return 0;
        if (face.reason === 'face_not_detected' || face.face_visible === false || (!face.face_detected && Number(face.pose_head_roi_ratio || 0) >= 0.5)) return 0;
        if (face.available && face.emotion_reliable && Number(face.emotion_confidence || 0) > 0) return Math.round(Number(face.emotion_confidence || 0) * 100);
        if (face.available && Number.isFinite(Number(face.actual_face_ratio))) {
            return Math.round(Number(face.facial_confidence || 0) * Number(face.actual_face_ratio || 0) * 100);
        }
        if (face.available && Number(face.facial_confidence || 0) > 0) return Math.round(Number(face.facial_confidence || 0) * 100);
        if (face.available && Number(face.driver_state_confidence || 0) > 0) return Math.round(Number(face.driver_state_confidence || 0) * 100);
        if (face.reason === 'face_not_detected') return 0;
        return Math.round(Number(face.support_score || 0) * 100);
    }
    public facialEvidenceLines(result?: any): string[] {
        const face = this.facialStateAux(result);
        const lines = Array.isArray(face?.evidence_summary) ? face.evidence_summary : [];
        return lines.map((line: any) => String(line || '')).filter(Boolean).slice(0, 5);
    }
    public facialStateItems(result?: any): any[] {
        const face = this.facialStateAux(result);
        if (!face) return [];
        const pct = (value: any) => Math.max(0, Math.min(100, Math.round(Number(value || 0) * 100)));
        if (face.reason === 'face_not_detected' || face.face_visible === false || (!face.face_detected && Number(face.pose_head_roi_ratio || 0) >= 0.5)) {
            return [
                { key: 'actual_face', label: '실제얼굴', percent: pct(face.actual_face_ratio), active: false, color: '#94a3b8' },
                { key: 'pose_fallback', label: 'pose대체', percent: pct(face.pose_head_roi_ratio), active: Number(face.pose_head_roi_ratio || 0) > 0, color: '#64748b' },
                { key: 'emotion_skip', label: '감정미사용', percent: 100, active: false, color: '#71717a' },
            ];
        }
        const emotionColors: Record<string, string> = {
            neutral: '#64748b',
            happiness: '#14b8a6',
            embarrassed: '#f59e0b',
            surprise: '#f59e0b',
            anxiety: '#a855f7',
            distress: '#f43f5e',
            non_distress: '#64748b',
            hurt: '#e11d48',
            sadness: '#6366f1',
            anger: '#ef4444',
            disgust: '#84cc16',
            fear: '#a855f7',
            contempt: '#f97316',
        };
        const probs = face.emotion_probs || {};
        const driverProbs = face.driver_state_probs || {};
        const showEmotionCandidates = Boolean(face.emotion_reliable) || Number(face.support_score || 0) > 0;
        const emotionItems = (showEmotionCandidates ? Object.keys(probs) : [])
            .map((key: string) => ({
                key,
                label: this.facialEmotionLabel(key),
                percent: pct(probs[key]),
                active: key === face.emotion_top && Boolean(face.emotion_reliable),
                color: emotionColors[key] || '#71717a',
            }))
            .sort((a: any, b: any) => b.percent - a.percent)
            .slice(0, 3);
        if (emotionItems.length > 0) {
            emotionItems.push({
                key: 'quality',
                label: '모델신뢰',
                percent: face.emotion_quality === 'high' ? 90 : (face.emotion_quality === 'medium' ? 62 : 28),
                active: Boolean(face.emotion_reliable),
                color: face.emotion_reliable ? '#0ea5e9' : '#94a3b8',
            });
            emotionItems.push({
                key: 'distress',
                label: '신뢰보정 불편',
                percent: pct(face.trusted_distress_score ?? face.distress_score),
                active: Number(face.trusted_distress_score ?? face.distress_score ?? 0) >= 0.30,
                color: '#f43f5e',
            });
            if (Object.keys(driverProbs).length > 0) {
                emotionItems.push({
                    key: 'driver_state',
                    label: `상태 ${face.driver_state_top_label || this.facialDriverStateLabel(face.driver_state_top)}`,
                    percent: pct(face.driver_state_risk),
                    active: Boolean(face.driver_state_reliable) && Number(face.driver_state_risk || 0) >= 0.35,
                    color: '#0ea5e9',
                });
            }
            emotionItems.push({
                key: 'face',
                label: '실제얼굴',
                percent: pct(face.actual_face_ratio ?? face.facial_confidence),
                active: Number(face.actual_face_ratio || 0) >= 0.50,
                color: '#22d3ee',
            });
            return emotionItems;
        }
        const items: any[] = [
            { key: 'face', label: '얼굴', percent: face.face_detected ? pct(face.facial_confidence) : 0, active: Boolean(face.face_detected), color: '#22d3ee' },
            { key: 'calm', label: '평온/차분', percent: Number(face.support_score || 0) > 0 ? 0 : 100, active: Number(face.support_score || 0) <= 0, color: '#14b8a6' },
            { key: 'distress', label: '신뢰보정 불편', percent: pct(face.support_score), active: Number(face.support_score || 0) > 0, color: '#f59e0b' },
            { key: 'driver_state', label: `상태 ${face.driver_state_top_label || this.facialDriverStateLabel(face.driver_state_top)}`, percent: pct(face.driver_state_risk), active: Number(face.driver_state_risk || 0) >= 0.35, color: '#0ea5e9' },
            { key: 'eyes', label: '눈감김', percent: pct(face.eye_closed_ratio), active: Boolean(face.eye_closed), color: '#8b5cf6' },
            { key: 'mouth', label: '입/긴장', percent: Math.max(pct(face.mouth_open_ratio), pct(face.upper_tension_ratio)), active: Number(face.mouth_open_ratio || 0) > 0 || Number(face.upper_tension_ratio || 0) > 0, color: '#f43f5e' },
        ];
        if (!face.available && face.reason !== 'face_not_detected') {
            return [{ key: 'status', label: this.facialStateLabel(result), percent: 100, active: false, color: '#71717a' }];
        }
        return items;
    }
    public facialStateAuxClass(result?: any): string {
        const face = this.facialStateAux(result);
        if (face?.applied) return 'border-amber-200 bg-amber-50 text-amber-800';
        if (face?.available && Number(face?.support_score || 0) > 0) return 'border-sky-200 bg-sky-50 text-sky-800';
        return 'border-zinc-200 bg-zinc-50 text-zinc-600';
    }
    public isDualModeResult(result?: any): boolean { const res = result || this.analysisResult || {}; return ['rf-dual', 'rf-dual-person'].includes(res?.runtime_key || ''); }
    public rawPostureProbs(result?: any): Record<string, number> {
        const res = result || this.analysisResult || {};
        return res?.posture_candidate_probs || res?.runtime_inference?.posture_candidate_probs || res?.posture_raw_probs || res?.runtime_inference?.posture_raw_probs || res?.posture_probs || res?.runtime_inference?.posture_probs || {};
    }
    public isObjectResult(result?: any): boolean {
        const res = result || this.analysisResult || {};
        return res?.entity_classification?.entity_type === 'object' || ['object_filtered', 'static_object_filtered'].includes(res?.decision_state || '');
    }
    public isFinalNonFall(result?: any): boolean { const res = result || this.analysisResult || {}; return Boolean(res) && res?.fall_detected === false; }
    public topNonFallPostureCode(result?: any): string {
        const res = result || this.analysisResult || {};
        const direct = res?.posture_label || res?.runtime_inference?.posture_label || '';
        if (direct && direct !== 'fall' && direct !== 'unknown') return direct;
        const probs = this.displayPostureProbs(res);
        const entries = Object.entries(probs || {});
        if (entries.length === 0) return this.isFinalNonFall(res) ? 'stand' : (direct || 'unknown');
        entries.sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
        return entries[0][0] || (this.isFinalNonFall(res) ? 'stand' : (direct || 'unknown'));
    }
    public displayPostureProbs(result?: any): Record<string, number> {
        const res = result || this.analysisResult || {};
        const direct = res?.posture_probs || res?.runtime_inference?.posture_probs || {};
        const directKeys = Object.keys(direct || {});
        const raw = this.rawPostureProbs(res);
        const positiveOnly = (source: Record<string, number>) => {
            const out: Record<string, number> = {};
            for (const [key, value] of Object.entries(source || {})) {
                if (key === 'unknown') continue;
                const num = Number(value || 0);
                if (Number.isFinite(num) && num > 0.0001) out[key] = num;
            }
            return out;
        };
        const normalizeWithoutFall = (source: Record<string, number>) => {
            const nonFallEntries = Object.entries(source || {}).filter(([key, value]) => key !== 'fall' && key !== 'unknown' && Number(value || 0) > 0.0001);
            const total = nonFallEntries.reduce((acc, [, value]) => acc + Number(value || 0), 0);
            if (total <= 0) return {} as Record<string, number>;
            const normalized: Record<string, number> = {};
            for (const [key, value] of nonFallEntries) normalized[key] = Number(value || 0) / total;
            return normalized;
        };
        if (directKeys.length > 0) {
            if (!this.isFinalNonFall(res)) return direct;
            if (directKeys.includes('fall')) {
                const normalized = normalizeWithoutFall(raw && Object.keys(raw).length > 0 ? raw : direct);
                return Object.keys(normalized).length > 0 ? normalized : direct;
            }
            const directPositive = positiveOnly(direct);
            if (Object.keys(directPositive).length > 0) return directPositive;
            const rawNormalized = normalizeWithoutFall(raw);
            if (Object.keys(rawNormalized).length > 0) return rawNormalized;
            return directPositive;
        }
        if (this.isFinalNonFall(res)) {
            const normalized = normalizeWithoutFall(raw);
            if (Object.keys(normalized).length > 0) return normalized;
        }
        return positiveOnly(raw);
    }
    public hasPostureData(result?: any): boolean {
        if (this.isObjectResult(result)) return false;
        return Object.values(this.displayPostureProbs(result)).some((v: any) => Number(v || 0) > 0.0001);
    }
    public postureLabel(result?: any): string {
        const res = result || this.analysisResult || {};
        if (this.isObjectResult(res)) return '사물 · 행동 분석 제외';
        const direct = res?.posture_label || res?.runtime_inference?.posture_label || '';
        let code = direct;
        if (!code || (this.isFinalNonFall(res) && (code === 'fall' || code === 'unknown'))) code = this.topNonFallPostureCode(res);
        return this.POSTURE_LABEL_MAP[code] || this.POSTURE_LABEL_MAP['unknown'];
    }
    public postureDisplayCaption(): string { return this.isDualModeResult() ? '행동' : '자세'; }
    private postureDiagnostics(result?: any): any {
        const res = result || this.analysisResult || {};
        return res?.runtime_inference?.posture_diagnostics || {};
    }
    public postureDiagnosticText(result?: any): string {
        const res = result || this.analysisResult || {};
        const diag = this.postureDiagnostics(res);
        const reason = String(diag?.reason || '');
        const postureCode = res?.posture_label || res?.runtime_inference?.posture_label || '';
        if (!reason && postureCode !== 'unknown') return '';
        const pct = (value: any) => `${Math.round(Number(value || 0) * 100)}%`;
        if (diag?.description) {
            const bits = [];
            if (diag.avg_conf != null) bits.push(`포즈 ${pct(diag.avg_conf)}`);
            if (diag.lower_body_visibility != null) bits.push(`하반신 ${pct(diag.lower_body_visibility)}`);
            return `${diag.description}${bits.length ? ` (${bits.join(', ')})` : ''}`;
        }
        if (reason === 'low_pose_confidence') return `포즈 신뢰도(${pct(diag.avg_conf)}) 또는 하반신 검출률(${pct(diag.lower_body_visibility)})이 낮아 행동을 확정하지 않았습니다.`;
        if (reason === 'lie_evidence_too_weak') return `원 모델은 눕기에 기울었지만 전신 가로비, 직립 억제, 하반신 검출 조건이 부족해 미확인으로 표시했습니다.`;
        if (reason === 'low_margin') return `1순위와 2순위 행동 확률 차이가 작아 행동을 확정하지 않았습니다.`;
        if (reason === 'no_windows') return `행동분류에 사용할 유효 포즈 구간을 만들지 못했습니다.`;
        if (postureCode === 'unknown') return `행동분류 신뢰도가 낮아 실시간 기본 행동으로 추정 표시했습니다.`;
        return '';
    }
    public postureDisplayHint(): string {
        const diagnostic = this.postureDiagnosticText();
        if (diagnostic) return diagnostic;
        const raw = this.rawPostureProbs();
        const rawFall = Number(raw?.['fall'] || 0);
        if (this.isFinalNonFall() && rawFall > 0) return `raw 낙상 신호 ${Math.round(rawFall * 100)}%는 진단용 값이며, 표시는 비낙상 5-class 기준입니다.`;
        return '';
    }
    public decisionStateCode(result?: any): string {
        const res = result || this.analysisResult || {};
        return String(res?.final_event_state || res?.decision_state || res?.runtime_inference?.decision_state || '').toLowerCase();
    }
    public fallEventShadow(result?: any): any {
        const res = result || this.analysisResult || {};
        return res?.fall_event_shadow || res?.runtime_inference?.fall_event_shadow || null;
    }
    public userDecisionStateCode(result?: any): string {
        return this.decisionStateCode(result);
    }
    public decisionStateMeta(result?: any): any {
        const state = this.decisionStateCode(result);
        return this.DECISION_STATE_META[state] || { label: '판단 확인 필요', color: 'text-amber-700', icon: '❓', badgeCls: 'bg-amber-50 text-amber-900 border-amber-300' };
    }
    public decisionStageTone(result?: any): 'normal' | 'review' | 'suspected' | 'confirmed' | 'excluded' | 'posture' {
        const state = this.decisionStateCode(result);
        if (state === 'fall_confirmed') return 'confirmed';
        if (state === 'fall_suspected') return 'suspected';
        if (['uncertain', 'insufficient_data', 'insufficient_detection', 'near_fall_recovered'].includes(state)) return 'review';
        if (['object_filtered', 'static_object_filtered'].includes(state)) return 'excluded';
        if (state === 'posture_only') return 'posture';
        return 'normal';
    }
    public decisionRiskBarClass(result?: any): string {
        const tone = this.decisionStageTone(result);
        if (tone === 'confirmed') return 'bg-rose-500';
        if (tone === 'suspected') return 'bg-orange-500';
        if (tone === 'review') return 'bg-amber-400';
        if (tone === 'excluded') return 'bg-zinc-400';
        if (tone === 'posture') return 'bg-sky-500';
        return 'bg-emerald-500';
    }
    public realtimeOverlayCardClass(): string {
        const tone = this.decisionStageTone(this.analysisResult);
        if (tone === 'confirmed') return 'bg-rose-900/80 ring-2 ring-rose-400';
        if (tone === 'suspected' || this.realtimeOverlayLevel === 'high') return 'bg-orange-900/80 ring-2 ring-orange-400';
        return 'bg-black/70';
    }
    public realtimeOverlayScoreClass(): string {
        const tone = this.decisionStageTone(this.analysisResult);
        if (tone === 'confirmed') return 'text-rose-400';
        if (tone === 'suspected' || this.realtimeOverlayLevel === 'high') return 'text-orange-400';
        if (tone === 'review' || this.realtimeOverlayLevel === 'medium') return 'text-amber-400';
        return 'text-emerald-400';
    }
    public realtimeOverlayBarClass(): string {
        const tone = this.decisionStageTone(this.analysisResult);
        if (tone === 'confirmed') return 'bg-rose-400';
        if (tone === 'suspected' || this.realtimeOverlayLevel === 'high') return 'bg-orange-400';
        if (tone === 'review' || this.realtimeOverlayLevel === 'medium') return 'bg-amber-400';
        return 'bg-emerald-400';
    }
    public isAmbiguousDecision(result?: any): boolean { const state = this.decisionStateCode(result); return ['uncertain', 'fall_suspected', 'near_fall_recovered', 'insufficient_data', 'insufficient_detection'].includes(state); }
    public postureItems(result?: any): any[] {
        const res = result || this.analysisResult || {};
        const probs = this.displayPostureProbs(res);
        const iconMap: Record<string, string> = { stand: '🧍', walk: '🚶', run: '🏃', sit: '🪑', lie: '🛌', fall: '⚠️' };
        const currentCode = this.topNonFallPostureCode(res);
        return Object.entries(probs).filter(([key, val]) => key !== 'unknown' && Number(val || 0) > 0.0001).map(([key, val]) => ({ code: key, key, label: this.POSTURE_LABEL_MAP[key] || key, icon: iconMap[key] || '❓', prob: Number(val || 0), percent: Math.round(Number(val || 0) * 100), score: Math.round(Number(val || 0) * 100), active: key === currentCode })).sort((a, b) => b.prob - a.prob);
    }
    public getExplainItems(): string[] { return this.analysisResult?.explain || []; }
    public getSuppressedBy(): string[] { return this.analysisResult?.runtime_inference?.suppressed_by || []; }
    public finalDecisionExplanation(result?: any): string {
        const res = result || this.analysisResult || {};
        const state = this.decisionStateCode(res);
        const postureCode = String(res?.posture_label || res?.runtime_inference?.posture_label || '');
        const posture = this.POSTURE_LABEL_MAP[postureCode] || '';
        if (!state) return '세부 판단 근거를 확인할 수 없습니다.';
        if (state === 'safe') return '낙상으로 판단할 만한 움직임이 확인되지 않았습니다.';
        if (state === 'posture_only' && posture) return `현재 자세는 ${posture}이며, 낙상 이벤트 여부는 전체 시간 구간에서 별도로 평가했습니다.`;
        if (state === 'fall_suspected') return '빠른 중심 하강 또는 자세 붕괴가 감지되어 현장 확인이 필요합니다.';
        if (state === 'fall_confirmed') return '낙상 가능성이 높은 움직임이 감지되었습니다. 대상자의 상태를 즉시 확인하세요.';
        if (state === 'near_fall_recovered') return '균형 상실 후보 뒤 정상 자세로 회복했지만 안전 확인이 필요합니다.';
        if (state === 'uncertain' || state === 'insufficient_data' || state === 'insufficient_detection') return '분석 결과가 불확실합니다. 영상을 직접 확인하세요.';
        if (state === 'object_filtered' || state === 'static_object_filtered') return '사람이 아닌 대상으로 판단되어 낙상 분석에서 제외되었습니다.';
        return '세부 판단 근거를 확인할 수 없습니다.';
    }
    public analysisQualityText(result?: any): string {
        const res = result || this.analysisResult || {};
        const warning = res?.runtime_warning;
        if (warning?.description) return String(warning.description);
        const state = this.decisionStateCode(res);
        if (state === 'insufficient_data' || state === 'insufficient_detection') return '사람 검출 프레임이 부족해 비낙상으로 확정하지 않았습니다.';
        const suppressed = res?.runtime_inference?.suppressed_by || [];
        if (suppressed.includes('occlusion_fall_risk_hold')) return '가림으로 인해 낙상 판단을 보류했습니다.';
        const face = this.facialStateAux(res);
        if (face?.reason === 'face_not_detected') return '얼굴 미검출로 표정 보조 신호를 사용하지 않았습니다.';
        return res?.model_runtime ? '분석이 완료되었습니다.' : '분석 품질 정보를 확인할 수 없습니다.';
    }
    public aiEvidenceItems(result?: any): Array<{ icon: string; label: string; value: string }> {
        const res = result || this.analysisResult || {};
        const ri = res?.runtime_inference || {};
        const items: Array<{ icon: string; label: string; value: string }> = [];
        const eventEvidence = res?.postprocess_output?.evidence || {};
        const eventFlags = eventEvidence?.flags || {};
        if (eventFlags.rapid_center_drop) items.push({ icon: '↓', label: '급격한 중심 하강', value: `최대 ${Number(eventEvidence.center_drop_ratio || 0).toFixed(5)}` });
        if (eventFlags.torso_tilt_increase) items.push({ icon: '↘', label: '몸통 기울기 변화', value: `최대 ${Number(eventEvidence.torso_angle_change_deg || 0).toFixed(1)}°` });
        if (eventFlags.abrupt_aspect_change) items.push({ icon: '↔', label: 'bbox 형태 변화', value: `종횡비 변화 ${Number(eventEvidence.bbox_aspect_change || 0).toFixed(5)}` });
        if (eventFlags.floor_or_mat_approach) items.push({ icon: '▾', label: '바닥·매트 접근', value: '접근 특징 확인' });
        if (Number(eventEvidence.combined_shadow_risk_score || 0) > 0) items.push({ icon: '◆', label: 'Shadow 짧은 구간 위험', value: `${Math.round(Number(eventEvidence.combined_shadow_risk_score || 0) * 100)}%` });
        if (items.length > 0) return items.slice(0, 5);
        const postureCode = String(res?.posture_label || ri?.posture_label || '');
        if (postureCode && postureCode !== 'unknown') items.push({ icon: '🧍', label: '현재 자세', value: this.POSTURE_LABEL_MAP[postureCode] || postureCode });
        const transition = String(res?.chunk_analysis?.summary?.behavior_transition_line || this.uploadChunkSummary?.behavior_transition_line || '').replace(/^행동 변화:\s*/, '');
        if (transition) items.push({ icon: '↪', label: '행동 변화', value: transition });
        const shadow = this.fallEventShadow(res);
        const shadowReasons = Array.isArray(shadow?.fall_reason_codes) ? shadow.fall_reason_codes : [];
        const collapse = ri?.rapid_collapse_transition;
        if (shadow?.fall_event_candidate_detected) items.push({ icon: shadowReasons.includes('RAPID_VERTICAL_DROP') ? '⚠️' : '↘', label: '바닥 방향 하강', value: shadowReasons.includes('RAPID_VERTICAL_DROP') ? '빠른 중심 하강 후보' : '하강 후보 유지' });
        else if (collapse && typeof collapse === 'object' && collapse.triggered != null) items.push({ icon: collapse.triggered ? '⚠️' : '✓', label: '급격한 중심 하강', value: collapse.triggered ? '확인됨' : '없음' });
        const guard = ri?.motion_guard;
        if (guard && typeof guard === 'object') items.push({ icon: guard.applied ? '🛡' : '✓', label: '실제 움직임 확인', value: guard.applied ? '움직임 근거 부족' : '정상' });
        const suppressed = Array.isArray(ri?.suppressed_by) ? ri.suppressed_by : [];
        if (suppressed.includes('occlusion_fall_risk_hold')) items.push({ icon: '👁', label: '하체 가림', value: '가림으로 인해 낙상 판단을 보류함' });
        const entity = res?.entity_classification || {};
        if (entity?.entity_type === 'person') items.push({ icon: '👤', label: '사람 판정 필터', value: '사람으로 확인' });
        else if (entity?.entity_type === 'object' || ['object_filtered', 'static_object_filtered'].includes(this.decisionStateCode(res))) items.push({ icon: '⏸', label: '사람 판정 필터', value: '사람이 아닌 대상으로 분석 제외' });
        const face = this.facialStateAux(res);
        if (face?.reason === 'face_not_detected') items.push({ icon: '🙂', label: '표정 분석', value: '얼굴 미검출로 표정 분석 미사용' });
        else if (face?.available || face?.face_detected) items.push({ icon: '🙂', label: '표정 분석', value: face.applied ? `보조 신호 반영 ${Math.round(Number(face.support_score || 0) * 100)}%p` : '보조 신호로 분석됨' });
        if (items.length === 0) return [{ icon: 'ℹ️', label: '판단 근거', value: '세부 판단 근거를 확인할 수 없습니다.' }];
        return items.slice(0, 5);
    }
    private fallDifferentiationEvidence(result?: any): any {
        const res = result || this.analysisResult || {};
        return res?.postprocess_output?.evidence || {};
    }
    public hasFallDifferentiationEvidence(result?: any): boolean {
        const timeline = this.fallDifferentiationEvidence(result)?.precision_timeline || {};
        return timeline?.available === true;
    }
    public fallDifferentiationItems(result?: any): Array<{
        step: string; title: string; actual: string; criterion: string;
        interpretation: string; tone: 'fall' | 'nonfall' | 'review'; badge: string;
    }> {
        const res = result || this.analysisResult || {};
        const evidence = this.fallDifferentiationEvidence(res);
        const timeline = evidence?.precision_timeline || {};
        const thresholds = evidence?.precision_support_thresholds || {};
        const shadow = this.fallEventShadow(res) || {};
        const risk = Number(evidence?.combined_shadow_risk_score ?? shadow?.fall_risk_score ?? 0);
        const drop = Number(evidence?.center_drop_ratio ?? 0);
        const heightRatioValue = timeline?.tail_bbox_height_ratio;
        const heightRatio = heightRatioValue == null ? null : Number(heightRatioValue);
        const riskThreshold = Number(thresholds?.minimum_shadow_risk ?? 0.45);
        const dropThreshold = Number(thresholds?.minimum_center_drop_ratio ?? 0.09);
        const heightThreshold = Number(thresholds?.maximum_tail_bbox_height_ratio ?? 0.92);
        const handContact = timeline?.peak_hand_contact_candidate === true;
        const riskPassed = risk >= riskThreshold;
        const lateralCollapse = evidence?.precision_lateral_collapse_qualified === true;
        const dropPassed = drop >= dropThreshold || lateralCollapse;
        const heightPassed = heightRatio != null && Number.isFinite(heightRatio) && heightRatio <= heightThreshold;
        return [
            {
                step: '1', title: '순간 이벤트 강도',
                actual: `${(risk * 100).toFixed(1)}%`,
                criterion: `낙상 쪽 기준 ${Math.round(riskThreshold * 100)}% 이상`,
                interpretation: riskPassed ? '짧은 구간의 움직임 강도는 낙상 후보 수준입니다.' : '움직임 강도가 낙상 후보 기준보다 낮습니다.',
                tone: riskPassed ? 'fall' : 'nonfall', badge: riskPassed ? '기준 충족' : '기준 미달',
            },
            {
                step: '2', title: '몸 중심·형태 붕괴',
                actual: lateralCollapse ? `중심 ${(drop * 100).toFixed(1)}% · 측면 붕괴` : `중심 ${(drop * 100).toFixed(1)}%`,
                criterion: `중심 하강 ${Math.round(dropThreshold * 100)}% 이상 또는 측면 형태 붕괴`,
                interpretation: lateralCollapse
                    ? '위에서 촬영되어 중심 하강은 작지만, 신체 높이 감소와 가로 형태 변화가 지속됐습니다.'
                    : (dropPassed ? '몸 중심이 짧은 시간에 충분히 아래로 이동했습니다.' : '빠른 동작은 있어도 실제 아래 방향 이동은 작았습니다.'),
                tone: dropPassed ? 'fall' : 'nonfall', badge: dropPassed ? '기준 충족' : '기준 미달',
            },
            {
                step: '3', title: '손 접촉 후보',
                actual: handContact ? '감지됨' : '감지되지 않음',
                criterion: '바닥·매트 방향 손 접촉 후보 확인',
                interpretation: handContact ? '하강 순간에 손으로 바닥을 짚는 형태의 보조 근거가 있습니다.' : '손 접촉이 없어도 낮은 자세가 끝까지 유지되면 낙상 근거가 될 수 있습니다.',
                tone: handContact ? 'fall' : 'review', badge: handContact ? '보조 근거 있음' : '보조 근거 없음',
            },
            {
                step: '4', title: '낙상 후 낮은 자세 유지',
                actual: heightRatio == null || !Number.isFinite(heightRatio) ? '측정 불가' : `시작 대비 ${(heightRatio * 100).toFixed(1)}%`,
                criterion: `낙상 쪽 기준 ${Math.round(heightThreshold * 100)}% 이하`,
                interpretation: heightPassed ? '영상 끝에서도 신체 높이가 낮아진 상태로 유지됐습니다.' : '영상 끝에는 신체 높이가 유지되거나 회복되어 의도적 앉기·굽힘 가능성이 큽니다.',
                tone: heightPassed ? 'fall' : (heightRatio == null ? 'review' : 'nonfall'),
                badge: heightPassed ? '낮은 자세 지속' : (heightRatio == null ? '확인 필요' : '회복·높이 유지'),
            },
        ];
    }
    public fallDifferentiationCardClass(item: any): string {
        if (item?.tone === 'fall') return 'border-orange-200 bg-orange-50/60';
        if (item?.tone === 'nonfall') return 'border-emerald-200 bg-emerald-50/60';
        return 'border-zinc-200 bg-zinc-50';
    }
    public fallDifferentiationBadgeClass(item: any): string {
        if (item?.tone === 'fall') return 'border-orange-200 bg-orange-100 text-orange-900';
        if (item?.tone === 'nonfall') return 'border-emerald-200 bg-emerald-100 text-emerald-900';
        return 'border-zinc-200 bg-white text-zinc-700';
    }
    public fallDifferentiationVerdict(result?: any): string {
        const res = result || this.analysisResult || {};
        const qualified = this.fallDifferentiationEvidence(res)?.precision_support_qualified === true;
        const state = this.decisionStateCode(res);
        if (qualified && ['fall_suspected', 'fall_confirmed'].includes(state)) {
            return '낙상 쪽 근거 충족: 순간 강도와 중심·형태 붕괴가 확인됐고, 손 접촉 후보 또는 낮은 자세 지속이 함께 확인됐습니다.';
        }
        if (!qualified && !['fall_suspected', 'fall_confirmed'].includes(state)) {
            return '비낙상 쪽 근거 충족: 급격한 동작이 일부 있어도 실제 하강량이나 낙상 후 낮은 자세 지속 근거가 함께 확인되지 않았습니다.';
        }
        if (qualified) return '동작 근거는 낙상 쪽 기준을 충족했지만 최종 모델 판정과 차이가 있어 영상을 직접 확인해야 합니다.';
        return '전체 영상 근거가 낙상 조합 기준을 충족하지 않아 비낙상 가능성이 높지만, 최종 판정과 차이가 있어 확인이 필요합니다.';
    }
    public fallDifferentiationVerdictClass(result?: any): string {
        const qualified = this.fallDifferentiationEvidence(result)?.precision_support_qualified === true;
        return qualified
            ? 'border-orange-200 bg-orange-50 text-orange-950'
            : 'border-emerald-200 bg-emerald-50 text-emerald-950';
    }
    public topPostureItem(result?: any): any { return this.postureItems(result)[0] || null; }
    public userCurrentPostureText(result?: any): string {
        const res = result || this.analysisResult || {};
        const direct = String(res?.posture_label || res?.runtime_inference?.posture_label || '');
        if (direct && direct !== 'unknown') return this.POSTURE_LABEL_MAP[direct] || direct;
        const top = this.topPostureItem(res);
        return top?.label || '자세 정보 없음';
    }
    public postureDecisionText(result?: any): string {
        const res = result || this.analysisResult || {};
        const code = this.topPostureItem(res)?.code || res?.posture_label || res?.runtime_inference?.posture_label || '';
        if (code === 'lie') return '현재 자세: 눕기 · 낙상 이벤트 별도 평가';
        if (this.decisionStateCode(res) === 'posture_only') return `현재 자세: ${code === 'sit' ? '앉기' : (this.POSTURE_LABEL_MAP[code] || '자세 감지')} · 낙상 이벤트 별도 평가`;
        if (this.decisionStateCode(res) === 'safe' && code === 'walk') return '정상 보행';
        return this.decisionStateMeta(res).label;
    }
    public fallEventTimeline(result?: any): any[] {
        const items = this.fallEventShadow(result)?.event_timeline;
        return Array.isArray(items) ? items : [];
    }
    public shadowEventWindows(result?: any): any[] {
        const items = this.fallEventShadow(result)?.event_windows;
        return Array.isArray(items) ? [...items].sort((a: any, b: any) => this.shadowEventStart(a) - this.shadowEventStart(b)) : [];
    }
    public fallEventRequestedIntervals(result?: any): any[] {
        const items = this.fallEventShadow(result)?.requested_interval_analysis;
        return Array.isArray(items) ? items : [];
    }
    public fallEventRiskPercent(result?: any): number { return Number(this.fallEventShadow(result)?.fall_risk_score || 0) * 100; }
    public operationalRiskPercentExact(result?: any): number {
        const res = result || this.analysisResult || {};
        return Number(res?.displayed_risk_score ?? res?.event_risk_score ?? res?.raw_model_risk_score ?? res?.risk_score ?? 0) * 100;
    }
    public rawModelRiskPercentExact(result?: any): number {
        const res = result || this.analysisResult || {};
        return Number(res?.raw_model_risk_score ?? res?.model_output?.risk_score ?? res?.risk_score ?? 0) * 100;
    }
    public shadowWindowSeconds(result?: any): number { return Number(this.fallEventShadow(result)?.window_policy?.window_sec || 1.25); }
    public shadowStrideSeconds(result?: any): number { return Number(this.fallEventShadow(result)?.window_policy?.stride_sec || 0.25); }
    public analysisTimelineDuration(result?: any): number {
        const res = result || this.analysisResult || {};
        const duration = Number(res?.video_meta?.duration || this.videoMeta?.duration || this.uploadChunkSummary?.max_end_sec || 0);
        const chunkEnd = this.uploadLogEntries.reduce((max: number, entry: any) => Math.max(max, Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? 0)), 0);
        const shadowEnd = this.fallEventTimeline(res).reduce((max: number, item: any) => Math.max(max, this.shadowEventEnd(item)), 0);
        return Math.max(duration, chunkEnd, shadowEnd, 1);
    }
    public timelineLeft(second: any, result?: any): string {
        const value = Math.max(0, Math.min(this.analysisTimelineDuration(result), Number(second || 0)));
        return `${(value / this.analysisTimelineDuration(result)) * 100}%`;
    }
    public timelineWidth(start: any, end: any, result?: any): string {
        const duration = this.analysisTimelineDuration(result);
        const safeStart = Math.max(0, Math.min(duration, Number(start || 0)));
        const safeEnd = Math.max(safeStart, Math.min(duration, Number(end || safeStart)));
        return `${Math.max(((safeEnd - safeStart) / duration) * 100, 0.35)}%`;
    }
    public timelineTicks(result?: any): number[] {
        const duration = this.analysisTimelineDuration(result);
        const step = duration <= 12 ? 2 : (duration <= 30 ? 5 : 10);
        const ticks: number[] = [];
        for (let second = 0; second < duration; second += step) ticks.push(second);
        ticks.push(Math.round(duration * 10) / 10);
        return Array.from(new Set(ticks));
    }
    public shadowEventStart(item: any): number { return Number(item?.start_sec ?? item?.start_time ?? 0); }
    public shadowEventEnd(item: any): number { return Number(item?.end_sec ?? item?.end_time ?? this.shadowEventStart(item)); }
    public shadowEventState(item: any): string { return String(item?.state || item?.status || 'INSUFFICIENT_DATA'); }
    public shadowEventStatusLabel(item: any): string {
        const state = this.shadowEventState(item);
        const labels: Record<string, string> = {
            FALL: this.decisionStageTone() === 'confirmed' ? '낙상 확정 이벤트' : '낙상 의심 이벤트',
            FALL_CANDIDATE: this.decisionStageTone() === 'confirmed' ? '낙상 확정 이벤트' : '낙상 의심 이벤트',
            CONTROLLED_DESCENT: '통제된 하강 후보', NON_FALL_CONTROLLED_TRANSITION: '통제된 하강 후보',
            NORMAL_ACTIVITY: '일반 활동', NON_FALL_NORMAL_ACTIVITY: '일반 활동', LOW_POSITION_STABLE: '일반 활동',
            DESCENT_CANDIDATE: '추가 확인 필요', INDETERMINATE_OCCLUSION: '추가 확인 필요',
            INDETERMINATE_LOW_CONFIDENCE: '분석 데이터 부족', INSUFFICIENT_DATA: '분석 데이터 부족',
        };
        return labels[state] || '추가 확인 필요';
    }
    public shadowSummaryStatus(result?: any): string {
        const shadow = this.fallEventShadow(result) || {};
        return this.shadowEventStatusLabel({ state: shadow.decision_state || (shadow.fall_event_candidate_detected ? 'DESCENT_CANDIDATE' : 'NORMAL_ACTIVITY') });
    }
    public shadowEventClass(item: any): string {
        const state = this.shadowEventState(item);
        if ((state === 'FALL' || state === 'FALL_CANDIDATE') && this.decisionStageTone() === 'confirmed') return 'border-rose-600 bg-rose-500 text-white';
        if (state === 'FALL' || state === 'FALL_CANDIDATE') return 'border-orange-600 bg-orange-500 text-white';
        if (state === 'CONTROLLED_DESCENT' || state === 'NON_FALL_CONTROLLED_TRANSITION') return 'border-amber-400 bg-amber-200 text-amber-950';
        if (state.includes('INDETERMINATE') || state === 'INSUFFICIENT_DATA' || state === 'DESCENT_CANDIDATE') return 'border-zinc-500 border-dashed bg-zinc-200 text-zinc-800';
        return 'border-zinc-300 bg-zinc-100 text-zinc-600';
    }
    public shadowEventSelectionClass(item: any): string {
        if (!this.isSelectedShadowEvent(item)) return '';
        return this.decisionStageTone() === 'confirmed'
            ? 'ring-2 ring-rose-700 ring-offset-2'
            : 'ring-2 ring-orange-700 ring-offset-2';
    }
    public shadowEventPanelClass(): string {
        return this.decisionStageTone() === 'confirmed'
            ? 'border-rose-200 bg-rose-50/40'
            : 'border-orange-200 bg-orange-50/40';
    }
    public shadowEventAccentClass(): string {
        return this.decisionStageTone() === 'confirmed' ? 'text-rose-950' : 'text-orange-950';
    }
    public shadowSummaryCardClass(): string {
        return this.decisionStageTone() === 'confirmed'
            ? 'border-rose-200 bg-rose-50/50'
            : 'border-orange-200 bg-orange-50/50';
    }
    public shadowSummaryAccentClass(): string {
        return this.decisionStageTone() === 'confirmed' ? 'text-rose-900' : 'text-orange-900';
    }
    private logDecisionState(entry: any): string {
        return String(entry?.decisionState || entry?.result?.final_event_state || entry?.result?.decision_state || '').toLowerCase();
    }
    public logRiskClass(entry: any): string {
        const state = this.logDecisionState(entry);
        if (state === 'fall_confirmed') return 'text-rose-600';
        if (state === 'fall_suspected') return 'text-orange-600';
        if (['uncertain', 'insufficient_data', 'insufficient_detection', 'near_fall_recovered'].includes(state)) return 'text-amber-600';
        const level = String(entry?.level || entry || '');
        if (level === 'high') return 'text-orange-600';
        if (level === 'medium') return 'text-amber-600';
        return 'text-emerald-600';
    }
    public logRiskDotClass(entry: any): string {
        const state = this.logDecisionState(entry);
        if (state === 'fall_confirmed') return 'bg-rose-500';
        if (state === 'fall_suspected') return 'bg-orange-500';
        if (['uncertain', 'insufficient_data', 'insufficient_detection', 'near_fall_recovered'].includes(state)) return 'bg-amber-400';
        const level = String(entry?.level || entry || '');
        if (level === 'high') return 'bg-orange-500';
        if (level === 'medium') return 'bg-amber-500';
        return 'bg-emerald-500';
    }
    public logTimelineClass(entry: any): string {
        const state = this.logDecisionState(entry);
        if (state === 'fall_confirmed') return 'border-rose-300 bg-rose-100 text-rose-800';
        if (state === 'fall_suspected') return 'border-orange-300 bg-orange-100 text-orange-800';
        if (['uncertain', 'insufficient_data', 'insufficient_detection', 'near_fall_recovered'].includes(state)) return 'border-amber-300 bg-amber-100 text-amber-800';
        if (entry?.level === 'high') return 'border-orange-300 bg-orange-100 text-orange-800';
        if (entry?.level === 'medium') return 'border-amber-300 bg-amber-100 text-amber-800';
        return 'border-emerald-200 bg-emerald-100 text-emerald-700';
    }
    public alertDecisionTone(): 'normal' | 'review' | 'suspected' | 'confirmed' {
        const state = String(this.alertWorkflow?.final_state || '').toLowerCase();
        if (state === 'fall_confirmed') return 'confirmed';
        if (state === 'fall_suspected') return 'suspected';
        if (['uncertain', 'insufficient_data', 'insufficient_detection', 'review_required'].includes(state)) return 'review';
        return 'normal';
    }
    public alertCardClass(): string {
        const tone = this.alertDecisionTone();
        if (tone === 'confirmed') return 'border-rose-200 bg-rose-50';
        if (tone === 'suspected') return 'border-orange-200 bg-orange-50';
        if (tone === 'review') return 'border-amber-200 bg-amber-50';
        return 'border-emerald-200 bg-emerald-50';
    }
    public alertTitleClass(): string {
        const tone = this.alertDecisionTone();
        if (tone === 'confirmed') return 'text-rose-800';
        if (tone === 'suspected') return 'text-orange-800';
        if (tone === 'review') return 'text-amber-800';
        return 'text-emerald-800';
    }
    public alertStatusText(): string {
        const tone = this.alertDecisionTone();
        if (tone === 'confirmed') return '낙상 확정 · 응급 대응';
        if (tone === 'suspected') return '낙상 의심 · 현장 확인';
        if (tone === 'review') return '판단 확인 필요';
        return '알림 발송 대기';
    }
    public alertActionClass(): string {
        return this.alertDecisionTone() === 'confirmed'
            ? 'bg-rose-600 hover:bg-rose-700'
            : 'bg-orange-600 hover:bg-orange-700';
    }
    public alertActionText(): string {
        return this.alertDecisionTone() === 'confirmed' ? '🚨 응급 프로토콜' : '⚠️ 현장 확인 프로토콜';
    }
    public alertPopupHeaderClass(): string {
        return this.alertDecisionTone() === 'confirmed' ? 'bg-rose-600' : 'bg-orange-500';
    }
    public alertPopupCalloutClass(): string {
        return this.alertDecisionTone() === 'confirmed'
            ? 'bg-rose-50 border-rose-200 text-rose-800'
            : 'bg-orange-50 border-orange-200 text-orange-900';
    }
    public alertPopupTitle(): string {
        return this.alertDecisionTone() === 'confirmed' ? '응급 상황' : '낙상 의심 · 현장 확인';
    }
    public shadowTimelineEvents(result?: any): any[] {
        return this.fallEventTimeline(result).filter((item: any) => this.shadowEventState(item) !== 'NORMAL_ACTIVITY');
    }
    public shadowEventRiskPercent(item: any): number { return Number(item?.risk_score ?? item?.fall_risk_score ?? 0) * 100; }
    public shadowEventConfidencePercent(item: any): number { return Number(item?.confidence ?? 0) * 100; }
    public shadowEventAriaLabel(item: any): string {
        return `${this.shadowEventStart(item)}초부터 ${this.shadowEventEnd(item)}초까지 ${this.shadowEventStatusLabel(item)}, 위험 ${Math.round(this.shadowEventRiskPercent(item))}퍼센트`;
    }
    public isSelectedShadowEvent(item: any): boolean {
        if (!this.selectedShadowEvent || !item) return false;
        return this.shadowEventStart(item) === this.shadowEventStart(this.selectedShadowEvent)
            && this.shadowEventEnd(item) === this.shadowEventEnd(this.selectedShadowEvent)
            && this.shadowEventState(item) === this.shadowEventState(this.selectedShadowEvent);
    }
    public selectShadowEvent(item: any): void {
        this.selectedShadowEvent = item || null;
        this.shadowFeedbackMessage = '';
        this.service.render();
    }
    public shadowReasonItems(item: any): string[] {
        const labels: Record<string, string> = {
            RAPID_VERTICAL_DROP: '몸 중심이 빠르게 아래로 이동함',
            ABRUPT_TORSO_ROTATION: '몸통 방향이 갑자기 크게 변함',
            IMPACT_CANDIDATE: '충격과 유사한 움직임이 감지됨',
            LOW_POSITION_PERSISTENCE: '낮은 자세가 일정 시간 유지됨',
            CONTROLLED_CONTACT_UNVERIFIED: '손이나 무릎 접촉 여부를 확인할 수 없음',
            OCCLUSION_RISK: '신체 일부가 가려져 판단이 제한됨',
            OCCLUSION_LIMITED: '신체 일부가 가려져 판단이 제한됨',
            CONTROLLED_DESCENT: '의도적인 앉기 또는 낮아짐 가능성이 있음',
            INSUFFICIENT_DATA: '분석 프레임이 부족함',
            INSUFFICIENT_VALID_KEYPOINTS: '분석 프레임이 부족함',
        };
        const reasons = Array.isArray(item?.reason_codes) ? item.reason_codes : [];
        const translated = reasons.map((code: string) => labels[code]).filter(Boolean);
        if (this.shadowEventState(item) === 'CONTROLLED_DESCENT' && !reasons.includes('CONTROLLED_DESCENT')) translated.push(labels.CONTROLLED_DESCENT);
        if (this.shadowEventState(item) === 'INSUFFICIENT_DATA' && translated.length === 0) translated.push(labels.INSUFFICIENT_DATA);
        return Array.from(new Set(translated));
    }
    public fallEventReasonText(item: any): string { return this.shadowReasonItems(item).join(' · ') || '세부 판단 근거 정보 없음'; }
    public highestOperationalChunk(): any {
        return [...this.uploadLogEntries].sort((a: any, b: any) => Number(b?.score || 0) - Number(a?.score || 0))[0] || null;
    }
    public operationalTransitionText(): string {
        return String(this.uploadChunkSummary?.behavior_transition_line || '').replace(/^행동 변화:\s*/, '') || '변화 정보 없음';
    }
    public shadowSummaryInterval(result?: any): string {
        const interval = this.fallEventShadow(result)?.fall_event_interval;
        return Array.isArray(interval) && interval.length >= 2 ? `${interval[0]}~${interval[1]}초` : '후보 없음';
    }
    public combinedAnalysisNarrative(result?: any): string {
        const res = result || this.analysisResult || {};
        const operationalState = this.decisionStateCode(res);
        const operationalFall = Boolean(res?.fall_detected) || ['fall_suspected', 'fall_confirmed'].includes(operationalState);
        const shadow = this.fallEventShadow(res);
        const shadowState = String(shadow?.decision_state || '');
        const shadowFall = shadowState === 'FALL';
        const shadowInsufficient = shadowState === 'INDETERMINATE_LOW_CONFIDENCE' || !shadow;
        if (!operationalFall && shadowFall) return `Shadow 근거가 운영 점수보다 높더라도 독립 근거 2개 미만이면 최종 판정을 바꾸지 않습니다. ${this.shadowSummaryInterval(res)} 구간을 확인하세요.`;
        if (!operationalFall && shadowState === 'NON_FALL_NORMAL_ACTIVITY') return '전체 영상에서 낙상으로 의심할 만한 빠른 자세 변화가 확인되지 않았습니다.';
        if (operationalFall && shadowFall) return '운영 모델 또는 근거 기반 Shadow 후처리에서 낙상 위험이 확인되어 이벤트를 유지했습니다.';
        if (operationalFall && shadowInsufficient) return '운영 분석에서 낙상 위험이 확인되었으나, 짧은 구간 분석은 데이터가 부족합니다.';
        if (!shadow) return `최종 운영 판정은 ${this.decisionStateMeta(res).label}입니다. Shadow 분석 정보는 없습니다.`;
        return `최종 운영 판정은 ${this.decisionStateMeta(res).label}이며, Shadow 보조 분석은 ${this.shadowSummaryStatus(res)}입니다. Shadow 점수는 모델 점수와 합산하지 않습니다.`;
    }
    public shadowEventMarkers(item: any): any[] {
        if (!item) return [];
        const candidates = [
            { key: 'peak', label: '최고 위험', icon: '◆', time: item?.peak_time_sec ?? item?.peak_time },
            { key: 'drop', label: '중심 하강 시작', icon: '↓', time: item?.rapid_drop_start_sec ?? item?.rapid_drop_start_time },
            { key: 'low', label: '낮은 자세 도달', icon: '▾', time: item?.low_position_reached_sec ?? item?.low_position_time },
            { key: 'end', label: '이벤트 종료', icon: '■', time: item?.event_end_sec ?? item?.end_sec ?? item?.end_time },
        ];
        return candidates.filter(marker => marker.time != null && Number.isFinite(Number(marker.time)));
    }
    public eventOverlapsChunk(event: any, entry: any): boolean {
        const chunkStart = Number(entry?.chunkWindow?.start_sec ?? entry?.chunkWindow?.startSec ?? 0);
        const chunkEnd = Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? chunkStart);
        return this.shadowEventStart(event) < chunkEnd && this.shadowEventEnd(event) > chunkStart;
    }
    public overlappingShadowEvents(entry: any): any[] {
        return this.shadowTimelineEvents().filter((event: any) => this.eventOverlapsChunk(event, entry));
    }
    public selectedShadowOverlapsChunk(entry: any): boolean {
        return Boolean(this.selectedShadowEvent && this.eventOverlapsChunk(this.selectedShadowEvent, entry));
    }
    public overlappingShadowRange(entry: any): string {
        const events = this.overlappingShadowEvents(entry);
        if (events.length === 0) return '';
        return events.map((event: any) => `${this.shadowEventStart(event)}~${this.shadowEventEnd(event)}초`).join(', ');
    }
    public overlappingShadowMaxRisk(entry: any): number {
        return Math.max(...this.overlappingShadowEvents(entry).map((event: any) => this.shadowEventRiskPercent(event)), 0);
    }
    public async submitShadowEventFeedback(actualFall: boolean): Promise<void> {
        if (!this.selectedShadowEvent || !this.analysisSavedName || this.shadowFeedbackSubmitting) return;
        this.shadowFeedbackSubmitting = true;
        this.shadowFeedbackMessage = '';
        await this.service.render();
        try {
            const params: any = {
                saved_name: this.analysisSavedName,
                predicted_label: 'Y',
                feedback_status: actualFall ? 'correct' : 'incorrect',
                actual_label: actualFall ? 'Y' : 'N',
                note: `Shadow event review ${this.shadowEventStart(this.selectedShadowEvent)}-${this.shadowEventEnd(this.selectedShadowEvent)}`,
                retrain: 'false', posture_class: '', predicted_posture: this.analysisResult?.posture_label || '',
                ambiguity_flag: 'false', occlusion_flag: 'false', short_clip_flag: 'true', static_filter_label: '',
                track_id: this.feedbackTrackId(this.analysisResult),
                event_start_sec: String(this.shadowEventStart(this.selectedShadowEvent)),
                event_end_sec: String(this.shadowEventEnd(this.selectedShadowEvent)),
            };
            const { code, data } = await wiz.call('submit_analysis_feedback', params);
            if (code === 200) {
                this.shadowFeedbackMessage = actualFall ? '실제 낙상으로 저장했습니다.' : '낙상이 아닌 동작으로 저장했습니다.';
                this.shadowFeedbackMessageType = 'success';
            } else {
                this.shadowFeedbackMessage = data?.message || '이벤트 확인 결과를 저장하지 못했습니다.';
                this.shadowFeedbackMessageType = 'error';
            }
        } catch (e: any) {
            this.shadowFeedbackMessage = e?.message || '이벤트 확인 결과를 저장하지 못했습니다.';
            this.shadowFeedbackMessageType = 'error';
        }
        this.shadowFeedbackSubmitting = false;
        await this.service.render();
    }
    public facialAnalysisStatus(result?: any): string {
        const face = this.facialStateAux(result);
        if (!face || face.reason === 'face_not_detected' || face.face_visible === false) return '분석하지 않음';
        return face.available || face.face_detected ? '분석 완료' : '분석하지 않음';
    }
    public facialAnalysisDescription(result?: any): string {
        const face = this.facialStateAux(result);
        if (!face) return '표정 분석 데이터가 제공되지 않았습니다.';
        if (face.reason === 'face_not_detected' || face.face_visible === false) return '얼굴이 충분히 검출되지 않아 표정 분석을 낙상 점수에 사용하지 않았습니다.';
        return '표정 분석은 의료적 진단이 아닌 낙상 판단의 보조 신호로만 사용됩니다.';
    }
    public facialSupportPercent(result?: any): number { return Math.round(Number(this.facialStateAux(result)?.support_score || 0) * 100); }
    public userAnalysisEngineLabel(result?: any): string {
        const res = result || this.analysisResult || {};
        if (['rf-dual', 'rf-dual-person'].includes(res?.runtime_key || '')) return 'RF-Dual 사람별 분석';
        return String(res?.model_runtime?.label || res?.runtime_label || '분석 엔진 정보 없음');
    }
    public analysisStatusText(result?: any): string {
        const state = this.decisionStateCode(result);
        return ['uncertain', 'fall_suspected', 'near_fall_recovered', 'insufficient_data', 'insufficient_detection'].includes(state) ? '확인 필요' : '완료';
    }
    public llmInterpretationText(): string {
        return String(this.analysisResult?.llm_interpretation?.text || '').trim();
    }
    public llmInterpretationStatus(): string {
        const llm = this.analysisResult?.llm_interpretation || {};
        if (llm?.status === 'generated') return `${llm.model || 'LLM'} 판독`;
        if (llm?.status === 'local_fast') return '로컬 빠른 근거 요약';
        if (llm?.status === 'queued') return 'LLM 판독 대기';
        if (llm?.status === 'cached') return `${llm.model || 'LLM'} 캐시 판독`;
        if (llm?.status === 'missing_api_key') return 'LLM API Key 필요';
        if (llm?.status === 'error') return `LLM 판독 실패: ${llm.error || '오류'}`;
        if (llm?.status === 'disabled') return 'LLM 판독 꺼짐';
        return '';
    }

    private numericTiming(value: any): number {
        const num = Number(value || 0);
        return Number.isFinite(num) ? num : 0;
    }

    public getUploadAnalysisSlowReason(): string {
        const st = this.analysisResult?.server_timing || {};
        if (!st) return '';
        const inference = this.numericTiming(st.inference_sec);
        const build = this.numericTiming(st.result_build_sec);
        const llm = this.numericTiming(st.llm_sec);
        const total = this.numericTiming(st.total_server_sec || st.response_ready_sec);
        if (llm >= 1 && llm >= inference * 0.5 && llm >= build * 0.5) {
            return `LLM 동기 판독이 ${llm.toFixed(2)}초 걸렸습니다. local_fast 또는 비동기 판독으로 낮출 수 있습니다.`;
        }
        if (inference >= build && inference >= 1) {
            return `주요 지연은 RF-Dual 영상 추론입니다. 추론 ${inference.toFixed(2)}초 / 전체 ${total.toFixed(2)}초입니다.`;
        }
        if (build >= 1) {
            return `주요 지연은 업로드 분할 로그와 결과 구성입니다. 결과 구성 ${build.toFixed(2)}초 / LLM ${llm.toFixed(2)}초입니다.`;
        }
        return `LLM ${llm.toFixed(2)}초, 전체 ${total.toFixed(2)}초로 병목은 크지 않습니다.`;
    }

    public getServerTimingDetail(): any[] {
        const st = this.analysisResult?.server_timing;
        if (!st) return [];
        return [
            { label: '파일 읽기', value: st.file_read_sec },
            { label: '파일 저장', value: st.file_save_sec },
            { label: '모델 로드', value: st.summary_load_sec },
            { label: 'RF-Dual 추론', value: st.inference_sec },
            { label: '분할 로그/결과', value: st.result_build_sec },
            { label: 'LLM 설명', value: st.llm_sec },
            { label: '알림 디스패치', value: st.alert_dispatch_sec },
            { label: '전체 응답', value: st.response_ready_sec },
        ].filter(i => i.value != null);
    }

    private startFeedbackTimer() {
        this.feedbackElapsedSec = 0;
        this.stopFeedbackTimer();
        this.feedbackTimerHandle = setInterval(async () => { this.feedbackElapsedSec++; await this.service.render(); }, 1000);
    }
    private stopFeedbackTimer() { if (this.feedbackTimerHandle) { clearInterval(this.feedbackTimerHandle); this.feedbackTimerHandle = null; } }

    public staticFilterValidationIntake(): any {
        return this.prototypeInfo?.static_object_filter_validation_intake || {};
    }

    private feedbackTrackId(result: any = null): string {
        const direct = result?.track_id ?? result?.runtime_inference?.track_id ?? result?.model_runtime?.track_id;
        if (direct != null && String(direct).trim()) return String(direct);
        const selected = this.findPersonAnalysisItem(this.selectedPersonAnalysisId);
        return selected?.track_id != null ? String(selected.track_id) : '';
    }

    private currentFeedbackEventWindow(): { startSec: number; endSec: number } {
        const selected = this.findPersonAnalysisItem(this.selectedPersonAnalysisId);
        return {
            startSec: Number(selected?.first_time_sec || 0),
            endSec: Number(selected?.last_time_sec || 0),
        };
    }

    public setFeedbackStatus(status: string) {
        const normalized = status === 'incorrect' ? 'incorrect' : 'correct';
        this.feedbackStatus = normalized;
        this.feedbackStaticFilterLabel = '';
        this.feedbackActualLabel = normalized === 'incorrect'
            ? (this.analysisResult?.fall_detected ? 'N' : 'Y')
            : '';
    }

    public setLogFeedbackStatus(status: string) {
        const normalized = status === 'incorrect' ? 'incorrect' : 'correct';
        this.logFeedbackStatus = normalized;
        this.logFeedbackStaticFilterLabel = '';
        this.logFeedbackActualLabel = normalized === 'incorrect'
            ? (this.logDetailEntry?.result?.fall_detected ? 'N' : 'Y')
            : '';
    }

    public setFeedbackStaticFilterLabel(code: string) {
        const next = this.feedbackStaticFilterLabel === code ? '' : code;
        this.feedbackStaticFilterLabel = next;
        if (!next) return;
        const option = this.staticFilterValidationLabels.find(item => item.code === next);
        if (!option) return;
        const predicted = this.analysisResult?.fall_detected ? 'Y' : 'N';
        this.feedbackActualLabel = option.actualLabel;
        this.feedbackStatus = predicted === option.actualLabel ? 'correct' : 'incorrect';
        this.feedbackRetrainRequested = false;
    }

    public setLogFeedbackStaticFilterLabel(code: string) {
        const next = this.logFeedbackStaticFilterLabel === code ? '' : code;
        this.logFeedbackStaticFilterLabel = next;
        if (!next) return;
        const option = this.staticFilterValidationLabels.find(item => item.code === next);
        if (!option) return;
        const predicted = this.logDetailEntry?.result?.fall_detected ? 'Y' : 'N';
        this.logFeedbackActualLabel = option.actualLabel;
        this.logFeedbackStatus = predicted === option.actualLabel ? 'correct' : 'incorrect';
        this.logFeedbackRetrainRequested = false;
    }

    public async submitAnalysisFeedback() {
        if (!this.feedbackStatus || !this.analysisSavedName) return;
        this.feedbackSubmitting = true;
        this.feedbackMessage = '';
        this.startFeedbackTimer();
        await this.service.render();
        try {
            const eventWindow = this.currentFeedbackEventWindow();
            const params: any = {
                saved_name: this.analysisSavedName,
                predicted_label: this.analysisResult?.fall_detected ? 'Y' : 'N',
                feedback_status: this.feedbackStatus,
                actual_label: this.feedbackActualLabel || '',
                note: this.feedbackNote || '',
                retrain: String(this.feedbackRetrainRequested && !this.feedbackStaticFilterLabel),
                posture_class: this.feedbackPostureClass || '',
                predicted_posture: this.analysisResult?.posture_label || '',
                ambiguity_flag: String(this.feedbackAmbiguityFlag),
                occlusion_flag: String(this.feedbackOcclusionFlag),
                short_clip_flag: String(this.feedbackShortClipFlag),
                static_filter_label: this.feedbackStaticFilterLabel || '',
                track_id: this.feedbackTrackId(this.analysisResult),
                event_start_sec: String(eventWindow.startSec || 0),
                event_end_sec: String(eventWindow.endSec || 0),
            };
            const { code, data } = await wiz.call('submit_analysis_feedback', params);
            if (code === 200) {
                const elapsed = this.feedbackElapsedSec;
                this.feedbackMessage = data?.learning_message || `✅ 피드백 저장 완료 (${elapsed}초)`;
                this.feedbackMessageType = 'success';
                if (data?.static_filter_validation_summary && this.prototypeInfo) {
                    this.prototypeInfo.static_object_filter_validation_intake = data.static_filter_validation_summary;
                }
                if (data?.manual_training_job_started) {
                    this.feedbackRetrainDone = true;
                    this.feedbackMessage = data?.learning_message || `✅ 피드백 저장 + 후보 학습 시작 (${elapsed}초)`;
                }
            } else {
                this.feedbackMessage = data?.message || '피드백 저장 실패';
                this.feedbackMessageType = 'error';
            }
        } catch (e: any) {
            this.feedbackMessage = '오류: ' + (e.message || e);
            this.feedbackMessageType = 'error';
        }
        this.stopFeedbackTimer();
        this.feedbackSubmitting = false;
        await this.service.render();
    }
    public toggleFeedbackFlag(flag: string) { if (flag === 'ambiguity') this.feedbackAmbiguityFlag = !this.feedbackAmbiguityFlag; if (flag === 'occlusion') this.feedbackOcclusionFlag = !this.feedbackOcclusionFlag; if (flag === 'short_clip') this.feedbackShortClipFlag = !this.feedbackShortClipFlag; }

    private realtimeFeedTone(result?: any): 'normal' | 'review' | 'suspected' | 'confirmed' {
        const tone = this.decisionStageTone(result || this.analysisResult);
        if (tone === 'confirmed') return 'confirmed';
        if (tone === 'suspected') return 'suspected';
        if (tone === 'review' || tone === 'excluded') return 'review';
        return 'normal';
    }
    private realtimeFeedPersonIsPresent(result: any): boolean {
        const multi = result?.multi_person || {};
        const runtime = result?.runtime_inference || {};
        const modelRuntime = result?.model_runtime || {};
        const logicalPeople = Number(multi?.logical_person_count ?? multi?.person_count ?? runtime?.tracked_persons ?? modelRuntime?.tracked_persons ?? 0);
        const detectedFrames = Number(modelRuntime?.detected_person_frames ?? runtime?.detected_person_frames ?? 0);
        return logicalPeople > 0 || detectedFrames > 0 || result?.entity_classification?.entity_type === 'person';
    }
    private realtimeFeedSignalStarted(key: string, active: boolean): boolean {
        const wasActive = this.realtimeFeedActiveSignals.has(key);
        if (active) this.realtimeFeedActiveSignals.add(key);
        else this.realtimeFeedActiveSignals.delete(key);
        return active && !wasActive;
    }
    private realtimeFeedClock(): string {
        return new Intl.DateTimeFormat('ko-KR', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        }).format(new Date());
    }
    private realtimeFeedEventPriority(eventType: string): number {
        const priorities: Record<string, number> = {
            person_detected: 10,
            tracking_started: 20,
            posture_changed: 30,
            fall_candidate: 40,
            risk_rise: 50,
            review_required: 60,
            tracking_ended: 70,
            upload_analysis_completed: 80,
            realtime_video_ended: 90,
        };
        return Number(priorities[eventType] ?? 55);
    }
    private realtimeFeedVideoTimestampMs(eventType: string, event: any, chunkWindow: any): number {
        const explicitMs = Number(event?.videoTimestampMs);
        if (Number.isFinite(explicitMs)) return Math.max(0, explicitMs);
        const eventSec = Number(chunkWindow?.eventTimeSec);
        if (Number.isFinite(eventSec)) return Math.max(0, eventSec * 1000);
        const startSec = Number(chunkWindow?.startSec ?? chunkWindow?.start_sec);
        const endSec = Number(chunkWindow?.endSec ?? chunkWindow?.end_sec);
        if (['person_detected', 'tracking_started'].includes(eventType) && Number.isFinite(startSec)) {
            return Math.max(0, startSec * 1000);
        }
        if (Number.isFinite(endSec)) return Math.max(0, endSec * 1000);
        if (Number.isFinite(startSec)) return Math.max(0, startSec * 1000);
        return Math.max(0, this.realtimeSourceTime() * 1000);
    }
    private realtimeFeedVideoTimeLabel(timestampMs: number): string {
        const totalSeconds = Math.max(0, timestampMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds - minutes * 60;
        return minutes > 0
            ? `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`
            : `${seconds.toFixed(1)}초`;
    }
    private sortRealtimeFeedEntries() {
        this.realtimeLogEntries.sort((left: any, right: any) => {
            const timeDelta = Number(left?.eventVideoTimestampMs || 0) - Number(right?.eventVideoTimestampMs || 0);
            if (Math.abs(timeDelta) > 0.5) return -timeDelta;
            const priorityDelta = Number(left?.eventOrderPriority || 0) - Number(right?.eventOrderPriority || 0);
            if (priorityDelta !== 0) return -priorityDelta;
            return Number(right?.eventSequence || 0) - Number(left?.eventSequence || 0);
        });
    }
    private fallEventEmotionText(result: any, liveFacial: any): string {
        if (this.agentWeights.emotion <= 0) return '웨이트 0% · 사용 안 함';
        const face = this.facialStateAux(result);
        if (face) {
            if (face?.reason === 'face_not_detected' || face?.face_visible === false) {
                return '얼굴 미검출 · 자세·환경 근거로 판정 계속';
            }
            const label = this.facialStateLabel(result) || face?.emotion_display_label || face?.emotion_top_label || '분석 완료';
            const percent = this.facialStatePercent(result);
            const applied = face?.applied === true ? '낙상 판정 반영' : '낙상 시점 보조 분석';
            return `${label}${percent > 0 ? ` ${percent}%` : ''} · ${applied}`;
        }
        if (liveFacial?.label) {
            const confidence = Math.max(0, Math.min(100, Math.round(Number(liveFacial?.confidence || 0))));
            return `${liveFacial.label}${confidence > 0 ? ` ${confidence}%` : ''} · 낙상 시점 실시간 분석`;
        }
        return '낙상 시점 감정 분석 대기';
    }
    private fallEventDetails(status: string, riskPercent: number, chunkWindow: any, liveFacial: any = null): { label: string; value: string }[] {
        const statusLabel: Record<string, string> = {
            suspected: '낙상 의심 · 확인 시작',
            confirming: '낙상 후보 · 정밀 확인 중',
            confirmed: '낙상 확인',
            dismissed: '낙상 후보 해제',
            review_required: '판단 보류 · 확인 필요',
        };
        const startSec = Number(chunkWindow?.startSec ?? chunkWindow?.start_sec);
        const endSec = Number(chunkWindow?.endSec ?? chunkWindow?.end_sec);
        const interval = Number.isFinite(startSec) && Number.isFinite(endSec)
            ? `${Math.max(0, startSec).toFixed(2)}~${Math.max(startSec, endSec).toFixed(2)}초`
            : String(chunkWindow?.label || '현재 재생 구간');
        const evidence = this.realtimeFallEvidenceLabels();
        if (Number(this.realtimeDiagnostics?.fall_event_max_down_speed || 0) >= 0.55) evidence.push('짧은 시간의 빠른 중심 하강');
        if (Number(this.realtimeDiagnostics?.fall_event_aspect_ratio_std || 0) >= 0.08) evidence.push('세로 자세에서 낮은 자세로 급변');
        if (Number(this.realtimeDiagnostics?.fall_event_height_range_ratio || 0) >= 0.45) evidence.push('큰 높이 변화와 저점 도달');
        const uniqueEvidence = Array.from(new Set(evidence));
        const fallbackEvidence = status === 'confirmed'
            ? ['중심 급하강', '몸통·형태 변화', '저점 도달']
            : ['자세 및 이동 변화'];
        return [
            { label: '판단', value: `${statusLabel[status] || status} · 위험도 ${Math.round(riskPercent)}%` },
            { label: '영상 시점', value: interval },
            { label: '판단 근거', value: (uniqueEvidence.length > 0 ? uniqueEvidence : fallbackEvidence).join(' · ') },
            { label: '현재 자세', value: `${this.realtimeFastPostureLabel || '확인 중'} · 낙상 사건과 별도 판단` },
            { label: '감정 분석', value: this.fallEventEmotionText(this.analysisResult, liveFacial) },
        ];
    }
    private upsertRealtimeFallEvent(
        status: 'suspected' | 'confirming' | 'confirmed' | 'dismissed' | 'review_required',
        riskPercent: number,
        chunkWindow: any,
        description: string,
    ) {
        const now = performance.now();
        if (!this.realtimeFastFallEventKey) {
            this.realtimeFastFallEventKey = `${this.realtimeSessionId || 'realtime'}:fall-candidate`;
            this.realtimeFastFallCreatedAt = now;
        }
        const meta: Record<string, any> = {
            suspected: { tone: 'suspected', icon: '⚠️', title: '낙상 동작 감지 중' },
            confirming: { tone: 'suspected', icon: '⌛', title: '낙상 후보 · 정밀 확인 중' },
            confirmed: { tone: 'confirmed', icon: '🚨', title: '낙상 확인' },
            dismissed: { tone: 'normal', icon: '✓', title: '낙상 후보 해제' },
            review_required: { tone: 'review', icon: '❓', title: '낙상 가능성 확인 필요' },
        };
        const view = meta[status];
        const fallFacialSnapshot = this.currentLiveFacialSnapshot();
        const existing = this.realtimeLogEntries.find(entry => (
            entry?.eventType === 'fall_candidate' && entry?.fallEventKey === this.realtimeFastFallEventKey
        ));
        if (this.realtimeVideoEnded && (!existing || now - this.realtimeVideoEndedAt > this.REALTIME_END_HARD_STOP_MS)) {
            this.realtimeLatePrecisionIgnoredCount += 1;
            return;
        }
        if (existing?.eventStatus === 'confirmed' && status !== 'confirmed') return;
        if (existing) {
            existing.eventStatus = status;
            existing.eventTone = view.tone;
            existing.eventIcon = view.icon;
            existing.label = view.title;
            existing.oneLine = description;
            existing.basis = description;
            existing.riskPercent = Math.round(riskPercent);
            existing.showRisk = true;
            existing.score = Math.max(0, Math.min(100, riskPercent)) / 100;
            existing.chunkWindow = chunkWindow;
            existing.chunkLabel = chunkWindow?.label || existing.chunkLabel;
            if (fallFacialSnapshot) existing.liveFacial = fallFacialSnapshot;
            existing.eventDetails = this.fallEventDetails(status, riskPercent, chunkWindow, existing.liveFacial);
            existing.eventVideoTimestampMs = this.realtimeFeedVideoTimestampMs('fall_candidate', {}, chunkWindow);
            existing.eventVideoTimeLabel = this.realtimeFeedVideoTimeLabel(existing.eventVideoTimestampMs);
            existing.confirmedAtRealtimeMs = ['confirmed', 'dismissed', 'review_required'].includes(status) ? now : null;
        } else {
            const entry = this.appendRealtimeFeedEvent({
                type: 'fall_candidate', tone: view.tone, icon: view.icon, title: view.title,
                description, riskPercent, showRisk: true,
            }, null, -1, chunkWindow, fallFacialSnapshot);
            entry.fallEventKey = this.realtimeFastFallEventKey;
            entry.eventStatus = status;
            entry.createdAtVideoTimeMs = Math.max(0, Number(chunkWindow?.eventTimeSec ?? chunkWindow?.endSec ?? this.realtimeSourceTime()) * 1000);
            entry.createdAtRealtimeMs = now;
            entry.fastRiskScore = Math.max(0, Math.min(1, riskPercent / 100));
            entry.eventDetails = this.fallEventDetails(status, riskPercent, chunkWindow, fallFacialSnapshot);
        }
        this.sortRealtimeFeedEntries();
        const renderRequestedAt = performance.now();
        this.requestRealtimeUiRender(true);
        requestAnimationFrame(() => {
            const delay = Math.max(0, performance.now() - renderRequestedAt);
            this.recordRealtimePerf('eventFeedRender', delay);
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                event_feed_render_delay_ms: delay,
                provisional_fall_event_status: status,
            };
        });
    }
    private appendRealtimeFeedEvent(event: any, blob: Blob | null, chunkId: number, chunkWindow: any, facialSnapshot: any = null) {
        const result = this.analysisResult || {};
        const riskPercent = Number(event?.riskPercent);
        const showRisk = event?.showRisk === true && Number.isFinite(riskPercent);
        const blobUrl = blob ? URL.createObjectURL(blob) : '';
        const entry: any = this.createLogEntry(result, {
            id: `${chunkId}:${String(event?.type || 'event')}`,
            timestamp: this.realtimeFeedClock(),
            blobUrl,
            elapsed: this.realtimeLastElapsed,
            label: String(event?.title || '주요 이벤트'),
            oneLine: String(event?.description || ''),
            basis: String(event?.description || ''),
            cumulativeScore: this.realtimeCumulativeScore,
            chunkWindow,
            chunkLabel: chunkWindow?.label || '',
            liveFacial: facialSnapshot,
        });
        entry.eventType = String(event?.type || 'event');
        entry.eventTone = event?.tone || this.realtimeFeedTone(result);
        entry.eventIcon = String(event?.icon || '•');
        entry.riskPercent = showRisk ? Math.round(riskPercent) : null;
        entry.showRisk = showRisk;
        entry.score = showRisk ? Math.max(0, Math.min(100, riskPercent)) / 100 : entry.score;
        entry.decisionState = this.decisionStateCode(result);
        entry.eventSequence = ++this.realtimeFeedSequence;
        entry.eventOrderPriority = this.realtimeFeedEventPriority(entry.eventType);
        entry.eventVideoTimestampMs = this.realtimeFeedVideoTimestampMs(entry.eventType, event, chunkWindow);
        entry.eventVideoTimeLabel = this.realtimeFeedVideoTimeLabel(entry.eventVideoTimestampMs);
        this.realtimeLogEntries.push(entry);
        this.sortRealtimeFeedEntries();
        if (this.realtimeLogEntries.length > 100) {
            const removed = this.realtimeLogEntries.pop();
            if (removed?.blobUrl) URL.revokeObjectURL(removed.blobUrl);
        }
        return entry;
    }
    private pushRealtimeLog(blob: Blob | null, chunkId: number, chunkWindow: any, facialSnapshot: any = null) {
        const result = this.analysisResult || {};
        const postureRaw = this.analysisResult?.posture_label || this.analysisResult?.runtime_inference?.posture_label || '';
        const currentCode = postureRaw && postureRaw !== 'unknown' ? postureRaw : this.topNonFallPostureCode(this.analysisResult);
        const currentLabel = this.POSTURE_LABEL_MAP[currentCode] || currentCode || '';
        const changed = Boolean(this.currentPosture && currentCode && this.currentPosture !== currentCode);
        const behaviorTransition = {
            current_code: currentCode || '',
            current_label: currentLabel || '',
            previous_code: this.currentPosture || '',
            previous_label: this.currentPostureLabel || '',
            next_code: '',
            next_label: '',
            changed_from_previous: changed,
            changes_to_next: false,
            display_label: changed ? `${this.currentPostureLabel || this.currentPosture} → ${currentLabel || currentCode}` : (currentLabel || ''),
            context_label: changed ? `이전 ${this.currentPostureLabel || this.currentPosture} · 현재 ${currentLabel || currentCode}` : (currentLabel ? `현재 ${currentLabel}` : ''),
        };
        const events: any[] = [];
        const riskPercent = this.riskScorePercent(result);
        const tone = this.realtimeFeedTone(result);
        const personPresentNow = this.realtimeFeedPersonIsPresent(result);
        if (personPresentNow) {
            this.realtimeFeedPersonMissCount = 0;
            if (this.realtimeFeedPersonPresent !== true) {
                events.push({ type: 'person_detected', tone: 'normal', icon: '👤', title: '사람 감지', description: '분석 대상 사람이 확인되었습니다.' });
                events.push({ type: 'tracking_started', tone: 'normal', icon: '◎', title: '사람 추적 시작', description: '동일 대상의 움직임 추적을 시작했습니다.' });
                this.realtimeFeedPersonPresent = true;
            }
        } else if (this.realtimeFeedPersonPresent === true) {
            this.realtimeFeedPersonMissCount += 1;
            if (this.realtimeFeedPersonMissCount >= 2) {
                events.push({ type: 'tracking_ended', tone: 'review', icon: '◌', title: '사람 추적 종료', description: '연속 분석 구간에서 사람이 확인되지 않아 추적을 종료했습니다.' });
                this.realtimeFeedPersonPresent = false;
                this.realtimeFeedPersonMissCount = 0;
            }
        } else if (this.realtimeFeedPersonPresent === null) {
            this.realtimeFeedPersonPresent = false;
        }

        const evidence = result?.postprocess_output?.evidence || {};
        const flags = evidence?.flags || {};
        if (this.realtimeFeedSignalStarted('rapid_center_drop', flags?.rapid_center_drop === true)) {
            this.realtimeFallEvidenceSignals.add('rapid_center_drop');
        }
        if (this.realtimeFeedSignalStarted('torso_tilt_increase', flags?.torso_tilt_increase === true)) {
            this.realtimeFallEvidenceSignals.add('torso_tilt_change');
        }
        const shadow = this.fallEventShadow(result) || {};
        const shadowState = String(shadow?.decision_state || '');
        const shadowActive = shadow?.fall_event_detected === true
            || shadow?.fall_event_candidate_detected === true
            || ['FALL', 'FALL_CANDIDATE', 'DESCENT_CANDIDATE'].includes(shadowState);
        if (this.realtimeFeedSignalStarted('shadow_event', shadowActive)) {
            this.realtimeFallEvidenceSignals.add('shadow_event');
            this.upsertRealtimeFallEvent(
                tone === 'confirmed' ? 'confirmed' : 'confirming',
                Math.round(Number(shadow?.fall_risk_score ?? riskPercent / 100) * 100),
                this.realtimeResultEventWindow(result, chunkWindow),
                `짧은 구간 낙상 근거를 동일 사건에 병합했습니다. 근거: ${this.realtimeFallEvidenceLabels().join(' · ')}.`,
            );
        }
        const fallbackActive = String(result?.runtime_key || result?.runtime_inference?.runtime_key || '').includes('fallback');
        if (this.realtimeFeedSignalStarted('analysis_fallback', fallbackActive)) {
            events.push({
                type: 'analysis_fallback', tone: 'review', icon: '↪', title: '대체 분석 경로',
                description: '기본 분석 경로를 사용할 수 없어 기존 대체 경로로 분석했습니다.',
                riskPercent, showRisk: true,
            });
        }

        if (this.realtimeFeedLastRiskPercent != null) {
            const riskDelta = riskPercent - this.realtimeFeedLastRiskPercent;
            if (riskDelta >= this.REALTIME_FEED_RISK_RISE_THRESHOLD) {
                events.push({
                    type: 'risk_rise', tone: tone === 'normal' ? 'review' : tone, icon: '↗', title: `위험도 상승 ${this.realtimeFeedLastRiskPercent}% → ${riskPercent}%`,
                    description: `이전 분석 구간보다 사건 위험도가 ${Math.round(riskDelta)}%p 상승했습니다.`,
                    riskPercent, showRisk: true,
                });
            }
        }
        this.realtimeFeedLastRiskPercent = riskPercent;

        if (tone !== this.realtimeFeedLastTone) {
            if (tone === 'confirmed') {
                this.upsertRealtimeFallEvent('confirmed', riskPercent, this.realtimeResultEventWindow(result, chunkWindow), '정밀 분석에서 낙상으로 확인됐습니다. 즉시 상태를 확인하세요.');
            }
            else if (tone === 'suspected') {
                this.upsertRealtimeFallEvent('confirming', riskPercent, this.realtimeResultEventWindow(result, chunkWindow), '실시간 후보를 정밀 분석으로 확인 중입니다.');
            }
            else if (tone === 'review') events.push({ type: 'review_required', tone, icon: '❓', title: '확인 필요', description: '검출 품질 또는 판단 근거가 충분하지 않아 확인이 필요합니다.', riskPercent, showRisk: true });
            else if (this.realtimeFeedLastTone && this.realtimeFeedLastTone !== 'normal') {
                if (this.realtimeFastFallEventKey) {
                    this.upsertRealtimeFallEvent('dismissed', riskPercent, this.realtimeResultEventWindow(result, chunkWindow), '정밀 분석에서 낙상 후보가 해제됐습니다.');
                } else {
                    events.push({ type: 'normal_recovered', tone, icon: '✓', title: '정상 복귀', description: '최종 상태가 정상으로 돌아왔습니다.', riskPercent, showRisk: true });
                }
            }
            this.realtimeFeedLastTone = tone;
        }

        for (const event of events) this.appendRealtimeFeedEvent(event, blob, chunkId, chunkWindow, facialSnapshot);
        const timelineEntry = this.createLogEntry(result, { id: chunkId, timestamp: this.realtimeFeedClock(), chunkWindow, behaviorTransition });
        this.trackPostureTimeline(timelineEntry.posture, timelineEntry.currentPostureLabel || timelineEntry.postureLabel, result?.posture_score || 0, chunkId, timelineEntry.timestamp);
        if (events.length > 0) this.scrollLogToBottom();
    }
    public realtimeCurrentStatusMeta(): any {
        if (this.realtimeLocalFallState === 'confirmed') {
            return { tone: 'confirmed', icon: '🔴', label: '낙상 확정' };
        }
        if (['suspected', 'verifying'].includes(this.realtimeLocalFallState)) {
            return { tone: 'suspected', icon: '🟠', label: '낙상 의심' };
        }
        if (this.realtimeLocalFallState === 'review_required') {
            return { tone: 'review', icon: '🟡', label: '낙상 여부 확인 필요' };
        }
        if (this.realtimeLocalFallState === 'cleared') {
            return { tone: 'normal', icon: '🟢', label: '낙상 아님' };
        }
        if (this.analysisResult) {
            const tone = this.realtimeFeedTone(this.analysisResult);
            if (tone === 'confirmed') return { tone, icon: '🔴', label: '낙상 확정' };
            if (tone === 'suspected') return { tone, icon: '🟠', label: '낙상 의심' };
            if (tone === 'review') return { tone, icon: '🟡', label: '낙상 여부 확인 필요' };
            return { tone, icon: '🟢', label: '낙상 아님' };
        }
        if (this.realtimeVideoEnded && this.realtimeUploadedFinalizeRunning) {
            return { tone: 'review', icon: '⏳', label: '최종 낙상 판정 정리 중' };
        }
        if (this.realtimeUploadedAnalysisRunning) {
            return {
                tone: 'review',
                icon: '⏳',
                label: '낙상 여부 분석 중',
            };
        }
        if (this.realtimePrecisionAnalysisPending) return { tone: 'review', icon: '⏳', label: '낙상 여부 분석 중' };
        if (this.realtimeUploadedAnalysisError && !this.analysisResult) return { tone: 'review', icon: '!', label: '분석 실패' };
        if (this.realtimeVideoEnded && this.realtimeUploadedAnalysisComplete) {
            return { tone: 'review', icon: '🟡', label: '낙상 판단 결과 없음' };
        }
        return { tone: 'review', icon: '⏳', label: '낙상 여부 분석 대기' };
    }
    public realtimeCurrentStatusDescription(): string {
        const meta = this.realtimeCurrentStatusMeta();
        if (meta.tone === 'confirmed') return '낙상으로 판정되었습니다. 즉시 대상자의 상태를 확인하세요.';
        if (meta.tone === 'suspected') return '낙상 가능성이 감지되었습니다. 영상과 현장을 확인하세요.';
        if (meta.tone === 'normal') return '현재 분석 결과에서 낙상 징후가 확인되지 않았습니다.';
        if (meta.label === '분석 실패') return '분석 오류로 낙상 여부를 확인하지 못했습니다.';
        if (meta.label === '낙상 판단 결과 없음') return '완료된 분석에서 낙상 판정 결과를 확인하지 못했습니다.';
        if (meta.label.includes('분석 중') || meta.label.includes('정리 중')) return '최종 낙상 판정을 확인하고 있습니다.';
        return '낙상 여부를 확인하려면 분석을 시작하세요.';
    }
    public realtimeCurrentRiskPercent(): number {
        const serverRisk = this.analysisResult ? this.riskScorePercent(this.analysisResult) : 0;
        return ['suspected', 'verifying', 'confirmed', 'review_required'].includes(this.realtimeLocalFallState)
            ? Math.max(serverRisk, this.realtimeLocalFallRiskPercent)
            : serverRisk;
    }
    public realtimeAnalysisStageItems(): any[] {
        const poseReady = this.mpDetectedPoseCount > 0;
        const candidateLabel = this.realtimeLocalFallState === 'confirmed'
            ? `낙상 확인 ${this.realtimeLocalFallRiskPercent}%`
            : this.realtimeLocalFallState === 'review_required'
                ? '판단 보류 · 확인 필요'
            : this.realtimeLocalFallState === 'cleared'
                ? '후보 해제'
                : ['suspected', 'verifying'].includes(this.realtimeLocalFallState)
                    ? `낙상 후보 ${this.realtimeLocalFallRiskPercent}%`
                    : this.realtimeRfFallStatus === 'ready'
                        ? `정상 · ${this.realtimeRfFallLatencyMs}ms`
                        : this.realtimeRfFallStatus === 'error' ? '연결 오류' : '1.5초 버퍼 준비';
        const precisionLabel = this.realtimePrecisionAnalysisPending
            ? '검증 중'
            : this.realtimeUploadedAnalysisRunning ? '백그라운드 분석 중'
                : this.realtimeUploadedAnalysisComplete ? '최종 결과 완료' : '대기';
        return [
            { label: '즉시 화면', value: `Pose ${poseReady ? this.mpFps + 'fps' : '준비 중'} · ${this.realtimeFastPostureLabel}`, active: poseReady },
            { label: '환경 객체', value: this.realtimeEnvironmentSnapshotPending ? '확인 중' : `${this.realtimeEnvironmentSnapshotCount}개 · ${ENVIRONMENT_INFERENCE_FPS}fps`, active: this.realtimeEnvironmentSnapshotCount > 0 },
            { label: '실시간 RF', value: candidateLabel, active: this.realtimeRfFallStatus === 'ready' },
            { label: '서버 정밀 검증', value: precisionLabel, active: this.realtimePrecisionAnalysisPending || this.realtimeUploadedAnalysisRunning },
        ];
    }
    public realtimePostureStatusText(): string {
        if (this.realtimeFastPostureStatus === 'precision') return '정밀 확인';
        if (this.realtimeFastPostureStatus === 'estimated') return '실시간 추정';
        return '실시간 자세 분석 중';
    }
    public realtimeFeedCardClass(toneArg?: string): string {
        const tone = String(toneArg || this.realtimeCurrentStatusMeta().tone);
        if (tone === 'confirmed') return 'border-rose-500/60 bg-rose-950/40';
        if (tone === 'suspected') return 'border-orange-500/60 bg-orange-950/40';
        if (tone === 'review') return 'border-amber-500/60 bg-amber-950/30';
        return 'border-emerald-500/60 bg-emerald-950/30';
    }
    public realtimeFeedAccentClass(toneArg?: string): string {
        const tone = String(toneArg || this.realtimeCurrentStatusMeta().tone);
        if (tone === 'confirmed') return 'text-rose-300';
        if (tone === 'suspected') return 'text-orange-300';
        if (tone === 'review') return 'text-amber-300';
        return 'text-emerald-300';
    }
    public realtimeFeedIconClass(toneArg?: string): string {
        const tone = String(toneArg || 'normal');
        if (tone === 'confirmed') return 'border-rose-500/50 bg-rose-900/60 text-rose-200';
        if (tone === 'suspected') return 'border-orange-500/50 bg-orange-900/60 text-orange-200';
        if (tone === 'review') return 'border-amber-500/50 bg-amber-900/50 text-amber-200';
        return 'border-emerald-500/50 bg-emerald-900/50 text-emerald-200';
    }
    private scrollLogToBottom() { setTimeout(() => { const el = this.logScrollContainerRef?.nativeElement; if (el) el.scrollTo({ top: 0, behavior: 'smooth' }); }, 120); }
    private trackPostureTimeline(posture: string, label: string, score: number, chunkId: number, timestamp: string) {
        if (!posture || posture === 'unknown') return;
        this.postureTimeline.push({ time: timestamp, posture, label, score, chunkId });
        if (this.postureTimeline.length > 60) this.postureTimeline.shift();
        if (this.currentPosture && this.currentPosture !== posture) {
            this.postureTransitions.push({ time: timestamp, from: this.currentPosture, to: posture, fromLabel: this.currentPostureLabel || this.currentPosture, toLabel: label || posture });
            if (this.postureTransitions.length > 20) this.postureTransitions.shift();
            this.postureTransitionText = `${this.currentPostureLabel || this.currentPosture} → ${label || posture}`;
        } else this.postureTransitionText = '';
        this.currentPosture = posture;
        this.currentPostureLabel = label;
    }
    public recentPostureSummary(): { posture: string; label: string; count: number; pct: number }[] {
        const recent = this.postureTimeline.slice(-20);
        if (recent.length === 0) return [];
        const counts: Record<string, { label: string; count: number }> = {};
        for (const entry of recent) {
            if (!counts[entry.posture]) counts[entry.posture] = { label: entry.label, count: 0 };
            counts[entry.posture].count++;
        }
        return Object.entries(counts).map(([p, v]) => ({ posture: p, label: v.label, count: v.count, pct: Math.round((v.count / recent.length) * 100) })).sort((a, b) => b.count - a.count);
    }
    public isPostureFrequentChange(): boolean { if (this.postureTransitions.length < 3) return false; return this.postureTransitions.slice(-5).length >= 3; }
    public cleanupLogBlobUrls() { for (const entry of this.realtimeLogEntries) if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl); this.realtimeLogEntries = []; }
    public async clearAllLogs() { this.cleanupLogBlobUrls(); await this.service.render(); }

    public async openLogDetail(entry: any) {
        this.logDetailEntry = entry;
        this.logDetailOpen = true;
        this.logDetailVideoUrl = '';
        this.logDetailVideoLoading = false;
        this.logDetailVideoError = '';
        this.logDetailVideoRange = null;
        this.logDetailVideoMode = '';
        if (this.isAnalysisFailureLogEntry(entry)) {
            // 요청/런타임 실패는 모델 판정 결과가 아니므로 영상 구간과 정답 피드백을 만들지 않습니다.
        } else if (entry?.blobUrl) {
            this.logDetailVideoUrl = entry.blobUrl; this.logDetailVideoMode = 'blob';
        } else if (this.realtimeSourceMode === 'video' && this.realtimeUploadedVideoUrl) {
            this.logDetailVideoUrl = this.realtimeUploadedVideoUrl; this.logDetailVideoMode = 'upload';
            const startSec = Number(entry?.chunkWindow?.start_sec ?? entry?.chunkWindow?.startSec ?? 0);
            const endSecRaw = Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? startSec);
            this.logDetailVideoRange = { startSec: Math.max(0, startSec), endSec: Math.max(Math.max(0, startSec), endSecRaw) };
        } else if (this.selectedVideoUrl) {
            this.logDetailVideoUrl = this.selectedVideoUrl; this.logDetailVideoMode = 'upload';
            const startSec = Number(entry?.chunkWindow?.start_sec ?? entry?.chunkWindow?.startSec ?? 0);
            const endSecRaw = Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? startSec);
            this.logDetailVideoRange = { startSec: Math.max(0, startSec), endSec: Math.max(Math.max(0, startSec), endSecRaw) };
        } else if (this.analysisSavedName) {
            this.logDetailVideoLoading = true; this.logDetailVideoMode = 'upload';
            const startSec = Number(entry?.chunkWindow?.start_sec ?? entry?.chunkWindow?.startSec ?? 0);
            const endSecRaw = Number(entry?.chunkWindow?.end_sec ?? entry?.chunkWindow?.endSec ?? startSec);
            this.logDetailVideoRange = { startSec: Math.max(0, startSec), endSec: Math.max(Math.max(0, startSec), endSecRaw) };
            this.logDetailVideoUrl = `/wiz/api/page.dashboard/analysis_video?saved_name=${encodeURIComponent(this.analysisSavedName)}`;
        } else {
            this.logDetailVideoError = '부분 영상을 준비하지 못했습니다.';
        }
        this.logFeedbackStatus = ''; this.logFeedbackActualLabel = ''; this.logFeedbackPostureClass = ''; this.logFeedbackNote = ''; this.logFeedbackAmbiguityFlag = false; this.logFeedbackOcclusionFlag = false; this.logFeedbackShortClipFlag = false; this.logFeedbackStaticFilterLabel = ''; this.logFeedbackRetrainRequested = false;
        await this.service.render();
    }
    public isAnalysisFailureLogEntry(entry: any = this.logDetailEntry): boolean {
        const eventType = String(entry?.eventType || '');
        return eventType === 'upload_analysis_failed'
            || eventType === 'analysis_error'
            || eventType.endsWith('_failed');
    }
    public canFeedbackLogDetail(): boolean {
        if (!this.logDetailEntry || this.isAnalysisFailureLogEntry(this.logDetailEntry)) return false;
        const result = this.logDetailEntry?.result;
        return Boolean(result && typeof result === 'object' && Object.keys(result).length > 0);
    }
    public async closeLogDetail() {
        this.logDetailOpen = false; this.logDetailEntry = null; this.logDetailVideoUrl = ''; this.logDetailVideoLoading = false; this.logDetailVideoError = ''; this.logDetailVideoRange = null; this.logDetailVideoMode = '';
        await this.service.render();
    }
    public async onLogDetailVideoLoaded(videoEl?: HTMLVideoElement) {
        if (!this.logDetailOpen) return;
        if (videoEl && this.logDetailVideoMode === 'upload' && this.logDetailVideoRange) {
            try { videoEl.currentTime = this.logDetailVideoRange.startSec; } catch (e) { }
        }
        this.logDetailVideoLoading = false; this.logDetailVideoError = ''; await this.service.render();
    }
    public async onLogDetailVideoError() { if (!this.logDetailOpen) return; this.logDetailVideoLoading = false; this.logDetailVideoError = '부분 영상을 불러오지 못했습니다.'; await this.service.render(); }
    public onLogDetailVideoTimeUpdate(videoEl: HTMLVideoElement) {
        if (!videoEl || this.logDetailVideoMode !== 'upload' || !this.logDetailVideoRange) return;
        const endSec = Number(this.logDetailVideoRange.endSec || 0); const startSec = Number(this.logDetailVideoRange.startSec || 0);
        if (endSec > startSec && videoEl.currentTime >= endSec) { videoEl.pause(); try { videoEl.currentTime = startSec; } catch (e) { } }
    }
    public async deleteLogEntry(entry: any, event?: Event) {
        if (event) event.stopPropagation();
        const idx = this.realtimeLogEntries.indexOf(entry);
        if (idx >= 0) { if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl); this.realtimeLogEntries.splice(idx, 1); }
        const uploadIdx = this.uploadLogEntries.indexOf(entry);
        if (uploadIdx >= 0) this.uploadLogEntries.splice(uploadIdx, 1);
        if (this.logDetailEntry === entry) { this.logDetailOpen = false; this.logDetailEntry = null; this.logDetailVideoUrl = ''; this.logDetailVideoLoading = false; this.logDetailVideoError = ''; this.logDetailVideoRange = null; this.logDetailVideoMode = ''; }
        await this.service.render();
    }
    public logDetailRangeLabel(): string {
        const start = Number(this.logDetailEntry?.chunkWindow?.start_sec ?? this.logDetailEntry?.chunkWindow?.startSec ?? 0);
        const end = Number(this.logDetailEntry?.chunkWindow?.end_sec ?? this.logDetailEntry?.chunkWindow?.endSec ?? start);
        if (end <= start) return '';
        return `${this.formatChunkSecond(start)}s → ${this.formatChunkSecond(end)}s`;
    }
    public logDetailEventMarkers(): any[] {
        const result = this.logDetailEntry?.result || {};
        const shadow = this.fallEventShadow(result) || {};
        const interval = result?.fall_event_interval ?? shadow?.fall_event_interval;
        if (!Array.isArray(interval) || interval.length < 2) return [];
        const start = Number(interval[0]);
        const end = Number(interval[1]);
        if (![start, end].every(Number.isFinite) || end <= start) return [];
        const windows = Array.isArray(shadow?.event_windows) ? shadow.event_windows : [];
        const overlapping = windows.filter((item: any) => (
            Number(item?.end_sec || 0) >= start && Number(item?.start_sec || 0) <= end
        ));
        const impact = overlapping
            .filter((item: any) => item?.impact_candidate === true)
            .sort((left: any, right: any) => Number(right?.peak_vertical_acceleration || 0) - Number(left?.peak_vertical_acceleration || 0))[0];
        const low = overlapping
            .filter((item: any) => Array.isArray(item?.reason_codes) && item.reason_codes.includes('LOW_POSITION_PERSISTENCE'))
            .sort((left: any, right: any) => Number(right?.risk_score || 0) - Number(left?.risk_score || 0))[0];
        const markers = [
            { key: 'start', label: '낙상 시작', time: start },
            impact ? { key: 'impact', label: '충격 시점', time: (Number(impact.start_sec) + Number(impact.end_sec)) / 2 } : null,
            low ? { key: 'low', label: '최저 자세', time: Number(low.end_sec) } : null,
            { key: 'stable', label: '안정화', time: end },
        ].filter(Boolean);
        return markers.filter((item: any, index: number) => (
            markers.findIndex((candidate: any) => candidate && Math.abs(Number(candidate.time) - Number(item.time)) < 0.04) === index
        ));
    }
    public logDetailMarkerPosition(marker: any): number {
        const range = this.logDetailVideoRange;
        if (!range) return 0;
        return Math.max(0, Math.min(100, (
            (Number(marker?.time || 0) - range.startSec) / Math.max(0.001, range.endSec - range.startSec)
        ) * 100));
    }
    public chunkRiskLabel(entry: any): string {
        if (entry?.displayRiskLabel) return entry.displayRiskLabel;
        if (entry?.level === 'high') return '고위험';
        if (entry?.level === 'medium') return '주의';
        return '안정';
    }
    public async submitLogFeedback() {
        if (!this.logFeedbackStatus || !this.logDetailEntry) return;
        this.logFeedbackSubmitting = true;
        this.logFeedbackMessage = '';
        this.logFeedbackElapsedSec = 0;
        if (this.logFeedbackTimerHandle) clearInterval(this.logFeedbackTimerHandle);
        this.logFeedbackTimerHandle = setInterval(async () => { this.logFeedbackElapsedSec++; await this.service.render(); }, 1000);
        await this.service.render();
        try {
            const entry = this.logDetailEntry;
            const params: any = {
                saved_name: entry.result?.saved_name || this.analysisSavedName || '',
                predicted_label: entry.result?.fall_detected ? 'Y' : 'N',
                feedback_status: this.logFeedbackStatus,
                actual_label: this.logFeedbackActualLabel || '',
                note: this.logFeedbackNote || '',
                retrain: String(this.logFeedbackRetrainRequested && !this.logFeedbackStaticFilterLabel),
                posture_class: this.logFeedbackPostureClass || '',
                predicted_posture: entry.result?.posture_label || '',
                ambiguity_flag: String(this.logFeedbackAmbiguityFlag),
                occlusion_flag: String(this.logFeedbackOcclusionFlag),
                short_clip_flag: String(this.logFeedbackShortClipFlag),
                static_filter_label: this.logFeedbackStaticFilterLabel || '',
                track_id: this.feedbackTrackId(entry.result),
                event_start_sec: String(entry.chunkWindow?.start_sec ?? entry.chunkWindow?.startSec ?? 0),
                event_end_sec: String(entry.chunkWindow?.end_sec ?? entry.chunkWindow?.endSec ?? 0),
            };
            const { code, data } = await wiz.call('submit_analysis_feedback', params);
            if (code === 200) {
                entry.feedbackDone = true;
                const elapsed = this.logFeedbackElapsedSec;
                this.logFeedbackMessage = data?.learning_message || `✅ 피드백 저장 완료 (${elapsed}초)`;
                this.logFeedbackMessageType = 'success';
                if (data?.static_filter_validation_summary && this.prototypeInfo) {
                    this.prototypeInfo.static_object_filter_validation_intake = data.static_filter_validation_summary;
                }
                if (data?.manual_training_job_started) this.logFeedbackMessage = data?.learning_message || `✅ 피드백 저장 + 후보 학습 시작 (${elapsed}초)`;
            } else {
                this.logFeedbackMessage = data?.message || '저장 실패';
                this.logFeedbackMessageType = 'error';
            }
        } catch (e: any) {
            this.logFeedbackMessage = '오류: ' + (e.message || e);
            this.logFeedbackMessageType = 'error';
        }
        if (this.logFeedbackTimerHandle) { clearInterval(this.logFeedbackTimerHandle); this.logFeedbackTimerHandle = null; }
        this.logFeedbackSubmitting = false;
        await this.service.render();
    }
    public toggleLogFeedbackFlag(flag: string) { if (flag === 'ambiguity') this.logFeedbackAmbiguityFlag = !this.logFeedbackAmbiguityFlag; if (flag === 'occlusion') this.logFeedbackOcclusionFlag = !this.logFeedbackOcclusionFlag; if (flag === 'short_clip') this.logFeedbackShortClipFlag = !this.logFeedbackShortClipFlag; }
    public setLogFeedbackPostureClass(code: string) { this.logFeedbackPostureClass = this.logFeedbackPostureClass === code ? '' : code; }

    public onUploadPreviewLoadedMetadata() {
        this.drawUploadDetectionAtCurrentTime();
    }

    public getDetectionFrames(): any[] {
        const personFrames = this.analysisResult?.model_runtime?.detection_frames || [];
        if (Array.isArray(personFrames) && personFrames.length > 0) return personFrames;
        const environmentFrames = this.environmentObjectAnalysis()?.frames || [];
        return Array.isArray(environmentFrames)
            ? environmentFrames.map((frame: any) => ({ ...frame, detections: [] }))
            : [];
    }
    public async toggleDetectionReplay() { if (this.detectionReplayPlaying) this.stopDetectionReplay(); else this.startDetectionReplay(); await this.service.render(); }
    private startDetectionReplay() {
        const frames = this.getDetectionFrames();
        if (frames.length === 0) return;
        this.detectionReplayPlaying = true; this.detectionReplayIndex = 0; this.drawDetectionFrame(0);
        this.detectionReplayTimer = setInterval(() => {
            this.detectionReplayIndex++;
            if (this.detectionReplayIndex >= frames.length) { this.stopDetectionReplay(); return; }
            this.drawDetectionFrame(this.detectionReplayIndex);
        }, 200);
    }
    public stopDetectionReplay() { this.detectionReplayPlaying = false; if (this.detectionReplayTimer) { clearInterval(this.detectionReplayTimer); this.detectionReplayTimer = null; } }
    public seekDetectionReplay(idx: number) { const frames = this.getDetectionFrames(); if (idx >= 0 && idx < frames.length) { this.detectionReplayIndex = idx; this.drawDetectionFrame(idx); } }
    private polygonArea(points: number[][]): number {
        let area = 0;
        for (let index = 0; index < points.length; index++) {
            const next = (index + 1) % points.length;
            area += points[index][0] * points[next][1] - points[next][0] * points[index][1];
        }
        return Math.abs(area) / 2;
    }
    private pointLineDistance(point: number[], start: number[], end: number[]): number {
        const dx = end[0] - start[0]; const dy = end[1] - start[1];
        if (dx === 0 && dy === 0) return Math.hypot(point[0] - start[0], point[1] - start[1]);
        const t = Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / (dx * dx + dy * dy)));
        return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dy));
    }
    private simplifyPolygon(points: number[][], epsilon: number): number[][] {
        if (points.length <= 4) return points;
        const simplify = (values: number[][]): number[][] => {
            if (values.length <= 2) return values;
            let maxDistance = 0; let splitIndex = 0;
            for (let index = 1; index < values.length - 1; index++) {
                const distance = this.pointLineDistance(values[index], values[0], values[values.length - 1]);
                if (distance > maxDistance) { maxDistance = distance; splitIndex = index; }
            }
            if (maxDistance <= epsilon) return [values[0], values[values.length - 1]];
            return [...simplify(values.slice(0, splitIndex + 1)).slice(0, -1), ...simplify(values.slice(splitIndex))];
        };
        const result = simplify([...points, points[0]]).slice(0, -1);
        return result.length >= 3 ? result : points;
    }
    private smoothPolygon(points: number[][]): number[][] {
        if (points.length < 5) return points;
        return points.map((point, index) => {
            const previous = points[(index - 1 + points.length) % points.length];
            const next = points[(index + 1) % points.length];
            return [previous[0] * 0.2 + point[0] * 0.6 + next[0] * 0.2, previous[1] * 0.2 + point[1] * 0.6 + next[1] * 0.2];
        });
    }
    private environmentPointToCanvas(
        object: any,
        sourceX: number,
        sourceY: number,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ): { x: number; y: number } {
        const sourceWidth = Math.max(1, Number(object?.source_frame_width || object?.sourceFrameWidth || object?.coordinate_width || 1));
        const sourceHeight = Math.max(1, Number(object?.source_frame_height || object?.sourceFrameHeight || object?.coordinate_height || 1));
        return sourcePointToCanvasPoint(sourceX, sourceY, sourceWidth, sourceHeight, {
            x: offX,
            y: offY,
            width: sourceWidth * scaleX,
            height: sourceHeight * scaleY,
        });
    }

    private environmentDisplayGeometry(object: any): any {
        const transition = object?.geometry_transition;
        if (!transition || !Array.isArray(transition?.from_bbox)) return object;
        const durationMs = Math.max(1, Number(transition?.duration_ms || 300));
        const progress = Math.max(0, Math.min(1, (Date.now() - Number(transition?.started_at_ms || 0)) / durationMs));
        if (progress >= 1) return object;
        const targetBbox = this.environmentObjectBbox(object);
        const fromBbox = transition.from_bbox.map(Number);
        const bbox = targetBbox.map((value: number, index: number) => Number(fromBbox[index] ?? value) * (1 - progress) + value * progress);
        const targetSegments = Array.isArray(object?.segments) ? object.segments : [];
        const fromSegments = Array.isArray(transition?.from_segments) ? transition.from_segments : [];
        const segments = targetSegments.map((polygon: any, polygonIndex: number) => {
            const previous = fromSegments[polygonIndex];
            if (!Array.isArray(previous) || previous.length !== polygon.length) return polygon;
            return polygon.map((point: any, pointIndex: number) => {
                const oldPoint = previous[pointIndex];
                if (!Array.isArray(oldPoint) || oldPoint.length < 2) return point;
                return [
                    Number(oldPoint[0]) * (1 - progress) + Number(point[0]) * progress,
                    Number(oldPoint[1]) * (1 - progress) + Number(point[1]) * progress,
                ];
            });
        });
        return { ...object, bbox, x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3], segments };
    }

    private prepareEnvironmentDisplayPolygons(object: any, scaleX: number, scaleY: number, offX: number, offY: number, canvasArea: number): number[][][] {
        const polygons = Array.isArray(object?.smoothed_segments) && object.smoothed_segments.length > 0
            ? object.smoothed_segments
            : (Array.isArray(object?.segments) ? object.segments : []);
        const key = this.resolvedEnvironmentDisplayClass(object);
        const ratio = CLEAN_CLASS_MIN_AREA_RATIO[key]
            ?? (this.isEnvironmentHazard(object) ? CLEAN_OVERLAY_CONFIG.hazardMinAreaRatio : CLEAN_OVERLAY_CONFIG.minAreaRatio);
        const minArea = this.overlayMode === 'DEBUG_RAW' ? 0 : canvasArea * ratio;
        return polygons.map((polygon: any) => {
            if (!Array.isArray(polygon)) return [];
            const points = polygon
                .map((point: any) => {
                    const canvasPoint = this.environmentPointToCanvas(
                        object, Number(point?.[0]), Number(point?.[1]), scaleX, scaleY, offX, offY,
                    );
                    return [canvasPoint.x, canvasPoint.y];
                })
                .filter((point: number[]) => point.every(Number.isFinite));
            if (points.length < 3 || this.polygonArea(points) < minArea) return [];
            const smoothed = this.overlayMode === 'DEBUG_RAW' ? points : this.smoothPolygon(points);
            const diagonal = Math.hypot(
                Math.max(...smoothed.map(point => point[0])) - Math.min(...smoothed.map(point => point[0])),
                Math.max(...smoothed.map(point => point[1])) - Math.min(...smoothed.map(point => point[1])),
            );
            const epsilonRatio = CLEAN_THIN_STRUCTURE_CLASSES.has(key) ? 0.003 : CLEAN_OVERLAY_CONFIG.contourEpsilonRatio;
            const simplified = this.overlayMode === 'DEBUG_RAW' ? smoothed : this.simplifyPolygon(smoothed, Math.max(1.5, diagonal * epsilonRatio));
            const maxPoints = CLEAN_THIN_STRUCTURE_CLASSES.has(key) ? 240 : 160;
            if (simplified.length <= maxPoints) return simplified;
            const step = Math.ceil(simplified.length / maxPoints);
            return simplified.filter((_point, index) => index % step === 0).slice(0, maxPoints);
        }).filter((polygon: number[][]) => polygon.length >= 3);
    }
    private drawEnvironmentObjectSegmentation(
        ctx: CanvasRenderingContext2D,
        object: any,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ): boolean {
        const canvasRect = ctx.canvas.getBoundingClientRect();
        const canvasArea = Math.max(1, Number(canvasRect.width || ctx.canvas.clientWidth || 1) * Number(canvasRect.height || ctx.canvas.clientHeight || 1));
        const polygons = this.prepareEnvironmentDisplayPolygons(object, scaleX, scaleY, offX, offY, canvasArea);
        if (polygons.length === 0) return false;
        const reviewRequired = object?.review_required === true;
        const color = this.overlayMode === 'DEBUG_RAW'
            ? (reviewRequired ? '#fb7185' : '#22d3ee')
            : this.environmentClassColor(object);
        ctx.save();
        const displayOpacity = this.overlayMode === 'DEBUG_RAW' ? 1 : this.cleanObjectMaskOpacity(object);
        ctx.strokeStyle = this.colorWithAlpha(color, (this.overlayMode === 'DEBUG_RAW' ? 0.95 : 0.82) * displayOpacity);
        ctx.fillStyle = this.colorWithAlpha(color, (this.overlayMode === 'DEBUG_RAW' ? 0.36 : CLEAN_OVERLAY_CONFIG.maskAlpha) * displayOpacity);
        ctx.lineWidth = this.overlayMode === 'DEBUG_RAW' ? 2.5 : CLEAN_OVERLAY_CONFIG.outlineWidth;
        ctx.setLineDash([]);
        let drawn = false;
        for (const polygon of polygons) {
            if (!Array.isArray(polygon) || polygon.length < 3) continue;
            const points = polygon;
            if (points.length < 3) continue;
            ctx.beginPath();
            ctx.moveTo(points[0][0], points[0][1]);
            for (let index = 1; index < points.length; index++) ctx.lineTo(points[index][0], points[index][1]);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            drawn = true;
        }
        ctx.restore();
        if (!drawn) return false;
        if (this.overlayMode === 'DEBUG_RAW') this.drawEnvironmentObjectLabel(ctx, object, scaleX, scaleY, offX, offY, reviewRequired);
        return true;
    }
    private environmentShortLabel(item: any): string {
        const labels: Record<string, string> = {
            facility_bed: '침대', wheelchair: '휠체어', walker: '보행기', camera_tripod: '삼각대',
            camera: '카메라', iv_pole: '수액대', monitor: '모니터',
            equipment_cart: '장비 카트', medical_cart: '의료 카트', cart: '카트',
            air_conditioner: '에어컨', floor_mat: '매트', obstacle: '장애물', cable_on_floor: '전선',
            door: '문', curtain: '커튼', chair: '의자', table: '테이블', cabinet: '수납장',
            waste_bin: '쓰레기통', sink: '세면대', toilet: '화장실', refrigerator: '냉장고', couch: '소파',
        };
        return labels[this.resolvedEnvironmentDisplayClass(item)] || String(item?.label_ko || item?.label || '사물');
    }
    private drawCleanEnvironmentLabels(
        ctx: CanvasRenderingContext2D,
        objects: any[],
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ) {
        // 환경 객체 전용 화면과 Skeleton 결합 화면은 같은 환경 추론 결과와
        // 동일한 짧은 이름 라벨을 사용한다. 모드 차이는 사람 Skeleton 표시 여부뿐이다.
        if (!['ENVIRONMENT', 'SKELETON_ENVIRONMENT'].includes(this.overlayMode)) return;
        const occupied: { left: number; top: number; right: number; bottom: number }[] = [];
        let rendered = 0;
        for (const item of objects) {
            if (rendered >= CLEAN_OVERLAY_CONFIG.maxInlineLabels) break;
            if (String(item?.object_state || item?.state || '').toLowerCase() !== 'confirmed') continue;
            const bbox = this.environmentObjectBbox(item);
            if (bbox.length < 4) continue;
            const label = this.environmentShortLabel(item);
            ctx.save();
            ctx.font = '600 11px sans-serif';
            const width = ctx.measureText(label).width + 12;
            const height = 20;
            const center = this.environmentPointToCanvas(item, (bbox[0] + bbox[2]) / 2, bbox[1], scaleX, scaleY, offX, offY);
            const centerX = center.x;
            const baseY = center.y + 6;
            const cssWidth = Math.max(1, Number(ctx.canvas.getBoundingClientRect().width || ctx.canvas.clientWidth || 1));
            const cssHeight = Math.max(1, Number(ctx.canvas.getBoundingClientRect().height || ctx.canvas.clientHeight || 1));
            let left = Math.max(4, Math.min(cssWidth - width - 4, centerX - width / 2));
            let top = Math.max(34, baseY);
            const overlaps = (candidateTop: number) => occupied.some(area => left < area.right && left + width > area.left && candidateTop < area.bottom && candidateTop + height > area.top);
            let attempts = 0;
            while (overlaps(top) && attempts < 3) { top += height + 4; attempts += 1; }
            if (overlaps(top) || top + height > cssHeight - 44) { ctx.restore(); continue; }
            ctx.fillStyle = 'rgba(9,9,11,0.72)';
            ctx.strokeStyle = this.colorWithAlpha(this.environmentClassColor(item), 0.92);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.roundRect(left, top, width, height, 7);
            ctx.fill(); ctx.stroke();
            ctx.fillStyle = '#fafafa';
            ctx.fillText(label, left + 6, top + 14);
            ctx.restore();
            occupied.push({ left, top, right: left + width, bottom: top + height });
            rendered += 1;
        }
    }
    private drawEnvironmentObjectLabel(
        ctx: CanvasRenderingContext2D,
        object: any,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
        reviewRequired: boolean,
    ) {
        const bbox = this.environmentObjectBbox(object);
        if (bbox.length < 4) return;
        const start = this.environmentPointToCanvas(object, Number(bbox[0]), Number(bbox[1]), scaleX, scaleY, offX, offY);
        const x = start.x;
        const y = Math.max(0, start.y - 18);
        if (![x, y].every(Number.isFinite)) return;
        const state = String(object?.object_state || object?.state || 'unknown');
        const stableId = String(object?.stable_object_id ?? object?.track_id ?? '-');
        const label = `${object?.label_ko || object?.label || '사물'} ${Math.round(Number(object?.smoothed_confidence ?? object?.confidence ?? 0) * 100)}% · ${state} · ID ${stableId}${reviewRequired ? ' · 검토 필요' : ''}`;
        ctx.save();
        ctx.font = '11px sans-serif';
        const labelWidth = ctx.measureText(label).width + 8;
        ctx.fillStyle = reviewRequired ? 'rgba(251, 113, 133, 0.94)' : 'rgba(245, 158, 11, 0.94)';
        ctx.fillRect(x, y, labelWidth, 18);
        ctx.fillStyle = '#111827';
        ctx.fillText(label, x + 4, y + 13);
        ctx.restore();
    }
    private drawEnvironmentObjectOverlay(
        ctx: CanvasRenderingContext2D,
        object: any,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ): boolean {
        if (!this.environmentDisplayEnabled(object)) return false;
        const displayObject = this.environmentDisplayGeometry(object);
        let drawn = this.drawEnvironmentObjectSegmentation(ctx, displayObject, scaleX, scaleY, offX, offY);
        const bbox = this.environmentObjectBbox(displayObject);
        if (!drawn && bbox.length >= 4) {
            const start = this.environmentPointToCanvas(displayObject, bbox[0], bbox[1], scaleX, scaleY, offX, offY);
            const end = this.environmentPointToCanvas(displayObject, bbox[2], bbox[3], scaleX, scaleY, offX, offY);
            const x = start.x;
            const y = start.y;
            const width = end.x - start.x;
            const height = end.y - start.y;
            if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                ctx.save();
                ctx.strokeStyle = this.colorWithAlpha(this.environmentClassColor(object), 0.9);
                ctx.fillStyle = this.colorWithAlpha(this.environmentClassColor(object), 0.08);
                ctx.lineWidth = 2;
                ctx.setLineDash([]);
                ctx.fillRect(x, y, width, height);
                ctx.strokeRect(x, y, width, height);
                ctx.restore();
                drawn = true;
            }
        }
        if (this.overlayMode === 'DEBUG_RAW') {
            if (bbox.length >= 4) {
                const start = this.environmentPointToCanvas(displayObject, bbox[0], bbox[1], scaleX, scaleY, offX, offY);
                const end = this.environmentPointToCanvas(displayObject, bbox[2], bbox[3], scaleX, scaleY, offX, offY);
                ctx.save();
                ctx.strokeStyle = this.colorWithAlpha(this.environmentClassColor(object), 0.85);
                ctx.lineWidth = 1;
                ctx.setLineDash([5, 3]);
                ctx.strokeRect(start.x, start.y, end.x - start.x, end.y - start.y);
                ctx.restore();
            }
        }
        return drawn;
    }
    private drawRawEnvironmentDiagnostics(
        ctx: CanvasRenderingContext2D,
        result: any,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ): number {
        if (!this.isDebugOverlayMode()) return 0;
        const candidates = result?.environment_analysis?.raw_diagnostics?.candidates;
        if (!Array.isArray(candidates) || candidates.length === 0) return 0;
        const latestFrame = Math.max(...candidates.map((item: any) => Number(item?.frame_idx || 0)));
        let drawn = 0;
        for (const item of candidates.filter((candidate: any) => Number(candidate?.frame_idx || 0) === latestFrame)) {
            const bbox = Array.isArray(item?.bbox) ? item.bbox.map(Number) : [];
            if (bbox.length < 4 || !bbox.every(Number.isFinite)) continue;
            const finalUsed = item?.final_used === true;
            const dropped = String(item?.disposition || '') === 'filter_drop';
            const color = finalUsed ? '#22c55e' : (dropped ? '#9ca3af' : '#3b82f6');
            const label = `${item?.source_label || item?.canonical_label || 'raw'} ${Math.round(Number(item?.confidence || 0) * 100)}%${dropped ? ` · ${item?.drop_reason || 'filter drop'}` : ''}`;
            const x = bbox[0] * scaleX + offX;
            const y = bbox[1] * scaleY + offY;
            ctx.save();
            ctx.strokeStyle = color;
            ctx.lineWidth = finalUsed ? 2.5 : 1.5;
            ctx.setLineDash(dropped ? [4, 4] : []);
            ctx.strokeRect(x, y, (bbox[2] - bbox[0]) * scaleX, (bbox[3] - bbox[1]) * scaleY);
            ctx.font = '10px sans-serif';
            ctx.fillStyle = color;
            ctx.fillText(label, x + 2, Math.max(11, y - 3));
            ctx.restore();
            drawn += 1;
        }
        return drawn;
    }
    private environmentFrameScale(
        frame: any,
        fallbackWidth: number,
        fallbackHeight: number,
        scaleX: number,
        scaleY: number,
    ): { scaleX: number; scaleY: number } {
        const coordinateWidth = Number(frame?.coordinate_width || frame?.image_width || fallbackWidth || 1);
        const coordinateHeight = Number(frame?.coordinate_height || frame?.image_height || fallbackHeight || 1);
        return {
            scaleX: scaleX * Math.max(1, fallbackWidth) / Math.max(1, coordinateWidth),
            scaleY: scaleY * Math.max(1, fallbackHeight) / Math.max(1, coordinateHeight),
        };
    }
    private detectionEntityType(det: any, result?: any): string {
        const root = result || this.currentRootAnalysisResult() || this.analysisResult || {};
        const directType = String(det?.entity_type || '').trim().toLowerCase();
        const trackId = String(det?.track_id ?? det?.source_track_id ?? '').trim();
        const personLabel = String(det?.person_label || '').trim();
        const people = [
            ...(Array.isArray(root?.multi_person?.people) ? root.multi_person.people : []),
            ...(Array.isArray(root?.multi_person?.shadow_only_tracks) ? root.multi_person.shadow_only_tracks : []),
        ];
        const matchedPeople = people.filter((item: any) => {
            const sourceTrackIds = Array.isArray(item?.source_track_ids)
                ? item.source_track_ids.map((value: any) => String(value))
                : [];
            const aliases = Array.isArray(item?.alias_track_ids)
                ? item.alias_track_ids.map((value: any) => String(value))
                : [];
            return (trackId && (
                String(item?.track_id ?? '') === trackId
                || String(item?.primary_track_id ?? '') === trackId
                || sourceTrackIds.includes(trackId)
                || aliases.includes(trackId)
            )) || (personLabel && String(item?.person_label || '') === personLabel);
        });
        const matched = matchedPeople.find((item: any) => item?.is_logical_primary !== false) || matchedPeople[0];
        if (matched) return this.personEntityType(matched);
        const selectedType = String(root?.entity_classification?.entity_type || '').trim().toLowerCase();
        return selectedType || directType || 'unknown';
    }
    private matchedLogicalPerson(det: any, result?: any): any {
        const root = result || this.currentRootAnalysisResult() || {};
        const trackId = String(det?.track_id ?? det?.source_track_id ?? '').trim();
        const personLabel = String(det?.person_label || '').trim();
        const people = Array.isArray(root?.multi_person?.people) ? root.multi_person.people : [];
        const matches = people.filter((item: any) => {
            const sourceTrackIds = Array.isArray(item?.source_track_ids)
                ? item.source_track_ids.map((value: any) => String(value))
                : [];
            const aliases = Array.isArray(item?.alias_track_ids)
                ? item.alias_track_ids.map((value: any) => String(value))
                : [];
            return (trackId && (
                String(item?.track_id ?? '') === trackId
                || String(item?.primary_track_id ?? '') === trackId
                || sourceTrackIds.includes(trackId)
                || aliases.includes(trackId)
            )) || (personLabel && String(item?.person_label || '') === personLabel);
        });
        return matches.find((item: any) => item?.is_logical_primary !== false) || matches[0] || null;
    }
    private isRenderableLogicalDetection(det: any, result?: any): boolean {
        return this.realtimeDetectionDecision(det, result).renderable;
    }
    private approvedLogicalPersonBoxes(result?: any): { x1: number; y1: number; x2: number; y2: number }[] {
        const root = result || this.currentRootAnalysisResult() || {};
        const frames = root?.model_runtime?.detection_frames || [];
        const frame = Array.isArray(frames) && frames.length > 0 ? frames[frames.length - 1] : null;
        const width = Math.max(1, Number(frame?.coordinate_width || root?.video_meta?.width || this.webcamVideoRef?.nativeElement?.videoWidth || 1));
        const height = Math.max(1, Number(frame?.coordinate_height || root?.video_meta?.height || this.webcamVideoRef?.nativeElement?.videoHeight || 1));
        return (Array.isArray(frame?.detections) ? frame.detections : [])
            // 로컬 Pose 매칭용 사람 영역은 Skeleton 안전 게이트의 결과에 의존하면 안 된다.
            // 서버가 사람으로 판정했거나 유효한 사람 Pose가 있으면 실제 사람 보호 영역으로 사용한다.
            .filter((det: any) => (
                this.detectionEntityType(det, root) === 'person'
                || this.realtimePoseEvidence(det).valid
            ))
            .map((det: any) => {
                const x1 = Number(det?.x1 || 0) / width;
                const y1 = Number(det?.y1 || 0) / height;
                const x2 = Number(det?.x2 || 0) / width;
                const y2 = Number(det?.y2 || 0) / height;
                const padX = Math.max(0.02, (x2 - x1) * 0.12);
                const padY = Math.max(0.02, (y2 - y1) * 0.12);
                return {
                    x1: Math.max(0, x1 - padX), y1: Math.max(0, y1 - padY),
                    x2: Math.min(1, x2 + padX), y2: Math.min(1, y2 + padY),
                };
            });
    }
    private protectPeopleFromEnvironmentMask(
        ctx: CanvasRenderingContext2D,
        result: any,
        coordinateWidth: number,
        coordinateHeight: number,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
    ) {
        const boxes = this.approvedLogicalPersonBoxes(result);
        if (boxes.length === 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        for (const box of boxes) {
            const x = box.x1 * coordinateWidth * scaleX + offX;
            const y = box.y1 * coordinateHeight * scaleY + offY;
            const width = (box.x2 - box.x1) * coordinateWidth * scaleX;
            const height = (box.y2 - box.y1) * coordinateHeight * scaleY;
            if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) continue;
            ctx.fillRect(x, y, width, height);
        }
        ctx.restore();
    }
    private drawFilteredPersonCandidate(
        ctx: CanvasRenderingContext2D,
        det: any,
        scaleX: number,
        scaleY: number,
        offX: number,
        offY: number,
        result?: any,
    ) {
        const x = Number(det?.x1 || 0) * scaleX + offX;
        const y = Number(det?.y1 || 0) * scaleY + offY;
        const w = (Number(det?.x2 || 0) - Number(det?.x1 || 0)) * scaleX;
        const h = (Number(det?.y2 || 0) - Number(det?.y1 || 0)) * scaleY;
        if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return;
        const person = this.matchedLogicalPerson(det, result || this.currentRootAnalysisResult());
        const ghost = person?.ghost_suppression || person?.result?.ghost_suppression;
        const label = ghost?.suppressed
            ? `${det?.person_label || person?.person_label || '검출 후보'} · Ghost ${Math.round(Number(ghost?.ghost_score || 0) * 100)}% · ${ghost?.equipment_label || '장비'} overlap ${Math.round(Number(ghost?.equipment_overlap_iou || 0) * 100)}% · 제외`
            : `${det?.person_label || '검출 후보'} · 사람 제외`;
        ctx.save();
        ctx.strokeStyle = '#f59e0b';
        ctx.fillStyle = '#f59e0b';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + w, y + h);
        ctx.moveTo(x + w, y);
        ctx.lineTo(x, y + h);
        ctx.stroke();
        ctx.font = '10px sans-serif';
        ctx.fillText(label, x + 2, Math.max(11, y - 4));
        ctx.restore();
    }
    private drawDetectionFrame(idx: number) {
        const frames = this.getDetectionFrames(); const frame = frames[idx]; if (!frame) return;
        const canvasEl = this.detCanvasRef?.nativeElement; if (!canvasEl) return;
        let ctx = canvasEl.getContext('2d'); if (!ctx) return;
        const objectCanvasEl = this.environmentMaskCanvasRef?.nativeElement;
        let objectCtx = objectCanvasEl?.getContext('2d') || null;
        const rect = canvasEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            ctx = resizeCanvasForDisplay(canvasEl, rect.width, rect.height).context;
            if (objectCanvasEl) objectCtx = resizeCanvasForDisplay(objectCanvasEl, rect.width, rect.height).context;
        }
        if (!ctx) return;
        const videoEl = this.uploadPreviewVideoRef?.nativeElement;
        const personTransform = this.overlayCoordinateTransform(
            this.currentRootAnalysisResult(), frame, canvasEl, videoEl,
        );
        const vidW = personTransform.sourceWidth;
        const vidH = personTransform.sourceHeight;
        const offX = personTransform.offsetX;
        const offY = personTransform.offsetY;
        const scaleX = personTransform.scaleX;
        const scaleY = personTransform.scaleY;
        this.clearCanvasFully(ctx, canvasEl);
        if (objectCtx && objectCanvasEl) this.clearCanvasFully(objectCtx, objectCanvasEl);
        if (!this.canShowSkeletonOverlay()) return;
        const detections = frame.detections || [];
        const showPerson = this.canShowPersonOverlay();
        const showDebug = this.isDebugOverlayMode();
        for (const det of detections) {
            if (this.detectionEntityType(det) === 'object') {
                if (showDebug) this.drawFilteredPersonCandidate(ctx, det, scaleX, scaleY, offX, offY, this.analysisResult);
                continue;
            }
            if (!showPerson) continue;
            if (showDebug) {
                ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 2;
                const x = det.x1 * scaleX + offX; const y = det.y1 * scaleY + offY; const w = (det.x2 - det.x1) * scaleX; const h = (det.y2 - det.y1) * scaleY;
                ctx.strokeRect(x, y, w, h);
                ctx.fillStyle = '#22d3ee'; ctx.font = '10px sans-serif'; ctx.fillText(`${(det.conf * 100).toFixed(0)}%`, x, y - 4);
            }
            const kps = det.keypoints;
            if (kps && kps.length >= 17) {
                const replaceSkeletonFace = this.replaceSkeletonFaceWithExpressionFeatures();
                this.drawCocoFacePrivacyVeil(ctx, det, kps, scaleX, scaleY, offX, offY);
                ctx.save();
                ctx.shadowColor = 'rgba(0,0,0,0.92)';
                ctx.shadowBlur = 4;
                for (const [i, j] of COCO_SKELETON_PAIRS) {
                    if (replaceSkeletonFace && (COCO_FACE_KEYPOINTS.has(i) || COCO_FACE_KEYPOINTS.has(j))) continue;
                    const a = kps[i], b = kps[j];
                    if (!this.cocoKeypointVisible(a, i) || !this.cocoKeypointVisible(b, j)) continue;
                    const alpha = Math.min(this.cocoKeypointAlpha(a), this.cocoKeypointAlpha(b));
                    ctx.beginPath(); ctx.moveTo(a[0] * scaleX + offX, a[1] * scaleY + offY); ctx.lineTo(b[0] * scaleX + offX, b[1] * scaleY + offY); ctx.strokeStyle = this.colorWithAlpha(this.skeletonLineColor(i, j), alpha); ctx.lineWidth = Math.max(1.8, Math.min(3.6, Math.min(canvasEl.width, canvasEl.height) / 240)); ctx.stroke();
                }
                for (let k = 0; k < Math.min(kps.length, 17); k++) {
                    if (replaceSkeletonFace && COCO_FACE_KEYPOINTS.has(k)) continue;
                    const kp = kps[k];
                    if (!this.cocoKeypointVisible(kp, k)) continue;
                    ctx.beginPath(); ctx.arc(kp[0] * scaleX + offX, kp[1] * scaleY + offY, Math.max(3, Math.min(5.3, Math.min(canvasEl.width, canvasEl.height) / 120)), 0, 2 * Math.PI); ctx.fillStyle = this.colorWithAlpha(COCO_KP_COLORS[k] || '#67e8f9', this.cocoKeypointAlpha(kp)); ctx.fill();
                }
                ctx.restore();
            }
        }
        const normalizedEnvironmentResult = this.normalizeEnvironmentAnalysis(
            this.currentRootAnalysisResult(), 'final_analysis', Number(frame?.time_sec || 0),
        );
        const targetTime = Number(frame?.time_sec ?? frame?.time ?? 0);
        const environmentFrame = this.environmentFrameForTimestamp(
            normalizedEnvironmentResult?.environment_analysis, targetTime,
        );
        if (environmentFrame) {
            const inferenceObjects = environmentFrame?.detections || [];
            const displayObjects = this.canShowEnvironmentMaskOverlay()
                ? this.environmentDisplayObjects(inferenceObjects)
                : [];
            const environmentTransform = this.overlayCoordinateTransform(
                normalizedEnvironmentResult, environmentFrame, canvasEl, videoEl,
            );
            const renderedObjects: any[] = [];
            if (objectCtx) {
                this.clipToRenderedVideoRect(objectCtx, environmentTransform.renderedVideoRect);
                this.drawRawEnvironmentDiagnostics(
                    objectCtx, normalizedEnvironmentResult,
                    environmentTransform.scaleX, environmentTransform.scaleY,
                    environmentTransform.offsetX, environmentTransform.offsetY,
                );
            }
            for (const object of displayObjects) {
                if (objectCtx && this.drawEnvironmentObjectOverlay(
                    objectCtx, object,
                    environmentTransform.scaleX, environmentTransform.scaleY,
                    environmentTransform.offsetX, environmentTransform.offsetY,
                )) renderedObjects.push(object);
            }
            this.latestCleanRenderedObjects = renderedObjects;
            this.logOverlayComparison(inferenceObjects, renderedObjects);
            if (objectCtx) this.drawCleanEnvironmentLabels(
                objectCtx, displayObjects,
                environmentTransform.scaleX, environmentTransform.scaleY,
                environmentTransform.offsetX, environmentTransform.offsetY,
            );
            if (objectCtx) {
                this.protectPeopleFromEnvironmentMask(
                    objectCtx, this.analysisResult,
                    environmentTransform.sourceWidth, environmentTransform.sourceHeight,
                    environmentTransform.scaleX, environmentTransform.scaleY,
                    environmentTransform.offsetX, environmentTransform.offsetY,
                );
                objectCtx.restore();
            }
        }
    }
    private clearCanvasFully(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, caller: string = 'overlay-renderer') {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.shadowBlur = 0;
        ctx.filter = 'none';
        ctx.setLineDash([]);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.shadowBlur = 0;
        ctx.filter = 'none';
        ctx.setLineDash([]);
        if (canvas.matches?.('[data-mp-pose-canvas]')) {
            this.realtimeFastPoseCounters.clear += 1;
            if (this.realtimeDeveloperMode) console.debug('[SKELETON_CANVAS] clear', {
                caller,
                frameId: this.mpInFlightFrame?.frameId ?? -1,
                time: performance.now(),
            });
        }
    }
    private clearRealtimeServerDetectionOverlay() {
        if (this.realtimeOverlayClearTimer) {
            clearTimeout(this.realtimeOverlayClearTimer);
            this.realtimeOverlayClearTimer = null;
        }
        this.clearRealtimeSkeletonOverlay();
        const objectCanvasEl = this.realtimeEnvironmentMaskCanvasRef?.nativeElement;
        const objectCtx = objectCanvasEl?.getContext('2d') || null;
        if (objectCtx && objectCanvasEl) this.clearCanvasFully(objectCtx, objectCanvasEl);
        const debugCanvasEl = this.realtimeDebugCanvasRef?.nativeElement;
        const debugCtx = debugCanvasEl?.getContext('2d') || null;
        if (debugCtx && debugCanvasEl) this.clearCanvasFully(debugCtx, debugCanvasEl);
    }
    private clearRealtimeSkeletonOverlay() {
        const canvasEl = this.realtimeBboxCanvasRef?.nativeElement;
        if (!canvasEl) return;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;
        this.clearCanvasFully(ctx, canvasEl);
        this.realtimePerfStats.renderedSkeletons = 0;
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            rendered_skeleton_count: 0,
        };
    }
    private scheduleRealtimeServerDetectionOverlay(result: any) {
        this.realtimeOverlayPendingResult = result;
        if (this.realtimeOverlayRafId) return;
        const draw = (now: number) => {
            const minInterval = 1000 / this.realtimePresetConfig().overlayFps;
            if (now - this.realtimeLastOverlayDrawAt < minInterval) {
                this.realtimeOverlayRafId = requestAnimationFrame(draw);
                return;
            }
            this.realtimeOverlayRafId = 0;
            this.realtimeLastOverlayDrawAt = now;
            const pendingResult = this.realtimeOverlayPendingResult;
            this.realtimeOverlayPendingResult = null;
            if (pendingResult) this.drawRealtimeServerDetectionOverlay(pendingResult);
        };
        this.realtimeOverlayRafId = requestAnimationFrame(draw);
    }
    private drawRealtimeServerDetectionOverlay(result: any) {
        const overlayStartedAt = performance.now();
        const environmentSnapshotOnly = result?._environment_snapshot_only === true;
        // 저지연 화면용 MediaPipe Pose는 4초 정밀 분석 결과와 독립적으로 유지합니다.
        // 서버 Skeleton은 로컬 Pose가 준비되지 않았을 때만 fallback으로 사용합니다.
        const clientPoseOwnsSkeleton = this.clientPoseOwnsRealtimeSkeleton();
        const frames = result?.model_runtime?.detection_frames || [];
        const frame = frames.length > 0 ? frames[frames.length - 1] : null;
        const normalizedEnvironmentResult = this.normalizeEnvironmentAnalysis(
            result,
            environmentSnapshotOnly ? 'realtime' : 'final_analysis',
            Number(result?.source_timestamp || 0),
        );
        const environmentFrame = this.environmentFrameForTimestamp(
            normalizedEnvironmentResult?.environment_analysis,
            this.realtimeSourceTime(),
        );
        const canvasEl = this.realtimeBboxCanvasRef?.nativeElement;
        const objectCanvasEl = this.realtimeEnvironmentMaskCanvasRef?.nativeElement;
        const debugCanvasEl = this.realtimeDebugCanvasRef?.nativeElement;
        if (!canvasEl) return;
        let ctx = canvasEl.getContext('2d');
        let objectCtx = objectCanvasEl?.getContext('2d') || null;
        let debugCtx = debugCanvasEl?.getContext('2d') || null;
        if (!ctx) return;
        const rect = canvasEl.getBoundingClientRect();
        const targetW = Math.max(1, Math.round(rect.width || canvasEl.clientWidth || 1));
        const targetH = Math.max(1, Math.round(rect.height || canvasEl.clientHeight || 1));
        ctx = resizeCanvasForDisplay(canvasEl, targetW, targetH).context;
        if (objectCanvasEl) objectCtx = resizeCanvasForDisplay(objectCanvasEl, targetW, targetH).context;
        if (debugCanvasEl) debugCtx = resizeCanvasForDisplay(debugCanvasEl, targetW, targetH).context;
        if (!ctx) return;
        if (!environmentSnapshotOnly) this.clearCanvasFully(ctx, canvasEl);
        if (objectCtx && objectCanvasEl) this.clearCanvasFully(objectCtx, objectCanvasEl);
        if (debugCtx && debugCanvasEl) this.clearCanvasFully(debugCtx, debugCanvasEl);
        if (!this.canShowSkeletonOverlay()) {
            this.realtimeDiagnostics = {
                ...this.realtimeDiagnostics,
                rendered_skeleton_count: 0,
                rendered_environment_object_count: 0,
                overlay_disposition: 'hidden_by_mode',
                display_mode: this.overlayMode,
                display_mode_label: this.overlayModeLabel(),
                render_skeleton_called: false,
                render_environment_called: false,
            };
            return;
        }
        if (!frame && !environmentFrame) return;
        const videoEl = this.webcamVideoRef?.nativeElement;
        const coordinateTransform = this.overlayCoordinateTransform(
            result, frame || environmentFrame, canvasEl, videoEl,
        );
        const vidW = coordinateTransform.coordinateWidth;
        const vidH = coordinateTransform.coordinateHeight;
        const offX = coordinateTransform.offsetX;
        const offY = coordinateTransform.offsetY;
        const scaleX = coordinateTransform.scaleX;
        const scaleY = coordinateTransform.scaleY;
        const environmentTransform = environmentFrame
            ? this.overlayCoordinateTransform(normalizedEnvironmentResult, environmentFrame, canvasEl, videoEl)
            : coordinateTransform;
        const detections = !environmentSnapshotOnly && this.canShowPersonOverlay() && !clientPoseOwnsSkeleton
            ? (frame?.detections || []) : [];
        let renderedSkeletons = clientPoseOwnsSkeleton
            ? Number(this.mpDetectedPoseCount || 0)
            : (environmentSnapshotOnly ? Number(this.realtimeDiagnostics?.rendered_skeleton_count || 0) : 0);
        let removedSkeletons = 0;
        const skeletonExclusionReasons: Record<string, number> = {};
        const renderedLogicalPersonIds = new Set<string>();
        for (const det of detections) {
            const decision = this.realtimeDetectionDecision(det, result);
            if (!decision.renderable) {
                removedSkeletons += 1;
                skeletonExclusionReasons[decision.reason] = Number(skeletonExclusionReasons[decision.reason] || 0) + 1;
                if (this.isDebugOverlayMode() && debugCtx) {
                    this.drawFilteredPersonCandidate(debugCtx, det, scaleX, scaleY, offX, offY, result);
                }
                continue;
            }
            const logicalPerson = this.matchedLogicalPerson(det, result);
            const logicalPersonId = String(
                logicalPerson?.logical_person_id
                || logicalPerson?.primary_track_id
                || logicalPerson?.track_id
                || det?.track_id
                || det?.source_track_id
                || ''
            );
            if (logicalPersonId && renderedLogicalPersonIds.has(logicalPersonId)) {
                removedSkeletons += 1;
                skeletonExclusionReasons.duplicate_logical_person = Number(
                    skeletonExclusionReasons.duplicate_logical_person || 0
                ) + 1;
                continue;
            }
            const labelColor = det?.is_primary ? '#22c55e' : '#22d3ee';
            const kps = det.keypoints;
            if (!kps || kps.length < 17) continue;
            if (logicalPersonId) renderedLogicalPersonIds.add(logicalPersonId);
            renderedSkeletons += 1;
            const replaceSkeletonFace = this.replaceSkeletonFaceWithExpressionFeatures();
            this.drawCocoFacePrivacyVeil(ctx, det, kps, scaleX, scaleY, offX, offY);
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.92)';
            ctx.shadowBlur = 4;
            for (const [i, j] of COCO_SKELETON_PAIRS) {
                if (replaceSkeletonFace && (COCO_FACE_KEYPOINTS.has(i) || COCO_FACE_KEYPOINTS.has(j))) continue;
                const a = kps[i], b = kps[j];
                if (!this.cocoKeypointVisible(a, i) || !this.cocoKeypointVisible(b, j)) continue;
                ctx.beginPath();
                ctx.moveTo(Number(a[0] || 0) * scaleX + offX, Number(a[1] || 0) * scaleY + offY);
                ctx.lineTo(Number(b[0] || 0) * scaleX + offX, Number(b[1] || 0) * scaleY + offY);
                const alpha = Math.min(this.cocoKeypointAlpha(a), this.cocoKeypointAlpha(b));
                ctx.strokeStyle = this.colorWithAlpha(this.skeletonLineColor(i, j), alpha);
                ctx.lineWidth = Math.max(1.8, Math.min(3.6, Math.min(canvasEl.width, canvasEl.height) / 240));
                ctx.stroke();
            }
            for (let k = 0; k < Math.min(kps.length, 17); k++) {
                if (replaceSkeletonFace && COCO_FACE_KEYPOINTS.has(k)) continue;
                const kp = kps[k];
                if (!this.cocoKeypointVisible(kp, k)) continue;
                ctx.beginPath();
                ctx.arc(Number(kp[0] || 0) * scaleX + offX, Number(kp[1] || 0) * scaleY + offY, Math.max(3, Math.min(5.3, Math.min(canvasEl.width, canvasEl.height) / 120)), 0, 2 * Math.PI);
                ctx.fillStyle = this.colorWithAlpha(COCO_KP_COLORS[k] || '#67e8f9', this.cocoKeypointAlpha(kp));
                ctx.fill();
            }
            ctx.restore();
            if (det?.person_label && this.isDebugOverlayMode()) {
                const x = Number(det.x1 || 0) * scaleX + offX + 3;
                const y = Math.max(12, Number(det.y1 || 0) * scaleY + offY - 4);
                const labelCtx = debugCtx || ctx;
                labelCtx.font = '11px sans-serif';
                labelCtx.fillStyle = labelColor;
                labelCtx.fillText(String(det.person_label), x, y);
            }
        }
        const inferenceObjects = environmentFrame?.detections || [];
        const displayObjects = this.canShowEnvironmentMaskOverlay()
            ? this.environmentDisplayObjects(inferenceObjects)
            : [];
        const renderedObjects: any[] = [];
        if (objectCtx) this.clipToRenderedVideoRect(objectCtx, environmentTransform.renderedVideoRect);
        if (debugCtx) this.clipToRenderedVideoRect(debugCtx, environmentTransform.renderedVideoRect);
        if (debugCtx) {
            this.drawRawEnvironmentDiagnostics(
                debugCtx, normalizedEnvironmentResult,
                environmentTransform.scaleX, environmentTransform.scaleY,
                environmentTransform.offsetX, environmentTransform.offsetY,
            );
        }
        for (const object of displayObjects) {
            if (objectCtx && this.drawEnvironmentObjectOverlay(
                objectCtx, object,
                environmentTransform.scaleX, environmentTransform.scaleY,
                environmentTransform.offsetX, environmentTransform.offsetY,
            )) renderedObjects.push(object);
        }
        this.latestCleanRenderedObjects = renderedObjects;
        this.logOverlayComparison(inferenceObjects, renderedObjects);
        if (objectCtx) this.drawCleanEnvironmentLabels(
            objectCtx, displayObjects,
            environmentTransform.scaleX, environmentTransform.scaleY,
            environmentTransform.offsetX, environmentTransform.offsetY,
        );
        if (objectCtx) {
            this.protectPeopleFromEnvironmentMask(
                objectCtx, result,
                environmentTransform.sourceWidth, environmentTransform.sourceHeight,
                environmentTransform.scaleX, environmentTransform.scaleY,
                environmentTransform.offsetX, environmentTransform.offsetY,
            );
        }
        if (objectCtx) objectCtx.restore();
        if (debugCtx) debugCtx.restore();
        if (!environmentSnapshotOnly) {
            this.realtimePerfStats.renderedSkeletons = renderedSkeletons;
            this.realtimePerfStats.ghostSkeletonsRemoved = removedSkeletons;
        }
        this.realtimeDiagnostics = {
            ...this.realtimeDiagnostics,
            video_current_time: this.realtimeSourceTime(),
            rendered_skeleton_count: renderedSkeletons,
            safe_skeleton_count: clientPoseOwnsSkeleton
                ? Number(this.mpDetectedPoseCount || 0)
                : renderedLogicalPersonIds.size,
            skeleton_exclusion_reasons: clientPoseOwnsSkeleton
                ? (this.realtimeDiagnostics?.skeleton_exclusion_reasons || {})
                : skeletonExclusionReasons,
            rendered_environment_object_count: renderedObjects.length,
            filtered_person_count: environmentSnapshotOnly
                ? Number(this.realtimeDiagnostics?.filtered_person_count || 0) : removedSkeletons,
            filtered_object_count: Math.max(0, inferenceObjects.length - renderedObjects.length),
            stale_overlay_discard_count: this.realtimeDelayedResultCount,
            overlay_disposition: Number(this.realtimeDiagnostics?.video_to_result_lag_ms || 0) > 1500
                ? 'delayed_visible' : 'visible',
            coordinate_space: coordinateTransform.coordinateSpace,
            source_width: coordinateTransform.sourceWidth,
            source_height: coordinateTransform.sourceHeight,
            coordinate_width: coordinateTransform.coordinateWidth,
            coordinate_height: coordinateTransform.coordinateHeight,
            letterbox_offset_x: coordinateTransform.letterboxOffsetX,
            letterbox_offset_y: coordinateTransform.letterboxOffsetY,
            overlay_scale_x: coordinateTransform.scaleX,
            overlay_scale_y: coordinateTransform.scaleY,
            display_mode: this.overlayMode,
            display_mode_label: this.overlayModeLabel(),
            render_skeleton_called: clientPoseOwnsSkeleton || (!environmentSnapshotOnly && this.canShowPersonOverlay()),
            skeleton_source: clientPoseOwnsSkeleton ? 'client-mediapipe' : 'server-yolo-pose',
            render_environment_called: this.canShowEnvironmentMaskOverlay(),
            input_skeleton_count: detections.length,
            input_environment_count: inferenceObjects.length,
            skeleton_canvas_visible: this.canShowPersonOverlay(),
            environment_canvas_visible: this.canShowEnvironmentMaskOverlay(),
            debug_canvas_visible: this.isDebugOverlayMode(),
            skeleton_canvas_z_index: 5,
            environment_canvas_z_index: 2,
            debug_canvas_z_index: 6,
            source_chunk_id: String(result?.chunk_id || result?.realtime_chunk_id || ''),
            current_chunk_id: String(this.realtimeLatestAppliedChunkId || ''),
            coordinate_transform_valid: [
                coordinateTransform.scaleX, coordinateTransform.scaleY,
                coordinateTransform.offsetX, coordinateTransform.offsetY,
            ].every(Number.isFinite),
            environment_coordinate_space: environmentTransform.coordinateSpace,
            environment_source_frame_size: `${environmentTransform.sourceWidth}x${environmentTransform.sourceHeight}`,
            rendered_video_rect: environmentTransform.renderedVideoRect,
            canvas_css_size: `${environmentTransform.canvasCssWidth}x${environmentTransform.canvasCssHeight}`,
            canvas_backing_size: `${canvasEl.width}x${canvasEl.height}`,
            device_pixel_ratio: environmentTransform.devicePixelRatio,
        };
        if (!environmentSnapshotOnly && renderedSkeletons > 0) {
            if (this.realtimeOverlayClearTimer) clearTimeout(this.realtimeOverlayClearTimer);
            this.realtimeOverlayClearTimer = setTimeout(() => {
                this.realtimeOverlayClearTimer = null;
                const currentVideo = this.webcamVideoRef?.nativeElement;
                if (this.realtimeSourceMode === 'video' && (currentVideo?.ended || currentVideo?.paused)) return;
                this.clearRealtimeSkeletonOverlay();
            }, this.REALTIME_SKELETON_TTL_MS);
        }
        this.recordRealtimePerf('overlay', performance.now() - overlayStartedAt);
    }
    private overlayCoordinateTransform(
        result: any,
        frame: any,
        canvasEl: HTMLCanvasElement,
        videoEl?: HTMLVideoElement,
    ): {
        coordinateSpace: string;
        sourceWidth: number;
        sourceHeight: number;
        coordinateWidth: number;
        coordinateHeight: number;
        letterboxOffsetX: number;
        letterboxOffsetY: number;
        offsetX: number;
        offsetY: number;
        scaleX: number;
        scaleY: number;
        renderedVideoRect: RenderedVideoRect;
        canvasCssWidth: number;
        canvasCssHeight: number;
        devicePixelRatio: number;
    } {
        const metadata = result?.client_coordinate_metadata || result?.coordinate_metadata || {};
        const coordinateSpace = String(metadata?.coordinate_space || frame?.coordinate_space || 'source_pixels');
        const sourceWidth = Math.max(1, Number(
            metadata?.source_width || result?.video_meta?.width || videoEl?.videoWidth || 1
        ));
        const sourceHeight = Math.max(1, Number(
            metadata?.source_height || result?.video_meta?.height || videoEl?.videoHeight || 1
        ));
        const coordinateWidth = Math.max(1, Number(
            frame?.coordinate_width || metadata?.analysis_width || result?.video_meta?.width || sourceWidth
        ));
        const coordinateHeight = Math.max(1, Number(
            frame?.coordinate_height || metadata?.analysis_height || result?.video_meta?.height || sourceHeight
        ));
        const renderedRect = videoEl && videoEl.videoWidth > 0
            ? this.getVideoContentRect(videoEl)
            : { x: 0, y: 0, w: canvasEl.clientWidth || canvasEl.width, h: canvasEl.clientHeight || canvasEl.height };
        const renderedVideoRect: RenderedVideoRect = {
            x: renderedRect.x,
            y: renderedRect.y,
            width: renderedRect.w,
            height: renderedRect.h,
        };
        const canvasCssWidth = Math.max(1, Number(canvasEl.getBoundingClientRect().width || canvasEl.clientWidth || renderedRect.w));
        const canvasCssHeight = Math.max(1, Number(canvasEl.getBoundingClientRect().height || canvasEl.clientHeight || renderedRect.h));
        const devicePixelRatio = Math.max(1, canvasEl.width / canvasCssWidth);
        if (coordinateSpace === 'letterboxed_analysis_input') {
            const contentWidth = Math.max(1, Number(metadata?.content_width || coordinateWidth));
            const contentHeight = Math.max(1, Number(metadata?.content_height || coordinateHeight));
            const letterboxOffsetX = Number(metadata?.content_offset_x || 0);
            const letterboxOffsetY = Number(metadata?.content_offset_y || 0);
            const scaleX = renderedRect.w / contentWidth;
            const scaleY = renderedRect.h / contentHeight;
            return {
                coordinateSpace,
                sourceWidth,
                sourceHeight,
                coordinateWidth,
                coordinateHeight,
                letterboxOffsetX,
                letterboxOffsetY,
                offsetX: renderedRect.x - letterboxOffsetX * scaleX,
                offsetY: renderedRect.y - letterboxOffsetY * scaleY,
                scaleX,
                scaleY,
                renderedVideoRect,
                canvasCssWidth,
                canvasCssHeight,
                devicePixelRatio,
            };
        }
        const normalized = metadata?.normalized === true || frame?.normalized === true;
        return {
            coordinateSpace: normalized ? 'normalized' : coordinateSpace,
            sourceWidth,
            sourceHeight,
            coordinateWidth,
            coordinateHeight,
            letterboxOffsetX: 0,
            letterboxOffsetY: 0,
            offsetX: renderedRect.x,
            offsetY: renderedRect.y,
            scaleX: normalized ? renderedRect.w : renderedRect.w / coordinateWidth,
            scaleY: normalized ? renderedRect.h : renderedRect.h / coordinateHeight,
            renderedVideoRect,
            canvasCssWidth,
            canvasCssHeight,
            devicePixelRatio,
        };
    }
    private getVideoContentRect(videoEl: HTMLVideoElement): { x: number; y: number; w: number; h: number } {
        const rect = videoEl.getBoundingClientRect();
        const rendered = calculateContainedVideoRect(
            rect.width,
            rect.height,
            videoEl.videoWidth || 1,
            videoEl.videoHeight || 1,
        );
        return { x: rendered.x, y: rendered.y, w: rendered.width, h: rendered.height };
    }

    private clipToRenderedVideoRect(ctx: CanvasRenderingContext2D, rect: RenderedVideoRect) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(rect.x, rect.y, rect.width, rect.height);
        ctx.clip();
    }
    private drawUploadDetectionAtCurrentTime() {
        if (!this.analysisResult || this.detectionReplayPlaying) return;
        const videoEl = this.uploadPreviewVideoRef?.nativeElement;
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;
        const frames = this.getDetectionFrames(); if (frames.length === 0) return;
        let bestIdx = 0, bestDiff = Math.abs((frames[0].time_sec || 0) - currentTime);
        for (let i = 1; i < frames.length; i++) {
            const diff = Math.abs((frames[i].time_sec || 0) - currentTime);
            if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
        }
        this.detectionReplayIndex = bestIdx; this.drawDetectionFrame(bestIdx);
    }

    public onUploadVideoTimeUpdate() {
        this.drawUploadDetectionAtCurrentTime();
    }
    public drawBboxesOnCanvas(canvas: HTMLCanvasElement, detections: any[], w: number, h: number) {
        const ctx = canvas.getContext('2d'); if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!this.bboxOverlayEnabled) return;
        for (const det of detections) {
            const color = det?.is_primary ? '#22c55e' : '#22d3ee';
            ctx.strokeStyle = color; ctx.lineWidth = det?.is_primary ? 2.5 : 2;
            ctx.strokeRect(det.x1 * w, det.y1 * h, (det.x2 - det.x1) * w, (det.y2 - det.y1) * h);
            if (det?.person_label) {
                ctx.fillStyle = color; ctx.font = '11px sans-serif';
                ctx.fillText(String(det.person_label), det.x1 * w + 3, Math.max(12, det.y1 * h - 4));
            }
        }
    }

    public async openRiskAlertWorkflow() {
        if (!this.analysisResult || !this.analysisSavedName) return;
        try {
            const displayedRisk = this.analysisResult.displayed_risk_score ?? this.analysisResult.event_risk_score ?? this.analysisResult.raw_model_risk_score ?? this.analysisResult.risk_score ?? 0;
            const params = {
                saved_name: this.analysisSavedName,
                risk_level: this.analysisResult.risk_level || 'low',
                risk_score: String(displayedRisk),
                displayed_risk_score: this.analysisResult.displayed_risk_score == null ? '' : String(this.analysisResult.displayed_risk_score),
                event_risk_score: this.analysisResult.event_risk_score == null ? '' : String(this.analysisResult.event_risk_score),
                raw_model_risk_score: String(this.analysisResult.raw_model_risk_score ?? this.analysisResult.model_output?.risk_score ?? this.analysisResult.risk_score ?? 0),
                final_state: this.analysisResult.final_event_state || String(this.analysisResult.decision_state || '').toUpperCase(),
                risk_label: this.analysisResult.risk_label || '',
                summary: this.analysisResult.summary || '',
                fall_detected: String(this.analysisResult.fall_detected || false),
            };
            const { code, data } = await wiz.call('dispatch_risk_alerts', params);
            if (code === 200) {
                this.alertWorkflow = data;
                if (data?.emergency?.popup_required) {
                    this.emergencyPopupOpen = true;
                    if (this.inputMode === 'webcam' && this.realtimeFeedSignalStarted('emergency_protocol', true)) {
                        this.appendRealtimeFeedEvent({
                            type: 'emergency_protocol',
                            tone: this.realtimeFeedTone(this.analysisResult),
                            icon: '🚨',
                            title: '응급 프로토콜 실행',
                            description: '현재 사건에 대한 알림 및 응급 대응 절차를 실행했습니다.',
                            riskPercent: this.realtimeCurrentRiskPercent(),
                            showRisk: true,
                        }, null, this.realtimeChunkCount, null);
                    }
                }
            }
        } catch (e) { }
        await this.service.render();
    }
    public closeEmergencyPopup() { this.emergencyPopupOpen = false; }
    public async openReferencePopup(sceneId: string) {
        this.referencePopupLoading = true; this.referencePopupOpen = true; await this.service.render();
        try {
            const { code, data } = await wiz.call('reference_preview_info', { scene_id: sceneId });
            if (code === 200) this.referencePopupData = data;
        } catch (e) { }
        this.referencePopupLoading = false;
        await this.service.render();
    }
    public closeReferencePopup() { this.referencePopupOpen = false; this.referencePopupData = null; }
    public goPipelinePage() { this.service.href('/pipeline'); }
    public goManualPage() { this.service.href('/manual'); }
    public goAdminPage() { this.service.href('/admin/analysis'); }
}

export default PageDashboardComponent;